// Zenioo Integration Version 2.0 - Forced re-compile
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environmentCAS';

// PrimeNG imports
import { StepsModule } from 'primeng/steps';
import { MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SliderModule } from 'primeng/slider';
import { PanelModule } from 'primeng/panel';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AccordionModule } from 'primeng/accordion';
import { TabViewModule } from 'primeng/tabview';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MarkdownModule } from 'ngx-markdown';
import { FormsModule } from '@angular/forms';

// App services and models
import { CompareService } from '../../services/compare.service';
import { InsuranceService } from '../../services/insurance.service';
import { AlptisService } from '../../services/alptis.service';
import { GeneraliService } from '../../services/generali.service';
import { GeneraliSanteProService } from '../../services/generali-sante-pro.service';
import { NeolianeService, NeolianeProduct } from '../../services/neoliane.service';
import { NeolianeV2Service, NeolianeV2ProfileRequest, NeolianeV2QuotationRequest } from '../../services/neoliane-v2.service';
import { ValueAnalyzerService, SliderConfig, ContractAnalysis } from '../../services/value-analyzer.service';
import { ProximityService } from '../../services/proximity.service';
import { DeuxMaService } from '../../services/deuxma.service';
import { SwissLifeService } from '../../services/swisslife.service';
import { FormService } from '../../services/form.service';
import { ContractsService } from '../../services/contracts.service';
import { ComparisonHistoryService } from '../../services/comparison-history.service';
import { BesoinClient } from '../../models/comparateur.model';
import { InsuranceQuoteForm, InsuredPerson } from '../../models/project-model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { startWith, finalize, debounceTime, catchError, switchMap, map, distinctUntilChanged, timeout } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { AprilService } from '../../services/april.service';
import { ApicilService, ApicilTarificationRequest, ApicilBeneficiaire } from '../../services/apicil.service';
import { CommissionSettingsService } from '../../services/commission-settings.service';
import { CAS_COMMISSION_LIST, normalizeCasCommission } from '../../constants/cas-commission';
import { mapToAprilMandatoryScheme } from '../../constants/april-mandatory-scheme';
import { ApiMapperUtil } from '../../utils/api-mapper.util';
import { PricingFeedbackUtil, PricingUnavailableReason } from '../../utils/pricing-feedback.util';
import { RingoverService } from '../../../../shared/services/ringover.service';

/** Taux CAS par défaut si aucun n’est défini dans les paramètres (tableau / admin commissions). */
const CAS_COMMISSION_FALLBACK = '15/15';

interface ApiviaBeneficiaire {
  typeBeneficiaire: string;
  dateDeNaissance: string;
  typeRegime: string;
  regimeSocial: string;
}

interface GuaranteeValues {
  hospitalisation: number | string;
  honoraires: number | string;
  chambreParticuliere: number | string;
  dentaire: number | string;
  orthodontie: number | string;
  forfaitDentaire: number | string;
  forfaitOptique: number | string;
  medecineComplementaire: number | string;
}

interface ComparisonResult {
  assurance: string;
  formule: string;
  logo: string;
  prix: string | number;
  garanties: GuaranteeValues;
  correspondencePercentage: number;
  weakPoint: string;
  isAprilProduct?: boolean;
  isPricingLoading?: boolean;
  tarifGlobal?: number;
  /** Code / libellé assureur (ex. merge backend contrats). */
  insurer?: string;
  alptisProjectId?: string;
  zeniooCodeProduit?: string;
  zeniooCodeFormule?: string;
  zeniooTauxCommission?: string;
  utwinTauxCommission?: string;  // ✅ Ajouté pour UTWIN
  productId?: string;  // ✅ Ajouté pour Néoliane
  formulaId?: string;  // ✅ Ajouté pour Néoliane
  selectedCommission?: string; // ✅ Ajouté pour la commission spécifique
  apicilPrestations?: any[];
  contractName?: string;
  /** Explication affichée quand le tarif est N/A ou Erreur */
  pricingDetail?: string;
}

interface CommissionOption {
  label: string;
  value: string;
}

interface GarantieDefinition {
  label: string;
  formControlName: keyof GuaranteeValues;
  unit: '%' | '€';
}

interface EmailValidationResult {
  valid: boolean;
  reason?: string;
  message?: string;
}

interface EmailValidationState {
  checking: boolean;
  valid: boolean | null;
  message: string;
}

@Component({
  selector: 'app-compare',
  templateUrl: './compare.component.html',
  styleUrls: ['./compare.component.scss'],
  standalone: true,
  imports: [
    CommonModule, HttpClientModule, ReactiveFormsModule, FormsModule, StepsModule, ToastModule, SliderModule, PanelModule,
    DropdownModule, RadioButtonModule, CalendarModule, InputTextModule, InputMaskModule, ButtonModule, DividerModule,
    TooltipModule, InputNumberModule, CheckboxModule, CardModule, ProgressSpinnerModule, AccordionModule, TabViewModule,
    MessageModule, DialogModule, ConfirmDialogModule, MarkdownModule
  ],
  providers: [ConfirmationService]
})
export class CompareComponent implements OnInit, OnDestroy {
  loading: boolean = false;
  comparisonExecutionDate: Date | null = new Date();

  /**
   * Souscriptions du composant, liberees dans `ngOnDestroy`.
   *
   * ANOMALIE CORRIGEE : le composant s'abonnait aux `valueChanges` du
   * formulaire, aux query params et aux dates de naissance des enfants sans
   * jamais se desabonner. En navigant plusieurs fois vers /compare, les
   * anciens abonnements restaient actifs et continuaient a ecrire dans les
   * champs du composant detruit.
   */
  private subscriptions: Subscription[] = [];

  /** Souscriptions aux dates de naissance des enfants, liberees a la destruction. */
  private enfantsSubscriptions: Subscription[] = [];

  // Âges calculés de façon réactive (fonctionnent dans tous les modes de build)
  mainInsuredAge: number | null = null;
  conjointAge: number | null = null;
  enfantsAges: (number | null)[] = [];

  /**
   * Le bloc « Informations du conjoint » est-il ouvert ?
   *
   * Fermé par défaut : la plupart des dossiers n'ont pas de conjoint, et six
   * champs vides à parcourir pour rien alourdissaient la saisie. Il s'ouvre
   * par le bouton « Ajouter un conjoint », ou automatiquement quand un projet
   * rechargé en comporte un.
   */
  aConjoint = false;

  /** Âge de l'assuré principal - getter comme dans le CAC */
  get ageAssurePrincipal(): number | null {
    const naissance = this.insuranceForm?.get('personalInfo.dateNaissance')?.value;
    if (!naissance) return null;
    return this.calculateAgeFromValue(naissance);
  }

  /** Âge du conjoint - getter comme dans le CAC */
  get ageConjoint(): number | null {
    const naissance = this.insuranceForm?.get('personalInfo.conjoint.dateNaissance')?.value;
    if (!naissance) return null;
    return this.calculateAgeFromValue(naissance);
  }

  /** Âge d'un enfant par index - méthode directe */
  getAgeEnfant(index: number): number | null {
    const naissance = this.enfants?.at(index)?.get('dateNaissance')?.value;
    if (!naissance) return null;
    return this.calculateAgeFromValue(naissance);
  }

  getCalculatedAge(fieldPath: string): number | null {
    const value = this.insuranceForm?.get(fieldPath)?.value;
    if (!value) return null;
    return this.calculateAgeFromValue(value);
  }

  getEnfantCalculatedAge(index: number): number | null {
    const enfantControl = this.enfants?.at(index);
    const value = enfantControl?.get('dateNaissance')?.value;
    if (!value) return null;
    return this.calculateAgeFromValue(value);
  }

  private calculateAgeFromValue(value: any): number | null {
    if (!value) return null;
    let birthDate: Date | null = null;

    if (value instanceof Date) {
      birthDate = value;
    } else if (typeof value === 'string') {
      const str = value.trim();
      if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year) && year >= 1900 && year <= new Date().getFullYear()) {
            birthDate = new Date(year, month - 1, day);
          }
        }
      } else if (str.includes('-')) {
        const d = new Date(str);
        if (!isNaN(d.getTime())) birthDate = d;
      } else if (str.length === 8 && !isNaN(Number(str))) {
        const day = parseInt(str.substring(0, 2), 10);
        const month = parseInt(str.substring(2, 4), 10);
        const year = parseInt(str.substring(4, 8), 10);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && year >= 1900 && year <= new Date().getFullYear()) {
          birthDate = new Date(year, month - 1, day);
        }
      }
    }

    if (!birthDate || isNaN(birthDate.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  }

  /** Nom complet du client - getter comme dans le CAC */
  get clientFullName(): string {
    const personal = this.insuranceForm?.get('personalInfo')?.value;
    if (!personal) return '';
    const civilite = personal.civilite ? personal.civilite + ' ' : '';
    const nom = personal.nom ? personal.nom.toUpperCase() : '';
    const prenom = personal.prenom ? personal.prenom : '';
    const full = `${civilite}${nom} ${prenom}`.trim();
    return full || '';
  }

  // Kept for backwards compatibility
  getClientFullName(): string { return this.clientFullName; }

  // === Commission Panel Methods (Like CAC) ===
  showCommissionPanel = false;
  commissionItems: any[] = [];
  hasChanges = false;
  canManageCommissions = true;
  originalCommissions = new Map<string, string>();

  toggleCommissionPanel(): void {
    this.showCommissionPanel = !this.showCommissionPanel;
    if (this.showCommissionPanel) {
      this.initCommissionItems();
    }
  }

  initCommissionItems(): void {
    this.commissionItems = [];
    this.hasChanges = false;
    this.originalCommissions.clear();

    for (const result of this.results) {
      const insurer = result.insurer || result.assurance;
      const opts = this.getCommissionOptionsForResult(result);
      const val = result.selectedCommission || opts[0]?.value;

      this.originalCommissions.set(insurer, val);

      this.commissionItems.push({
        id: insurer,
        insurer: insurer,
        result: result,
        options: opts,
        selectedValue: val,
        originalValue: val
      });
    }
  }

  onCommissionSelect(item: any, value: string): void {
    item.selectedValue = value;
    this.checkCommissionChanges();
  }

  isItemChanged(item: any): boolean {
    return item.selectedValue !== item.originalValue;
  }

  checkCommissionChanges(): void {
    this.hasChanges = this.commissionItems.some(i => i.selectedValue !== i.originalValue);
  }

  resetCommissions(): void {
    for (const item of this.commissionItems) {
      item.selectedValue = item.originalValue;
    }
    this.hasChanges = false;
  }

  trackByKey(index: number, item: any): string {
    return item.id;
  }

  rerunWithNewCommissions(): void {
    if (!this.hasChanges) return;
    const changedItems = this.commissionItems.filter(i => i.selectedValue !== i.originalValue);
    
    for (const item of changedItems) {
      item.result.selectedCommission = item.selectedValue;
      item.originalValue = item.selectedValue;
      this.onFormuleSelect(item.result, item.result.formule);
    }
    
    this.hasChanges = false;
    this.showCommissionPanel = false;
  }


  insuranceForm!: FormGroup;
  activeIndex: number = 0;
  smsRecipientPhone: string = '';
  submitting: boolean = false;
  loadingQuote: boolean = false;
  isComparing: boolean = false;
  results: any[] = [];
  comparisonResults: any[] = [];
  an = false;
  private comparisonSubscription: any = null;
  private priceCheckTimeout: any = null;
  private historyAlreadySaved = false; // Flag pour éviter les doublons d'enregistrement

  // Variables du Mail Économiser (CAS)
  showEconomiserEmailDialog = false;
  economiserClientName = '';
  economiserCivility: '' | 'Monsieur' | 'Madame' = 'Monsieur';
  economiserRecipientEmail = '';
  economiserAssurance = '';
  economiserRef = '';
  economiserProduit = '';
  sendingEconomiserEmail = false;
  // PDF extraction properties
  selectedPdfFile: File | null = null;
  /** PDF(s) utilisés pour la comparaison — joints à l'email de devis */
  private comparisonAttachmentFiles: File[] = [];
  selectedInsurer: string = '';
  selectedLevelName: string = '';
  isExtractingPdf: boolean = false;
  pdfExtractionMessage: { type: 'success' | 'error' | 'info' | 'warn', text: string } | null = null;
  availableInsurers: { label: string, value: string }[] = [];
  availableLevels: { label: string, value: string }[] = [];
  contractsData: any[] = [];
  neolianeProducts: NeolianeProduct[] | null = null;

  // Nouvelles propriétés pour l'analyse automatique des valeurs
  extractedContract: any = null;
  contractAnalysis: ContractAnalysis | null = null;
  dynamicSliders: SliderConfig[] = [];
  isAnalyzingValues: boolean = false;
  showDynamicSliders: boolean = false;
  slidersByCategory: { [category: string]: SliderConfig[] } = {};
  steps: MenuItem[] = [];
  aprilPricingPayload: any = null;
  garanties: GarantieDefinition[] = [];
  utwinMessages: string[] = [];
  private shownToastKeys: Set<string> = new Set<string>();
  emailValidationState: EmailValidationState = { checking: false, valid: null, message: '' };
  coEmailValidationState: EmailValidationState = { checking: false, valid: null, message: '' };

  casCommissionOptions: CommissionOption[] = CAS_COMMISSION_LIST.map(c => ({ label: c, value: c }));
  private readonly zeniooCommissionRules: Record<string, { default: string; allowed: string[] }> = {
    ZSFO55: { default: '30/10L 24L', allowed: ['15/15L'] },
    ZSS2: { default: '30/10', allowed: ['15/15', '17/17', '35/10', '40/10', '45/10'] },
    ZSFOF: { default: '30/10L 24L', allowed: ['15/15L'] },
    ZSEVO: {
      default: '30/10L 24L',
      allowed: ['15/15L', '17/17P 12L', '18/18P 12L', '20/20P 12L', '35/10P 18L', '40/10P 18L', '35/12P 18L', '38/13P 18L', '16/16P 12L', '19/19P 12L', '20/15/25P 12L', '18/15/20P 12L', '30/10P 24L']
    },
    ZSOPT: {
      default: '30/10L 24L',
      allowed: ['15/15L', '17/17P 12L', '18/18P 12L', '20/20P 12L', '16/16P 12L', '19/19P 12L', '30/10P 24N', '35/10P 24N', '40/10P 24N']
    },
    ZSPX: { default: '30/10L 24L', allowed: ['15/15L', '16/16L', '17/17L', '18/18L'] },
    ZSC2: { default: '30/10', allowed: ['15/15', '17/17', '35/10', '40/10', '45/10'] },
    ZSPE2: { default: '30/10', allowed: [] },
    ZSI55: { default: '15/15L', allowed: ['15/15', '17/17', '18/18', '20/20', '30/10', '35/10', '40/10', '45/10', '10/10', '25/15'] },
    ZSIF: { default: '30/10 24L', allowed: ['15/15L'] },
    ZSPP: { default: '30/10', allowed: ['10/10', '15/15', '20/20', '25/15'] },
    ZSE: { default: '30/10', allowed: ['15/15', '22/22', '35/10'] },
    ZSFS: { default: '15/15', allowed: [] },
    ZSEQ: { default: '30/10', allowed: ['15/15'] }
  };

  civiliteOptions = [{ label: 'Monsieur', value: 'M' }, { label: 'Madame', value: 'F' }];
  regimeOptions = [
    { label: 'Sécurité Sociale des Indépendants', value: 'TNS' },
    { label: 'Profession Libérale', value: 'PROF_LIBE' },
    { label: 'Régime Général', value: 'REGIME_GENERAL' },
    { label: 'Professions Médicales et Non Médicales Non Salariées', value: 'PROF_MED_NON_MED_NON_SAL' },
    { label: 'Sécurité Sociale', value: 'SS' }
  ];
  EtatcivilOptions = [
    { label: 'Célibataire', value: 'celibataire' }, { label: 'Marié(e)', value: 'marie' },
    { label: 'Parent isolé', value: 'parentIsole' }, { label: 'Séparé(e)', value: 'separe' },
    { label: 'Union libre', value: 'unionLibre' }, { label: 'Veuf(ve)', value: 'veuf' },
    { label: 'Non déclaré', value: 'situationNonDeclaree' }
  ];
  sexeOptions = [{ label: 'Garçon', value: 'garcon' }, { label: 'Fille', value: 'fille' }];

  // Options pour Alptis
  categorieSocioProfessionnelleOptions = [
    { label: 'Artisans', value: 'ARTISANS' },
    { label: 'Commerçants et assimilés', value: 'COMMERCANTS_ET_ASSIMILES' },
    { label: 'Professions libérales et assimilés', value: 'PROFESSIONS_LIBERALES_ET_ASSIMILES' },
    { label: 'Chefs d\'entreprise', value: 'CHEFS_D_ENTREPRISE' }
  ];

  statutProfessionnelOptions = [
    { label: 'Professions libérales', value: 'PROFESSIONS_LIBERALES' },
    { label: 'Artisan commerçant', value: 'ARTISAN_COMMERCANT' }
  ];
  minDate: Date | null = null;
  maxDate: Date | null = null;

  // Variables pour le dialog des points faibles
  showWeakPointDialog: boolean = false;
  selectedWeakPointDetails: any = null;

  // Mapping des logos des assureurs
  insurerLogos: { [key: string]: string } = {
    '2ma': 'assets/images/logos/2MA.PNG',
    'aesio': 'assets/images/logos/AESIO.PNG',
    'alptis': 'assets/images/logos/ALPTIS.PNG',
    'apicil': 'assets/images/logos/APICIL.PNG',
    'apivia': 'assets/images/logos/APIVIA.png',
    'april': 'assets/images/logos/APRIL.PNG',
    'asaf': 'assets/images/logos/ASAF.PNG',
    'axa': 'assets/images/logos/AXA.PNG',
    'entoria': 'assets/images/logos/ENTORIA.PNG',
    'generali': 'assets/images/logos/GENERALI.PNG',
    'harmonie': 'assets/images/logos/HARMONIE.PNG',
    'malakoff': 'assets/images/logos/MALAKOFF.PNG',
    'henner': 'assets/images/logos/HENNER.PNG',
    'solly': 'assets/images/logos/SOLLYAZAR.PNG',
    'spvie': 'assets/images/logos/SPIVE.PNG',
    'swisslife': 'assets/images/logos/SWISSLIFE.PNG',
    'utwin': 'assets/images/logos/UTWIN.PNG',
    'neoliane': 'assets/images/logos/NEOLIANE.PNG',
    'zenioo': 'assets/images/logos/ZENIO.PNG',
    'eca': 'assets/images/logos/ECA.PNG',
    'smatis': 'assets/images/logos/SMATIS.PNG',
    'mutualia': 'assets/images/logos/MUTUALIA.PNG',
    'miltis': 'assets/images/logos/MILTIS.PNG'
  };

  // SMS
  showSmsDialog = false;
  smsType: 'rdv' | 'devis_envoye' | 'relance' | '' = '';
  smsContent = '';
  rdvTime = '14H30';
  sendingSms = false;

  constructor(
    private fb: FormBuilder,
    private compareService: CompareService,
    private insuranceService: InsuranceService,
    private alptisService: AlptisService,
    private generaliService: GeneraliService,
    private generaliSanteProService: GeneraliSanteProService,
    private neolianeService: NeolianeService,
    private neolianeV2Service: NeolianeV2Service,
    private valueAnalyzerService: ValueAnalyzerService,
    private proximityService: ProximityService,
    private contractsService: ContractsService,
    private comparisonHistoryService: ComparisonHistoryService,
    private messageService: MessageService,
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private aprilService: AprilService,
    private commissionSettingsService: CommissionSettingsService,
    private apicilService: ApicilService,
    private ringoverService: RingoverService,
    private deuxMaService: DeuxMaService,
    private swissLifeService: SwissLifeService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) {
    const today = new Date();
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach(souscription => souscription.unsubscribe());
    this.subscriptions = [];
    this.enfantsSubscriptions.forEach(souscription => souscription.unsubscribe());
    this.enfantsSubscriptions = [];
    this.comparisonSubscription?.unsubscribe?.();
  }

  /**
   * Remet un numero venant de l'agenda au format attendu par
   * `phoneNumberValidator` (0XXXXXXXXX ou +33XXXXXXXXX).
   *
   * Sans cela un numero transmis avec des espaces ou des points par n8n
   * arrivait tel quel dans le champ et le formulaire s'ouvrait deja en erreur.
   */
  private normalizePhoneParam(valeur: any): string {
    const brut = (valeur ?? '').toString().replace(/[^\d+]/g, '');
    if (brut.startsWith('+33')) {
      return '0' + brut.slice(3);
    }
    if (brut.startsWith('33') && brut.length === 11) {
      return '0' + brut.slice(2);
    }
    return brut;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.updateSteps();
    this.setupGaranties();
    this.setupConjointListener();
    this.setupEmailValidationListeners();
    this.setupPrincipalLastNameSync();
    this.setupPrincipalCiviliteSync();

    // Check query params to prefill from Agenda
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        if (params['nom'] || params['prenom'] || params['telephone'] || params['email']) {
          const personalInfo = this.insuranceForm.get('personalInfo');

          if (personalInfo) {
            if (params['nom']) personalInfo.get('nom')?.setValue(params['nom']);
            if (params['prenom']) personalInfo.get('prenom')?.setValue(params['prenom']);
            if (params['civilite']) {
              let civ = params['civilite'].toUpperCase().replace(/[^A-Z]/g, '');
              if (civ === 'MR' || civ === 'MONSIEUR' || civ === 'M') {
                personalInfo.get('civilite')?.setValue('M');
              } else if (civ === 'MME' || civ === 'MADAME' || civ === 'F' || civ === 'MADEMOISELLE') {
                personalInfo.get('civilite')?.setValue('F');
              }
              // ANOMALIE CORRIGEE : le `else` recopiait la valeur brute du
              // parametre d'URL dans le controle. Une civilite non reconnue
              // posait donc une valeur absente de `civiliteOptions` : le
              // dropdown paraissait vide mais le champ etait valide.
            }

            // ANOMALIE CORRIGEE : le telephone et l'email etaient ecrits dans
            // un groupe `contactInfo` qui n'existe pas dans ce formulaire
            // (seuls `personalInfo` et `coverageSliders` sont declares).
            // `insuranceForm.get('contactInfo')` renvoyait null, la condition
            // etait donc toujours fausse et la reprise depuis l'agenda perdait
            // silencieusement les coordonnees du prospect.
            if (params['telephone']) {
              personalInfo.get('telephone1')?.setValue(this.normalizePhoneParam(params['telephone']));
            }
            if (params['email']) {
              personalInfo.get('email')?.setValue(params['email']);
            }
          }
        }
      })
    );

    // Check for loaded comparison from history
    const loadedComp = this.comparisonHistoryService.getComparisonToLoad();
    if (loadedComp) {
      setTimeout(() => {
        this.prefillFromHistory(loadedComp);
        this.comparisonHistoryService.clearComparisonToLoad();
      });
    }

    // Charger les contrats depuis le fichier JSON
    this.contractsService.loadContracts().subscribe({
      next: (contracts) => {
        console.log(`✅ ${contracts.length} contrats chargés avec succès`);
        // Afficher le résumé des contrats disponibles
        this.contractsService.getContractsSummary();
      },
      error: (error) => {
        console.error('❌ Erreur lors du chargement des contrats:', error);
      }
    });

    // Précharger la liste des produits / formules Néoliane (pour le mapping produit/formule -> IDs)
    this.neolianeService.getProducts().subscribe({
      next: (products) => {
        console.log(`✅ Produits Néoliane chargés: ${products.length}`);
        this.neolianeProducts = products;
      },
      error: (error) => {
        console.error('❌ Erreur lors du chargement des produits Néoliane:', error);
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupPostalCodeListener();
    this.setupGaranties();
    this.initializeAvailableInsurers();

    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state as { insuranceData: InsuranceQuoteForm };

    if (state?.insuranceData) {
      const data = state.insuranceData;
      const effectDate = new Date(data.effectDate);
      const mainInsured = data.insuredPersons.find(p => {
        const birth = new Date(p.birthDate);
        let age = effectDate.getFullYear() - birth.getFullYear();
        const monthDiff = effectDate.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && effectDate.getDate() < birth.getDate())) {
          age--;
        }
        return age >= 18;
      }) || data.insuredPersons[0];

      this.insuranceForm.patchValue({
        personalInfo: {
          civilite: mainInsured.gender,
          nom: mainInsured.lastName,
          prenom: mainInsured.firstName,
          dateNaissance: new Date(mainInsured.birthDate),
          email: mainInsured.email,
          telephone1: mainInsured.phoneNumber,
          codePostal: mainInsured.address.postalCode,
          regime: mainInsured.regime,
          dateEffet: new Date(data.effectDate),
          etatCivil: mainInsured.situation
        }
      });

      this.submitComparison();
    }
  }

  initializeForm(): void {
    // Calculer la date d'effet par défaut (Date du jour + 35 jours)
    const today = new Date();
    const defaultEffectDate = new Date(today);
    defaultEffectDate.setDate(today.getDate() + 35);

    const formatDefaultDate = (date: Date): string => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    this.insuranceForm = this.fb.group({
      personalInfo: this.fb.group({
        // ANOMALIE CORRIGEE : la valeur par defaut etait 'Monsieur', qui n'existe
        // pas dans civiliteOptions (les valeurs sont 'M' / 'F'). Le p-dropdown
        // affichait donc "- Choisir -" alors que le controle etait rempli et
        // valide, et 'Monsieur' partait vers les assureurs la ou tout le reste
        // du code teste `civilite === 'M'`.
        civilite: ['M', Validators.required],
        nom: ['', [Validators.required, this.requiredTrimmedValidator, this.noSpecialCharactersValidator]],
        prenom: ['', [Validators.required, this.requiredTrimmedValidator, this.noSpecialCharactersValidator]],
        // ANOMALIE CORRIGEE : dateFormatValidator accepte toute annee entre 1920
        // et 2030, donc une date de naissance dans le futur passait la
        // validation et remontait un age negatif aux assureurs.
        dateNaissance: ['', [Validators.required, this.dateFormatValidator, this.pastDateValidator]],
        codePostalNaissance: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
        villeNaissance: ['', [Validators.required, this.requiredTrimmedValidator, this.noSpecialCharactersValidator]],
        adresse: ['', [Validators.required, this.requiredTrimmedValidator, Validators.minLength(5)]],
        complementAdresse: [''],
        codePostal: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
        ville: ['', [Validators.required, this.requiredTrimmedValidator, this.noSpecialCharactersValidator]],
        pays: ['France', Validators.required],
        dateEffet: [formatDefaultDate(defaultEffectDate), [Validators.required, this.dateFormatValidator]],
        email: ['', [Validators.required, Validators.email]],
        telephone1: ['', [Validators.required, this.phoneNumberValidator]],
        etatCivil: [null, Validators.required],
        regime: [null, Validators.required],
        categorieSocioProfessionnelle: [null, Validators.required],
        statutProfessionnel: [null],
        conjoint: this.fb.group({
          civilite: [''],
          nom: ['', [this.requiredTrimmedIfFilledValidator(), this.noSpecialCharactersValidator]],
          prenom: ['', [this.requiredTrimmedIfFilledValidator(), this.noSpecialCharactersValidator]],
          email: ['', Validators.email],
          dateNaissance: ['', [this.dateFormatValidator, this.pastDateValidator]],
          regime: ['']
        }, { validators: this.conjointConsistencyValidator() }),
        enfants: this.fb.array([]),
        termination: this.fb.group({
          hasFormerContract: [false],
          formerInsurer: [''],
          formerContractReference: [''],
          isInsuredFor12Months: [true] // Par défaut "oui"
        })
      }),
      coverageSliders: this.fb.group({
        hospitalisation: [0], chambreParticuliere: [50], honoraires: [0],
        dentaire: [0], orthodontie: [0], forfaitDentaire: [100], forfaitOptique: [100],
        medecineComplementaire: [0],
      }),
    });

    // --- Souscriptions réactives pour le calcul de l'âge ---
    this.insuranceForm.get('personalInfo.dateNaissance')?.valueChanges.subscribe(val => {
      this.mainInsuredAge = this.calculateAgeFromValue(val);
    });
    this.insuranceForm.get('personalInfo.conjoint.dateNaissance')?.valueChanges.subscribe(val => {
      this.conjointAge = this.calculateAgeFromValue(val);
    });
    // Calcul initial (si valeur pré-remplie dès le chargement)
    this.mainInsuredAge = this.calculateAgeFromValue(
      this.insuranceForm.get('personalInfo.dateNaissance')?.value
    );
    this.conjointAge = this.calculateAgeFromValue(
      this.insuranceForm.get('personalInfo.conjoint.dateNaissance')?.value
    );
  }

  setupConjointListener(): void {
    const conjointGroup = this.insuranceForm.get('personalInfo.conjoint');
    if (conjointGroup) {
      conjointGroup.enable();
      const nomControl = conjointGroup.get('nom');
      const prenomControl = conjointGroup.get('prenom');
      const dateNaissanceControl = conjointGroup.get('dateNaissance');
      const regimeControl = conjointGroup.get('regime');
      const emailControl = conjointGroup.get('email');

      // ANOMALIE CORRIGEE : `setValidators` REMPLACE la liste posee dans
      // `initializeForm`. Cette methode, appelee juste apres, effacait donc
      // `requiredTrimmedIfFilledValidator` sur le nom et le prenom (un conjoint
      // saisi avec des espaces uniquement redevenait accepte) et
      // `pastDateValidator` sur la date de naissance. Les validateurs sont
      // desormais ajoutes aux existants.
      if (nomControl) {
        nomControl.addValidators([this.noSpecialCharactersValidator.bind(this)]);
        nomControl.updateValueAndValidity({ emitEvent: false });
      }

      if (prenomControl) {
        prenomControl.addValidators([this.noSpecialCharactersValidator.bind(this)]);
        prenomControl.updateValueAndValidity({ emitEvent: false });
      }

      if (dateNaissanceControl) {
        dateNaissanceControl.addValidators([this.dateFormatValidator.bind(this)]);
        dateNaissanceControl.updateValueAndValidity({ emitEvent: false });
      }

      if (regimeControl) {
        regimeControl.clearValidators();
        regimeControl.updateValueAndValidity();
      }

      if (emailControl) {
        emailControl.setValidators([Validators.email]);
        emailControl.updateValueAndValidity();
      }
    }
  }

  private setupEmailValidationListeners(): void {
    const mainEmailControl = this.insuranceForm.get('personalInfo.email');
    const conjointEmailControl = this.insuranceForm.get('personalInfo.conjoint.email');

    if (mainEmailControl) {
      mainEmailControl.valueChanges.pipe(
        startWith(mainEmailControl.value),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: string | null) => {
          const email = (value || '').trim();
          this.clearCustomError(mainEmailControl, 'abstractEmail');
          this.setEmailValidationState(false, false, null, '');
          this.enforceBorrowerEmailsDifference();

          if (!email) {
            return of(null);
          }

          if (!this.isBasicEmailFormat(email)) {
            this.setEmailValidationState(false, false, false, 'Format email invalide.');
            return of(null);
          }

          return this.validateEmailWithProvider(false, email);
        })
      ).subscribe();
    }

    if (conjointEmailControl) {
      conjointEmailControl.valueChanges.pipe(
        startWith(conjointEmailControl.value),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: string | null) => {
          const email = (value || '').trim();
          this.clearCustomError(conjointEmailControl, 'abstractEmail');
          this.setEmailValidationState(true, false, null, '');
          const emailsAreDifferent = this.enforceBorrowerEmailsDifference();

          if (!emailsAreDifferent || !email) {
            return of(null);
          }

          if (!this.isBasicEmailFormat(email)) {
            this.setEmailValidationState(true, false, false, 'Format email invalide.');
            return of(null);
          }

          return this.validateEmailWithProvider(true, email);
        })
      ).subscribe();
    }
  }

  private setupPrincipalLastNameSync(): void {
    const mainLastNameControl = this.insuranceForm.get('personalInfo.nom');
    if (!mainLastNameControl) {
      return;
    }

    mainLastNameControl.valueChanges.pipe(startWith(mainLastNameControl.value)).subscribe((value: string | null) => {
      const principalLastName = (value || '').trim();
      if (!principalLastName) {
        return;
      }

      // Uniquement si un conjoint est réellement déclaré : écrire dans un bloc
      // fermé le faisait passer pour « commencé » auprès du validateur.
      const conjointLastNameControl = this.insuranceForm.get('personalInfo.conjoint.nom');
      if (this.aConjoint && conjointLastNameControl) {
        conjointLastNameControl.setValue(principalLastName, { emitEvent: false });
      }

      this.enfants.controls.forEach((enfantControl) => {
        const enfantLastNameControl = enfantControl.get('nom');
        if (enfantLastNameControl) {
          enfantLastNameControl.setValue(principalLastName, { emitEvent: false });
        }
      });
    });
  }

  private setupPrincipalCiviliteSync(): void {
    const principalCiviliteControl = this.insuranceForm.get('personalInfo.civilite');
    const conjointCiviliteControl = this.insuranceForm.get('personalInfo.conjoint.civilite');
    if (!principalCiviliteControl || !conjointCiviliteControl) {
      return;
    }

    // ANOMALIE CORRIGEE : le `startWith` declenchait la synchronisation des le
    // chargement de la page. La civilite du conjoint etait donc pre-remplie
    // alors qu'aucun conjoint n'etait saisi, ce qui rendait le groupe conjoint
    // "commence" et bloquait tout le formulaire (cf. conjointConsistencyValidator).
    // La civilite n'est plus deduite que si un conjoint est effectivement en
    // cours de saisie.
    this.subscriptions.push(
      principalCiviliteControl.valueChanges.subscribe((value: string | null) => {
        const normalized = (value || '').toString().trim().toUpperCase();
        let inverse = '';

        if (normalized === 'M' || normalized === 'MONSIEUR') {
          inverse = 'F';
        } else if (normalized === 'F' || normalized === 'MADAME') {
          inverse = 'M';
        } else {
          return;
        }

        const conjointGroup = this.insuranceForm.get('personalInfo.conjoint');
        if (!conjointGroup || !this.conjointFieldsFilled(conjointGroup).rempli) {
          return;
        }

        conjointCiviliteControl.setValue(inverse, { emitEvent: false });
      })
    );

    // Des que le conjoint commence a etre saisi, on lui pose la civilite
    // inverse de celle du principal (comportement d'origine, mais declenche
    // par la saisie du conjoint et non par le chargement de la page).
    const conjointGroup = this.insuranceForm.get('personalInfo.conjoint');
    if (conjointGroup) {
      this.subscriptions.push(
        conjointGroup.valueChanges.subscribe(() => {
          if (conjointCiviliteControl.value) {
            return;
          }
          if (!this.conjointFieldsFilled(conjointGroup).rempli) {
            return;
          }
          const normalized = (principalCiviliteControl.value || '')
            .toString()
            .trim()
            .toUpperCase();
          if (normalized === 'M' || normalized === 'MONSIEUR') {
            conjointCiviliteControl.setValue('F', { emitEvent: false });
          } else if (normalized === 'F' || normalized === 'MADAME') {
            conjointCiviliteControl.setValue('M', { emitEvent: false });
          }
        })
      );
    }
  }

  private validateEmailWithProvider(isCoBorrower: boolean, email: string) {
    this.setEmailValidationState(isCoBorrower, true, null, 'Verification email en cours...');
    const casValidationUrl = `${environment.casApiUrl}/api/v1/devis/email/validate`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    const applyValidationResult = (result: EmailValidationResult) => {
      const controlPath = isCoBorrower ? 'personalInfo.conjoint.email' : 'personalInfo.email';
      const control = this.insuranceForm.get(controlPath);
      if (!control) {
        return result;
      }

      if (!result?.valid) {
        this.setCustomError(control, 'abstractEmail', result?.message || 'Email non valide.');
        this.setEmailValidationState(isCoBorrower, false, false, result?.message || 'Email non valide.');
        return result;
      }

      this.clearCustomError(control, 'abstractEmail');
      if (result.reason === 'service_unavailable') {
        this.setEmailValidationState(isCoBorrower, false, null, result.message || 'Verification indisponible.');
      } else {
        this.setEmailValidationState(isCoBorrower, false, true, result.message || 'Email valide.');
      }
      return result;
    };

    return this.http.post<EmailValidationResult>(casValidationUrl, { email }, { headers }).pipe(
      map((result) => {
        return applyValidationResult(result);
      }),
      catchError((error) => {
        const controlPath = isCoBorrower ? 'personalInfo.conjoint.email' : 'personalInfo.email';
        const control = this.insuranceForm.get(controlPath);

        // ANOMALIE CORRIGEE : toute panne du service de vérification posait
        // l'erreur `abstractEmail` sur le champ, ce qui rendait le formulaire
        // invalide et empêchait l'agent de passer à l'étape suivante. Un
        // backend momentanément absent bloquait donc la saisie, avec pour seul
        // retour « Verification email indisponible ».
        //
        // La vérification distante est un confort, pas une règle métier : on
        // retombe sur le contrôle de format local et on laisse continuer.
        const formatCorrect = this.isBasicEmailFormat(email);
        console.warn('Vérification email indisponible, repli sur le contrôle local.', error?.status);

        if (control) {
          this.clearCustomError(control, 'abstractEmail');
          if (!formatCorrect) {
            this.setCustomError(control, 'abstractEmail', 'Format d\'adresse invalide.');
          }
        }

        if (!formatCorrect) {
          const message = 'Format d\'adresse invalide.';
          this.setEmailValidationState(isCoBorrower, false, false, message);
          return of({ valid: false, reason: 'syntax', message } as EmailValidationResult);
        }

        // `valid: null` : ni validé ni rejeté. Le champ reste utilisable.
        const message = 'Vérification indisponible, format vérifié localement.';
        this.setEmailValidationState(isCoBorrower, false, null, message);
        return of({ valid: true, reason: 'service_unavailable', message } as EmailValidationResult);
      })
    );
  }

  private setEmailValidationState(isCoBorrower: boolean, checking: boolean, valid: boolean | null, message: string): void {
    const next: EmailValidationState = { checking, valid, message };
    if (isCoBorrower) {
      this.coEmailValidationState = next;
      return;
    }
    this.emailValidationState = next;
  }

  private setCustomError(control: AbstractControl, key: string, value: string): void {
    const errors = { ...(control.errors || {}) };
    errors[key] = value;
    control.setErrors(errors);
  }

  private clearCustomError(control: AbstractControl, key: string): void {
    if (!control.errors || !control.errors[key]) {
      return;
    }
    const errors = { ...control.errors };
    delete errors[key];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private isBasicEmailFormat(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private enforceBorrowerEmailsDifference(): boolean {
    const mainEmailControl = this.insuranceForm.get('personalInfo.email');
    const conjointEmailControl = this.insuranceForm.get('personalInfo.conjoint.email');
    if (!mainEmailControl || !conjointEmailControl) {
      return true;
    }

    const mainEmail = (mainEmailControl.value || '').toString().trim().toLowerCase();
    const conjointEmail = (conjointEmailControl.value || '').toString().trim().toLowerCase();

    this.clearCustomError(conjointEmailControl, 'sameAsPrincipalEmail');
    if (!mainEmail || !conjointEmail) {
      return true;
    }

    if (mainEmail === conjointEmail) {
      this.setCustomError(conjointEmailControl, 'sameAsPrincipalEmail', 'L\'email du conjoint doit etre different de celui de l\'assure principal.');
      this.setEmailValidationState(true, false, false, 'Email conjoint identique a l\'assure principal.');
      return false;
    }

    return true;
  }

  private async validateEmailsBeforeSubmit(): Promise<boolean> {
    const emailsAreDifferent = this.enforceBorrowerEmailsDifference();
    if (!emailsAreDifferent) {
      return false;
    }

    const checks: Promise<boolean>[] = [];
    const mainEmailControl = this.insuranceForm.get('personalInfo.email');
    const mainEmail = (mainEmailControl?.value || '').trim();

    if (mainEmail) {
      if (!this.isBasicEmailFormat(mainEmail) || !!mainEmailControl?.hasError('email')) {
        return false;
      }
      checks.push(this.validateEmailControlOnce(false, mainEmail));
    }

    const conjointEmailControl = this.insuranceForm.get('personalInfo.conjoint.email');
    const conjointEmail = (conjointEmailControl?.value || '').trim();
    if (conjointEmail) {
      if (!this.isBasicEmailFormat(conjointEmail) || !!conjointEmailControl?.hasError('email')) {
        return false;
      }
      checks.push(this.validateEmailControlOnce(true, conjointEmail));
    }

    if (!checks.length) {
      return true;
    }

    const results = await Promise.all(checks);
    return results.every(Boolean);
  }

  private validateEmailControlOnce(isCoBorrower: boolean, email: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.validateEmailWithProvider(isCoBorrower, email).subscribe({
        next: (result) => resolve(result?.valid === true),
        error: () => resolve(false),
      });
    });
  }

  private prefillFromHistory(comp: any): void {
    const personalInfo = this.insuranceForm.get('personalInfo');
    if (!personalInfo) return;

    // Chemin privilégié : l'instantané complet du formulaire, enregistré avec
    // la comparaison. Il restitue les trente champs de la saisie, là où les
    // colonnes à plat de l'historique n'en portent qu'une dizaine.
    if (comp.formulaire?.personalInfo) {
      this.restaurerFormulaire(comp.formulaire);
      return;
    }

    // Convert civility to standard format
    let civ = (comp.principalCivility || '').toUpperCase();
    if (civ === 'M' || civ === 'MR' || civ === 'MONSIEUR') civ = 'M';
    else if (civ === 'F' || civ === 'MME' || civ === 'MADAME') civ = 'F';

    personalInfo.patchValue({
      civilite: civ,
      nom: comp.principalName,
      prenom: comp.principalFirstName,
      // Les dates passent par la normalisation : l'historique peut les rendre
      // au format ISO alors que le formulaire n'accepte que JJ/MM/AAAA.
      dateNaissance: this.normaliserDateHistorique(comp.principalBirthDate),
      telephone1: comp.principalPhone,
      codePostal: comp.codePostal,
      dateEffet: this.normaliserDateHistorique(comp.dateEffet),
      regime: comp.regimeObligatoire,
      etatCivil: comp.situationFamiliale,
    });

    if (comp.conjoint) {
      this.aConjoint = true;
      const conjointGroup = personalInfo.get('conjoint');
      if (conjointGroup) {
        conjointGroup.patchValue({
          nom: comp.conjoint.name,
          prenom: comp.conjoint.firstName,
          dateNaissance: this.normaliserDateHistorique(comp.conjoint.birthDate),
        });
      }
    }

    if (comp.enfants && comp.enfants.count > 0 && comp.enfants.details) {
      // ANOMALIE CORRIGEE : les groupes étaient construits à la main avec
      // seulement `prenom`, `dateNaissance` et `regime`. Le template déclare
      // aussi `formControlName="civilite"` et `formControlName="nom"` : Angular
      // levait « Cannot find control with name: 'civilite' » et la section
      // Enfants cassait le rendu du formulaire après un chargement depuis
      // l'historique.
      //
      // `addEnfant()` construit le groupe complet, avec ses validateurs et son
      // abonnement de calcul d'âge : on s'en sert, puis on remplit.
      while (this.enfants.length) {
        this.removeEnfant(0);
      }

      comp.enfants.details.forEach((enfant: any) => {
        this.addEnfant();
        this.enfants.at(this.enfants.length - 1).patchValue({
          dateNaissance: this.normaliserDateHistorique(enfant.birthDate),
          regime: enfant.regime || this.insuranceForm.get('personalInfo.regime')?.value || '',
        });
      });
    }
  }

  /**
   * Restaure intégralement le formulaire depuis un instantané.
   *
   * Le tableau des enfants ne peut pas être rempli par un simple
   * `patchValue` : un `FormArray` n'accepte que les entrées déjà existantes.
   * On reconstruit donc autant de groupes que nécessaire avec `addEnfant()`,
   * qui pose les bons contrôles et les bons validateurs.
   */
  private restaurerFormulaire(instantane: any): void {
    const personalInfo = this.insuranceForm.get('personalInfo') as FormGroup;
    const donnees = instantane.personalInfo || {};

    // Les enfants d'abord : le FormArray doit avoir la bonne taille avant que
    // `patchValue` ne tente de le remplir.
    while (this.enfants.length) {
      this.removeEnfant(0);
    }
    (donnees.enfants || []).forEach(() => this.addEnfant());

    personalInfo.patchValue({
      ...donnees,
      dateNaissance: this.normaliserDateHistorique(donnees.dateNaissance),
      dateEffet: this.normaliserDateHistorique(donnees.dateEffet),
    });

    // L'état est posé AVANT le remplissage : le validateur du groupe conjoint
    // le consulte, et patcher d'abord le ferait tourner avec l'ancienne valeur.
    this.aConjoint = !!donnees.conjoint?.dateNaissance;
    if (donnees.conjoint) {
      personalInfo.get('conjoint')?.patchValue({
        ...donnees.conjoint,
        dateNaissance: this.normaliserDateHistorique(donnees.conjoint.dateNaissance),
      });
      personalInfo.get('conjoint')?.updateValueAndValidity();
    }

    (donnees.enfants || []).forEach((enfant: any, index: number) => {
      this.enfants.at(index)?.patchValue({
        ...enfant,
        dateNaissance: this.normaliserDateHistorique(enfant.dateNaissance),
      });
    });

    if (instantane.coverageSliders) {
      this.insuranceForm.get('coverageSliders')?.patchValue(instantane.coverageSliders);
    }

    this.cdr.detectChanges();
  }

  /**
   * Convertit une date d'historique au format attendu par le formulaire.
   *
   * La base stocke les dates telles qu'elles ont été saisies (`JJ/MM/AAAA`)
   * ou au format ISO selon l'origine de la comparaison. Le formulaire n'accepte
   * que `JJ/MM/AAAA` : sans conversion, une date ISO rechargée depuis
   * l'historique était rejetée par `dateFormatValidator` et le champ
   * apparaissait en erreur.
   */
  private normaliserDateHistorique(valeur: any): string {
    if (!valeur) return '';

    const texte = valeur.toString().trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(texte)) {
      return texte;
    }

    const iso = texte.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }

    const date = new Date(texte);
    if (!isNaN(date.getTime())) {
      const jour = String(date.getDate()).padStart(2, '0');
      const mois = String(date.getMonth() + 1).padStart(2, '0');
      return `${jour}/${mois}/${date.getFullYear()}`;
    }

    return texte;
  }

  // Custom validators
  requiredTrimmedValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value !== 'string') {
      return null;
    }
    return value.trim().length === 0 ? { requiredTrimmed: true } : null;
  }

  requiredTrimmedIfFilledValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      if (typeof value === 'string' && value.trim().length === 0) {
        return { requiredTrimmed: true };
      }
      return null;
    };
  }

  phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const raw = (control.value || '').toString().replace(/\s/g, '');
    if (!raw) {
      return null;
    }
    // Formats acceptés: 0612345678 ou 33123456789 (+33 normalisé côté masque/espace)
    const isFrenchMobileOrLandline = /^(\+?33|0)[1-9]\d{8}$/.test(raw);
    return isFrenchMobileOrLandline ? null : { invalidPhone: true };
  }

  /**
   * Refuse une date situee dans le futur.
   *
   * ANOMALIE CORRIGEE : `dateFormatValidator` se contente de verifier que
   * l'annee est comprise entre 1920 et 2030. Une date de naissance au
   * 01/01/2029 etait donc acceptee, et `calculateAgeFromValue` renvoyait un age
   * negatif qui partait tel quel dans les requetes de tarification.
   *
   * Ce validateur est volontairement separe : `dateEffet` utilise le meme
   * `dateFormatValidator` mais doit, elle, pouvoir etre dans le futur.
   */
  pastDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const match = value.toString().trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null; // le format est deja signale par dateFormatValidator

    const [, jour, mois, annee] = match;
    const date = new Date(Number(annee), Number(mois) - 1, Number(jour));

    const aujourdhui = new Date();
    aujourdhui.setHours(23, 59, 59, 999);

    return date > aujourdhui ? { futureDate: { value: control.value } } : null;
  }

  /**
   * Champs qui prouvent qu'un conjoint est reellement saisi.
   *
   * ANOMALIE CORRIGEE : `civilite` ne fait plus partie de ce test. Elle est
   * remplie automatiquement par `setupPrincipalCiviliteSync` (civilite inverse
   * de celle de l'assure principal) : le groupe conjoint etait donc considere
   * comme "commence" des le chargement de la page, le validateur reclamait les
   * cinq autres champs, et `personalInfo` restait invalide. L'utilisateur ne
   * pouvait pas passer a l'etape suivante sans saisir un conjoint qu'il n'avait
   * pas, avec pour seul retour "Veuillez remplir tous les champs obligatoires"
   * et aucun champ signale en rouge (l'erreur `incompleteConjoint` n'est
   * affichee nulle part dans le template).
   *
   * Cette definition rejoint celle utilisee partout ailleurs dans le composant
   * (`hasConjoint = personalInfo.conjoint && personalInfo.conjoint.dateNaissance`).
   */
  private conjointFieldsFilled(conjoint: AbstractControl): {
    rempli: boolean;
    manquants: string[];
  } {
    const lire = (nom: string) => (conjoint.get(nom)?.value || '').toString().trim();

    const champs = {
      civilite: lire('civilite'),
      nom: lire('nom'),
      prenom: lire('prenom'),
      email: lire('email'),
      dateNaissance: lire('dateNaissance'),
      regime: lire('regime'),
    };

    // La civilite est exclue de la detection : elle est posee automatiquement.
    const rempli = !!(
      champs.nom ||
      champs.prenom ||
      champs.email ||
      champs.dateNaissance ||
      champs.regime
    );

    const manquants = Object.entries(champs)
      .filter(([, valeur]) => !valeur)
      .map(([nom]) => nom);

    return { rempli, manquants };
  }

  /** Libellés lisibles des champs conjoint, pour le message d'erreur. */
  private static readonly LIBELLES_CONJOINT: Record<string, string> = {
    civilite: 'civilité',
    nom: 'nom',
    prenom: 'prénom',
    email: 'email',
    dateNaissance: 'date de naissance',
    regime: 'régime',
  };

  /**
   * Champs conjoint encore vides, en clair.
   *
   * ANOMALIE CORRIGEE : l'erreur `incompleteConjoint` est posée sur le groupe
   * `conjoint`, et aucun message ne lui correspondait dans le template. Le
   * formulaire était donc bloqué à l'étape 1 sans qu'aucun champ ne soit
   * signalé en rouge — l'agent ne voyait qu'un toast « Veuillez remplir tous
   * les champs obligatoires » sans savoir lesquels. Ce getter alimente le
   * message désormais affiché sous le bloc conjoint.
   */
  get conjointChampsManquants(): string[] {
    const erreurs = this.insuranceForm?.get('personalInfo.conjoint')?.errors;
    const manquants = erreurs?.['incompleteConjoint']?.manquants as string[] | undefined;
    if (!manquants) {
      return [];
    }
    return manquants.map(champ => CompareComponent.LIBELLES_CONJOINT[champ] ?? champ);
  }

  conjointConsistencyValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const conjoint = group as FormGroup;
      if (!conjoint) {
        return null;
      }

      // Le conjoint est FACULTATIF. Tant que le bloc n'a pas été ouvert par
      // « Ajouter un conjoint », aucun de ses champs n'est exigé.
      //
      // ANOMALIE CORRIGEE : la présence d'un conjoint était déduite du fait
      // qu'un champ soit rempli. Or `setupPrincipalLastNameSync` recopie le
      // nom de l'assuré principal dans `conjoint.nom` dès sa saisie : le
      // groupe paraissait donc « commencé » sans que l'agent ait rien
      // demandé, et le formulaire réclamait civilité, prénom, email, date de
      // naissance et régime d'un conjoint inexistant.
      if (!this.aConjoint) {
        return null;
      }

      const { manquants } = this.conjointFieldsFilled(conjoint);
      return manquants.length ? { incompleteConjoint: { manquants } } : null;
    };
  }

  noSpecialCharactersValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) return null;

    const specialCharsRegex = /[^a-zA-ZÀ-ÿ\s\-']/;
    if (specialCharsRegex.test(value)) {
      return { 'specialCharacters': { value: control.value } };
    }
    return null;
  }

  dateFormatValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) return null;

    // Vérifier le format JJ/MM/AAAA
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(dateRegex);

    if (!match) {
      return { 'invalidDateFormat': { value: control.value } };
    }

    const [, day, month, year] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    // Validation des valeurs
    if (dayNum < 1 || dayNum > 31) {
      return { 'invalidDay': { value: control.value } };
    }
    if (monthNum < 1 || monthNum > 12) {
      return { 'invalidMonth': { value: control.value } };
    }
    if (yearNum < 1920 || yearNum > 2030) {
      return { 'invalidYear': { value: control.value } };
    }

    // Vérifier si la date est valide
    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum) {
      return { 'invalidDate': { value: control.value } };
    }

    return null;
  }

  /**
   * Formate une saisie de date en JJ/MM/AAAA au fil de la frappe.
   *
   * ANOMALIES CORRIGÉES :
   *
   * 1. Le curseur sautait en fin de champ à chaque caractère. Réaffecter
   *    `input.value` repositionne le caret à la fin : corriger un chiffre au
   *    milieu d'une date était impossible, la frappe suivante repartait de la
   *    fin. On mémorise donc combien de chiffres précèdent le curseur, et on
   *    le replace au même endroit après reformatage.
   *
   * 2. Les séparateurs étaient indestructibles. Effacer un « / » ne changeait
   *    aucun chiffre : la fonction le remettait aussitôt et le curseur filait
   *    à la fin. Une suppression sur un séparateur retire maintenant le
   *    chiffre qui le précède, comportement attendu d'un champ masqué.
   */
  formatDateInput(event: any, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    const brut: string = input.value ?? '';
    const caret = input.selectionStart ?? brut.length;
    const suppression = (event as InputEvent)?.inputType?.startsWith('delete') ?? false;

    // Chiffres saisis, séparateurs ignorés.
    let chiffres = brut.replace(/\D/g, '');

    // Nombre de chiffres situés avant le curseur : c'est le repère stable,
    // les séparateurs étant réinsérés automatiquement.
    let chiffresAvantCaret = brut.slice(0, caret).replace(/\D/g, '').length;

    // Suppression pile sur un séparateur : on retire le chiffre précédent,
    // sinon la touche n'aurait aucun effet visible.
    if (suppression && caret > 0 && !/\d/.test(brut.charAt(caret)) && brut.length < 10) {
      const avant = brut.slice(0, caret).replace(/\D/g, '');
      if (avant.length === chiffresAvantCaret && chiffresAvantCaret > 0) {
        chiffres =
          chiffres.slice(0, chiffresAvantCaret - 1) + chiffres.slice(chiffresAvantCaret);
        chiffresAvantCaret -= 1;
      }
    }

    chiffres = chiffres.substring(0, 8);

    let formate = chiffres;
    if (chiffres.length > 4) {
      formate = `${chiffres.slice(0, 2)}/${chiffres.slice(2, 4)}/${chiffres.slice(4)}`;
    } else if (chiffres.length > 2) {
      formate = `${chiffres.slice(0, 2)}/${chiffres.slice(2)}`;
    }

    input.value = formate;

    // Position du caret : après le n-ième chiffre, séparateurs compris.
    let position = 0;
    let vus = 0;
    while (position < formate.length && vus < chiffresAvantCaret) {
      if (/\d/.test(formate.charAt(position))) {
        vus += 1;
      }
      position += 1;
    }
    // Sauter le séparateur qui suit, pour que la frappe continue sur un chiffre.
    if (!suppression && formate.charAt(position) === '/') {
      position += 1;
    }
    input.setSelectionRange(position, position);

    const control = this.insuranceForm.get(`personalInfo.${fieldName}`);
    if (control) {
      control.setValue(formate);
      control.markAsTouched();
    }
  }

  setupPostalCodeListener(): void {
    const codePostalControl = this.insuranceForm.get('personalInfo.codePostal');
    const villeControl = this.insuranceForm.get('personalInfo.ville');
    const paysControl = this.insuranceForm.get('personalInfo.pays');

    if (codePostalControl && villeControl && paysControl) {
      // Variables pour éviter les boucles infinies
      let isUpdatingFromCode = false;
      let isUpdatingFromCity = false;

      // Listener pour code postal -> ville
      codePostalControl.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(codePostal => {
        if (!isUpdatingFromCity && codePostal && /^\d{5}$/.test(codePostal)) {
          console.log('Code postal détecté:', codePostal);
          isUpdatingFromCode = true;
          this.lookupCityFromPostalCode(codePostal, villeControl, paysControl);
          setTimeout(() => { isUpdatingFromCode = false; }, 100);
        }
      });

      // Listener pour ville -> code postal (seulement si l'utilisateur tape activement)
      villeControl.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(ville => {
        if (!isUpdatingFromCode && ville && ville.length >= 3) {
          console.log('Ville détectée:', ville);
          isUpdatingFromCity = true;
          this.lookupPostalCodeFromCity(ville, codePostalControl, paysControl);
          setTimeout(() => { isUpdatingFromCity = false; }, 100);
        }
      });
    }
  }

  private lookupCityFromPostalCode(codePostal: string, villeControl: AbstractControl, paysControl: AbstractControl): void {
    // Base de données étendue des codes postaux français
    const postalCodeCities: { [key: string]: string } = {
      // Paris arrondissements
      '75001': 'Paris', '75002': 'Paris', '75003': 'Paris', '75004': 'Paris', '75005': 'Paris',
      '75006': 'Paris', '75007': 'Paris', '75008': 'Paris', '75009': 'Paris', '75010': 'Paris',
      '75011': 'Paris', '75012': 'Paris', '75013': 'Paris', '75014': 'Paris', '75015': 'Paris',
      '75016': 'Paris', '75017': 'Paris', '75018': 'Paris', '75019': 'Paris', '75020': 'Paris',

      // Lyon et région
      '69001': 'Lyon', '69002': 'Lyon', '69003': 'Lyon', '69004': 'Lyon', '69005': 'Lyon',
      '69006': 'Lyon', '69007': 'Lyon', '69008': 'Lyon', '69009': 'Lyon',
      '69100': 'Villeurbanne', '69120': 'Vaulx-en-Velin', '69140': 'Rillieux-la-Pape',
      '69200': 'Vénissieux', '69300': 'Caluire-et-Cuire', '69400': 'Arnas',

      // Marseille et région
      '13001': 'Marseille', '13002': 'Marseille', '13003': 'Marseille', '13004': 'Marseille',
      '13005': 'Marseille', '13006': 'Marseille', '13007': 'Marseille', '13008': 'Marseille',
      '13009': 'Marseille', '13010': 'Marseille', '13011': 'Marseille', '13012': 'Marseille',
      '13013': 'Marseille', '13014': 'Marseille', '13015': 'Marseille', '13016': 'Marseille',
      '13100': 'Aix-en-Provence', '13200': 'Arles', '13300': 'Salon-de-Provence',
      '13400': 'Aubagne', '13500': 'Martigues', '13600': 'La Ciotat', '13640': 'La Roque-d\'Anthéron',

      // Bordeaux et région
      '33000': 'Bordeaux', '33100': 'Bordeaux', '33200': 'Bordeaux', '33300': 'Bordeaux',
      '33400': 'Talence', '33500': 'Libourne', '33600': 'Pessac', '33700': 'Mérignac',
      '33800': 'Bordeaux', '33900': 'Bordeaux',

      // Toulouse et région
      '31000': 'Toulouse', '31100': 'Toulouse', '31200': 'Toulouse', '31300': 'Toulouse',
      '31400': 'Toulouse', '31500': 'Toulouse', '31700': 'Blagnac', '31770': 'Colomiers',

      // Lille et région
      '59000': 'Lille', '59100': 'Roubaix', '59200': 'Tourcoing', '59300': 'Valenciennes',
      '59400': 'Cambrai', '59500': 'Douai', '59600': 'Maubeuge', '59700': 'Marcq-en-Barœul',
      '59800': 'Lille', '59160': 'Lomme',

      // Nice et Côte d\'Azur
      '06000': 'Nice', '06100': 'Nice', '06200': 'Nice', '06300': 'Nice',
      '06400': 'Cannes', '06600': 'Antibes', '06800': 'Cagnes-sur-Mer',

      // Nantes et région
      '44000': 'Nantes', '44100': 'Nantes', '44200': 'Nantes', '44300': 'Nantes',
      '44400': 'Rezé', '44800': 'Saint-Herblain', '44900': 'Nantes',

      // Strasbourg et région
      '67000': 'Strasbourg', '67100': 'Strasbourg', '67200': 'Strasbourg',
      '67300': 'Schiltigheim', '67400': 'Illkirch-Graffenstaden',

      // Montpellier et région
      '34000': 'Montpellier', '34070': 'Montpellier', '34080': 'Montpellier',
      '34090': 'Montpellier', '34170': 'Castelnau-le-Lez', '34200': 'Sète',

      // Rennes et région
      '35000': 'Rennes', '35100': 'Rennes', '35200': 'Rennes', '35700': 'Rennes',

      // Grenoble et région
      '38000': 'Grenoble', '38100': 'Grenoble', '38700': 'La Tronche',
      '38400': 'Saint-Martin-d\'Hères', '38600': 'Fontaine',

      // Autres grandes villes
      '21000': 'Dijon', '25000': 'Besançon', '30000': 'Nîmes', '37000': 'Tours',
      '42000': 'Saint-Étienne', '45000': 'Orléans', '49000': 'Angers',
      '51100': 'Reims', '54000': 'Nancy', '57000': 'Metz', '63000': 'Clermont-Ferrand',
      '68000': 'Colmar', '76000': 'Rouen', '80000': 'Amiens', '87000': 'Limoges',

      // Villes moyennes importantes
      '14000': 'Caen', '29000': 'Quimper', '29200': 'Brest', '35400': 'Saint-Malo',
      '56000': 'Vannes', '64000': 'Pau', '65000': 'Tarbes', '66000': 'Perpignan',
      '71000': 'Mâcon', '72000': 'Le Mans', '73000': 'Chambéry', '74000': 'Annecy',
      '83000': 'Toulon', '84000': 'Avignon', '86000': 'Poitiers', '88000': 'Épinal',
      '90000': 'Belfort', '17000': 'La Rochelle', '79000': 'Niort'
    };

    const city = postalCodeCities[codePostal];
    if (city) {
      console.log('Ville trouvée pour', codePostal, ':', city);
      villeControl.setValue(city);
      paysControl.setValue('France');
    } else {
      console.log('Aucune ville trouvée pour', codePostal, ', recherche par département');
      // Enhanced department-based city lookup
      const department = codePostal.substring(0, 2);
      const departmentCities: { [key: string]: string } = {
        '01': 'Bourg-en-Bresse', '02': 'Laon', '03': 'Moulins', '04': 'Digne-les-Bains',
        '05': 'Gap', '06': 'Nice', '07': 'Privas', '08': 'Charleville-Mézières',
        '09': 'Foix', '10': 'Troyes', '11': 'Carcassonne', '12': 'Rodez',
        '13': 'Marseille', '14': 'Caen', '15': 'Aurillac', '16': 'Angoulême',
        '17': 'La Rochelle', '18': 'Bourges', '19': 'Tulle', '20': 'Ajaccio',
        '21': 'Dijon', '22': 'Saint-Brieuc', '23': 'Guéret', '24': 'Périgueux',
        '25': 'Besançon', '26': 'Valence', '27': 'Évreux', '28': 'Chartres',
        '29': 'Quimper', '30': 'Nîmes', '31': 'Toulouse', '32': 'Auch',
        '33': 'Bordeaux', '34': 'Montpellier', '35': 'Rennes', '36': 'Châteauroux',
        '37': 'Tours', '38': 'Grenoble', '39': 'Lons-le-Saunier', '40': 'Mont-de-Marsan',
        '41': 'Blois', '42': 'Saint-Étienne', '43': 'Le Puy-en-Velay', '44': 'Nantes',
        '45': 'Orléans', '46': 'Cahors', '47': 'Agen', '48': 'Mende',
        '49': 'Angers', '50': 'Saint-Lô', '51': 'Châlons-en-Champagne', '52': 'Chaumont',
        '53': 'Laval', '54': 'Nancy', '55': 'Bar-le-Duc', '56': 'Vannes',
        '57': 'Metz', '58': 'Nevers', '59': 'Lille', '60': 'Beauvais',
        '61': 'Alençon', '62': 'Arras', '63': 'Clermont-Ferrand', '64': 'Pau',
        '65': 'Tarbes', '66': 'Perpignan', '67': 'Strasbourg', '68': 'Colmar',
        '69': 'Lyon', '70': 'Vesoul', '71': 'Mâcon', '72': 'Le Mans',
        '73': 'Chambéry', '74': 'Annecy', '75': 'Paris', '76': 'Rouen',
        '77': 'Melun', '78': 'Versailles', '79': 'Niort', '80': 'Amiens',
        '81': 'Albi', '82': 'Montauban', '83': 'Toulon', '84': 'Avignon',
        '85': 'La Roche-sur-Yon', '86': 'Poitiers', '87': 'Limoges', '88': 'Épinal',
        '89': 'Auxerre', '90': 'Belfort', '91': 'Évry', '92': 'Nanterre',
        '93': 'Bobigny', '94': 'Créteil', '95': 'Pontoise'
      };

      const defaultCity = departmentCities[department];
      if (defaultCity) {
        console.log('Ville trouvée par département', department, ':', defaultCity);
        villeControl.setValue(defaultCity);
      } else {
        console.log('Aucune ville trouvée pour le département', department);
      }
      paysControl.setValue('France');
    }
  }

  private lookupPostalCodeFromCity(ville: string, codePostalControl: AbstractControl, paysControl: AbstractControl): void {
    // Normaliser la ville (supprimer accents, espaces, casse)
    const normalizedVille = ville.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    // Mapping ville -> code postal principal
    const cityToPostalCode: { [key: string]: string } = {
      // Grandes villes avec codes postaux principaux
      'paris': '75001',
      'lyon': '69001',
      'marseille': '13001',
      'toulouse': '31000',
      'nice': '06000',
      'nantes': '44000',
      'montpellier': '34000',
      'strasbourg': '67000',
      'bordeaux': '33000',
      'lille': '59000',
      'rennes': '35000',
      'reims': '51100',
      'saint-etienne': '42000',
      'toulon': '83000',
      'grenoble': '38000',
      'dijon': '21000',
      'angers': '49000',
      'nimes': '30000',
      'villeurbanne': '69100',
      'clermont-ferrand': '63000',
      'le havre': '76600',
      'aix-en-provence': '13100',
      'la-roque-d-antheron': '13640',
      'la roque d antheron': '13640',
      'brest': '29200',
      'tours': '37000',
      'amiens': '80000',
      'limoges': '87000',
      'annecy': '74000',
      'perpignan': '66000',
      'besancon': '25000',
      'metz': '57000',
      'orleans': '45000',
      'rouen': '76000',
      'mulhouse': '68100',
      'caen': '14000',
      'nancy': '54000',
      'argenteuil': '95100',
      'montreuil': '93100',
      'roubaix': '59100',
      'tourcoing': '59200',
      'dunkerque': '59140',
      'avignon': '84000',
      'poitiers': '86000',
      'fort-de-france': '97200',
      'courbevoie': '92400',
      'versailles': '78000',
      'colombes': '92700',
      'aulnay-sous-bois': '93600',
      'pau': '64000',
      'la rochelle': '17000',
      'calais': '62100',
      'cannes': '06400',
      'antibes': '06600',
      'boulogne-billancourt': '92100',
      'meudon': '92190',
      'beziers': '34500',
      'bourges': '18000',
      'quimper': '29000',
      'valence': '26000',
      'merignac': '33700',
      'troyes': '10000'
    };

    // Chercher correspondance exacte
    let foundPostalCode = cityToPostalCode[normalizedVille];

    // Si pas de correspondance exacte, chercher par similarité
    if (!foundPostalCode) {
      for (const [cityKey, postalCode] of Object.entries(cityToPostalCode)) {
        if (cityKey.includes(normalizedVille) || normalizedVille.includes(cityKey)) {
          foundPostalCode = postalCode;
          break;
        }
      }
    }

    if (foundPostalCode) {
      console.log('Code postal trouvé pour', ville, ':', foundPostalCode);
      // Mettre systématiquement à jour le code postal pour refléter la ville saisie
      codePostalControl.setValue(foundPostalCode);
      console.log('Code postal mis à jour automatiquement:', foundPostalCode);
      paysControl.setValue('France');
    } else {
      console.log('Aucun code postal trouvé pour', ville);
    }
  }

  setupGaranties(): void {
    // ...
    this.garanties = [
      { label: 'Hospitalisation', formControlName: 'hospitalisation', unit: '%' },
      { label: 'Chambre Particulière', formControlName: 'chambreParticuliere', unit: '€' },
      { label: 'Honoraires', formControlName: 'honoraires', unit: '%' },
      { label: 'Orthodontie', formControlName: 'orthodontie', unit: '%' },
      { label: 'Prothèse fixe', formControlName: 'dentaire', unit: '%' },
      { label: 'Forfait Dentaire', formControlName: 'forfaitDentaire', unit: '€' },
      { label: 'Forfait Optique', formControlName: 'forfaitOptique', unit: '€' },
      { label: 'Médecine complémentaire', formControlName: 'medecineComplementaire', unit: '€' },
    ];
  }

  initializeAvailableInsurers(): void {
    this.availableInsurers = [
      { label: 'ALPTIS', value: 'ALPTIS' },
      { label: 'APIVIA', value: 'APIVIA' },
      { label: 'UTWIN', value: 'UTWIN' },
      { label: 'APRIL', value: 'APRIL' },
      { label: 'ACHEEL', value: 'ACHEEL' },
      { label: 'AXA', value: 'AXA' },
      { label: 'MAAF', value: 'MAAF' },
      { label: 'MATMUT', value: 'MATMUT' },
      { label: 'HARMONIE MUTUELLE', value: 'HARMONIE_MUTUELLE' },
      { label: 'MGEN', value: 'MGEN' }
    ];
  }

  updateSteps(): void {
    this.steps = [
      { label: 'Informations personnelles', styleClass: this.getStepClass(0) },
      { label: 'Garanties', styleClass: this.getStepClass(1) },
      { label: 'Résultats', styleClass: this.getStepClass(2) }
    ];

    // Forcer la mise à jour des classes CSS après le rendu
    setTimeout(() => {
      this.applyStepClasses();
    }, 0);
  }

  getStepClass(stepIndex: number): string {
    if (stepIndex < this.activeIndex) {
      return 'step-completed';
    } else if (stepIndex === this.activeIndex) {
      return 'step-active';
    } else {
      return 'step-future';
    }
  }

  applyStepClasses(): void {
    const stepItems = document.querySelectorAll('.p-steps-item');
    console.log('🎨 applyStepClasses - Nombre d\'étapes trouvées:', stepItems.length);
    console.log('🎨 activeIndex actuel:', this.activeIndex);

    stepItems.forEach((item, index) => {
      // Retirer toutes les classes personnalisées
      item.classList.remove('step-completed', 'step-active', 'step-future');

      // Ajouter la classe appropriée
      const stepClass = this.getStepClass(index);
      item.classList.add(stepClass);

      console.log(`🎨 Étape ${index}: classe appliquée = ${stepClass}`);
      console.log(`🎨 Étape ${index}: classes actuelles =`, item.className);
    });
  }

  get enfants(): FormArray {
    return this.insuranceForm.get('personalInfo.enfants') as FormArray;
  }

  addEnfant(): void {
    const principalLastName = (this.insuranceForm.get('personalInfo.nom')?.value || '').toString().trim();

    // ANOMALIE CORRIGEE : le regime de l'enfant etait fige a 'TNS' (Securite
    // Sociale des Independants). Un enfant a charge n'a pas de regime
    // d'independant : il suit celui de l'assure principal. La valeur par defaut
    // partait telle quelle vers les assureurs et faussait la tarification.
    const regimePrincipal = (this.insuranceForm.get('personalInfo.regime')?.value || '')
      .toString()
      .trim();
    const regimeEnfant = regimePrincipal || 'REGIME_GENERAL';

    const groupeEnfant = this.fb.group({
      // ANOMALIE CORRIGEE : 'Monsieur' n'existe pas dans `civiliteOptions`
      // ('M' / 'F'). Le dropdown affichait "- Choisir -" alors que le champ
      // etait rempli. On laisse le choix a l'utilisateur, le champ restant
      // obligatoire.
      civilite: [null, Validators.required],
      nom: [principalLastName, [Validators.required, this.noSpecialCharactersValidator]],
      prenom: ['', [Validators.required, this.noSpecialCharactersValidator]],
      dateNaissance: ['', [Validators.required, this.dateFormatValidator, this.pastDateValidator]],
      regime: [regimeEnfant, Validators.required],
    });

    this.enfants.push(groupeEnfant);
    this.enfantsAges.push(null);

    // ANOMALIE CORRIGEE : l'abonnement capturait l'index au moment de l'ajout.
    // Apres la suppression d'un enfant, les enfants suivants gardaient leur
    // ancien index et ecrivaient leur age dans la mauvaise case de
    // `enfantsAges`. L'index est desormais resolu au moment de l'evenement.
    this.enfantsSubscriptions.push(
      groupeEnfant.get('dateNaissance')!.valueChanges.subscribe(val => {
        const position = this.enfants.controls.indexOf(groupeEnfant);
        if (position >= 0) {
          this.enfantsAges[position] = this.calculateAgeFromValue(val);
        }
      })
    );
  }

  removeEnfant(index: number): void {
    // L'abonnement de l'enfant retire doit partir avec lui, sinon il continue
    // d'ecrire dans `enfantsAges` apres la suppression.
    this.enfantsSubscriptions[index]?.unsubscribe();
    this.enfantsSubscriptions.splice(index, 1);
    this.enfants.removeAt(index);
    this.enfantsAges.splice(index, 1);
  }

  /**
   * Ouvre le bloc « Informations du conjoint ».
   *
   * Le bloc est masqué tant qu'aucun conjoint n'est déclaré : la majorité des
   * dossiers n'en comportent pas, et six champs vides à faire défiler pour
   * rien allongeaient inutilement la saisie.
   */
  addConjoint(): void {
    this.aConjoint = true;

    // Civilité inverse de celle de l'assuré principal, choix le plus probable.
    const principal = (this.insuranceForm.get('personalInfo.civilite')?.value || '')
      .toString()
      .toUpperCase();
    const inverse = principal === 'M' ? 'F' : principal === 'F' ? 'M' : '';

    this.insuranceForm.get('personalInfo.conjoint')?.patchValue({
      civilite: inverse,
      nom: (this.insuranceForm.get('personalInfo.nom')?.value || '').toString().trim(),
      regime: this.insuranceForm.get('personalInfo.regime')?.value || '',
    });
    this.insuranceForm.get('personalInfo.conjoint')?.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  removeConjoint(): void {
    const conjointGroup = this.insuranceForm.get('personalInfo.conjoint');
    if (!conjointGroup) {
      return;
    }

    // Referme le bloc : sans conjoint, ces champs n'ont pas à rester visibles.
    this.aConjoint = false;

    conjointGroup.reset({
      civilite: '',
      nom: '',
      prenom: '',
      email: '',
      dateNaissance: '',
      regime: ''
    });

    const conjointEmailControl = conjointGroup.get('email');
    if (conjointEmailControl) {
      this.clearCustomError(conjointEmailControl, 'sameAsPrincipalEmail');
      this.clearCustomError(conjointEmailControl, 'abstractEmail');
    }
    this.coEmailValidationState = { checking: false, valid: null, message: '' };
    conjointGroup.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  private getCurrentStepGroup(stepIndex: number): FormGroup | null {
    const stepGroups = ['personalInfo', 'coverageSliders'];
    return stepGroups[stepIndex] ? this.insuranceForm.get(stepGroups[stepIndex]) as FormGroup : null;
  }

  /** Libelles lisibles des champs, pour les messages d'erreur. */
  private static readonly LIBELLES_CHAMPS: Record<string, string> = {
    civilite: 'Civilite',
    nom: 'Nom',
    prenom: 'Prenom',
    dateNaissance: 'Date de naissance',
    codePostalNaissance: 'Code postal de naissance',
    villeNaissance: 'Ville de naissance',
    adresse: 'Adresse',
    complementAdresse: "Complement d'adresse",
    codePostal: 'Code postal',
    ville: 'Ville',
    pays: 'Pays',
    dateEffet: "Date d'effet",
    email: 'Email',
    telephone1: 'Telephone',
    etatCivil: 'Etat civil',
    regime: 'Regime',
    categorieSocioProfessionnelle: 'Categorie socio-professionnelle',
    statutProfessionnel: 'Statut professionnel',
  };

  /**
   * Parcourt le groupe et renvoie les libelles des champs en erreur.
   *
   * Descend dans les sous-groupes et les FormArray : c'est ce qui permet de
   * nommer un enfant incomplet ou un conjoint partiellement saisi, alors que le
   * template n'affiche aucune erreur pour ces deux cas.
   */
  private listerChampsInvalides(
    groupe: AbstractControl | null,
    prefixe: string = ''
  ): string[] {
    if (!groupe) {
      return [];
    }

    const libelle = (cle: string): string =>
      CompareComponent.LIBELLES_CHAMPS[cle] ?? cle;

    if (groupe instanceof FormArray) {
      const resultats: string[] = [];
      groupe.controls.forEach((enfant, index) => {
        resultats.push(...this.listerChampsInvalides(enfant, `Enfant ${index + 1} - `));
      });
      return resultats;
    }

    if (groupe instanceof FormGroup) {
      const resultats: string[] = [];

      // Erreur portee par le groupe lui-meme (ex. incompleteConjoint).
      const erreurGroupe = groupe.errors?.['incompleteConjoint'];
      if (erreurGroupe?.manquants?.length) {
        resultats.push(
          'Conjoint : ' +
            erreurGroupe.manquants.map((cle: string) => libelle(cle)).join(', ')
        );
      }

      Object.keys(groupe.controls).forEach(cle => {
        const controle = groupe.get(cle);
        if (!controle || controle.valid) {
          return;
        }
        if (controle instanceof FormGroup || controle instanceof FormArray) {
          const sousPrefixe = cle === 'conjoint' ? 'Conjoint - ' : prefixe;
          resultats.push(...this.listerChampsInvalides(controle, sousPrefixe));
        } else {
          resultats.push(`${prefixe}${libelle(cle)}`);
        }
      });

      // Doublons possibles quand un groupe porte une erreur ET des champs vides.
      return Array.from(new Set(resultats));
    }

    return [];
  }

  nextStep(event?: Event): void {
    // Empêcher le comportement par défaut du scroll
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // ANOMALIE CORRIGEE : le marquage ne descendait que d'un niveau et ignorait
    // les FormArray. Les champs des enfants n'etaient donc jamais marques
    // "touched" : un enfant incomplet bloquait le passage a l'etape suivante
    // sans qu'aucun message n'apparaisse sous ses champs.
    const personalInfoGroup = this.insuranceForm.get('personalInfo') as FormGroup;
    if (personalInfoGroup) {
      personalInfoGroup.markAllAsTouched();
    }

    const emailsAreDifferent = this.enforceBorrowerEmailsDifference();
    if (!emailsAreDifferent) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Emails invalides',
        detail: 'L email du conjoint doit etre different de l email principal.',
        life: 4000
      });
      return;
    }

    // Vérifier si le formulaire est valide
    if (this.insuranceForm.get('personalInfo')?.valid) {
      if (this.activeIndex < 2) {
        this.activeIndex++;
        this.updateSteps();
      }
    } else {
      // Sauvegarder la position actuelle du scroll
      const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      // ANOMALIE CORRIGEE : le message ne disait pas quels champs manquaient.
      // Comme l'erreur `incompleteConjoint` n'est affichee nulle part dans le
      // template, l'utilisateur voyait "Champs manquants" sans qu'aucun champ
      // ne soit signale, sans moyen de comprendre ce qui bloquait.
      const manquants = this.listerChampsInvalides(personalInfoGroup);
      this.messageService.add({
        severity: 'warn',
        summary: 'Champs manquants',
        detail: manquants.length
          ? `Champs a corriger : ${manquants.join(', ')}.`
          : 'Veuillez remplir tous les champs obligatoires avant de continuer.',
        life: 6000
      });

      // Forcer le maintien de la position de scroll après l'affichage du toast
      setTimeout(() => {
        window.scrollTo(0, currentScrollPosition);
      }, 0);
    }
  }

  prevStep(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.updateSteps();
    }
  }

  goToStep(index: number): void {
    // ANOMALIE CORRIGEE : le composant p-steps est cliquable
    // (`[readonly]="false"`) et `goToStep` ne validait rien. On pouvait donc
    // cliquer directement sur "Garanties" ou "Resultats" avec un formulaire
    // vide, ce qui contournait entierement la validation faite par `nextStep`
    // et lancait une comparaison sur des donnees incompletes.
    // Revenir en arriere reste libre.
    if (index > this.activeIndex) {
      const personalInfoGroup = this.insuranceForm.get('personalInfo') as FormGroup;
      if (personalInfoGroup && personalInfoGroup.invalid) {
        personalInfoGroup.markAllAsTouched();
        const manquants = this.listerChampsInvalides(personalInfoGroup);
        this.messageService.add({
          severity: 'warn',
          summary: 'Champs manquants',
          detail: manquants.length
            ? `Champs a corriger : ${manquants.join(', ')}.`
            : 'Veuillez remplir tous les champs obligatoires avant de continuer.',
          life: 6000
        });
        this.updateSteps(); // restaure la surbrillance sur l'etape courante
        return;
      }
    }

    this.activeIndex = index;
    this.updateSteps();
  }

  async submitComparison(): Promise<void> {
    // 🔒 PROTECTION ANTI-DOUBLE-CLIC: Bloquer si déjà en cours
    if (this.submitting || this.isComparing) {
      console.warn('⚠️ Comparaison déjà en cours, requête ignorée');
      return;
    }

    const emailsAreDifferent = this.enforceBorrowerEmailsDifference();
    if (!emailsAreDifferent) {
      this.messageService.add({
        severity: 'error',
        summary: 'Emails invalides',
        detail: 'L email du conjoint doit etre different de l email principal.'
      });
      return;
    }

    const emailsValidated = await this.validateEmailsBeforeSubmit();
    if (!emailsValidated) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email invalide',
        detail: 'Veuillez saisir une adresse email valide et verifiable (SMTP).'
      });
      return;
    }

    this.insuranceForm.markAllAsTouched();
    if (this.insuranceForm.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Formulaire Invalide', detail: 'Veuillez remplir tous les champs.' });
      return;
    }

    // Marquer immédiatement comme en cours AVANT tout traitement
    this.submitting = true;
    this.isComparing = true;
    this.comparisonExecutionDate = new Date();

    // 🔒 Réinitialiser le flag d'enregistrement pour la nouvelle comparaison
    this.historyAlreadySaved = false;
    console.log('🔄 Flag historyAlreadySaved réinitialisé à false pour nouvelle comparaison');
    console.log('🔄 Timestamp:', new Date().toISOString());

    this.shownToastKeys.clear();

    // Bloquer instantanément les sliders
    this.disableSliders();

    const needs = this.buildComparePayload();

    this.comparisonSubscription = this.compareService.getComparisonResults(needs)
      .subscribe({
        next: (response: any) => {
          let markdownResponse: string | undefined;
          let backendContracts: any[] | undefined;

          if (response && typeof response === 'object') {
            if ((response as any).table_markdown) {
              markdownResponse = (response as any).table_markdown;
            } else if ((response as any).table) {
              markdownResponse = (response as any).table;
            }
            // Métadonnées de contrats renvoyées par le backend (comparateur.py)
            if ((response as any).contracts && Array.isArray((response as any).contracts)) {
              backendContracts = (response as any).contracts;
            }
          } else if (typeof response === 'string') {
            markdownResponse = response;
          }

          if (!markdownResponse) {
            this.submitting = false;
            this.isComparing = false;
            this.enableSliders();
            this.comparisonSubscription = null;
            const detail = this.extractApiErrorMessage(
              response,
              'Le format de la réponse du serveur est inattendu.'
            );
            this.messageService.add({ severity: 'error', summary: 'Erreur de réponse', detail });
            return;
          }

          try {
            // 1) Parser le tableau Markdown en résultats bruts
            this.comparisonResults = this.parseMarkdownTable(markdownResponse);

            // 2) Si le backend renvoie les contrats normalisés, les fusionner avec les résultats
            if (backendContracts && backendContracts.length > 0) {
              this.mergeBackendContractsIntoResults(backendContracts);
            } else {
              // Fallback historique : reconstruire les garanties depuis contracts.json
              this.updateAllGuaranteesFromContracts();
            }

            // 3) Appliquer les couleurs de proximité (création d'une nouvelle liste enrichie)
            this.applyProximityColorsToResults();

            this.allSelected = false;

            // 4) Utiliser la liste enrichie pour l'affichage du tableau
            this.results = this.comparisonResults;

            // 5) Commissions = réglages /cas/admin/commissions (API Brokins + secours local), puis tarification
            // ⚠️ NE PAS afficher le tableau tout de suite (activeIndex = 2)
            // Le tableau sera affiché automatiquement dans finalizePriceLoading()
            // 📝 L'enregistrement dans l'historique se fera APRÈS le chargement des prix dans finalizePriceLoading()
            this.applyCasCommissionsFromAdminSettingsThenFetchPrices();
          } catch (error) {
            console.error('Erreur lors du traitement des résultats:', error);
            this.submitting = false;
            this.isComparing = false;
            this.enableSliders();
            this.comparisonSubscription = null;
            const detail = this.extractApiErrorMessage(
              error,
              'Une erreur inattendue est survenue lors du traitement des résultats.'
            );
            this.messageService.add({ severity: 'error', summary: 'Erreur de traitement', detail });
          }
        },
        error: (error) => {
          console.error('Erreur lors de la récupération des résultats:', error);
          this.submitting = false;
          this.isComparing = false;
          this.enableSliders();
          this.comparisonSubscription = null;
          const detail = this.extractApiErrorMessage(
            error,
            'Une erreur inattendue est survenue lors de la récupération des résultats.'
          );
          this.messageService.add({ severity: 'error', summary: 'Erreur de récupération', detail });
        }
      });
  }

  private parseMarkdownTable(markdown: string): ComparisonResult[] {
    if (!markdown) {
      return [];
    }

    const lines = markdown
      .split('\n')
      .map(l => l.trim())
      .filter(l => !!l);

    const tableLines = lines.filter(l => l.startsWith('|') && l.endsWith('|'));
    if (tableLines.length < 2) {
      return [];
    }

    const splitRow = (row: string): string[] => {
      const trimmed = row.replace(/^\|/, '').replace(/\|$/, '');
      return trimmed.split('|').map(c => (c || '').trim());
    };

    const normalizeHeader = (h: string): string => {
      return (h || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[’']/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    };

    const headers = splitRow(tableLines[0]).map(normalizeHeader);

    const isSeparator = (row: string): boolean => {
      const cells = splitRow(row);
      return cells.length > 0 && cells.every(c => /^:?-{2,}:?$/.test(c));
    };

    const dataLines = tableLines.slice(1).filter(l => !isSeparator(l));

    const findIndex = (pred: (h: string) => boolean): number => headers.findIndex(pred);

    const idxAssurance = findIndex(h => h.includes('assurance') || h.includes('assureur') || h.includes('compagnie'));
    const idxFormule = findIndex(h => h.includes('formule') || h.includes('niveau') || h.includes('offre'));
    const idxCorrespondance = findIndex(h => h.includes('correspondance') || h.includes('score') || h.includes('match'));
    const idxWeakPoint = findIndex(h => h.includes('point faible') || h.includes('weak'));

    const garantieIndexMap: Partial<Record<keyof GuaranteeValues, number>> = {};
    const setGIdx = (key: keyof GuaranteeValues, pred: (h: string) => boolean) => {
      const idx = findIndex(pred);
      if (idx >= 0) {
        garantieIndexMap[key] = idx;
      }
    };

    setGIdx('hospitalisation', h => h.includes('hospitalisation'));
    setGIdx('chambreParticuliere', h => h.includes('chambre'));
    setGIdx('honoraires', h => h.includes('honoraires'));
    setGIdx('dentaire', h => h.includes('dentaire') && !h.includes('forfait'));
    setGIdx('orthodontie', h => h.includes('orthodontie'));
    setGIdx('forfaitDentaire', h => h.includes('forfait dentaire') || (h.includes('dentaire') && h.includes('forfait')));
    setGIdx('forfaitOptique', h => h.includes('optique') || h.includes('forfait optique'));
    setGIdx('medecineComplementaire', h => h.includes('medecine') || h.includes('complementaire'));

    const parsePercentage = (val: string): number => {
      if (!val) {
        return 0;
      }
      const m = val.toString().replace(',', '.').match(/\d+(?:\.\d+)?/);
      if (!m) {
        return 0;
      }
      const n = parseFloat(m[0]);
      return isNaN(n) ? 0 : n;
    };

    const safeCell = (cells: string[], idx: number): string => {
      if (idx < 0 || idx >= cells.length) {
        return '';
      }
      return (cells[idx] || '').toString().trim();
    };

    return dataLines
      .map(line => splitRow(line))
      .filter(cells => cells.length >= 2)
      .map(cells => {
        const assurance = safeCell(cells, idxAssurance) || safeCell(cells, 0);
        const formule = safeCell(cells, idxFormule) || safeCell(cells, 1);

        const garanties: GuaranteeValues = {
          hospitalisation: safeCell(cells, garantieIndexMap.hospitalisation ?? -1) || '-',
          honoraires: safeCell(cells, garantieIndexMap.honoraires ?? -1) || '-',
          chambreParticuliere: safeCell(cells, garantieIndexMap.chambreParticuliere ?? -1) || '-',
          dentaire: safeCell(cells, garantieIndexMap.dentaire ?? -1) || '-',
          orthodontie: safeCell(cells, garantieIndexMap.orthodontie ?? -1) || '-',
          forfaitDentaire: safeCell(cells, garantieIndexMap.forfaitDentaire ?? -1) || '-',
          forfaitOptique: safeCell(cells, garantieIndexMap.forfaitOptique ?? -1) || '-',
          medecineComplementaire: safeCell(cells, garantieIndexMap.medecineComplementaire ?? -1) || '-',
        };

        const correspondencePercentage = parsePercentage(safeCell(cells, idxCorrespondance));
        const weakPoint = safeCell(cells, idxWeakPoint);

        return {
          assurance,
          formule,
          logo: this.getInsurerLogo(assurance),
          prix: 'N/A',
          garanties,
          correspondencePercentage,
          weakPoint,
          isAprilProduct: assurance.toLowerCase().includes('april') && !assurance.toLowerCase().includes('apivia'),
          isPricingLoading: false
        };
      });
  }

  /** Payload comparateur IA : besoins + profil client (filtrage contrats côté Flask). */
  private buildComparePayload(): BesoinClient {
    const sliders = this.insuranceForm.get('coverageSliders')?.value || {};
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;

    const formatDateForApi = (d: any): string => {
      if (!d) {
        return '';
      }
      if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
        return d;
      }
      if (d instanceof Date && !isNaN(d.getTime())) {
        const day = (`0${d.getDate()}`).slice(-2);
        const month = (`0${d.getMonth() + 1}`).slice(-2);
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return String(d);
    };

    const assures: { role: string; dateDeNaissance: string; regime: string }[] = [];
    if (personalInfo?.dateNaissance) {
      assures.push({
        role: 'AssurePrincipal',
        dateDeNaissance: formatDateForApi(personalInfo.dateNaissance),
        regime: personalInfo.regime || 'TNS'
      });
    }
    if (personalInfo?.conjoint?.dateNaissance) {
      assures.push({
        role: 'Conjoint',
        dateDeNaissance: formatDateForApi(personalInfo.conjoint.dateNaissance),
        regime: personalInfo.conjoint.regime || personalInfo.regime || 'TNS'
      });
    }
    (Array.isArray(personalInfo?.enfants) ? personalInfo.enfants : []).forEach((enfant: any) => {
      if (enfant?.dateNaissance) {
        assures.push({
          role: 'Enfant',
          dateDeNaissance: formatDateForApi(enfant.dateNaissance),
          regime: enfant.regime || personalInfo.regime || 'TNS'
        });
      }
    });

    return {
      ...sliders,
      codePostal: personalInfo?.codePostal || '',
      dateEffet: formatDateForApi(personalInfo?.dateEffet),
      dateNaissance: formatDateForApi(personalInfo?.dateNaissance),
      regime: personalInfo?.regime || 'TNS',
      ageAtEffect: ApiMapperUtil.getAgeAtEffect(personalInfo?.dateNaissance, personalInfo?.dateEffet),
      categorieSocioProfessionnelle: personalInfo?.categorieSocioProfessionnelle,
      statutProfessionnel: personalInfo?.statutProfessionnel,
      situationProfessionnelle: personalInfo?.situationProfessionnelle,
      etatCivil: personalInfo?.etatCivil,
      assures
    };
  }

  private mergeBackendContractsIntoResults(backendContracts: any[]): void {
    this.comparisonResults.forEach((result: any, index) => {
      const contract = backendContracts[index];
      if (!contract) {
        return;
      }

      const insurer: string = contract.insurer || '';
      const contractName: string = contract.contract_name || '';
      const levelName: string = contract.level_name || '';
      const productFullName = levelName ? `${contractName} ${levelName}`.trim() : contractName;

      let displayContractName = contractName;
      if (insurer && contractName && contractName.toLowerCase().startsWith(insurer.toLowerCase())) {
        displayContractName = contractName.substring(insurer.length).trim();
      }

      result.insurer = insurer;
      result.contractName = displayContractName || contractName;
      result.levelName = levelName;
      result.assurance = `${insurer} - ${productFullName}`.trim();
      result.formule = productFullName;
      result.correspondencePercentage = contract.score ?? 0;
      result.exactMatch = contract.exact_match === true;

      const normalized = contract.normalized || {};
      result.garanties = {
        hospitalisation: normalized.hospitalisation,
        honoraires: normalized.honoraires,
        chambreParticuliere: normalized.chambreParticuliere,
        dentaire: normalized.dentaire,
        orthodontie: normalized.orthodontie,
        forfaitDentaire: normalized.forfaitDentaire,
        forfaitOptique: normalized.forfaitOptique,
        medecineComplementaire: normalized.medecineComplementaire,
      };
    });
  }

  private updateAllGuaranteesFromContracts(): void {
    return;
  }

  private addToastOnce(opts: { severity: string; summary: string; detail: string; context?: string; life?: number; force?: boolean }): void {
    const severity = (opts.severity || 'info').toString();
    const summary = (opts.summary || '').toString().trim();
    const detail = (opts.detail || '').toString().trim();
    const context = (opts.context || '').toString().trim();
    const life = opts.life;
    const force = !!opts.force;

    const key = `${severity}|${context}|${summary}|${detail}`;
    if (!force && this.shownToastKeys.has(key)) {
      return;
    }
    if (!force) {
      this.shownToastKeys.add(key);
    }

    this.messageService.add({
      severity,
      summary,
      detail,
      ...(life ? { life } : {})
    });
  }

  private notifyApiErrorToast(opts: {
    summary: string;
    fallback: string;
    error: any;
    context?: string;
    severity?: string;
    life?: number;
    result?: ComparisonResult;
    productCode?: string;
  }): void {
    const raw = this.extractApiErrorMessage(opts.error, opts.fallback);
    const insurer = opts.result ? this.getInsurerDisplayName(opts.result) : (opts.context || '');
    const interpreted = PricingFeedbackUtil.interpretMessage(raw, insurer, opts.productCode);
    const detail = opts.result
      ? `${PricingFeedbackUtil.buildProductLabel(opts.result.contractName, opts.result.formule, opts.result.assurance)} : ${interpreted}`
      : interpreted;
    if (opts.result) {
      opts.result.pricingDetail = interpreted;
      if (opts.result.isAprilProduct) {
        opts.result.tarifGlobal = 'Erreur' as any;
      } else {
        opts.result.prix = 'Erreur';
      }
    }
    const context = opts.result
      ? `${this.getInsurerDisplayName(opts.result)}|${PricingFeedbackUtil.buildProductLabel(opts.result.contractName, opts.result.formule, opts.result.assurance)}`
      : (opts.context || '');
    this.addToastOnce({
      severity: opts.severity || 'warn',
      summary: opts.result ? `Tarif indisponible — ${this.getInsurerDisplayName(opts.result)}` : opts.summary,
      detail,
      ...(context ? { context } : {}),
      ...(opts.life ? { life: opts.life } : { life: 14000 }),
      force: true
    });
  }

  private getInsurerDisplayName(result: ComparisonResult): string {
    const raw = (result.insurer || result.assurance || 'Assureur').toString().trim();
    if (!raw) {
      return 'Assureur';
    }
    return raw.split(/\s+/)[0];
  }

  /**
   * Notification unifiée lorsqu'un produit n'a pas de tarif (tous assureurs).
   */
  private notifyPricingUnavailable(
    result: ComparisonResult,
    reason: PricingUnavailableReason,
    options?: { rawMessage?: string; productCode?: string; extraDetail?: string }
  ): void {
    const insurer = this.getInsurerDisplayName(result);
    const product = PricingFeedbackUtil.buildProductLabel(result.contractName, result.formule, result.assurance);
    const detail =
      options?.extraDetail
      || (options?.rawMessage
        ? PricingFeedbackUtil.interpretMessage(options.rawMessage, insurer, options?.productCode)
        : PricingFeedbackUtil.reasonToDefaultMessage(reason));

    result.pricingDetail = detail;
    result.prix = (reason === 'api_error' || reason === 'technical_error') ? 'Erreur' : 'N/A';
    if (result.isAprilProduct) {
      result.tarifGlobal = result.prix as any;
    }

    this.addToastOnce({
      severity: 'warn',
      summary: `Tarif indisponible — ${insurer}`,
      detail: `${product} : ${detail}`,
      context: `${insurer}|${product}|${reason}`,
      life: 14000
    });
    this.cdr.detectChanges();
  }

  private extractApiErrorMessage(source: any, fallback: string): string {
    const isTechnicalHttpFailureMessage = (value: any): boolean => {
      const text = (value ?? '').toString().trim().toLowerCase();
      return !!text && (
        text.includes('http failure response for') ||
        text.includes('500 ok') ||
        text.includes('0 unknown error')
      );
    };

    const parseJsonPayloadFromString = (raw: string): any | null => {
      const text = (raw || '').toString().trim();
      if (!text) {
        return null;
      }

      try {
        return JSON.parse(text);
      } catch {
        // Continue: some backends embed JSON as "... Body={...}"
      }

      const bodyMarker = text.indexOf('Body=');
      if (bodyMarker >= 0) {
        const jsonCandidate = text.slice(bodyMarker + 5).trim();
        try {
          return JSON.parse(jsonCandidate);
        } catch {
          // Continue with regex extraction below
        }
      }

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch {
          return null;
        }
      }

      return null;
    };

    const getDescriptionsFromPayload = (payload: any): string[] => {
      if (!payload) {
        return [];
      }
      const arr = payload?.messages ?? payload?.error?.messages;
      if (!Array.isArray(arr)) {
        return [];
      }
      return arr
        .map((m: any) => (m?.description ?? m?.message ?? '').toString().trim())
        .filter((text: string) => text.length > 0);
    };

    const backendMessages = source?.error?.messages ?? source?.messages;
    if (Array.isArray(backendMessages) && backendMessages.length > 0) {
      const descriptions = backendMessages
        .map((m: any) => {
          if (typeof m === 'string') {
            return m.trim();
          }
          return (m?.description ?? m?.message ?? '').toString().trim();
        })
        .filter((text: string) => text.length > 0);
      if (descriptions.length > 0) {
        return descriptions.join(' | ');
      }
    }

    if (typeof source?.error === 'string') {
      const parsed = parseJsonPayloadFromString(source.error);
      const descriptions = getDescriptionsFromPayload(parsed);
      if (descriptions.length > 0) {
        return descriptions.join(' | ');
      }
      if (!isTechnicalHttpFailureMessage(source.error)) {
        return source.error.toString().trim();
      }
    }

    if (typeof source?.error?.error === 'string') {
      const parsed = parseJsonPayloadFromString(source.error.error);
      const descriptions = getDescriptionsFromPayload(parsed);
      if (descriptions.length > 0) {
        return descriptions.join(' | ');
      }
      if (!isTechnicalHttpFailureMessage(source.error.error)) {
        return source.error.error.toString().trim();
      }
    }

    if (typeof source?.message === 'string') {
      const parsed = parseJsonPayloadFromString(source.message);
      const descriptions = getDescriptionsFromPayload(parsed);
      if (descriptions.length > 0) {
        return descriptions.join(' | ');
      }
    }

    const primaryBackendMessage =
      source?.error?.description ??
      source?.error?.detail ??
      source?.error?.message ??
      source?.description ??
      source?.detail;
    if (primaryBackendMessage) {
      return primaryBackendMessage.toString().trim();
    }

    if (
      typeof source?.message === 'string' &&
      source.message.trim() &&
      !isTechnicalHttpFailureMessage(source.message)
    ) {
      return source.message.trim();
    }

    const seen = new Set<any>();
    const collected: string[] = [];

    const push = (value: any) => {
      const text = (value ?? '').toString().trim();
      if (text && !isTechnicalHttpFailureMessage(text) && !collected.includes(text)) {
        collected.push(text);
      }
    };

    const visit = (node: any): void => {
      if (node == null || seen.has(node)) {
        return;
      }
      if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
        push(node);
        return;
      }
      if (Array.isArray(node)) {
        seen.add(node);
        node.forEach(visit);
        return;
      }
      if (typeof node === 'object') {
        seen.add(node);
        [
          node.detail,
          node.message,
          node.error,
          node.description,
          node.reason,
          node.title
        ].forEach(visit);
        if (Array.isArray(node.errors)) {
          visit(node.errors);
        }
        if (Array.isArray(node.messages)) {
          visit(node.messages);
        }
      }
    };

    visit(source);
    const merged = collected.length ? collected.join(' | ') : fallback;
    const uniqueParts: string[] = [];
    for (const part of merged.split('|')) {
      const cleaned = part.trim().replace(/\s+/g, ' ');
      if (!cleaned) {
        continue;
      }
      if (!uniqueParts.some(existing => existing.toLowerCase() === cleaned.toLowerCase())) {
        uniqueParts.push(cleaned);
      }
    }
    const deduped = uniqueParts.join(' | ');
    return deduped.replace(/^(.+)\s+\1$/i, '$1').trim();
  }

  private extractApicilErrorDetail(err: any): string {
    const candidates = [
      err?.error?.detail,
      err?.error?.message,
      err?.message
    ].filter(Boolean).map((v: any) => v.toString());

    for (const raw of candidates) {
      const cleaned = raw
        .replace(/<EOL>/g, '\n')
        .replace(/\\"/g, '"')
        .trim();

      // Cas le plus fréquent: RuntimeException "Erreur APICIL: { ...json... }"
      const marker = 'Erreur APICIL:';
      const idx = cleaned.indexOf(marker);
      if (idx >= 0) {
        const maybeJson = cleaned.slice(idx + marker.length).trim();
        try {
          const parsed = JSON.parse(maybeJson);
          if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
            return parsed.errors.join(' | ');
          }
          if (parsed?.error?.errorDescription) {
            return parsed.error.errorDescription;
          }
          if (parsed?.message) {
            return parsed.message;
          }
        } catch {
          // continue and try other parsers
        }
      }

      // Cas d'un JSON stringifié/échappé dans le message
      const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
            return parsed.errors.join(' | ');
          }
          if (parsed?.error?.errorDescription) {
            return parsed.error.errorDescription;
          }
        } catch {
          // ignore parse error
        }
      }
    }

    return candidates[0]
      ?.replace(/<EOL>/g, '\n')
      ?.replace(/\\"/g, '"')
      || 'Impossible de créer le devis APICIL.';
  }

  private collectUtwinMessagesFromResponse(response: any): void {
    if (!response) {
      return;
    }

    const messages: string[] = [];

    const pushMessage = (msg: any) => {
      if (!msg) {
        return;
      }
      if (typeof msg === 'string') {
        messages.push(msg.trim());
      } else if (typeof msg === 'object') {
        const text = (msg.description || msg.libelle || msg.message || msg.detail || '').toString().trim();
        if (text) {
          messages.push(text);
        }
      }
    };

    if (Array.isArray((response as any).messages)) {
      (response as any).messages.forEach(pushMessage);
    }

    if (Array.isArray((response as any).propositions)) {
      (response as any).propositions.forEach((p: any) => {
        if (Array.isArray(p.messages)) {
          p.messages.forEach(pushMessage);
        }
      });
    }

    const uniqueMessages = Array.from(new Set(messages.filter((m: string) => !!m)));
    if (uniqueMessages.length > 0) {
      this.utwinMessages = uniqueMessages;
    }
  }

  /** Normalisation libellés UTWIN (Essentiel / Confort / …). */
  private normalizeUtwinText(value: any): string {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\+/g, ' plus ')
      .replace(/[^a-z0-9 ]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private getUtwinVariantHints(selectedFormulaNorm: string): {
    essentiel: boolean;
    confort: boolean;
    equilibre: boolean;
    hospitalisationSeule: boolean;
  } | null {
    const n = selectedFormulaNorm;
    const essentiel = n.includes('essentiel');
    const confort = n.includes('confort');
    const equilibre = n.includes('equilibre');
    const hospitalisationSeule =
      n.includes('hospitalisation seule')
      || (n.includes('hospitalisation') && !/\bformule\s+\d+/.test(n));
    if (!essentiel && !confort && !equilibre && !hospitalisationSeule) {
      return null;
    }
    return { essentiel, confort, equilibre, hospitalisationSeule };
  }

  private utwinLabelMatchesVariantHints(
    libelleFormuleNorm: string,
    hints: { essentiel: boolean; confort: boolean; equilibre: boolean; hospitalisationSeule: boolean }
  ): boolean {
    if (hints.hospitalisationSeule) {
      return libelleFormuleNorm.includes('hospitalisation') && !/\bformule\s+\d+/.test(libelleFormuleNorm);
    }
    if (hints.confort) {
      return libelleFormuleNorm.includes('confort');
    }
    if (hints.essentiel) {
      return libelleFormuleNorm.includes('essentiel') && !libelleFormuleNorm.includes('confort');
    }
    if (hints.equilibre) {
      return libelleFormuleNorm.includes('equilibre');
    }
    return true;
  }

  private applyUtwinVariantFilter(
    pool: any[],
    selectedFormulaNorm: string,
    extract: (p: any) => { libelleFormule?: string; codeFormule?: string }
  ): any[] {
    const hints = this.getUtwinVariantHints(selectedFormulaNorm);
    if (!hints || pool.length === 0) {
      return pool;
    }
    const explicitCode = selectedFormulaNorm.match(/\bn(\d+)\b/i)?.[0]?.toUpperCase();
    const narrowed = pool.filter((p) => {
      const ex = extract(p);
      const label = this.normalizeUtwinText(ex.libelleFormule || '');
      if (!this.utwinLabelMatchesVariantHints(label, hints)) {
        return false;
      }
      const code = (ex.codeFormule || '').toString().toUpperCase();
      if (explicitCode && code && code !== explicitCode) {
        return false;
      }
      return true;
    });
    return narrowed.length > 0 ? narrowed : pool;
  }

  private transformFormToQuote(): InsuranceQuoteForm {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;

    const parseFormDate = (dateStr: any): Date => {
      if (!dateStr) {
        return new Date();
      }
      if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
        return dateStr;
      }
      if (typeof dateStr === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('/');
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      }
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? new Date() : d;
    };

    const insuredPersons: InsuredPerson[] = [];

    const mainAddress = {
      street: (personalInfo?.adresse || '').toString(),
      postalCode: (personalInfo?.codePostal || '').toString(),
      city: (personalInfo?.ville || '').toString()
    };

    const toGender = (g: any): 'M' | 'F' => (g === 'F' ? 'F' : 'M');

    insuredPersons.push({
      lastName: (personalInfo?.nom || '').toString(),
      firstName: (personalInfo?.prenom || '').toString(),
      birthDate: parseFormDate(personalInfo?.dateNaissance),
      gender: toGender(personalInfo?.civilite),
      address: mainAddress,
      email: (personalInfo?.email || '').toString(),
      phoneNumber: (personalInfo?.telephone1 || '').toString(),
      regime: (personalInfo?.regime || '').toString(),
      situation: (personalInfo?.etatCivil || '').toString(),
      addressType: 'Actuelle'
    });

    const hasConjoint = !!personalInfo?.conjoint?.dateNaissance;
    if (hasConjoint) {
      insuredPersons.push({
        lastName: (personalInfo?.conjoint?.nom || personalInfo?.nom || '').toString(),
        firstName: (personalInfo?.conjoint?.prenom || '').toString(),
        birthDate: parseFormDate(personalInfo?.conjoint?.dateNaissance),
        gender: toGender(personalInfo?.conjoint?.civilite),
        address: mainAddress,
        email: (personalInfo?.conjoint?.email || '').toString(),
        phoneNumber: '',
        regime: (personalInfo?.conjoint?.regime || personalInfo?.regime || '').toString(),
        situation: (personalInfo?.etatCivil || '').toString(),
        addressType: 'Actuelle'
      });
    }

    if (Array.isArray(personalInfo?.enfants)) {
      personalInfo.enfants.forEach((enfant: any) => {
        if (!enfant?.dateNaissance) {
          return;
        }
        insuredPersons.push({
          lastName: (personalInfo?.nom || '').toString(),
          firstName: (enfant?.prenom || '').toString(),
          birthDate: parseFormDate(enfant?.dateNaissance),
          gender: enfant?.sexe === 'fille' ? 'F' : 'M',
          address: mainAddress,
          email: '',
          phoneNumber: '',
          regime: (enfant?.regime || personalInfo?.regime || '').toString(),
          situation: 'celibataire',
          addressType: 'Actuelle'
        });
      });
    }

    return {
      productReference: '',
      insuredPersons,
      contact: {
        email: (personalInfo?.email || '').toString(),
        phoneNumber: (personalInfo?.telephone1 || '').toString(),
        address: mainAddress
      },
      effectDate: parseFormDate(personalInfo?.dateEffet),
      garanties: []
    };
  }

  /**
   * Trouve le contrat Apivia correspondant dans le contracts.json
   */
  /**
   * Extrait la valeur numérique principale et le texte entre parenthèses.
   * Ex: "450 % BR (100€ max / an)" -> "450 (100€ max / an)"
   */
  private extractComplexValue(value: string | undefined): string {
    if (!value) {
      return '-';
    }

    const cleanValue = value.toString().trim();

    // Regex pour extraire le premier nombre et le contenu des parenthèses
    const numericMatch = cleanValue.match(/(\d*\.?\d+)/);
    const parenthesisMatch = cleanValue.match(/\(([^)]+)\)/);

    const numericPart = numericMatch ? numericMatch[0] : null;
    const parenthesisPart = parenthesisMatch ? `(${parenthesisMatch[1]})` : null;

    if (numericPart && parenthesisPart) {
      return `${numericPart} ${parenthesisPart}`;
    }

    if (numericPart) {
      return numericPart;
    }

    // Si c'est juste du texte sans nombre (ex: "-"), le retourner
    if (!numericPart && !parenthesisPart) {
      return cleanValue;
    }

    return '-';
  }

  private findApiviaContract(formulaNumber: string): any {
    // Simuler la recherche dans le contracts.json
    // En production, ces données devraient être chargées depuis le service
    const apiviaContracts = [
      {
        level_name: "Niveau 1",
        benefits: {
          HOSPITALISATION: { honoraires_chirurgien_optam: "200%", chambre_particuliere: "60 €/jour" },
          SOINS_COURANTS: { consultation_generaliste_optam: "225%" },
          DENTAIRE: { soins_dentaires: "125%", implantologie: "300 €", orthodontie: "225%" },
          OPTIQUE: { verres_complexes: "300 €" }
        }
      },
      {
        level_name: "Niveau 2",
        benefits: {
          HOSPITALISATION: { honoraires_chirurgien_optam: "225%", chambre_particuliere: "70 €/jour" },
          SOINS_COURANTS: { consultation_generaliste_optam: "250%" },
          DENTAIRE: { soins_dentaires: "135%", implantologie: "350 €", orthodontie: "250%" },
          OPTIQUE: { verres_complexes: "325 €" }
        }
      },
      {
        level_name: "Niveau 3",
        benefits: {
          HOSPITALISATION: { honoraires_chirurgien_optam: "240%", chambre_particuliere: "75 €/jour" },
          SOINS_COURANTS: { consultation_generaliste_optam: "260%" },
          DENTAIRE: { soins_dentaires: "140%", implantologie: "375 €", orthodontie: "260%" },
          OPTIQUE: { verres_complexes: "340 €" }
        }
      },
      {
        level_name: "Niveau 4",
        benefits: {
          HOSPITALISATION: { honoraires_chirurgien_optam: "250%", chambre_particuliere: "80 €/jour" },
          SOINS_COURANTS: { consultation_generaliste_optam: "275%" },
          DENTAIRE: { soins_dentaires: "150%", implantologie: "400 €", orthodontie: "275%" },
          OPTIQUE: { verres_complexes: "350 €" }
        }
      },
      {
        level_name: "Niveau 5",
        benefits: {
          HOSPITALISATION: { honoraires_chirurgien_optam: "275%", chambre_particuliere: "90 €/jour" },
          SOINS_COURANTS: { consultation_generaliste_optam: "300%" },
          DENTAIRE: { soins_dentaires: "175%", implantologie: "450 €", orthodontie: "300%" },
          OPTIQUE: { verres_complexes: "400 €" }
        }
      },
      {
        level_name: "Niveau 6",
        benefits: {
          HOSPITALISATION: { honoraires_chirurgien_optam: "300%", chambre_particuliere: "100 €/jour" },
          SOINS_COURANTS: { consultation_generaliste_optam: "325%" },
          DENTAIRE: { soins_dentaires: "200%", implantologie: "500 €", orthodontie: "325%" },
          OPTIQUE: { verres_complexes: "450 €" }
        }
      }
    ];

    return apiviaContracts.find(contract =>
      contract.level_name === `Niveau ${formulaNumber}`
    );
  }

  private fetchDeuxMaPrices(result: any, formule: string): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    result.isPricingLoading = true;

    // Build payload for Deuxmaltitude
    const formatDate = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
      }
      if (date instanceof Date && !isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${day}/${month}/${year}`;
      }
      return '';
    };

    const getBirthYear = (date: any): string => {
      const formatted = formatDate(date);
      if (formatted && formatted.length === 10) return formatted.substring(6, 10);
      return '1990';
    };

    const mapRegimeSocial = (regime: string): string => {
      const norm = ApiMapperUtil.normalizeRegime(regime);
      if (norm === 'TNS') return 'TNS_SYSTEM';
      if (norm === 'ALSACE_MOSELLE') return 'ALSACE_MOSELLE_SYSTEM';
      return 'SOCIAL_INSURANCE_SYSTEM';
    };

    const payload: any = {
      annee_naissance_adherent_principal: getBirthYear(personalInfo?.dateNaissance),
      regime_social_adherent_principal: mapRegimeSocial(personalInfo?.regime),
      date_effet: formatDate(personalInfo?.dateEffet) || formatDate(new Date()),
      cp: personalInfo?.codePostal || '75001',
      ville: personalInfo?.ville || 'PARIS',
      nb_adherents_sup: 0
    };

    if (personalInfo?.conjoint?.dateNaissance) {
      payload.annee_naissance_conjoint = getBirthYear(personalInfo.conjoint.dateNaissance);
      payload.regime_social_conjoint = mapRegimeSocial(personalInfo.conjoint.regime || personalInfo.regime);
    }

    if (Array.isArray(personalInfo?.enfants) && personalInfo.enfants.length > 0) {
      const enfantsCount = Math.min(personalInfo.enfants.length, 10);
      payload.nb_adherents_sup = enfantsCount;
      personalInfo.enfants.slice(0, 10).forEach((enfant: any, index: number) => {
        const i = index + 1;
        payload[`annee_naissance_adherent_sup_${i}`] = getBirthYear(enfant.dateNaissance);
        payload[`regime_social_adherent_sup_${i}`] = mapRegimeSocial(enfant.regime || personalInfo.regime);
      });
    }

    this.deuxMaService.get2MaltitudeTarif(payload).subscribe({
      next: (response: any) => {
        result.isPricingLoading = false;
        // The backend response is the array/list of prices directly or an object. Let's assume it returns a tarif list.
        // Usually, comparateur needs to find the correct formula in the response.
        // We'll leave it as finding the 'cotisation' if it's there or just use the price we have as a fallback for now.
        console.log('✅ Tarifs 2MA reçus:', response);
        if (response && response.formules) {
          // Extraire le DERNIER numéro de la formule (ex: "100 Niveau 6" -> "6" -> "F6")
          const numMatch = formule.match(/\d+(?!.*\d)/);
          let fKey = numMatch ? 'F' + numMatch[0] : formule;
          
          let fData = response.formules[fKey];
          
          if (!fData || (!fData.periodicRate && !fData.periodic_rate)) {
             console.warn(`Formule 2MA non trouvée ou prix manquant pour: ${fKey} (formule originale: ${formule})`, response.formules);
             this.notifyPricingUnavailable(result, 'api_error', { extraDetail: `La formule ${fKey} n'est pas proposée par 2MAltitude pour ce profil.` });
             return;
          }
          
          if (fData && (fData.periodicRate || fData.periodic_rate)) {
            const price = fData.periodicRate || fData.periodic_rate;
            result.prix = price;
            result.prixMensuel = price;
          }
        }
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('Erreur tarification 2MA', err);
        this.notifyPricingUnavailable(result, 'api_error', { extraDetail: 'Erreur appel API 2MA.' });
        this.cdr.detectChanges();
      }
    });
  }

  private fetchSwissLifePrices(result: any, formule: string): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    result.isPricingLoading = true;

    const formatDateYYYYMMDD = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }
      if (date instanceof Date && !isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
      }
      return new Date().toISOString().split('T')[0];
    };

    const assures: any[] = [];
    const pushAssure = (person: any, relation: string) => {
       const isTns = ApiMapperUtil.normalizeRegime(person.regime) === 'TNS';
       assures.push({
         sexe: person.gender === 'F' ? 'F' : 'M',
         dateNaissance: formatDateYYYYMMDD(person.dateNaissance),
         profession: isTns ? 'INDEPENDANT' : 'SALARIE',
         regime: isTns ? 'TNS' : 'RG',
         statut: relation,
         isTns: isTns,
         nombreEnfants: 0
       });
    };

    pushAssure(personalInfo, 'ASSURE_PRINCIPAL');
    if (personalInfo?.conjoint?.dateNaissance) {
       pushAssure(personalInfo.conjoint, 'CONJOINT');
    }
    if (Array.isArray(personalInfo?.enfants)) {
       personalInfo.enfants.forEach((enfant: any) => pushAssure(enfant, 'ENFANT'));
    }

    const payload = {
      assures: assures,
      produit: {
        gamme: 'MO',
        formule: formule || 'N1',
        combinaisons: [],
        niveaux: [],
        niveau: formule || 'N1'
      },
      codeDepartement: personalInfo?.codePostal?.substring(0, 2) || '75',
      dateEffet: formatDateYYYYMMDD(personalInfo?.dateEffet) || formatDateYYYYMMDD(new Date()),
      dateFin: '',
      isComparateur: true,
      courtier: ''
    };

    this.swissLifeService.getSwissLifeTarif(payload).subscribe({
      next: (response: any) => {
        result.isPricingLoading = false;
        console.log('✅ Tarifs SwissLife reçus:', response);
        if (response && response.primeTtc) {
          result.prix = response.primeTtc;
          result.prixMensuel = response.primeTtc;
        } else if (response && response.cotisation) {
          result.prix = response.cotisation;
          result.prixMensuel = response.cotisation;
        }
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('Erreur tarification SwissLife', err);
        this.notifyPricingUnavailable(result, 'api_error', { extraDetail: 'Erreur appel API SwissLife.' });
        this.cdr.detectChanges();
      }
    });
  }

  private fetchAlptisPrices(): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;

    const formatDate = (date: any): string => {
      if (!date) return '';

      // Si c'est déjà une chaîne au format DD/MM/YYYY, convertir en YYYY-MM-DD
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }

      // Si c'est un objet Date
      if (date instanceof Date && !isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
      }

      console.warn('Invalid date format for Alptis:', date);
      return '';
    };

    // Helper for Alptis regime mapping
    const mapAlptisRegime = (regime: string): string => {
      return ApiMapperUtil.getAlptisRegime(regime);
    };

    const alptisProducts = this.comparisonResults.filter(result => {
      const assuranceName = result.assurance?.toLowerCase() || '';
      return assuranceName.includes('alptis');
    });

    if (alptisProducts.length === 0) {
      return; // No Alptis products, no need to proceed
    }

    const hasSanteSelect = alptisProducts.some(result => {
      const label = `${result.assurance || ''} ${result.formule || ''}`.toLowerCase();
      return label.includes('select');
    });

    // Déterminer le statut professionnel selon les règles métier
    const getStatutProfessionnel = (categorie: string, statutChoisi?: string): string => {
      switch (categorie) {
        case 'ARTISANS':
        case 'COMMERCANTS_ET_ASSIMILES':
          return 'ARTISAN_COMMERCANT';
        case 'PROFESSIONS_LIBERALES_ET_ASSIMILES':
          return 'PROFESSIONS_LIBERALES';
        case 'CHEFS_D_ENTREPRISE':
          return statutChoisi || 'ARTISAN_COMMERCANT'; // Obligatoire de préciser
        default:
          return 'ARTISAN_COMMERCANT';
      }
    };

    // Construire les assurés
    const assures: any = {
      adherent: {
        cadre_exercice: 'INDEPENDANT',
        categorie_socioprofessionnelle: personalInfo.categorieSocioProfessionnelle,
        code_postal: personalInfo.codePostal,
        date_naissance: formatDate(personalInfo.dateNaissance),
        micro_entrepreneur: true,
        regime_obligatoire: mapAlptisRegime(personalInfo.regime || 'TNS'),
        statut_professionnel: getStatutProfessionnel(
          personalInfo.categorieSocioProfessionnelle,
          personalInfo.statutProfessionnel
        )
      }
    };

    // Debug pour diagnostiquer le problème
    console.log('🔍 DEBUG Alptis - personalInfo.etatCivil:', personalInfo.etatCivil);
    console.log('🔍 DEBUG Alptis - personalInfo.conjoint:', personalInfo.conjoint);
    console.log('🔍 DEBUG Alptis - personalInfo.enfants:', personalInfo.enfants);

    // Ajouter conjoint si présent
    if (personalInfo.conjoint?.dateNaissance) {
      console.log('✅ DEBUG Alptis - Ajout du conjoint');
      const conjointCsp = hasSanteSelect
        ? ApiMapperUtil.getAlptisSanteSelectConjointCsp(personalInfo.categorieSocioProfessionnelle)
        : personalInfo.categorieSocioProfessionnelle;
      assures.conjoint = {
        categorie_socioprofessionnelle: conjointCsp,
        date_naissance: formatDate(personalInfo.conjoint.dateNaissance),
        regime_obligatoire: mapAlptisRegime(personalInfo.conjoint.regime || personalInfo.regime || 'TNS')
      };
    } else {
      console.log('❌ DEBUG Alptis - Conjoint non ajouté - Conditions:', {
        hasConjoint: !!personalInfo.conjoint,
        hasConjointBirthDate: personalInfo.conjoint?.dateNaissance
      });
    }

    // Ajouter enfants si présents
    if (personalInfo.enfants && personalInfo.enfants.length > 0) {
      console.log('✅ DEBUG Alptis - Ajout des enfants:', personalInfo.enfants.length);
      assures.enfants = personalInfo.enfants.map((enfant: any) => ({
        date_naissance: formatDate(enfant.dateNaissance),
        regime_obligatoire: mapAlptisRegime(enfant.regime || personalInfo.regime || 'TNS')
      }));
    } else {
      console.log('❌ DEBUG Alptis - Enfants non ajoutés - Conditions:', {
        hasEnfants: !!personalInfo.enfants,
        enfantsLength: personalInfo.enfants?.length || 0
      });
    }

    console.log('🔍 DEBUG Alptis - assures final:', assures);

    alptisProducts.forEach(result => {
      result.isPricingLoading = true;

      // Extraire le niveau depuis la formule
      const niveauMatch = result.formule.match(/Niveau (\d+)/i);
      const niveau = niveauMatch ? `NIVEAU_${niveauMatch[1]}` : 'NIVEAU_1';

      // Déterminer sur_complementaire selon les règles du contracts.json
      const contractsData = [
        { contract_name: 'Santé Pro+ Surco', contract_type: 'Surcomplémentaire santé', sur_complementaire: true },
        { contract_name: 'Santé Pro+', contract_type: 'Complémentaire santé', sur_complementaire: false }
      ];

      const contractInfo = contractsData.find(c =>
        result.formule.toLowerCase().includes(c.contract_name.toLowerCase())
      );
      const surComplementaire = contractInfo ? contractInfo.sur_complementaire : false;

      // Calculer ayants_droit
      const ayantsDroit: any = {
        conjoint: false,
        enfants: 0
      };

      if (assures.conjoint) {
        ayantsDroit.conjoint = true;
      }
      if (assures.enfants && assures.enfants.length > 0) {
        ayantsDroit.enfants = assures.enfants.length;
      }

      const assuranceLower = (result.assurance || '').toLowerCase();
      const formuleLower = (result.formule || '').toLowerCase();
      const isFrontaliers = assuranceLower.includes('frontalier') || formuleLower.includes('frontalier');

      if (isFrontaliers) {
        const niveauMatch = result.formule.match(/Niveau (\d+)/i);
        const niveauSocle = niveauMatch ? `N${niveauMatch[1]}` : 'N1';

        const frontaliersPayload = {
          date_effet: formatDate(personalInfo.dateEffet),
          client: {
             date_naissance: formatDate(personalInfo.dateNaissance),
             categorie_socio_professionnelle: personalInfo.categorieSocioProfessionnelle || 'EMPLOYES_AGENTS_DE_MAITRISE',
             regime_protection_sociale: 'REGIME_FRANCAIS',
             regime_obligatoire: 'SECURITE_SOCIALE',
             code_postal: personalInfo.codePostal,
             ...(assures.conjoint ? { conjoint: {
                 date_naissance: formatDate(personalInfo.conjoint.dateNaissance),
                 categorie_socio_professionnelle: personalInfo.conjoint?.categorieSocioProfessionnelle || 'EMPLOYES_AGENTS_DE_MAITRISE',
                 regime_protection_sociale: 'REGIME_FRANCAIS',
                 regime_obligatoire: 'SECURITE_SOCIALE'
             } } : {}),
             ...(assures.enfants && assures.enfants.length > 0 ? { enfants: assures.enfants.map((e: any) => ({
                 date_naissance: e.date_naissance,
                 regime_protection_sociale: 'REGIME_FRANCAIS',
                 regime_obligatoire: 'SECURITE_SOCIALE'
             })) } : {})
          },
          combinaisons: [{
            numero_combinaison: 1,
            offre: {
              niveau_socle: niveauSocle,
              commissionnement: 'PREC_30_10',
              presence_du_renfort: false
            }
          }]
        };

        console.log('Sending Alptis Frontaliers Payload:', frontaliersPayload);

        this.alptisService.getFrontaliersTarification(frontaliersPayload).pipe(
          finalize(() => { result.isPricingLoading = false; })
        ).subscribe({
          next: (response: any) => {
            console.log('Alptis Frontaliers Response:', response);
            const combs = response?.tarification_combinaisons || response?.tarificationCombinaisons;
            
            if (response?.message && (!combs || combs.length === 0)) {
              this.notifyPricingUnavailable(result, 'api_no_result', {
                rawMessage: String(response.message),
                extraDetail: String(response.message)
              });
              return;
            }

            if (combs && combs.length > 0 && combs[0].tarifs?.cotisation_mensuelle?.total) {
              result.pricingDetail = undefined;
              result.prix = parseFloat(combs[0].tarifs.cotisation_mensuelle.total);
              console.log('Prix Alptis Frontaliers:', result.prix);
              this.cdr.detectChanges();
            } else {
              this.notifyPricingUnavailable(result, 'empty_response', {
                extraDetail: 'Aucun montant de cotisation mensuelle trouvé pour Frontaliers.'
              });
              console.warn('Aucun prix trouvé dans la réponse Alptis Frontaliers:', response);
            }
          },
          error: (err: any) => {
            console.error('Alptis Frontaliers pricing error:', err);
            this.notifyApiErrorToast({
              summary: 'Tarif indisponible — Alptis',
              fallback: 'Erreur lors de la tarification Alptis Frontaliers.',
              error: err,
              context: 'ALPTIS',
              result,
              severity: 'warn'
            });
          }
        });
        return; // Ne pas exécuter le code standard Alptis
      }

      const codeProduit = (assuranceLower.includes('protect') || formuleLower.includes('protect'))
        ? 'SANTE_PROTECT'
        : (assuranceLower.includes('select') || formuleLower.includes('select'))
          ? 'SANTE_SELECT'
          : 'SANTE_PRO_PLUS';

      const alptisPayload = {
        produit: result.assurance,
        date_effet: formatDate(personalInfo.dateEffet),
        assures: assures,
        combinaisons: [{
          numero: 1,
          offre: {
            code_produit: codeProduit,
            niveau: niveau,
            sur_complementaire: surComplementaire
          },
          ayants_droit: ayantsDroit,
          commissionnement: 'PREC30_10'
        }]
      };

      console.log('Sending Alptis Payload:', alptisPayload);

      this.alptisService.getTarification(alptisPayload).pipe(
        finalize(() => { result.isPricingLoading = false; })
      ).subscribe({
        next: (response: any) => {
          console.log('Alptis Response:', response);

          const tarificationArray = response?.resultatsTarification || response?.resultats_tarification;

          if (response?.message && (!tarificationArray || tarificationArray.length === 0)) {
            this.notifyPricingUnavailable(result, 'api_no_result', {
              rawMessage: String(response.message),
              extraDetail: String(response.message)
            });
            return;
          }

          console.log('📦 Alptis Full JSON:', JSON.stringify(response, null, 2));

          if (response && tarificationArray && tarificationArray.length > 0) {
            const tarificationResult = tarificationArray[0];

            // Chercher le prix dans total_mensuel en priorité
            if (tarificationResult.tarifs && tarificationResult.tarifs.total_mensuel) {
              result.pricingDetail = undefined;
              result.prix = parseFloat(tarificationResult.tarifs.total_mensuel);
              console.log('Prix Alptis extrait de total_mensuel:', result.prix);
              this.cdr.detectChanges();
            } else if (tarificationResult.tarifs && tarificationResult.tarifs.cotisationMensuelle) {
              result.pricingDetail = undefined;
              result.prix = parseFloat(tarificationResult.tarifs.cotisationMensuelle);
              console.log('Prix Alptis extrait de cotisationMensuelle:', result.prix);
              this.cdr.detectChanges();
            } else {
              this.notifyPricingUnavailable(result, 'empty_response', {
                extraDetail: 'Réponse Alptis reçue mais sans montant de cotisation mensuelle.'
              });
              console.warn('Aucun prix trouvé dans la réponse Alptis:', tarificationResult.tarifs);
            }

            // Ne pas utiliser generation_tarif comme id_projet:
            // ce champ peut contenir un millésime (ex: 2026) et non un identifiant projet.
            if (tarificationResult.generationTarif || tarificationResult.generation_tarif) {
              console.log('ℹ️ Alptis generation_tarif reçu (non utilisé comme id_projet):',
                tarificationResult.generationTarif || tarificationResult.generation_tarif);
            }
          } else {
            this.notifyPricingUnavailable(result, 'empty_response', {
              rawMessage: response?.message,
              extraDetail: 'Aucun résultat de tarification Alptis pour ce produit.'
            });
          }
        },
        error: (err: any) => {
          console.error('Alptis pricing error:', err);
          this.notifyApiErrorToast({
            summary: 'Tarif indisponible — Alptis',
            fallback: 'Erreur lors de la tarification Alptis.',
            error: err,
            context: 'ALPTIS',
            result,
            severity: 'warn'
          });
        }
      });
    });
  }

  loadExampleData(): void {
    const demoPhone = this.generateDemoPhone();
    // Le jeu d'exemple comporte un conjoint : le bloc doit être ouvert, sinon
    // ses champs seraient renseignés mais invisibles.
    this.aConjoint = true;
    this.insuranceForm.get('personalInfo')?.patchValue({
      civilite: 'M',
      nom: 'Dupont',
      prenom: 'Jean',
      dateNaissance: '16/04/1999',
      regime: 'TNS',
      etatCivil: 'celibataire',
      adresse: '123 Rue de la République',
      codePostal: '69001',
      ville: 'Lyon',
      complementAdresse: 'Appartement 15',
      dateEffet: (() => {
        const today = new Date();
        today.setDate(today.getDate() + 35);
        const day = today.getDate().toString().padStart(2, '0');
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const year = today.getFullYear();
        return `${day}/${month}/${year}`;
      })(),
      email: 'jean.dupont@example.com',
      telephone1: demoPhone,
      telephone2: '0478901234',
      conjoint: {
        civilite: 'F',
        nom: 'Dupont',
        prenom: 'Marie',
        dateNaissance: '22/08/2001'
      }
    });

    // Supprimer tous les enfants - pas d'enfant dans l'exemple
    const enfantsArray = this.insuranceForm.get('personalInfo.enfants') as FormArray;
    while (enfantsArray.length) { enfantsArray.removeAt(0); }

    // Mettre à jour les âges après chargement des données exemple
    this.mainInsuredAge = this.calculateAgeFromValue('16/04/1999');
    this.conjointAge = this.calculateAgeFromValue('22/08/2001');
    this.enfantsAges = [];

    this.messageService.add({
      severity: 'success',
      summary: 'Données chargées',
      detail: 'Tous les champs ont été pré-remplis avec des données d\'exemple (sans enfant).'
    });
  }

  /** Evite de réutiliser un numéro de test déjà connu par Néoliane. */
  private generateDemoPhone(): string {
    const suffix = (Date.now() % 100000000).toString().padStart(8, '0');
    return `07${suffix}`;
  }

  resetForm(): void {
    // Referme le bloc conjoint : sinon « Réinitialiser » laissait des champs
    // conjoint vides mais visibles, et donc exigés par le validateur.
    this.aConjoint = false;
    this.insuranceForm.reset();
    this.enfants.clear();
    this.initializeForm();
    this.setupConjointListener();
    this.activeIndex = 0;
    this.results = [];
    this.comparisonResults = [];
    // Réinitialiser les âges
    this.mainInsuredAge = null;
    this.conjointAge = null;
    this.enfantsAges = [];
    this.messageService.add({ severity: 'info', summary: 'Formulaire réinitialisé', detail: 'Le formulaire a été vidé.' });
  }

  hasResults(): boolean {
    return this.comparisonResults && this.comparisonResults.length > 0;
  }


  public getCoverageClass(value: number): string {
    if (value >= 300) return 'coverage-high';
    if (value >= 150) return 'coverage-medium';
    return 'coverage-low';
  }

  /**
   * Nouvelle méthode améliorée pour obtenir la classe CSS basée sur la proximité
   * Logique des couleurs :
   * - Vert clair : Résultat = Besoin (égalité)
   * - Vert foncé : Résultat > Besoin (supérieur)
   * - Orange : Résultat < Besoin de moins de 50 (%) ou moins de 100 (€)
   * - Rouge : Résultat < Besoin de plus de 50 (%) ou plus de 100 (€)
   */
  getProximityBasedCoverageClass(actualValue: string | number, neededValue: string | number, guaranteeType: string = ''): string {
    // Convertir les valeurs en nombres
    const actual = typeof actualValue === 'number' ? actualValue : this.extractNumericValue(actualValue);
    const needed = typeof neededValue === 'number' ? neededValue : this.extractNumericValue(neededValue);

    // Si pas de valeur ou valeur invalide
    if (actual === 0 || needed === 0 || actual === null || needed === null) {
      return 'coverage-none';
    }

    // Déterminer si c'est un forfait (€) ou un pourcentage (%)
    const isForfait = this.isForfaitGarantie(guaranteeType);
    const seuil = isForfait ? 100 : 50; // Seuil : 100€ pour forfaits, 50% pour pourcentages

    // Calculer la différence
    const difference = actual - needed;

    // Logique des couleurs
    let cssClass = '';
    if (difference === 0) {
      // Égalité : Vert clair
      cssClass = 'coverage-exact';
    } else if (difference > 0) {
      // Supérieur : Vert foncé
      cssClass = 'coverage-excellent';
    } else if (Math.abs(difference) < seuil) {
      // Inférieur de moins de seuil : Orange
      cssClass = 'coverage-partial';
    } else {
      // Inférieur de plus de seuil : Rouge
      cssClass = 'coverage-insufficient';
    }

    return cssClass;
  }

  /**
   * Détermine si une garantie est un forfait (€) ou un pourcentage (%)
   */
  private isForfaitGarantie(guaranteeType: string): boolean {
    // Trouver la garantie dans la liste
    const garantie = this.garanties.find(g => g.formControlName === guaranteeType);
    return garantie?.unit === '€';
  }

  /**
   * Obtient la description textuelle de la proximité pour les tooltips
   */
  getProximityDescription(actualValue: string | number, neededValue: string | number): string {
    const proximityInfo = this.proximityService.getProximityInfo(neededValue, actualValue);
    return proximityInfo.description;
  }

  /**
   * Obtient le score de proximité en pourcentage
   */
  getProximityScore(actualValue: string | number, neededValue: string | number): number {
    const proximityInfo = this.proximityService.getProximityInfo(neededValue, actualValue);
    return proximityInfo.proximityScore;
  }

  /**
   * Méthode de test pour vérifier le système de couleurs de proximité
   * À appeler depuis la console du navigateur pour tester
   */
  testProximityColors(): void {
    console.log('🎨 Test du système de couleurs de proximité:');

    const testCases = [
      { need: 100, actual: 100, description: 'Identique' },
      { need: 100, actual: 105, description: 'Très proche (+5%)' },
      { need: 100, actual: 120, description: 'Proche (+20%)' },
      { need: 100, actual: 150, description: 'Un peu loin (+50%)' },
      { need: 100, actual: 200, description: 'Loin (+100%)' },
      { need: 100, actual: 350, description: 'Très loin (+250%)' },
      { need: 100, actual: 0, description: 'Pas de couverture' },
      { need: '100%', actual: '125%', description: 'Pourcentages' },
      { need: '50€', actual: '75€', description: 'Euros' }
    ];

    testCases.forEach(test => {
      const cssClass = this.getProximityBasedCoverageClass(test.actual, test.need);
      const description = this.getProximityDescription(test.actual, test.need);
      const score = this.getProximityScore(test.actual, test.need);
      console.log(`${test.description}: Besoin=${test.need}, Actuel=${test.actual} → ${cssClass} (${description}, Score: ${score}%)`);
    });
  }

  getCashediCoverageClass(actualValue: number, neededValue: number): string {
    return this.getProximityBasedCoverageClass(actualValue, neededValue);
  }

  getCorrespondenceClass(percentage: number): string {
    if (percentage >= 90) return 'cashedi-correspondence-excellent';
    if (percentage >= 75) return 'cashedi-correspondence-good';
    if (percentage >= 60) return 'cashedi-correspondence-average';
    return 'cashedi-correspondence-poor';
  }

  isNumeric(value: any): boolean {
    if (value === null || value === undefined) return false;
    const num = parseFloat(value);
    return !isNaN(num) && isFinite(num);
  }

  generateQuote(result: ComparisonResult, action: 'envoiParEmail' | 'telechargement'): void {
    console.log('🔘 BOUTON CLIQUE - generateQuote appelé pour:', result.assurance);
    console.log('🔍 Détails du résultat:', result);
    console.log('🎯 Action demandée:', action);

    const payload = this.getQuotePayload(result);
    console.log('📦 Payload obtenu:', payload);

    if (!payload) {
      console.error('❌ ERREUR: Aucun payload généré');
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de générer le devis, données manquantes.',
        context: 'DEVIS'
      });
      return;
    }

    console.log('🔷 Traitement devis en cours...');

    // Normaliser le nom de l'assurance pour gérer les accents
    const normalizeName = (name: string): string => {
      return (name || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    };
    const assuranceLower = normalizeName(result.assurance);

    if (assuranceLower.includes('april') && !assuranceLower.includes('apivia')) {
      console.log('🔷 Traitement devis April - Envoi par email');
      console.log('April Quote Payload:', payload);
      this.insuranceService.sendAprilQuoteByEmail(payload).subscribe({
        next: () => this.addToastOnce({
          severity: 'success',
          summary: 'Email envoyé',
          detail: 'Le devis April a été envoyé par email.',
          context: 'APRIL'
        }),
        error: () => this.addToastOnce({
          severity: 'error',
          summary: 'Erreur Email',
          detail: 'Impossible d\'envoyer le devis April.',
          context: 'APRIL'
        })
      });
    } else if (assuranceLower.includes('generali') &&
      (assuranceLower.includes('sante pro') || result.formule.toLowerCase().includes('p'))) {
      // Génération PDF local pour Generali Santé Pro
      console.log('🔷 Traitement devis Generali Santé Pro - Génération PDF local');
      this.generateGeneraliPDF(result);
    } else if (assuranceLower.includes('generali')) {
      // Génération PDF local pour Generali standard
      console.log('🔷 Traitement devis Generali standard - Génération PDF local');
      this.generateGeneraliPDF(result);
    } else if (this.isZeniooOffer(result.assurance)) {
      // Traitement dynamique complet pour Zenioo: devis PDF + option souscription
      this.handleZeniooQuotation(result, payload);
      return;
    } else if (assuranceLower.includes('utwin')) {
      // UTWIN : utiliser le nouvel endpoint proposition commerciale
      console.log('🔷 Traitement devis Utwin via Proposition Commerciale');
      result.isPricingLoading = true;
      this.insuranceService.utwinPropositionCommerciale(
        payload as any,
        result.formule,
        (result as any).utwinCodeProduit || 'UTWIN_MULTI',
        'telechargement'
      ).subscribe({
        next: (response: any) => {
          result.isPricingLoading = false;
          console.log('✅ Proposition commerciale UTWIN:', response);

          // Chercher le document PropositionCommercialeSante dans la réponse
          const propositions = response?.propositions || [];
          let propositionDoc: any = null;
          for (const prop of propositions) {
            const docs = prop.documents || [];
            propositionDoc = docs.find((d: any) => d.type === 'PropositionCommercialeSante');
            if (propositionDoc) break;
          }

          if (propositionDoc?.identifiant) {
            // Télécharger le document via l'endpoint documents
            this.insuranceService.getUtwinDocumentById(propositionDoc.identifiant).subscribe({
              next: (docResponse: any) => {
                if (docResponse?.base64) {
                  // Convertir le base64 en blob et télécharger
                  const byteCharacters = atob(docResponse.base64);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  const blob = new Blob([byteArray], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = propositionDoc.nom || 'proposition-utwin.pdf';
                  a.click();
                  window.URL.revokeObjectURL(url);
                }
                this.addToastOnce({
                  severity: 'success',
                  summary: 'Téléchargement',
                  detail: `Proposition commerciale UTWIN téléchargée (Réf: ${response.referenceProposition}).`,
                  context: 'UTWIN'
                });
              },
              error: () => {
                this.addToastOnce({
                  severity: 'warn',
                  summary: 'Proposition créée',
                  detail: `Proposition créée (Réf: ${response.referenceProposition}) mais le téléchargement du document a échoué.`,
                  context: 'UTWIN'
                });
              }
            });
          } else {
            this.addToastOnce({
              severity: 'success',
              summary: 'Proposition créée',
              detail: `Proposition commerciale UTWIN créée (Réf: ${response.referenceProposition}).`,
              context: 'UTWIN'
            });
          }
        },
        error: (error: any) => {
          result.isPricingLoading = false;
          console.error('❌ Erreur proposition commerciale UTWIN:', error);
          this.notifyApiErrorToast({
            summary: 'Erreur',
            fallback: 'Impossible de générer la proposition commerciale Utwin.',
            error,
            context: 'UTWIN'
          });
        }
      });
    } else if (assuranceLower.includes('apivia')) {
      this.insuranceService.downloadApiviaQuotePdf(payload).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `devis-apivia.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.addToastOnce({
            severity: 'success',
            summary: 'Téléchargement',
            detail: 'Le devis Apivia est en cours de téléchargement.',
            context: 'APIVIA'
          });
        },
        error: (error) => this.notifyApiErrorToast({
          summary: 'Erreur PDF',
          fallback: 'Impossible de télécharger le devis Apivia.',
          error,
          context: 'APIVIA'
        })
      });
    } else if (assuranceLower.includes('apicil')) {
      result.isPricingLoading = true;
      console.log('(APICIL) Payload création devis:', JSON.stringify(payload, null, 2));
      const submitApicilDevis = (attempt: number = 0) => this.apicilService.createDevis(payload).subscribe({
        next: (response: any) => {
          result.isPricingLoading = false;
          const firstQuote = Array.isArray(response?.relatedQuotes) ? response.relatedQuotes[0] : null;
          const accessUrl = firstQuote?.accessURL || firstQuote?.accessUrl;
          const reference = firstQuote?.reference;
          const idOpportunite = response?.IdOpportunite;

          if (accessUrl) {
            window.open(accessUrl, '_blank');
          }

          if (idOpportunite) {
            const downloadApicilLiasse = (attempt: number = 0) => this.apicilService.downloadLiasseDevis(idOpportunite).subscribe({
              next: (zipBlob: Blob) => {
                const zipUrl = window.URL.createObjectURL(zipBlob);
                const a = document.createElement('a');
                a.href = zipUrl;
                a.download = `apicil-liasse-${reference || idOpportunite}.zip`;
                a.click();
                window.URL.revokeObjectURL(zipUrl);
              },
              error: (liasseErr: any) => {
                console.error('(APICIL) Erreur téléchargement liasse:', liasseErr);
                const liasseMsg = this.extractApicilErrorDetail(liasseErr);
                const isTimeout = /timeout exceeded/i.test(liasseMsg);
                if (isTimeout && attempt < 1) {
                  this.addToastOnce({
                    severity: 'warn',
                    summary: 'APICIL',
                    detail: 'Téléchargement liasse APICIL expiré, nouvelle tentative en cours...',
                    context: 'APICIL_LIASSE_TIMEOUT_RETRY'
                  });
                  setTimeout(() => downloadApicilLiasse(attempt + 1), 1200);
                  return;
                }
                this.addToastOnce({
                  severity: 'warn',
                  summary: 'APICIL',
                  detail: `Devis créé (${reference || idOpportunite}) mais téléchargement liasse indisponible: ${liasseMsg}`,
                  context: 'APICIL'
                });
              }
            });
            downloadApicilLiasse();
          }

          this.addToastOnce({
            severity: 'success',
            summary: 'Devis APICIL créé',
            detail: reference
              ? `Référence ${reference}${accessUrl ? ' - ouverture MyVERALTI.' : '.'}`
              : 'Projet créé avec succès.',
            context: 'APICIL'
          });
        },
        error: (err: any) => {
          const msg = this.extractApicilErrorDetail(err);
          const isTimeout = /timeout exceeded/i.test(msg);
          if (isTimeout && attempt < 1) {
            this.addToastOnce({
              severity: 'warn',
              summary: 'APICIL',
              detail: 'Le service APICIL a expiré, nouvelle tentative en cours...',
              context: 'APICIL_TIMEOUT_RETRY'
            });
            setTimeout(() => submitApicilDevis(attempt + 1), 1500);
            return;
          }

          result.isPricingLoading = false;
          console.error('(APICIL) createDevis erreur brute:', err);
          console.error('(APICIL) createDevis détail parsé:', msg);
          // Important: bypass addToastOnce to always display latest backend detail while debugging APICIL.
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur APICIL',
            detail: msg,
            life: 12000
          });
        }
      });
      submitApicilDevis();
    } else if (assuranceLower.includes('alptis')) {
      const isFrontaliers = assuranceLower.includes('frontalier') ||
        result.formule.toLowerCase().includes('frontalier');
      const isSanteProtect = assuranceLower.includes('protect') ||
        result.formule.toLowerCase().includes('protect');
      const isSanteSelect = assuranceLower.includes('select') ||
        result.formule.toLowerCase().includes('select');
      const isSanteProPlus = assuranceLower.includes('sante pro+') ||
        result.formule.toLowerCase().includes('sante pro+');

      if (isFrontaliers) {
        this.generateFrontaliersPDF(result);
      } else if (isSanteProtect) {
        console.log('📄 Génération Devis Santé Protect via Création Projet...');

        // Construction du payload pour la création de projet
        const payload = this.buildAlptisQuotePayload(result);

        if (!payload) {
          this.addToastOnce({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Données manquantes pour le devis Santé Protect.',
            context: 'ALPTIS'
          });
          return;
        }

        // Forcer le code produit pour Santé Protect
        if (payload.offre) {
          payload.offre.code_produit = 'SANTE_PROTECT';
        }

        // Si on a déjà un id projet Alptis, l'envoyer pour bypass la création projet côté backend.
        const existingProjectId = (result as any).alptisProjectId;
        if (existingProjectId) {
          payload.id_projet = String(existingProjectId);
          console.log('🆔 Santé Protect: id_projet envoyé au backend:', payload.id_projet);
        } else {
          console.warn('⚠️ Santé Protect: aucun alptisProjectId valide, le backend tentera une création projet.');
        }

        result.isPricingLoading = true;
        this.alptisService.generateEtudePersonnalisee(payload).subscribe({
          next: (blob) => {
            result.isPricingLoading = false;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Etude-Personnalisee-Sante-Protect-${result.assurance}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            this.addToastOnce({
              severity: 'success',
              summary: 'Succès',
              detail: 'Le devis Santé Protect a été téléchargé.',
              context: 'ALPTIS'
            });
          },
          error: (error) => {
            result.isPricingLoading = false;
            console.error('❌ Erreur API Santé Protect:', error);
            this.notifyApiErrorToast({
              summary: 'Erreur',
              fallback: 'Impossible de générer le devis Santé Protect.',
              error,
              context: 'ALPTIS'
            });
          }
        });

      } else if (isSanteSelect) {
        const alptisPayload = this.buildAlptisQuotePayload(result);
        if (!alptisPayload) {
          this.addToastOnce({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Impossible de générer le devis Santé Select, données manquantes.',
            context: 'ALPTIS'
          });
          return;
        }

        if (alptisPayload.offre) {
          alptisPayload.offre.code_produit = 'SANTE_SELECT';
        }

        result.isPricingLoading = true;
        this.alptisService.generateEtudePersonnalisee(alptisPayload).subscribe({
          next: (blob) => {
            result.isPricingLoading = false;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `etude-alptis-${result.formule || 'sante-select'}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            this.addToastOnce({
              severity: 'success',
              summary: 'Téléchargement',
              detail: 'Le devis Santé Select est en cours de téléchargement.',
              context: 'ALPTIS'
            });
          },
          error: (err) => {
            result.isPricingLoading = false;
            this.generateGenericDevisTemplate(result);
          }
        });
      } else {
        this.addToastOnce({
          severity: 'warn',
          summary: 'ALPTIS',
          detail: 'Type de produit ALPTIS non reconnu pour la génération du devis.',
          context: 'ALPTIS'
        });
        this.generateGenericDevisTemplate(result);
      }
    } else if (assuranceLower.includes('neoliane')) {
      console.log('📄 Génération devis Néoliane V2...');
      const profileId = (result as any).neolianeProfileId;
      const pricingId = (result as any).neolianePricingId;

      if (!profileId || !pricingId) {
        this.addToastOnce({
          severity: 'error',
          summary: 'Erreur',
          detail: 'IDs de profil/prix manquants. Génération du devis modèle...',
          context: 'NEOLIANE'
        });
        this.generateGenericDevisTemplate(result);
        return;
      }

      const pInfo = this.insuranceForm.get('personalInfo')?.value || {};
      const quotationRequest: NeolianeV2QuotationRequest = {
        gender: (pInfo.civilite === 'M' || pInfo.civilite === 'M.') ? 'M' : 'Mme',
        lastname: pInfo.nom || 'Dupont',
        firstname: pInfo.prenom || 'Jean',
        address: `${pInfo.adresse || ''} ${pInfo.codePostal || ''} ${pInfo.ville || ''}`.trim(),
        email: pInfo.email || ''
      };

      result.isPricingLoading = true;
      this.neolianeV2Service.downloadQuotation(profileId, pricingId, quotationRequest).subscribe({
        next: (blob) => {
          result.isPricingLoading = false;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `devis-neoliane-${result.formule || 'v2'}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.addToastOnce({
            severity: 'success',
            summary: 'Téléchargement',
            detail: 'Le devis Néoliane a été téléchargé en PDF.',
            context: 'NEOLIANE'
          });
        },
        error: (err) => {
          result.isPricingLoading = false;
          console.error('❌ Erreur téléchargement devis Néoliane V2:', err);
          this.generateGenericDevisTemplate(result);
        }
      });
    } else {
      console.log('📄 Génération Devis Template Générique pour:', result.assurance);
      this.generateGenericDevisTemplate(result);
    }
  }

  public generateGenericDevisHtmlString(result: ComparisonResult): string {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value || {};
    const conjoint = personalInfo.conjoint || {};
    const enfants = (this.enfants?.controls || []).map(c => c.value);

    const civilite = personalInfo.civilite || '';
    const nom = (personalInfo.nom || 'Client').toUpperCase();
    const prenom = personalInfo.prenom || '';
    const dateNaissance = personalInfo.dateNaissance || '';
    const mainAge = this.ageAssurePrincipal;

    const adresse = personalInfo.adresse || '';
    const codePostal = personalInfo.codePostal || '';
    const ville = personalInfo.ville || '';
    const email = personalInfo.email || '';
    const telephone = personalInfo.telephone1 || '';
    const dateEffet = personalInfo.dateEffet || '';

    const priceValue = this.isNumeric(result.prix) ? `${result.prix} €` : (result.tarifGlobal && this.isNumeric(result.tarifGlobal) ? `${result.tarifGlobal} €` : (result.prix || 'Sur devis'));
    const todayStr = new Date().toLocaleDateString('fr-FR');
    const refDevis = 'DEV-CAS-' + Math.floor(100000 + Math.random() * 900000);

    let garantiesRowsHtml = '';
    (this.garanties || []).forEach(garantie => {
      const valRaw = result.garanties ? result.garanties[garantie.formControlName] : null;
      let valDisplay = '-';
      if (valRaw !== undefined && valRaw !== null && valRaw !== '-' && valRaw !== 0) {
        valDisplay = this.isNumeric(valRaw) ? `${valRaw}${garantie.unit}` : `${valRaw}`;
      }
      const userNeed = this.getUserNeedDisplay(garantie.formControlName, garantie.unit);

      garantiesRowsHtml += `
        <tr>
          <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #334155;">${garantie.label}</td>
          <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: center; color: #64748b; background: #f8fafc;">${userNeed}</td>
          <td style="padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: center; font-weight: bold; color: #A102F2; background: #fcf5ff;">${valDisplay}</td>
        </tr>
      `;
    });

    let conjointHtml = '';
    if (conjoint && (conjoint.nom || conjoint.prenom)) {
      const ageConj = this.ageConjoint ? `(${this.ageConjoint} ans)` : '';
      conjointHtml = `<div class="info-row"><span class="info-label">Conjoint :</span><span class="info-value">${conjoint.civilite || ''} ${conjoint.nom || ''} ${conjoint.prenom || ''} - Né(e) le ${conjoint.dateNaissance || ''} ${ageConj}</span></div>`;
    }

    let enfantsHtml = '';
    if (enfants && enfants.length > 0) {
      const enfantsList = enfants.map((enf: any, idx: number) => {
        const ageEnf = this.getAgeEnfant(idx) ? `(${this.getAgeEnfant(idx)} ans)` : '';
        return `${enf.prenom || 'Enfant ' + (idx + 1)} - Né(e) le ${enf.dateNaissance || ''} ${ageEnf}`;
      }).join(', ');
      enfantsHtml = `<div class="info-row"><span class="info-label">Enfant(s) :</span><span class="info-value">${enfantsList}</span></div>`;
    }

    const produitName = (result as any).produit || (result as any).contractName || result.assurance;
    const levelName = (result as any).levelName || result.formule;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Devis Assurance Santé - ${result.assurance} - ${nom} ${prenom}</title>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 30px; color: #1e293b; background: #ffffff; line-height: 1.5; }
        .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #A102F2; padding-bottom: 15px; margin-bottom: 25px; }
        .brand { color: #A102F2; }
        .brand h1 { margin: 0; font-size: 26px; letter-spacing: -0.5px; }
        .brand p { margin: 2px 0 0 0; color: #64748b; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; }
        .doc-info { text-align: right; font-size: 13px; color: #475569; }
        .doc-info strong { color: #0f172a; }
        .grid-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px; }
        .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
        .card h3 { margin-top: 0; margin-bottom: 12px; color: #A102F2; font-size: 15px; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; display: flex; align-items: center; gap: 8px; }
        .info-row { display: flex; margin-bottom: 6px; font-size: 13px; }
        .info-label { width: 140px; font-weight: 600; color: #64748b; flex-shrink: 0; }
        .info-value { color: #0f172a; font-weight: 500; }

        .price-banner { background: linear-gradient(135deg, #A102F2 0%, #7c02b8 100%); color: white; border-radius: 8px; padding: 18px 24px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; box-shadow: 0 4px 12px rgba(161, 2, 242, 0.15); }
        .price-banner .title { font-size: 18px; font-weight: 600; margin: 0; }
        .price-banner .sub { font-size: 12px; opacity: 0.9; margin-top: 2px; }
        .price-banner .amount { font-size: 32px; font-weight: 800; letter-spacing: -0.5px; }
        .price-banner .period { font-size: 13px; opacity: 0.9; text-align: right; }

        table.garanties-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 13px; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
        table.garanties-table th { background: #f1f5f9; color: #334155; padding: 12px; font-weight: 700; text-align: left; border-bottom: 2px solid #cbd5e1; }
        table.garanties-table th.center { text-align: center; }

        .footer { margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 15px; text-align: center; font-size: 11px; color: #94a3b8; }
        .footer p { margin: 3px 0; }

        @media print {
            body { padding: 0; margin: 15mm; }
            .price-banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            table.garanties-table th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="brand">
            <h1>BROKINS</h1>
            <p>Courtage & Conseil en Assurance</p>
        </div>
        <div class="doc-info">
            <div><strong>PROPOSITION DE DEVIS SANTÉ</strong></div>
            <div>Réf: <strong>${refDevis}</strong></div>
            <div>Date: ${todayStr}</div>
            <div>Validité: 30 jours</div>
        </div>
    </div>

    <div class="grid-container">
        <div class="card">
            <h3>Informations du Souscripteur</h3>
            <div class="info-row"><span class="info-label">Assuré principal :</span><span class="info-value">${civilite} ${nom} ${prenom}</span></div>
            <div class="info-row"><span class="info-label">Date de naissance :</span><span class="info-value">${dateNaissance} ${mainAge ? '(' + mainAge + ' ans)' : ''}</span></div>
            <div class="info-row"><span class="info-label">Adresse :</span><span class="info-value">${adresse ? adresse + ', ' : ''}${codePostal} ${ville}</span></div>
            <div class="info-row"><span class="info-label">Email :</span><span class="info-value">${email || '-'}</span></div>
            <div class="info-row"><span class="info-label">Téléphone :</span><span class="info-value">${telephone || '-'}</span></div>
            ${conjointHtml}
            ${enfantsHtml}
        </div>

        <div class="card">
            <h3>Détails de la Formule Sélectionnée</h3>
            <div class="info-row"><span class="info-label">Compagnie :</span><span class="info-value"><strong>${result.assurance}</strong></span></div>
            <div class="info-row"><span class="info-label">Nom du produit :</span><span class="info-value">${produitName}</span></div>
            <div class="info-row"><span class="info-label">Formule / Niveau :</span><span class="info-value"><strong>${levelName}</strong></span></div>
            <div class="info-row"><span class="info-label">Date d'effet souhaitée :</span><span class="info-value">${dateEffet || 'Immédiate'}</span></div>
            <div class="info-row"><span class="info-label">Score de besoins :</span><span class="info-value">${result.correspondencePercentage ? result.correspondencePercentage + '%' : '100%'}</span></div>
        </div>
    </div>

    <div class="price-banner">
        <div>
            <div class="title">${result.assurance} - ${levelName}</div>
            <div class="sub">Tarification personnalisée estimée</div>
        </div>
        <div>
            <div class="amount">${priceValue}</div>
            <div class="period">TTC / mois</div>
        </div>
    </div>

    <h3 style="color: #A102F2; margin-bottom: 10px; font-size: 16px;">Tableau comparatif des garanties</h3>
    <table class="garanties-table">
        <thead>
            <tr>
                <th style="width: 40%;">Postes de Garantie</th>
                <th style="width: 30%;" class="center">Vos Besoins Exprimés</th>
                <th style="width: 30%;" class="center">Garantie Offerte (${result.formule})</th>
            </tr>
        </thead>
        <tbody>
            ${garantiesRowsHtml}
        </tbody>
    </table>

    <div class="footer">
        <p><strong>BROKINS COURTAGE</strong> - Ce document constitue une proposition tarifaire et de garanties personnalisée basée sur vos informations.</p>
        <p>Ce devis est valable 30 jours à compter du ${todayStr} et ne constitue pas un engagement contractuel sans souscription définitive.</p>
        <p>Pour toute question ou souscription, contactez votre conseiller Brokins.</p>
    </div>
</body>
</html>`;
  }

  /**
   * Produit le devis et l'enregistre directement en PDF.
   *
   * ANOMALIE CORRIGEE : le devis était auparavant ouvert dans une fenêtre puis
   * envoyé à `window.print()`, l'agent devant choisir « Enregistrer au format
   * PDF » dans la boîte de dialogue du navigateur. Ce n'était pas un
   * téléchargement mais une impression : le rendu dépendait de l'imprimante,
   * des marges et de l'échelle sélectionnées, deux devis du même dossier
   * pouvaient différer, et un bloqueur de fenêtres surgissantes empêchait
   * purement et simplement la génération.
   *
   * Le HTML — le gabarit existant, inchangé — est désormais converti en PDF
   * par le backend, et le fichier est enregistré en un clic.
   */
  public generateGenericDevisTemplate(result: ComparisonResult): void {
    const htmlContent = this.generateGenericDevisHtmlString(result);
    const nomFichier = `devis-${this.toSlug(result.assurance)}-${this.toSlug(result.formule)}.pdf`;

    this.telechargerDevisPdf(htmlContent, nomFichier, result);
  }

  /**
   * Convertit un devis HTML en PDF via le backend et déclenche l'enregistrement.
   *
   * En cas d'indisponibilité du service, on retombe sur un téléchargement du
   * HTML : mieux vaut un devis dans un format ouvrable qu'aucun devis.
   */
  private telechargerDevisPdf(
    htmlContent: string,
    nomFichier: string,
    result?: ComparisonResult
  ): void {
    const url = `${environment.casApiUrl}/api/v1/devis/pdf`;
    const libelle = result ? `${result.assurance} (${result.formule})` : 'la comparaison';

    this.http
      .post(url, { html: htmlContent, filename: nomFichier }, { responseType: 'blob' })
      .subscribe({
        next: (pdf: Blob) => {
          this.enregistrerFichier(pdf, nomFichier);
          this.addToastOnce({
            severity: 'success',
            summary: 'Devis téléchargé',
            detail: `Le devis pour ${libelle} a été enregistré en PDF.`,
            context: 'GENERIC_DEVIS'
          });
        },
        error: (err) => {
          console.error('Génération PDF indisponible, repli sur le HTML.', err);
          this.enregistrerFichier(
            new Blob([htmlContent], { type: 'text/html' }),
            nomFichier.replace(/\.pdf$/i, '.html')
          );
          this.addToastOnce({
            severity: 'warn',
            summary: 'Devis téléchargé en HTML',
            detail:
              'Le service de génération PDF est indisponible : le devis a été enregistré au format HTML.',
            context: 'GENERIC_DEVIS'
          });
        }
      });
  }

  /** Déclenche l'enregistrement d'un fichier par le navigateur. */
  private enregistrerFichier(contenu: Blob, nomFichier: string): void {
    const url = window.URL.createObjectURL(contenu);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nomFichier;
    document.body.appendChild(lien);
    lien.click();
    document.body.removeChild(lien);
    // Libère l'URL objet : sans cela le Blob reste en mémoire jusqu'au
    // rechargement de la page, ce qui compte pour des devis de plusieurs Mo.
    window.URL.revokeObjectURL(url);
  }

  startAprilSubscription(result: ComparisonResult): void {
    console.log('🔷 Démarrage souscription April pour:', result.assurance);

    const payload = this.buildAprilSubscriptionPayload(result);
    if (!payload) {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de démarrer la souscription, données manquantes.',
        context: 'APRIL_SUBSCRIPTION'
      });
      return;
    }
this.aprilService.startSmartSubscription(payload).subscribe({
      next: (response: any) => {
        result.isPricingLoading = false;
        console.log('✅ Souscription April démarrée:', response);

        // Ouvrir directement le lien April dans un nouvel onglet
        if (response.content) {
          window.open(response.content, '_blank');
          this.addToastOnce({
            severity: 'success',
            summary: 'Projet April créé',
            detail: `Le projet a été ouvert dans un nouvel onglet. ID: ${response.projectId}`,
            context: 'APRIL_SUBSCRIPTION'
          });
        } else if (response.projectId) {
          this.addToastOnce({
            severity: 'success',
            summary: 'Souscription initiée',
            detail: `Projet créé. ID: ${response.projectId}`,
            context: 'APRIL_SUBSCRIPTION'
          });

          // Télécharger le bulletin d'adhésion si disponible
          if (response.documents && response.documents.length > 0) {
            const doc = response.documents[0];
            this.aprilService.downloadDocument(response.projectId, doc.documentId).subscribe({
              next: (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `bulletin-adhesion-${result.assurance}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
              },
              error: (err: any) => console.error('Erreur téléchargement document:', err)
            });
          }
        } else if (response.marketplaceUrl) {
          // Si c'est une signature en ligne, afficher l'URL
          this.addToastOnce({
            severity: 'info',
            summary: 'Signature en ligne',
            detail: `Un email a été envoyé au client pour signer électroniquement.`,
            context: 'APRIL_SUBSCRIPTION'
          });
        }
      },
      error: (error: any) => {
        result.isPricingLoading = false;
        console.error('❌ Erreur souscription April:', error);
        this.addToastOnce({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error?.error || 'Impossible de démarrer la souscription April.',
          context: 'APRIL_SUBSCRIPTION'
        });
      }
    });
  }

  /**
   * Démarrer la souscription Néoliane avec le workflow complet EZ API
   * Workflow: profile → generateprices → cart → offer
   */
  startNeolianeSubscription(result: ComparisonResult): void {
    console.log('🔷 Démarrage souscription Néoliane EZ pour:', result.assurance);

    // Construire le payload complet pour le workflow
    const payload = this.buildNeolianeFullWorkflowPayload(result);
    if (!payload) {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de construire les données de souscription.',
        context: 'NEOLIANE_SUBSCRIPTION'
      });
      return;
    }

    result.isPricingLoading = true;
    console.log('📦 Full EZ Workflow payload:', JSON.stringify(payload, null, 2));

    // Utiliser le workflow complet: profile → prices → cart → offer
    this.neolianeService.fullEzSubscription(payload).subscribe({
      next: (response: any) => {
        result.isPricingLoading = false;
        console.log('✅ Workflow EZ Néoliane terminé:', response);

        if (response.id) {
          const leadId = response.lead;
          const offerId = response.id;

          // Afficher un toast avec les infos importantes
          this.addToastOnce({
            severity: 'success',
            summary: 'Offre Néoliane créée',
            detail: `Lead: ${leadId} - Connectez-vous à l'extranet pour finaliser`,
            context: 'NEOLIANE_SUBSCRIPTION'
          });

          // Afficher les contrats créés
          if (response.contracts) {
            response.contracts.forEach((contract: any) => {
              console.log(`📄 Contrat ${contract.id} - Status: ${contract.contractStatus} - Prix: ${contract.totalPrice}€`);
            });
          }

          // Ouvrir l'extranet Néoliane dans un nouvel onglet
          window.open('https://extranet.neoliane.fr', '_blank');

          // Afficher un message avec le lead pour retrouver l'affaire
          console.log(`🌐 Extranet ouvert - Recherchez par Lead ID: ${leadId} ou Email/Phone du client`);
        }
      },
      error: (error: any) => {
        result.isPricingLoading = false;
        console.error('❌ Erreur workflow EZ Néoliane:', error);
        this.addToastOnce({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error?.error || 'Impossible de créer l\'offre Néoliane.',
          context: 'NEOLIANE_SUBSCRIPTION'
        });
      }
    });
  }

  /**
   * Démarrer la souscription UTWIN Santé
   * Crée une proposition commerciale, initialise un parcours de souscription
   * et ouvre le lien d'accès dans un nouvel onglet
   */
  startUtwinSubscription(result: ComparisonResult): void {
    console.log('🔷 Démarrage souscription UTWIN pour:', result.assurance);

    const payload = this.buildUtwinSouscriptionPayload(result);
    if (!payload) {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de démarrer la souscription, données manquantes.',
        context: 'UTWIN_SUBSCRIPTION'
      });
      return;
    }

    result.isPricingLoading = true;

    this.insuranceService.utwinSouscription(
      payload,
      result.formule || '',
      (result as any).utwinCodeProduit || 'UTWIN_MULTI',
      normalizeCasCommission(result.selectedCommission)
    ).subscribe({
      next: (response: any) => {
        result.isPricingLoading = false;
        console.log('✅ Souscription UTWIN réussie:', response);

        // Chercher le lien de souscription dans les propositions
        let lienSouscription: string | null = null;
        if (response?.propositions && response.propositions.length > 0) {
          lienSouscription = response.propositions[0].lienSouscription;
        }

        if (lienSouscription) {
          // Ouvrir le lien de souscription dans un nouvel onglet
          window.open(lienSouscription, '_blank');
          this.addToastOnce({
            severity: 'success',
            summary: 'Souscription UTWIN',
            detail: `Parcours de souscription ouvert (Réf: ${response.referenceProposition}). Un email a été envoyé au client.`,
            context: 'UTWIN_SUBSCRIPTION'
          });
        } else {
          this.addToastOnce({
            severity: 'success',
            summary: 'Souscription UTWIN',
            detail: `Souscription initiée (Réf: ${response.referenceProposition}). Un email a été envoyé au client.`,
            context: 'UTWIN_SUBSCRIPTION'
          });
        }

        // Stocker la référence pour usage ultérieur
        (result as any).utwinReferenceProposition = response.referenceProposition;
        (result as any).utwinIdentifiantProjet = response.identifiantProjet;
      },
      error: (error: any) => {
        result.isPricingLoading = false;
        console.error('❌ Erreur souscription UTWIN:', error);
        this.notifyApiErrorToast({
          summary: 'Erreur',
          fallback: 'Impossible de démarrer la souscription UTWIN.',
          error,
          context: 'UTWIN_SUBSCRIPTION'
        });
      }
    });
  }

  /**
   * Construire le payload pour la souscription Néoliane
   */
  private buildNeolianeSubscriptionPayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): { year: string; month: string; day: string } => {
      if (!date) return { year: '', month: '', day: '' };
      let d: Date;
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        // Neoliane API expects NO leading zeros
        return {
          year,
          month: String(parseInt(month, 10)),
          day: String(parseInt(day, 10))
        };
      }
      if (date instanceof Date && !isNaN(date.getTime())) {
        d = date;
      } else {
        return { year: '', month: '', day: '' };
      }
      // Neoliane API expects NO leading zeros
      return {
        year: d.getFullYear().toString(),
        month: String(d.getMonth() + 1),
        day: String(d.getDate())
      };
    };

    const mainAge = ApiMapperUtil.getAge(personalInfo.dateNaissance);
    const regimeCode = ApiMapperUtil.getNeolianeRegime(
      personalInfo.regime,
      mainAge,
      personalInfo.categorieSocioProfessionnelle || personalInfo.profession || ''
    );
    const dateEffet = formatDate(personalInfo.dateEffet);
    const dateNaissance = formatDate(personalInfo.dateNaissance);

    // Récupérer les IDs Néoliane réels depuis le result (stockés lors de la tarification V2)
    const neolianeLeadId = (result as any).neolianeLeadId;
    const neolianeTotalAmount = (result as any).neolianeTotalAmount || result.prix || 50;

    console.log('🔍 IDs Néoliane pour souscription:', { neolianeLeadId, neolianeTotalAmount });

    // Si on a un lead_id V2, on l'utilise directement (pas besoin de créer un nouveau cart)
    // Sinon, on fallback sur l'ancienne API avec la structure CORRECTE
    const cart = neolianeLeadId ? null : {
      total_amount: Number(neolianeTotalAmount) || 50,
      profile: {
        date_effect: {
          day: parseInt(dateEffet.day),
          month: parseInt(dateEffet.month),
          year: parseInt(dateEffet.year)
        },
        zipcode: personalInfo.codePostal,
        producttype: 'sante',
        members: [{
          concern: 'a1',
          birthyear: dateNaissance.year,
          regime: regimeCode,
          products: [{
            product_id: String((result as any).neolianeGammeId || 667),
            formula_id: String((result as any).neolianeFormulaId || 6401)
          }]
        }]
      }
    };

    // Générer le NIR si non fourni
    console.log('🔍 DEBUG generateValidNIR input:', {
      villeNaissance: personalInfo.villeNaissance,
      codePostalNaissance: personalInfo.codePostalNaissance,
      ville: personalInfo.ville,
      codePostal: personalInfo.codePostal,
      dateNaissance: personalInfo.dateNaissance,
      civilite: personalInfo.civilite
    });
    // NIR de test Néoliane pour sandbox (validé par leur API)
    // En production, utiliser le vrai NIR saisi par l'utilisateur
    const testNir = '000000000000097'; // NIR de test officiel Néoliane
    const nir = personalInfo.numeroSecuriteSociale || testNir;
    console.log('🔢 NIR utilisé:', nir, '(manuel:', personalInfo.numeroSecuriteSociale, ', test:', testNir, ')');
    console.log('📊 Données personnelles:', {
      civilite: personalInfo.civilite,
      dateNaissance: personalInfo.dateNaissance,
      codePostal: personalInfo.codePostal,
      ville: personalInfo.ville
    });

    // Construire le concern request
    const concern = {
      members: [{
        is_politically_exposed: 0,
        gender: personalInfo.civilite === 'M' ? 'M' : 'F',
        lastname: personalInfo.nom,
        firstname: personalInfo.prenom,
        regime: personalInfo.regime || 'Salarié',
        birthdate: dateNaissance,
        birthplace: personalInfo.villeNaissance || personalInfo.ville || '',
        birthzipcode: personalInfo.codePostalNaissance || personalInfo.codePostal || '',
        birthcountry: 'France',
        csp: '11',
        numss: nir,
        numorganisme: ''
      }],
      streetnumber: '',
      street: personalInfo.adresse || '',
      streetbis: '',
      zipcode: personalInfo.codePostal,
      city: personalInfo.ville,
      email: personalInfo.email,
      phone: personalInfo.telephone1 || ''
    };

    console.log('📦 Concern payload:', JSON.stringify(concern, null, 2));

    // Construire le bank request
    const bank = {
      details: [{
        levydate: '5',
        levyfrequency: 'Mensuel',
        iban: '',
        bic: '',
        isDifferentFromStepConcern: '0',
        gender: '',
        lastname: '',
        firstname: '',
        streetnumber: '',
        street: '',
        streetbis: '',
        zipcode: '',
        city: '',
        country: ''
      }]
    };

    // Construire le payload final avec lead_id si disponible (V2 workflow)
    const payload: any = { cart, concern, bank };
    if (neolianeLeadId) {
      payload.lead_id = String(neolianeLeadId);
    }

    return payload;
  }

  /**
   * Construire le payload complet pour le workflow EZ Néoliane
   * Inclut: profile, productTypes, offer
   */
  private buildNeolianeFullWorkflowPayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    // Formater la date en YYYY-MM-DD pour l'API EZ
    const formatDateIso = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return '';
    };

    const birthDate = formatDateIso(personalInfo.dateNaissance);
    const birthYear = birthDate ? birthDate.split('-')[0] : '1990';

    // NIR de test Néoliane pour sandbox
    const testNir = '000000000000097';
    const nir = personalInfo.numeroSecuriteSociale || testNir;

    // Date d'effet: premier jour du mois prochain
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const dateEffect = nextMonth.toISOString().split('T')[0];

    // Mapper le régime social
    const mapRegime = (regime: string): string => {
      const norm = ApiMapperUtil.normalizeRegime(regime);
      if (norm === 'TNS') return 'selfEmployed';
      if (norm === 'ALSACE_MOSELLE') return 'alsaceMoselle';
      if (norm === 'AGRICOLE') return 'farmer';
      return 'employee';
    };

    console.log('🚀 Building full EZ workflow payload');

    return {
      // Étape 1: Profil
      profile: {
        zipCode: personalInfo.codePostal || '06230',
        dateEffect: dateEffect,
        productType: 'sante',
        profileHealth: [{
          socialSecurityScheme: mapRegime(personalInfo.regime || 'Salarié'),
          birthYear: birthYear,
          familyMember: 'holder'
        }]
      },
      // Types de produits
      productTypes: ['sante'],
      // Étape 4: Offre
      offer: {
        prospectType: 'optIn',
        persons: [{
          civility: (personalInfo.civilite === 'M' || personalInfo.civilite === 'Monsieur') ? 'mr' : 'mrs',
          lastName: personalInfo.nom,
          firstName: personalInfo.prenom,
          birthDate: birthDate,
          birthCity: personalInfo.villeNaissance || personalInfo.ville || 'Nice',
          birthCountry: 'France',
          birthZipCode: personalInfo.codePostalNaissance || personalInfo.codePostal || '06000',
          familyMember: 'holder',
          socialSecurityNumber: nir,
          isPoliticallyExposed: '0'
        }],
        bank: {
          iban: personalInfo.iban || 'FR7630006000011234567890189',
          paymentDate: 5,
          paymentFrequency: 'monthly'
        },
        address: {
          streetNumber: '',
          street: personalInfo.adresse || 'Avenue du test',
          streetBis: null,
          city: personalInfo.ville || 'Nice',
          phone: personalInfo.telephone1 || '',
          email: personalInfo.email || 'test@test.fr'
        }
      }
    };
  }

  /**
   * Construire le payload pour la souscription April
   */
  private buildAprilSubscriptionPayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return '';
    };

    const formula = result.formule || '';
    const levelCodeMatch = formula.match(/(?:FORMULE|Niveau)\s*(\d+)/i);
    const levelCode = levelCodeMatch ? levelCodeMatch[1].padStart(2, '0') : '01';

    const mainInsuredId = 'i-1';
    const mainApplicantId = 'a-1';
    const bankRefId = 'b-1';

    const productCode = this.mapAprilProductCode(result.assurance);
    const lowerProductCode = (productCode || '').toLowerCase();

    const isMadelinEligible = mapToAprilMandatoryScheme(personalInfo.regime) === 'TNS';

    let coverages: any[] = [];
    if (lowerProductCode.startsWith('santemix') || lowerProductCode === 'flexisante' || lowerProductCode.startsWith('tranquillitesante')) {
      coverages = [
        { insured: { '$ref': mainInsuredId }, guaranteeCode: 'GarantieHospitalisation', eligibleMadelinLaw: isMadelinEligible, levelCode },
        { insured: { '$ref': mainInsuredId }, guaranteeCode: 'GarantieFraisDeSante', eligibleMadelinLaw: isMadelinEligible, levelCode },
        { insured: { '$ref': mainInsuredId }, guaranteeCode: 'GarantieDentaireOptiqueAuditives', eligibleMadelinLaw: isMadelinEligible, levelCode },
        { insured: { '$ref': mainInsuredId }, guaranteeCode: 'GarantieBienEtrePrevention', eligibleMadelinLaw: isMadelinEligible, levelCode }
      ];
    } else if (lowerProductCode === 'santepro' || lowerProductCode === 'santeprostart' || lowerProductCode === 'santesolution') {
      coverages = [{ insured: { '$ref': mainInsuredId }, guaranteeCode: 'MaladieChirurgie', eligibleMadelinLaw: isMadelinEligible, levelCode }];
      if (levelCode !== '01') {
        coverages.push({ insured: { '$ref': mainInsuredId }, guaranteeCode: 'Surcomplementaire', eligibleMadelinLaw: isMadelinEligible });
      }
    } else {
      coverages = [{ insured: { '$ref': mainInsuredId }, guaranteeCode: 'MaladieChirurgie', eligibleMadelinLaw: isMadelinEligible, levelCode }];
    }

    return {
      '$type': 'PrevPro',
      properties: {
        addresses: [{
          '$id': 'adr-1',
          type: 'Actuelle',
          addressLine1: personalInfo.adresse,
          postCode: personalInfo.codePostal,
          city: personalInfo.ville
        }],
        email: personalInfo.email
      },
      persons: [{
        '$id': mainInsuredId,
        title: personalInfo.civilite === 'M' ? 'Monsieur' : 'Madame',
        lastName: personalInfo.nom,
        firstName: personalInfo.prenom,
        birthDate: formatDate(personalInfo.dateNaissance),
        mandatoryScheme: mapToAprilMandatoryScheme(personalInfo.regime),
        familyStatus: personalInfo.etatCivil,
        mobilePhone: { number: personalInfo.telephone1 },
        email: personalInfo.email
      }],
      products: [{
        '$id': 'p-1',
        productCode: productCode,
        effectiveDate: formatDate(personalInfo.dateEffet),
        insureds: [{ '$id': mainApplicantId, role: 'AssurePrincipal', person: { '$ref': mainInsuredId } }],
        coverages: coverages
      }]
    };
  }

  /**
   * Construire le formulaire InsuranceQuoteForm pour la souscription UTWIN
   */
  private buildUtwinSouscriptionPayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm?.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const parseDate = (dateStr: any): Date => {
      if (!dateStr) return new Date();
      if (dateStr instanceof Date) return dateStr;
      if (typeof dateStr === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('/');
        return new Date(+year, +month - 1, +day);
      }
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? new Date() : d;
    };

    // Construire l'objet InsuranceQuoteForm attendu par utwinSouscription()
    const selectedCommission =
      normalizeCasCommission(result.selectedCommission) || this.getCasCommissionFromSettings();

    return {
      insuredPersons: [{
        lastName: personalInfo.nom || 'NOM',
        firstName: personalInfo.prenom || 'PRENOM',
        birthDate: parseDate(personalInfo.dateNaissance),
        gender: personalInfo.civilite === 'M' || personalInfo.civilite === 'Monsieur' ? 'M' : 'F',
        address: {
          street: personalInfo.adresse || '',
          postalCode: personalInfo.codePostal || '75000',
          city: personalInfo.ville || ''
        },
        email: personalInfo.email || '',
        phoneNumber: personalInfo.telephone1 || '',
        regime: personalInfo.regime || 'TNS',
        situation: personalInfo.etatCivil || 'Celibataire',
        addressType: 'Actuelle' as 'Actuelle'
      }],
      contact: {
        email: personalInfo.email || '',
        phoneNumber: personalInfo.telephone1 || '',
        address: {
          street: personalInfo.adresse || '',
          postalCode: personalInfo.codePostal || '75000',
          city: personalInfo.ville || ''
        }
      },
      effectDate: parseDate(personalInfo.dateEffet),
      garanties: [],
      contexteDeVente: {
        produits: [{
          codeProduit: (result as any).utwinCodeProduit || 'UTWIN_MULTI',
          codeFormule: (result.formule || '').match(/\d+/) ? `N${(result.formule || '').match(/\d+/)?.[0]}` : 'N1',
          tauxCommission: selectedCommission
        }],
        venteAppelNonSollicite: false
      }
    };
  }

  private generateFrontaliersHtmlString(result: any): string | null {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo || !result.prix || result.prix === 'N/A' || result.prix === 'Erreur') {
      return null;
    }

    const niveauMatch = result.formule.match(/Niveau (\d+)/i);
    const niveauNum = niveauMatch ? parseInt(niveauMatch[1]) : 1;

    return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Devis Santé Frontaliers Suisses - Alptis</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #0056b3; padding-bottom: 20px; }
        .header h1 { color: #0056b3; margin: 0; font-size: 28px; }
        .header h2 { color: #666; margin: 10px 0 0; font-size: 18px; font-weight: normal; }
        .section { margin: 25px 0; }
        .section h3 { color: #0056b3; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .info-row { display: flex; margin: 8px 0; }
        .info-label { width: 200px; font-weight: bold; color: #555; }
        .info-value { flex: 1; }
        .tarif-box { background: #f0f8ff; border: 2px solid #0056b3; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .tarif-total { font-size: 32px; font-weight: bold; color: #0056b3; text-align: center; margin: 15px 0; }
        .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background-color: #0056b3; color: white; }
    </style>
</head>
<body>
    <div class="header">
        <h1>ALPTIS</h1>
        <h2>Santé Frontaliers Suisses - Devis Personnalisé</h2>
    </div>
    <div class="section">
        <h3>Informations du souscripteur</h3>
        <div class="info-row"><span class="info-label">Nom :</span><span class="info-value">${personalInfo.nom} ${personalInfo.prenom}</span></div>
        <div class="info-row"><span class="info-label">Date de naissance :</span><span class="info-value">${personalInfo.dateNaissance}</span></div>
        <div class="info-row"><span class="info-label">Adresse :</span><span class="info-value">${personalInfo.adresse || 'Non renseignée'}, ${personalInfo.codePostal} ${personalInfo.ville || ''}</span></div>
        <div class="info-row"><span class="info-label">Email :</span><span class="info-value">${personalInfo.email || 'Non renseigné'}</span></div>
        <div class="info-row"><span class="info-label">Téléphone :</span><span class="info-value">${personalInfo.telephone1 || 'Non renseigné'}</span></div>
    </div>
    <div class="section">
        <h3>Détails de l'offre</h3>
        <div class="info-row"><span class="info-label">Produit :</span><span class="info-value">${result.assurance}</span></div>
        <div class="info-row"><span class="info-label">Formule :</span><span class="info-value">${result.formule}</span></div>
        <div class="info-row"><span class="info-label">Niveau de garantie :</span><span class="info-value">Niveau ${niveauNum}</span></div>
        <div class="info-row"><span class="info-label">Date d'effet :</span><span class="info-value">${personalInfo.dateEffet}</span></div>
    </div>
    <div class="tarif-box">
        <h3 style="margin-top: 0; text-align: center;">Cotisation mensuelle</h3>
        <div class="tarif-total">${result.prix} €</div>
        <p style="text-align: center; margin: 0; color: #666;">TTC</p>
    </div>
    <div class="footer">
        <p><strong>Devis généré le ${new Date().toLocaleDateString('fr-FR')}</strong></p>
        <p>Ce devis est valable 30 jours à compter de sa date d'émission.</p>
        <p style="font-size: 10px; margin-top: 10px;">
            Alptis - Courtier en assurance spécialisé en santé frontaliers<br>
            Ce document est fourni à titre indicatif et ne constitue pas un contrat d'assurance.
        </p>
    </div>
</body>
</html>`;
  }

  private generateFrontaliersPDF(result: ComparisonResult): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value || { nom: 'Client', prenom: '' };
    const htmlContent = this.generateFrontaliersHtmlString(result);
    if (!htmlContent) {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de générer le devis. Prix non disponible ou données manquantes.',
        context: 'ALPTIS_FRONTALIERS'
      });
      return;
    }

    // Enregistrement direct du PDF : voir `generateGenericDevisTemplate`
    // pour le detail de l'anomalie (impression au lieu de telechargement).
    this.telechargerDevisPdf(htmlContent, `devis-frontaliers-suisses-${personalInfo.nom}-${personalInfo.prenom}.pdf`);
  }

  private generateSanteProtectPDF(result: ComparisonResult): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo || !result.prix || result.prix === 'N/A' || result.prix === 'Erreur') {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de générer le devis. Prix non disponible ou données manquantes.',
        context: 'ALPTIS_SANTE_PROTECT'
      });
      return;
    }

    const niveauMatch = result.formule.match(/Niveau (\d+)/i);
    const niveauNum = niveauMatch ? parseInt(niveauMatch[1]) : 1;

    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Devis Santé Protect - Alptis</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2E7D32; padding-bottom: 20px; }
        .header h1 { color: #2E7D32; margin: 0; font-size: 28px; }
        .header h2 { color: #666; margin: 10px 0 0; font-size: 18px; font-weight: normal; }
        .section { margin: 25px 0; }
        .section h3 { color: #2E7D32; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .info-row { display: flex; margin: 8px 0; }
        .info-label { width: 200px; font-weight: bold; color: #555; }
        .info-value { flex: 1; }
        .tarif-box { background: #E8F5E9; border: 2px solid #2E7D32; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .tarif-total { font-size: 32px; font-weight: bold; color: #2E7D32; text-align: center; margin: 15px 0; }
        .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background-color: #2E7D32; color: white; }
    </style>
</head>
<body>
    <div class="header">
        <h1>ALPTIS</h1>
        <h2>Santé Protect - Devis Personnalisé</h2>
    </div>
    <div class="section">
        <h3>Informations du souscripteur</h3>
        <div class="info-row"><span class="info-label">Nom :</span><span class="info-value">${personalInfo.nom} ${personalInfo.prenom}</span></div>
        <div class="info-row"><span class="info-label">Date de naissance :</span><span class="info-value">${personalInfo.dateNaissance}</span></div>
        <div class="info-row"><span class="info-label">Adresse :</span><span class="info-value">${personalInfo.adresse || 'Non renseignée'}, ${personalInfo.codePostal} ${personalInfo.ville || ''}</span></div>
        <div class="info-row"><span class="info-label">Email :</span><span class="info-value">${personalInfo.email || 'Non renseigné'}</span></div>
        <div class="info-row"><span class="info-label">Téléphone :</span><span class="info-value">${personalInfo.telephone1 || 'Non renseigné'}</span></div>
    </div>
    <div class="section">
        <h3>Détails de l'offre</h3>
        <div class="info-row"><span class="info-label">Produit :</span><span class="info-value">${result.assurance}</span></div>
        <div class="info-row"><span class="info-label">Formule :</span><span class="info-value">${result.formule}</span></div>
        <div class="info-row"><span class="info-label">Niveau de garantie :</span><span class="info-value">Niveau ${niveauNum}</span></div>
        <div class="info-row"><span class="info-label">Date d'effet :</span><span class="info-value">${personalInfo.dateEffet}</span></div>
    </div>
    <div class="tarif-box">
        <h3 style="margin-top: 0; text-align: center;">Cotisation mensuelle</h3>
        <div class="tarif-total">${result.prix} €</div>
        <p style="text-align: center; margin: 0; color: #666;">TTC</p>
    </div>
    <div class="footer">
        <p><strong>Devis généré le ${new Date().toLocaleDateString('fr-FR')}</strong></p>
        <p>Ce devis est valable 30 jours à compter de sa date d'émission.</p>
        <p style="font-size: 10px; margin-top: 10px;">
            Alptis - Santé Protect<br>
            Ce document est fourni à titre indicatif et ne constitue pas un contrat d'assurance.
        </p>
    </div>
</body>
</html>`;

    // Enregistrement direct du PDF : voir `generateGenericDevisTemplate`
    // pour le detail de l'anomalie (impression au lieu de telechargement).
    this.telechargerDevisPdf(htmlContent, `devis-sante-protect-${personalInfo.nom}-${personalInfo.prenom}.pdf`);
  }

  private generateGeneraliPDF(result: ComparisonResult): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo || !result.prix || result.prix === 'N/A' || result.prix === 'Erreur') {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de générer le devis. Prix non disponible ou données manquantes.',
        context: 'GENERALI'
      });
      return;
    }

    // Extraire le code de formule (P3, R4, etc.)
    let formulaCode = 'P3';
    const patterns = [/P(\d+)/i, /R(\d+)/i, /Niveau\s*(\d+)/i, /Formule\s*(\d+)/i];
    for (const pattern of patterns) {
      const match = result.formule.match(pattern);
      if (match && match[1]) {
        const prefix = result.formule.toLowerCase().includes('r') ? 'R' : 'P';
        formulaCode = prefix + match[1];
        break;
      }
    }

    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Etude Personnalisée - Generali</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #C41E3A; padding-bottom: 20px; }
        .header h1 { color: #C41E3A; margin: 0; font-size: 28px; }
        .header h2 { color: #666; margin: 10px 0 0; font-size: 18px; font-weight: normal; }
        .section { margin: 25px 0; }
        .section h3 { color: #C41E3A; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .info-row { display: flex; margin: 8px 0; }
        .info-label { width: 200px; font-weight: bold; color: #555; }
        .info-value { flex: 1; }
        .tarif-box { background: #FFF5F5; border: 2px solid #C41E3A; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .tarif-total { font-size: 32px; font-weight: bold; color: #C41E3A; text-align: center; margin: 15px 0; }
        .excel-notice { background: #FFF3CD; border: 1px solid #FFEAA7; border-radius: 4px; padding: 15px; margin: 20px 0; color: #856404; }
        .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 20px; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background-color: #C41E3A; color: white; }
    </style>
</head>
<body>
    <div class="header">
        <h1>GENERALI</h1>
        <h2>Etude Personnalisée Santé</h2>
    </div>
    <div class="section">
        <h3>Informations du souscripteur</h3>
        <div class="info-row"><span class="info-label">Nom :</span><span class="info-value">${personalInfo.nom} ${personalInfo.prenom}</span></div>
        <div class="info-row"><span class="info-label">Date de naissance :</span><span class="info-value">${personalInfo.dateNaissance}</span></div>
        <div class="info-row"><span class="info-label">Adresse :</span><span class="info-value">${personalInfo.adresse || 'Non renseignée'}, ${personalInfo.codePostal} ${personalInfo.ville || ''}</span></div>
        <div class="info-row"><span class="info-label">Email :</span><span class="info-value">${personalInfo.email || 'Non renseigné'}</span></div>
        <div class="info-row"><span class="info-label">Téléphone :</span><span class="info-value">${personalInfo.telephone1 || 'Non renseigné'}</span></div>
    </div>
    <div class="section">
        <h3>Détails de l'offre</h3>
        <div class="info-row"><span class="info-label">Produit :</span><span class="info-value">${result.assurance}</span></div>
        <div class="info-row"><span class="info-label">Formule :</span><span class="info-value">${result.formule}</span></div>
        <div class="info-row"><span class="info-label">Code formule :</span><span class="info-value">${formulaCode}</span></div>
        <div class="info-row"><span class="info-label">Date d'effet :</span><span class="info-value">${personalInfo.dateEffet}</span></div>
    </div>
    <div class="tarif-box">
        <h3 style="margin-top: 0; text-align: center;">Cotisation mensuelle</h3>
        <div class="tarif-total">${result.prix} €</div>
        <p style="text-align: center; margin: 0; color: #666;">TTC</p>
    </div>
    <div class="excel-notice">
        <strong>Note :</strong> Pour une étude complète avec toutes les formules, veuillez utiliser le calculateur Excel Generali (fichier XLSM) avec les mêmes paramètres saisis dans la feuille "Hypothèses de calcul".
    </div>
    <div class="footer">
        <p><strong>Devis généré le ${new Date().toLocaleDateString('fr-FR')}</strong></p>
        <p>Ce devis est valable 30 jours à compter de sa date d'émission.</p>
        <p style="font-size: 10px; margin-top: 10px;">
            Generali - Etude personnalisée<br>
            Ce document est fourni à titre indicatif et ne constitue pas un contrat d'assurance.
        </p>
    </div>
</body>
</html>`;

    // Enregistrement direct du PDF : voir `generateGenericDevisTemplate`
    // pour le detail de l'anomalie (impression au lieu de telechargement).
    this.telechargerDevisPdf(htmlContent, `etude-generali-${personalInfo.nom}-${personalInfo.prenom}.pdf`);
  }

  private buildAlptisQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';

      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }

      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      console.warn('Invalid date format for ISO date:', date);
      return '';
    };

    const getStatutProfessionnel = (categorie: string, statut: string): string => {
      if (statut) return statut;
      if (categorie?.toLowerCase().includes('tns')) return 'TRAVAILLEUR_INDEPENDANT';
      if (categorie?.toLowerCase().includes('salari')) return 'SALARIE';
      return 'TRAVAILLEUR_INDEPENDANT';
    };

    const mapAlptisRegime = (regime: string): string => {
      return ApiMapperUtil.getAlptisRegime(regime);
    };
    const regimePrincipal = mapAlptisRegime(personalInfo.regime || 'TNS');

    const assuranceName = ((result.assurance || '') + ' ' + (result.formule || ''))
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const assures: any = {
      adherent: {
        code_civilite: personalInfo.civilite === 'M' ? 'MONSIEUR' : 'MADAME',
        code_categorie_socio_professionnelle: personalInfo.categorieSocioProfessionnelle || 'ARTISANS',
        code_regime_obligatoire: regimePrincipal,
        code_cadre_exercice: 'INDEPENDANT',
        nom: personalInfo.nom,
        prenom: personalInfo.prenom,
        date_naissance: formatDate(personalInfo.dateNaissance),
        regime_obligatoire: regimePrincipal,
        statut_professionnel: getStatutProfessionnel(
          personalInfo.categorieSocioProfessionnelle,
          personalInfo.statutProfessionnel
        ),
        code_postal: personalInfo.codePostal,
        cadre_exercice: 'INDEPENDANT',
        categorie_socioprofessionnelle: personalInfo.categorieSocioProfessionnelle,
        micro_entrepreneur: true,
        coordonnees: {
          email: personalInfo.email,
          telephone: personalInfo.telephone1,
          adresse: {
            code_postal: personalInfo.codePostal,
            ville: personalInfo.ville,
            voie: personalInfo.adresse
          }
        }
      }
    };

    if (personalInfo.conjoint?.dateNaissance) {
      const isSanteSelect = assuranceName.includes('select');
      assures.conjoint = {
        categorie_socioprofessionnelle: isSanteSelect
          ? ApiMapperUtil.getAlptisSanteSelectConjointCsp(personalInfo.categorieSocioProfessionnelle)
          : personalInfo.categorieSocioProfessionnelle,
        date_naissance: formatDate(personalInfo.conjoint.dateNaissance),
        regime_obligatoire: mapAlptisRegime(personalInfo.conjoint.regime || personalInfo.regime || 'TNS'),
        nom: personalInfo.conjoint.nom || personalInfo.nom,
        prenom: personalInfo.conjoint.prenom
      };
    }

    if (personalInfo.enfants && personalInfo.enfants.length > 0) {
      assures.enfants = personalInfo.enfants.map((enfant: any) => ({
        date_naissance: formatDate(enfant.dateNaissance),
        regime_obligatoire: mapAlptisRegime(enfant.regime || personalInfo.regime || 'TNS'),
        nom: personalInfo.nom,
        prenom: enfant.prenom
      }));
    }

    const niveauMatch = result.formule.match(/Niveau (\d+)/i);
    const niveau = niveauMatch ? `NIVEAU_${niveauMatch[1]}` : 'NIVEAU_1';

    const contractsData = [
      { contract_name: 'Santé Pro+ Surco', contract_type: 'Surcomplémentaire santé', sur_complementaire: true },
      { contract_name: 'Santé Pro+', contract_type: 'Complémentaire santé', sur_complementaire: false }
    ];

    const contractInfo = contractsData.find(c =>
      result.formule.toLowerCase().includes(c.contract_name.toLowerCase())
    );
    const surComplementaire = contractInfo ? contractInfo.sur_complementaire : false;

    const ayantsDroit: any = {};
    if (assures.conjoint) {
      ayantsDroit.conjoint = true;
    }
    if (assures.enfants && assures.enfants.length > 0) {
      ayantsDroit.enfants = assures.enfants.length;
    }

    const codeProduit = assuranceName.includes('protect')
      ? 'SANTE_PROTECT'
      : assuranceName.includes('select')
        ? 'SANTE_SELECT'
        : 'SANTE_PRO_PLUS';

    return {
      date_effet: formatDate(personalInfo.dateEffet),
      commissionnement: 'PREC30_10',
      offre: {
        code_produit: codeProduit,
        niveau: niveau,
        sur_complementaire: surComplementaire
      },
      assures: assures
    };
  }

  private buildUtwinQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';

      // Si c'est déjà une chaîne au format DD/MM/YYYY, la retourner directement
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        return date;
      }

      // Si c'est un objet Date ou une chaîne ISO
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = ('0' + (d.getMonth() + 1)).slice(-2);
          const day = ('0' + d.getDate()).slice(-2);
          return `${year}-${month}-${day}`;
        }
      } catch (e) { }

      console.warn('Invalid date format:', date);
      return '';
    };

    const mapStatut = (statut: string): string => {
      if (!statut) return 'CELIBATAIRE';
      const s = statut.toLowerCase();
      if (s.includes('marie') || s.includes('union')) return 'MARIE';
      if (s.includes('pacse')) return 'PACSE';
      if (s.includes('divorce')) return 'DIVORCE';
      if (s.includes('veuf')) return 'VEUF';
      return 'CELIBATAIRE';
    };

    const mapRegime = (regime: string): string => {
      return ApiMapperUtil.getUtwinRegime(regime);
    };

    const mapCodeSexe = (civilite: string): string => {
      if (!civilite) return 'M';
      const c = civilite.toUpperCase().trim();
      if (c === 'M' || c === 'MONSIEUR' || c.includes('MONSIEUR') || c === 'GARCON') return 'M';
      if (c === 'F' || c === 'MADAME' || c === 'MME' || c.includes('MADAME') || c.includes('MME') || c === 'FILLE' || c.includes('MADEMOISELLE')) return 'F';
      return 'M'; // Default
    };

    const mapCodeCivilite = (civilite: string): string => {
      if (!civilite) return 'Monsieur';
      const c = civilite.toUpperCase().trim();
      if (c === 'M' || c === 'MONSIEUR' || c.includes('MONSIEUR')) return 'Monsieur';
      if (c === 'MADAME' || c === 'MME' || c.includes('MADAME') || c.includes('MME')) return 'Madame';
      if (c === 'MADEMOISELLE' || c.includes('MADEMOISELLE')) return 'Mademoiselle';
      if (c === 'F') return 'Madame'; // F defaults to Madame for adults
      return 'Monsieur'; // Default
    };

    const assures: any[] = [];

    // Assuré principal - VALIDATION complète
    if (!personalInfo.nom || !personalInfo.prenom) {
      console.error('❌ ERREUR UTWIN: Nom ou Prénom manquant pour assuré principal');
      return null; // Arrêter si données invalides
    }

    assures.push({
      codeRegimeObligatoire: this.mapRegimeToUtwin(personalInfo.regime),
      codeTypeRole: 'AssurePrincipal',
      codeSexe: mapCodeSexe(personalInfo.civilite),
      codeCivilite: mapCodeCivilite(personalInfo.civilite),
      codeStatutFamilial: this.mapStatutToUtwin(personalInfo.etatCivil),
      nom: personalInfo.nom || 'INCONNU',
      prenom: personalInfo.prenom || 'INCONNU',
      dateDeNaissance: formatDate(personalInfo.dateNaissance)
    });

    // Conjoint
    if (personalInfo.conjoint?.dateNaissance) {
      const conjointInfo = this.insuranceForm.get('personalInfo.conjoint')?.value;
      if (conjointInfo && conjointInfo.nom && conjointInfo.prenom) {
        assures.push({
          codeRegimeObligatoire: this.mapRegimeToUtwin(conjointInfo.regime),
          codeTypeRole: 'Conjoint',
          codeSexe: mapCodeSexe(conjointInfo.civilite),
          codeCivilite: mapCodeCivilite(conjointInfo.civilite),
          codeStatutFamilial: this.mapStatutToUtwin(conjointInfo.etatCivil || personalInfo.etatCivil),
          nom: conjointInfo.nom,
          prenom: conjointInfo.prenom,
          dateDeNaissance: formatDate(conjointInfo.dateNaissance)
        });
      }
    }

    // Enfants
    if (personalInfo.enfants && personalInfo.enfants.length > 0) {
      personalInfo.enfants.forEach((child: any) => {
        if (child && child.prenom) {  // ✅ Validation ajoutée
          assures.push({
            codeRegimeObligatoire: this.mapRegimeToUtwin(child.regime),
            codeTypeRole: 'Enfant',
            codeSexe: child.sexe === 'garcon' ? 'M' : 'F',
            codeCivilite: child.sexe === 'garcon' ? 'Monsieur' : 'Mademoiselle',
            codeStatutFamilial: 'CELIBATAIRE',
            nom: personalInfo.nom,  // Utiliser le nom du parent
            prenom: child.prenom,
            dateDeNaissance: formatDate(child.dateNaissance)
          });
        }
      });
    }

    const normalizeUtwin = (value: any): string => {
      return (value || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\+/g, ' plus ')
        .replace(/[^a-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const selectedTextRaw = (result.formule || result.assurance || '').toString();
    const selectedText = normalizeUtwin(selectedTextRaw);

    // Calculate age from date of birth for SENIOR product eligibility
    const calculateAge = (dateNaissance: string): number => {
      if (!dateNaissance) return 0;
      try {
        let birthDate: Date;
        if (typeof dateNaissance === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateNaissance)) {
          const [day, month, year] = dateNaissance.split('/');
          birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          birthDate = new Date(dateNaissance);
        }
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      } catch (e) {
        return 0;
      }
    };

    const principalAge = calculateAge(personalInfo.dateNaissance);

    let codeProduit = 'UTWIN_MULTI'; // Default fallback - code produit valide

    if (selectedText.includes('mixi')) {
      codeProduit = 'UTWIN_MIXI';
    } else if (selectedText.includes('vital') || selectedText.includes('vita')) {
      codeProduit = 'UTWIN_VITA';
    } else if (selectedText.includes('opti')) {
      codeProduit = 'UTWIN_OPTI';
    } else if (selectedText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes('senior')) {
      // ⚠️ UTWIN_BASIC_SENIOR requires principal to be at least 55 years old
      if (principalAge >= 55) {
        codeProduit = 'UTWIN_BASIC_SENIOR';
      } else {
        console.warn(`⚠️ Âge insuffisant pour UTWIN_BASIC_SENIOR (${principalAge} ans < 55). Fallback vers UTWIN_MULTI`);
        codeProduit = 'UTWIN_MULTI';
      }
    } else if (selectedText.includes('multi')) {
      codeProduit = 'UTWIN_MULTI';
    } else if (selectedText.includes('basic') && selectedText.includes('tns')) {
      codeProduit = 'UTWIN_BASIC_TNS';
    } else if (selectedText.includes('basic')) {
      // Utwin expose BASIC via le code UTWIN_BASIC_TNS (même avec régime RG).
      codeProduit = 'UTWIN_BASIC_TNS';
    }
    let codeFormule = 'N1'; // Default
    if (result.formule) {
      const match = result.formule.match(/(?:Niveau|Formule)\s*(\d+)/i);
      if (match && match[1]) {
        codeFormule = `N${match[1]}`;
      }
    }

    const connectedUser = this.authService.getCurrentUser();
    const coordonneesConseiller = connectedUser ? {
      nom: connectedUser.nom || '',
      prenom: connectedUser.prenom || '',
      email: connectedUser.email || ''
    } : undefined;

    const payload = {
      souscripteur: {
        telephonePortable: personalInfo.telephone1 || personalInfo.telephone || '',
        email: personalInfo.email || '',
        adresse: {
          codePostal: personalInfo.codePostal || ''
        }
      },
      besoin: {
        dateEffet: formatDate(personalInfo.dateEffet)
      },
      assures: assures,
      contexteDeVente: {
        produits: [{
          codeProduit: codeProduit || 'UTWIN_MULTI',
          codeFormule: codeFormule || 'N1',
          tauxCommission: normalizeCasCommission(result.selectedCommission),
          versionProduit: 1
        }],
        venteAppelNonSollicite: false,
        ...(coordonneesConseiller ? { coordonneesConseiller } : {})
      }
    };

    // ✅ Logs détaillés pour déboguer UTWIN
    console.log('✅ Payload UTWIN généré:', JSON.stringify(payload, null, 2));
    console.log('📊 Détails UTWIN:', {
      codeProduit: codeProduit,
      codeFormule: codeFormule,
      codeTauxCommission: normalizeCasCommission(result.selectedCommission),
      assuresCount: assures.length,
      assures: assures.map(a => ({
        typeRole: a.codeTypeRole,
        nom: a.nom,
        prenom: a.prenom,
        sexe: a.codeSexe,
        regime: a.codeRegimeObligatoire
      }))
    });

    return payload;
  }

  /**
   * Construire le payload complet et structuré pour Zenioo
   * Conforme à l'API Zenioo (souscripteur, contrat, bénéficiaires)
   */
  private buildZeniooQuotePayload(result: ComparisonResult, personalInfo: any): any {
    if (!result || !personalInfo) return null;

    // Normaliser la date au format YYYY-MM-DD
    const normalizeDateISO = (date: any): string => {
      if (!date) return new Date().toISOString().split('T')[0];

      if (date instanceof Date) {
        return date.toISOString().split('T')[0];
      }

      const dateStr = date.toString().trim();
      // Convertir DD/MM/YYYY → YYYY-MM-DD
      const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (match) {
        return `${match[3]}-${match[2]}-${match[1]}`;
      }

      // Si déjà au format YYYY-MM-DD
      return dateStr;
    };

    // Normaliser le téléphone en format +33xxxxxxxxx
    const normalizePhone = (phone: any): string => {
      if (!phone) return '';
      let p = phone.toString().trim();
      // Remplacer le 0 initial par +33
      if (p.startsWith('0')) {
        return '+33' + p.substring(1);
      }
      if (!p.startsWith('+')) {
        return '+33' + p;
      }
      return p;
    };

    // Valider et nettoyer la ville
    const validateCity = (city: any, zipcode: any): string => {
      if (!city) {
        city = '';
      }

      const cityStr = city.toString().trim();

      // Si la ville est vide, chercher une ville par défaut basée sur le code postal
      if (!cityStr) {
        const zip = zipcode?.toString().trim() || '';
        // Mapping simple pour les villes courantes (peut être étendu)
        const cityMap: { [key: string]: string } = {
          '69001': 'Lyon',
          '75001': 'Paris',
          '13001': 'Marseille',
          '33000': 'Bordeaux',
          '31000': 'Toulouse',
          '59000': 'Lille',
          '21000': 'Dijon',
          '38000': 'Grenoble',
          '67000': 'Strasbourg',
          '37000': 'Tours'
        };

        return cityMap[zip] || 'France'; // Fallback si pas de mapping
      }

      // Vérifier que la ville n'est pas trop courte (minimum 2 caractères)
      if (cityStr.length < 2) {
        return 'France'; // Fallback
      }

      return cityStr;
    };

    // Mapper les régimes vers les valeurs acceptées par Zenioo
    const mapZeniooRegime = (regime: string): string => {
      return ApiMapperUtil.getZeniooRegime(regime);
    };

    // Construire l'objet souscripteur
    // ⚠️ ATTENTION: Zenioo accepte UNIQUEMENT ces champs (pas ville, pas adresse1)
    const souscripteur = {
      civilite: personalInfo.civilite || 'monsieur',
      nom: personalInfo.nom || 'Inconnu',
      prenom: personalInfo.prenom || 'Inconnu',
      code_postal: personalInfo.codePostal || '',
      mobile: normalizePhone(personalInfo.telephone1 || personalInfo.telephone),
      email: personalInfo.email || ''
    };

    // Construire l'objet contrat
    // ⚠️ Structure selon l'exemple API Zenioo (avec date_devis et code_postal imbriqué)
    const contrat = {
      date_devis: new Date().toISOString().split('T')[0],
      code_produit: result.zeniooCodeProduit,
      code_formule: result.zeniooCodeFormule,
      options: [],
      taux_commission: normalizeCasCommission(result.selectedCommission),
      date_effet: normalizeDateISO(personalInfo.dateEffet),
      code_postal: personalInfo.codePostal || ''
    };

    // Construire l'array des bénéficiaires
    const beneficiaires: any[] = [];

    // Ajouter le souscripteur
    beneficiaires.push({
      type_beneficiaire: 'souscripteur',
      regime: mapZeniooRegime(personalInfo.regime),
      date_naissance: normalizeDateISO(personalInfo.dateNaissance)
    });

    // Ajouter le conjoint si applicable
    if (personalInfo.conjoint && personalInfo.conjoint.dateNaissance) {
      beneficiaires.push({
        type_beneficiaire: 'conjoint',
        regime: mapZeniooRegime(personalInfo.regime),
        date_naissance: normalizeDateISO(personalInfo.conjoint.dateNaissance)
      });
    }

    // Ajouter les enfants si applicable
    if (Array.isArray(personalInfo.enfants) && personalInfo.enfants.length > 0) {
      personalInfo.enfants.forEach((enfant: any, index: number) => {
        if (enfant && enfant.dateNaissance && index < 3) { // Max 3 enfants
          beneficiaires.push({
            type_beneficiaire: 'enfant',
            regime: mapZeniooRegime(personalInfo.regime),
            date_naissance: normalizeDateISO(enfant.dateNaissance)
          });
        }
      });
    }

    // Payload final structuré selon l'API Zenioo
    const payload = {
      souscripteur,
      contrat,
      beneficiaires,
      // On garde ces champs à la racine pour compatibilité backend, mais l'essentiel est maintenant imbriqué
      code_postal: personalInfo.codePostal,
      cp: personalInfo.codePostal,
      date_effet: normalizeDateISO(personalInfo.dateEffet)
    };

    console.log('✅ Payload Zenioo généré:', JSON.stringify(payload, null, 2));
    console.log('📊 Détails souscripteur:', {
      civilite: souscripteur.civilite,
      nom: souscripteur.nom,
      prenom: souscripteur.prenom,
      code_postal: souscripteur.code_postal,
      mobile: souscripteur.mobile,
      email: souscripteur.email
    });
    return payload;
  }

  private buildAprilQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';

      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }

      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      console.warn('Invalid date format for ISO date:', date);
      return '';
    };

    const formula = result.formule || '';
    const levelCodeMatch = formula.match(/(?:FORMULE|Niveau)\s*(\d+)/i);
    const levelCode = levelCodeMatch ? levelCodeMatch[1].padStart(2, '0') : '01';

    const mainInsuredId = 'i-1';
    const persons = [{
      '$id': mainInsuredId,
      birthDate: formatDate(personalInfo.dateNaissance),
      title: personalInfo.civilite === 'M' ? 'Monsieur' : 'Madame',
      lastName: personalInfo.nom,
      firstName: personalInfo.prenom,
      mandatoryScheme: mapToAprilMandatoryScheme(personalInfo.regime),
      familyStatus: personalInfo.etatCivil,
      mobilePhone: { number: personalInfo.telephone1 },
      email: personalInfo.email
    }];

    const mainApplicantId = 'a-1';
    const insureds = [{
      '$id': mainApplicantId,
      role: 'AssurePrincipal',
      person: { '$ref': mainInsuredId }
    }];

    const productCode = this.mapAprilProductCode(result.assurance);
    const lowerProductCode = (productCode || '').toLowerCase();

    const isMadelinEligible = mapToAprilMandatoryScheme(personalInfo.regime) === 'TNS';

    let coverages: any[] = [];
    if (lowerProductCode.startsWith('santemix') || lowerProductCode === 'flexisante' || lowerProductCode.startsWith('tranquillitesante')) {
      coverages = [{
        insured: { '$ref': mainInsuredId },
        guaranteeCode: 'GarantieHospitalisation',
        eligibleMadelinLaw: isMadelinEligible,
        levelCode: levelCode
      }, {
        insured: { '$ref': mainInsuredId },
        guaranteeCode: 'GarantieFraisDeSante',
        eligibleMadelinLaw: isMadelinEligible,
        levelCode: levelCode
      }, {
        insured: { '$ref': mainInsuredId },
        guaranteeCode: 'GarantieDentaireOptiqueAuditives',
        eligibleMadelinLaw: isMadelinEligible,
        levelCode: levelCode
      }, {
        insured: { '$ref': mainInsuredId },
        guaranteeCode: 'GarantieBienEtrePrevention',
        eligibleMadelinLaw: isMadelinEligible,
        levelCode: levelCode
      }];
    } else if (lowerProductCode === 'santepro' || lowerProductCode === 'santeprostart' || lowerProductCode === 'santesolution') {
      // 🚨 CORRECTION: Ne pas ajouter Surcomplementaire si MaladieChirurgie est au niveau 01 (incompatible selon April API)
      const baseCoverages = [{
        insured: { '$ref': mainInsuredId },
        guaranteeCode: 'MaladieChirurgie',
        eligibleMadelinLaw: isMadelinEligible,
        levelCode: levelCode
      }];

      // Ajouter Surcomplementaire seulement si le niveau n'est pas 01
      if (levelCode !== '01') {
        const surcomplementaireCoverage: any = {
          insured: { '$ref': mainInsuredId },
          guaranteeCode: 'Surcomplementaire',
          eligibleMadelinLaw: isMadelinEligible
        };
        baseCoverages.push(surcomplementaireCoverage);
      }

      coverages = baseCoverages;
    } else {
      coverages = [{
        insured: { '$ref': mainInsuredId },
        guaranteeCode: 'MaladieChirurgie',
        eligibleMadelinLaw: isMadelinEligible,
        levelCode: levelCode
      }];
    }

    return {
      '$type': 'PrevPro',
      properties: {
        addresses: [{
          '$id': 'adr-1',
          type: 'Actuelle',
          addressLine1: personalInfo.adresse,
          postCode: personalInfo.codePostal,
          city: personalInfo.ville
        }],
        email: personalInfo.email
      },
      persons: persons,
      products: [{
        '$id': 'p-1',
        productCode: productCode,
        effectiveDate: formatDate(personalInfo.dateEffet),
        ...(personalInfo.termination?.hasFormerContract && personalInfo.termination?.formerInsurer && personalInfo.termination?.formerContractReference ? {
          termination: {
            insurer: personalInfo.termination.formerInsurer,
            formerContractReference: personalInfo.termination.formerContractReference
          }
        } : {}),
        insureds: insureds,
        coverages: coverages
      }],
      marketingParameters: {
        requestType: 'Quotation'
      }
    };
  }

  private buildNeolianeQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return '';
    };

    // Extraction du productId et formulaId depuis neolianeProducts (IDs numériques)
    const normalize = (value: string): string => {
      return (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };

    const assuranceNorm = normalize(result.assurance || '');
    const formuleNorm = normalize(result.formule || '');

    // D'abord vérifier si les IDs sont déjà stockés dans le result (depuis la tarification)
    let productId = (result as any).neolianeProductId?.toString() || '1';
    let formulaId = (result as any).neolianeFormulaId?.toString() || '1';

    console.log('🔍 IDs Néoliane depuis tarification:', { productId, formulaId, hasStored: !!(result as any).neolianeProductId });

    // Si les IDs ne sont pas stockés, faire le mapping depuis neolianeProducts
    if (!(result as any).neolianeProductId && this.neolianeProducts && this.neolianeProducts.length > 0) {
      const matchedProduct = this.neolianeProducts.find(p => {
        const pName = normalize(p.name || '');
        const pType = normalize(p.type || '');
        return (pName && (assuranceNorm.includes(pName) || formuleNorm.includes(pName)))
          || (pType && (assuranceNorm.includes(pType) || formuleNorm.includes(pType)));
      });

      if (matchedProduct) {
        productId = String(matchedProduct.productId || (matchedProduct as any).product_id || '1');
        console.log('✅ Produit Néoliane matché:', matchedProduct.name, { productId });

        // Chercher la formule
        const niveauMatch = formuleNorm.match(/(?:niveau|formule|np)\s*(\d)/i);
        const niveau = niveauMatch?.[1] || '';

        const matchedFormula = matchedProduct.formulas?.find(f => {
          const fLabel = normalize(f.label || '');
          const fCode = normalize(f.code || '');
          return (niveau && (fLabel.includes(niveau) || fCode.includes(niveau)))
            || (fLabel && (formuleNorm.includes(fLabel) || assuranceNorm.includes(fLabel)));
        }) || matchedProduct.formulas?.[0];

        if (matchedFormula) {
          formulaId = String(matchedFormula.formulaId || (matchedFormula as any).formula_id || '1');
          console.log('✅ Formule Néoliane matchée:', matchedFormula.label, { formulaId });
        }
      }
    } else if (!(result as any).neolianeProductId) {
      // Fallback si neolianeProducts n'est pas chargé
      console.warn('⚠️ neolianeProducts non chargé, utilisation du mapping par défaut');
      if (assuranceNorm.includes('performance')) productId = '2';
      else if (assuranceNorm.includes('equilibre')) productId = '3';
      else if (assuranceNorm.includes('essentiel')) productId = '4';

      const npMatch = formuleNorm.match(/np(\d)/);
      if (npMatch) formulaId = npMatch[1];
    }

    console.log('📦 Payload Néoliane final:', { productId, formulaId });

    const payload = {
      productId,
      formulaId,
      personalInfo: {
        nom: personalInfo.nom,
        prenom: personalInfo.prenom,
        email: personalInfo.email,
        telephone: personalInfo.telephone1,
        dateNaissance: formatDate(personalInfo.dateNaissance),
        civilite: personalInfo.civilite,
        codePostal: personalInfo.codePostal,
        ville: personalInfo.ville,
        adresse: personalInfo.adresse,
        regime: personalInfo.regime
      }
    };

    return payload;
  }

  private mapAprilProductCode(assurance: string): string {
    const normalize = (value: any): string => {
      return (value || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const a = normalize(assurance);

    const map: Array<{ contains: string; code: string }> = [
      { contains: 'sante pro start', code: 'SanteProStart' },
      { contains: 'pro start', code: 'SanteProStart' },
      { contains: 'sante pro', code: 'SantePro' },
      { contains: 'surcomplementaire', code: 'SantePro' },
      { contains: 'sante mix', code: 'SanteMix' },
      { contains: 'sante vita', code: 'SanteVita' },
      { contains: 'sante zen', code: 'SanteZen' },
      { contains: 'sante eco', code: 'SanteEco' },
      { contains: 'sante brio', code: 'SanteBrio' },
      { contains: 'sante reflexe', code: 'SanteReflexe' },
      { contains: 'tranquillite sante', code: 'TranquilliteSante' },
      { contains: 'sante tranquill', code: 'TranquilliteSante' },
      { contains: 'sante peps', code: 'SantePeps' },
      { contains: 'only sante', code: 'OnlySante' },
      { contains: 'flexi sante', code: 'FlexiSante' }
    ];

    for (const entry of map) {
      if (a.includes(entry.contains)) {
        return entry.code;
      }
    }

    return 'SantePro';
  }

  onCommissionChange(result: ComparisonResult): void {
    if (!result) return;
    // La propriété selectedCommission dans le dropdown a déjà mis à jour result.selectedCommission via [(ngModel)]
    console.log(`Commission modifiée pour ${result.assurance} - ${result.formule} -> ${result.selectedCommission}`);

    // Le taux est normalisé avant envoi, mais on va juste relancer le pricing.
    // Relancer la récupération de prix (ça fera loading -> prix)
    this.onFormuleSelect(result, result.formule);
  }

  onFormuleSelect(result: ComparisonResult, formule: string): void {
    if (!result) {
      return;
    }

    const assuranceLower = this.normalizeInsurerName(result.assurance);

    if (assuranceLower.includes('henner')) {
      const personalInfo = this.insuranceForm.get('personalInfo')?.value;
      if (!personalInfo) {
        this.notifyPricingUnavailable(result, 'incomplete_form');
        return;
      }

      const hennerCivilite = (value: any): '01' | '02' => {
        const v = (value ?? '').toString().trim().toUpperCase();
        if (v === '01' || v === 'M' || v === 'MR' || v === 'MONSIEUR') return '01';
        if (v === '02' || v === 'F' || v === 'MME' || v === 'MADAME') return '02';
        // fallback
        return '01';
      };

      const hennerRegimeSocial = (value: any): '0001' | '0003' | '0050' => {
        const v = (value ?? '').toString().trim().toUpperCase();
        if (v.includes('ALSACE') || v.includes('MOSELLE') || v.includes('0050')) return '0050';
        if (v.includes('TNS') || v.includes('SSI') || v.includes('INDEPENDANT') || v.includes('0003')) return '0003';
        return '0001';
      };

      const toIsoDate = (d: any): string => {
        // attend YYYY-MM-DD côté backend
        if (!d) return '';
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
        if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
          const [day, month, year] = d.split('/');
          return `${year}-${month}-${day}`;
        }
        const dt = new Date(d);
        if (!isNaN(dt.getTime())) {
          const year = dt.getFullYear();
          const month = String(dt.getMonth() + 1).padStart(2, '0');
          const day = String(dt.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return '';
      };

      const safeCodePostal = (value: any): string => {
        const v = (value ?? '').toString().trim();
        const onlyDigits = v.replace(/\D/g, '');
        if (/^\d{5}$/.test(onlyDigits)) return onlyDigits;
        // fallback dev pour pouvoir tester l'API
        return '75001';
      };

      const safeEffectDate = (value: any): string => {
        const iso = toIsoDate(value);
        if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
        return toIsoDate(new Date()) || '2026-01-01';
      };

      // Best-effort mapping vers le backend Henner.
      // Pour obtenir un tarif réel, il faudra un identifiant d'option/formule Henner valide (levier.option.identifiant).
      const payload: any = {
        codeAppelant: 'HSS',
        identifiantApporteur: '149',
        identifiantCanalVente: '8',
        identifiantCommercial: '0',
        identifiantOffreCommerciale: '604',
        codePostal: safeCodePostal(personalInfo.codePostal),
        dateEffetSouhaitee: safeEffectDate(personalInfo.dateEffet),
        periodicite: 'M',
        typeDiagnostique: 'TC',
        ville: personalInfo.ville,
        beneficiaires: [
          {
            acceptationEdi: true,
            civilite: hennerCivilite(personalInfo.civilite),
            dateDeNaissance: toIsoDate(personalInfo.dateNaissance) || '1950-01-01',
            lienDeParente: 'AF',
            nom: personalInfo.nom || 'TEST',
            prenom: personalInfo.prenom || 'TEST',
            regimeSocial: hennerRegimeSocial(personalInfo.regime)
          }
        ]
      };

      result.isPricingLoading = true;
      this.insuranceService.getHennerRealiserDiagnostique(payload).pipe(
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (response: any) => {
          const ok = response?.success === true;
          const msg = response?.message || (ok ? 'Réponse Henner reçue' : 'Réponse Henner avec erreur');
          const details = Array.isArray(response?.erreurs) && response.erreurs.length > 0
            ? response.erreurs.join(' | ')
            : '';

          if (!ok) {
            this.notifyPricingUnavailable(result, 'api_error', {
              rawMessage: details || msg,
              extraDetail: details ? `${msg} — ${details}` : msg
            });
            return;
          }

          // Diagnostic OK mais pas encore de montant relié à la formule
          this.notifyPricingUnavailable(result, 'empty_response', {
            extraDetail: 'Henner a accepté le diagnostic, mais le tarif mensuel n\'est pas encore relié à cette formule dans le comparateur (identifiant d\'option manquant).'
          });
        },
        error: (err: any) => {
          this.notifyApiErrorToast({
            summary: 'Henner',
            fallback: 'Erreur lors de l\'appel Henner.',
            error: err,
            context: 'HENNER',
            result,
            severity: 'warn'
          });
          this.cdr.detectChanges();
        }
      });
      return;
    }

    if (assuranceLower.includes('april') && !assuranceLower.includes('apivia')) {
      const quoteForm = this.transformFormToQuote();
      result.isPricingLoading = true;
      const formattedCommission = normalizeCasCommission(result.selectedCommission)?.replace(/\D/g, '') || undefined;
      this.insuranceService.getAprilHealthTarif(quoteForm, result.assurance, formule, formattedCommission).pipe(
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (response: any) => {
          const tarifGlobal = response.data?.find((d: any) => d.priceType === 'TarifGlobal');
          if (tarifGlobal) {
            result.pricingDetail = undefined;
            result.tarifGlobal = tarifGlobal.contribution.contributionAmount;
          } else {
            this.notifyPricingUnavailable(result, 'empty_response', {
              extraDetail: 'APRIL n\'a pas renvoyé de tarif global pour cette formule.'
            });
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.notifyApiErrorToast({
            summary: 'Information April',
            fallback: 'Erreur lors du calcul du tarif APRIL.',
            error: err,
            context: 'APRIL',
            result,
            severity: 'warn'
          });
          this.cdr.detectChanges();
        }
      });
      return;
    }

    if (assuranceLower.includes('apivia')) {
      const personalInfo = this.insuranceForm.get('personalInfo')?.value;
      if (!personalInfo) {
        this.notifyPricingUnavailable(result, 'incomplete_form', {
          extraDetail: 'Renseignez les informations personnelles avant la tarification.'
        });
        return;
      }

      // Guard anti-duplication : si une tarification est déjà en cours pour ce résultat, on ignore
      if (result.isPricingLoading) {
        return;
      }

      result.isPricingLoading = true;

      const formatDate = (date: any): string => {
        if (!date) return '';
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          return date;
        }
        const toDdMmYyyy = (d: Date): string => {
          const day = ('0' + d.getDate()).slice(-2);
          const month = ('0' + (d.getMonth() + 1)).slice(-2);
          const year = d.getFullYear();
          return `${day}/${month}/${year}`;
        };
        if (date instanceof Date && !isNaN(date.getTime())) {
          return toDdMmYyyy(date);
        }
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          return toDdMmYyyy(d);
        }
        return '';
      };

      const getFormulaCode = (formulaName: string): string => {
        if (!formulaName) return '';
        const match = formulaName.match(/\d+/);
        return match ? match[0] : formulaName;
      };

      const apiviaProductCode = ApiMapperUtil.getApiviaProductCode(
        result.contractName,
        result.formule || formule,
        result.assurance
      );

      const getApiviaRegimeSocial = (regime: string): string => {
        return ApiMapperUtil.getApiviaRegimeSocial(
          regime,
          apiviaProductCode,
          personalInfo.categorieSocioProfessionnelle
        );
      };

      // typeRegime : 'GE' (Général) ou 'AM' (Alsace-Moselle) selon le code postal ou régime
      const getApiviaTypeRegime = (regime?: string): string => {
        const cp = (personalInfo.codePostal || '').toString();
        if (cp.startsWith('57') || cp.startsWith('67') || cp.startsWith('68')) return 'AM';
        return ApiMapperUtil.getApiviaRegime(regime || personalInfo.regime);
      };

      const beneficiaires: any[] = [];
      beneficiaires.push({
        typeBeneficiaire: 'PRINCIPAL',
        dateDeNaissance: formatDate(personalInfo.dateNaissance),
        typeRegime: getApiviaTypeRegime(personalInfo.regime),
        regimeSocial: getApiviaRegimeSocial(personalInfo.regime)
      });

      if (personalInfo.conjoint?.dateNaissance) {
        beneficiaires.push({
          typeBeneficiaire: 'CONJOINT',
          dateDeNaissance: formatDate(personalInfo.conjoint.dateNaissance),
          typeRegime: getApiviaTypeRegime(personalInfo.conjoint.regime || personalInfo.regime),
          regimeSocial: getApiviaRegimeSocial(personalInfo.conjoint.regime || personalInfo.regime)
        });
      }

      (Array.isArray(personalInfo.enfants) ? personalInfo.enfants : []).forEach((enfant: any) => {
        beneficiaires.push({
          typeBeneficiaire: 'ENFANT',
          dateDeNaissance: formatDate(enfant.dateNaissance),
          typeRegime: getApiviaTypeRegime(enfant.regime || personalInfo.regime),
          regimeSocial: getApiviaRegimeSocial(enfant.regime || personalInfo.regime)
        });
      });

      const payload: any = {
        action: 'tarification',
        format: 'json',
        produits: apiviaProductCode,
        formules: getFormulaCode(formule || result.formule),
        renforts: '',
        options: '',
        codePostal: personalInfo.codePostal,
        dateEffet: formatDate(personalInfo.dateEffet),
        dejaAssurance: personalInfo.dejaAssurance === true,
        isResiliationPourCompte: personalInfo.isResiliationPourCompte === true,
        typeAffaire: personalInfo.typeAffaire || '',
        typeObjectSortie: 'v2',
        beneficiaires
      };

      const parseApiviaTarifResponse = (response: any, productCode: string, formuleCode: string): number | null => {
          if (response?.status === 'error') {
            return null;
          }
          const list = response?.list;
          if (Array.isArray(list) && list.length > 0 && typeof list[0] === 'string') {
            return null;
          }
          if (Array.isArray(list) && list.length > 0 && typeof list[0] === 'object' && list[0] !== null) {
            const formuleDetails = list[0]?.formules?.[formuleCode];
            const base = formuleDetails?.base;
            const price = base?.complete ?? base?.mensuel ?? base?.mensuel_complete ?? base?.cotisation ?? null;
            const parsed = typeof price === 'number'
              ? price
              : (price != null ? parseFloat(price.toString().replace(',', '.')) : NaN);
            return isNaN(parsed) ? null : parsed;
          }
          return null;
        };

      const buildApiviaPayloadWithRegime = (regimeSocial: string) => ({
        ...payload,
        beneficiaires: beneficiaires.map((b, i) => i === 0 ? { ...b, regimeSocial } : { ...b, regimeSocial: b.regimeSocial || regimeSocial })
      });

      const primaryRegimeSocial = beneficiaires[0]?.regimeSocial || 'ASS_SOCIAL';

      this.insuranceService.getApiviaTarif(payload).pipe(
        switchMap((response: any) => {
          const firstPrice = parseApiviaTarifResponse(response, apiviaProductCode, payload.formules);
          const isNoResult = Array.isArray(response?.list)
            && response.list.length > 0
            && typeof response.list[0] === 'string';
          const m3Codes = new Set(['VITAMM3', 'SAKIM3']);
          if (firstPrice == null && isNoResult && m3Codes.has(apiviaProductCode) && primaryRegimeSocial === 'TNS') {
            return this.insuranceService.getApiviaTarif(buildApiviaPayloadWithRegime('ASS_SOCIAL')).pipe(
              map((retryResp) => ({ response: retryResp, retriedAssSocial: true }))
            );
          }
          return of({ response, retriedAssSocial: false });
        }),
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: ({ response, retriedAssSocial }: { response: any; retriedAssSocial: boolean }) => {
          if (response?.status === 'error') {
            const errorMsg = Array.isArray(response.list)
              ? response.list.join(' ')
              : (response.message || 'Erreur lors du calcul du tarif APIVIA.');
            this.notifyPricingUnavailable(result, 'api_error', {
              rawMessage: errorMsg,
              productCode: apiviaProductCode
            });
            return;
          }

          const list = response?.list;

          if (Array.isArray(list) && list.length > 0 && typeof list[0] === 'string') {
            const suffix = retriedAssSocial
              ? ' Un second essai avec le régime « Sécurité sociale » n\'a pas donné de tarif non plus.'
              : '';
            this.notifyPricingUnavailable(result, 'api_no_result', {
              rawMessage: list.join(' '),
              productCode: apiviaProductCode,
              extraDetail: `${list.join(' ')}${suffix}`
            });
            return;
          }

          const parsed = parseApiviaTarifResponse(response, apiviaProductCode, payload.formules);

          if (parsed == null) {
            this.notifyPricingUnavailable(result, 'empty_response', {
              productCode: apiviaProductCode,
              extraDetail: `Formule ${payload.formules} (${apiviaProductCode}) : aucun montant dans la réponse Apivia.`
            });
          } else {
            result.pricingDetail = undefined;
            result.prix = parsed;
          }
        },
        error: (err: any) => {
          this.notifyApiErrorToast({
            summary: 'Information Apivia',
            fallback: 'Erreur lors du calcul du tarif APIVIA.',
            error: err,
            context: 'APIVIA',
            result,
            productCode: apiviaProductCode,
            severity: 'warn'
          });
        }
      });
      return;
    }

    if (assuranceLower.includes('eca') || assuranceLower.includes('solly') || assuranceLower.includes('azar')) {
      const personalInfo = this.insuranceForm.get('personalInfo')?.value;
      result.isPricingLoading = true;

      const toDate = (date: any): Date => {
        if (!date) {
          return new Date();
        }
        if (date instanceof Date && !isNaN(date.getTime())) {
          return date;
        }
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
          return isNaN(d.getTime()) ? new Date() : d;
        }
        const d = new Date(date);
        return isNaN(d.getTime()) ? new Date() : d;
      };

      const birthDate = toDate(personalInfo?.dateNaissance);
      const effectDate = toDate(personalInfo?.dateEffet);
      const age = Math.max(0, effectDate.getFullYear() - birthDate.getFullYear() - (
        effectDate.getMonth() < birthDate.getMonth() || (effectDate.getMonth() === birthDate.getMonth() && effectDate.getDate() < birthDate.getDate()) ? 1 : 0
      ));

      const cp = (personalInfo?.codePostal || '').toString().trim();
      const departement = cp.length >= 2 ? cp.slice(0, 2) : cp;

      const normalize = (value: any): string => {
        return (value || '')
          .toString()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9 ]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      };

      const assuranceRaw = (result.assurance || '').toString();
      const assuranceNorm = normalize(result.assurance);
      const isEca = assuranceLower.includes('eca');
      const insurerName = isEca ? 'ECA-Assurances' : 'SOLLY AZAR';
      const uiLabel = isEca ? 'ECA' : 'SOLLY AZAR';

      const productName = (
        isEca
          ? assuranceRaw.replace(/\beca\b/ig, '')
          : assuranceRaw.replace(/\bsolly\b/ig, '').replace(/\bazar\b/ig, '')
      )
        .replace(/assurances?/ig, '')
        .replace(/[\-–—]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const regimeRaw = (personalInfo?.regime || '').toString();
      const regimeNorm = normalize(regimeRaw);
      const regime = regimeNorm.includes('tns') ? 'TNS' : 'Régime Général';

      const year = (effectDate && !isNaN(effectDate.getTime())) ? effectDate.getFullYear().toString() : new Date().getFullYear().toString();

      const payload: any = {
        insurer: insurerName,
        product_name: productName,
        regime,
        departement,
        formula: (formule || result.formule || '').toString(),
        age,
        year
      };

      result.isPricingLoading = true;
      this.insuranceService.getEcaTarif(payload).pipe(
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (resp: any) => {
          const rawPrice = resp?.tarif ?? resp?.price ?? resp?.monthly;
          const parsed = typeof rawPrice === 'number'
            ? rawPrice
            : (rawPrice != null ? parseFloat(rawPrice.toString().replace(',', '.')) : NaN);

          if (isNaN(parsed)) {
            this.notifyPricingUnavailable(result, 'empty_response', {
              extraDetail: 'Réponse reçue sans montant de cotisation exploitable.'
            });
          } else {
            result.pricingDetail = undefined;
            result.prix = parsed;
          }
        },
        error: (err: any) => {
          this.notifyApiErrorToast({
            summary: `Information ${uiLabel}`,
            fallback: `Erreur lors du calcul du tarif ${uiLabel}.`,
            error: err,
            context: uiLabel,
            result,
            severity: 'warn'
          });
        }
      });
      return;
    }

    if (assuranceLower.includes('utwin')) {
      const quote = this.transformFormToQuote();
      result.isPricingLoading = true;

      const normalizeUtwin = (value: any): string => {
        return (value || '')
          .toString()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\+/g, ' plus ')
          .replace(/[^a-z0-9 ]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      };

      const selectedTextRaw = (formule || result.formule || result.assurance || '').toString();
      const selectedText = normalizeUtwin(selectedTextRaw);

      let desiredProductCode = '';
      const normalizedText = selectedText.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalizedText.includes('mixi')) {
        desiredProductCode = 'UTWIN_MIXI';
      } else if (normalizedText.includes('vital') || normalizedText.includes('vita')) {
        desiredProductCode = 'UTWIN_VITA';
      } else if (normalizedText.includes('opti')) {
        desiredProductCode = 'UTWIN_OPTI';
      } else if (normalizedText.includes('senior')) {
        desiredProductCode = 'UTWIN_BASIC_SENIOR';
      } else if (normalizedText.includes('multi')) {
        desiredProductCode = 'UTWIN_MULTI';
      } else if (normalizedText.includes('basic')) {
        desiredProductCode = 'UTWIN_BASIC_TNS';
      }

      (result as any).utwinCodeProduit = desiredProductCode || 'UTWIN_MULTI';

      this.insuranceService.getUtwinTarif(
        quote,
        result.formule,
        desiredProductCode || 'UTWIN_MULTI',
        normalizeCasCommission(result.selectedCommission)
      ).subscribe({
        next: (response: any) => {
          this.utwinMessages = [];
          this.collectUtwinMessagesFromResponse(response);

          // Certaines erreurs métier UTWIN (400) sont renvoyées dans le body (status/messages)
          // et arrivent ici via le service. On affiche immédiatement le message API exact.
          const isUtwinBusinessError =
            !!response?.type?.toString()?.includes('ProblemeMetier') ||
            !!response?.title?.toString()?.toLowerCase().includes('echec') ||
            (response?.status && Number(response.status) >= 400) ||
            (Array.isArray(response?.messages) && response.messages.length > 0 && !Array.isArray(response?.propositions));

          if (isUtwinBusinessError) {
            const detail = this.extractApiErrorMessage(response, 'Tarification UTWIN indisponible.');
            this.notifyPricingUnavailable(result, 'api_error', {
              rawMessage: detail,
              extraDetail: detail
            });
            result.isPricingLoading = false;
            this.cdr.detectChanges();
            return;
          }

          if (this.utwinMessages && this.utwinMessages.length > 0) {
            this.utwinMessages.forEach((m: string) => {
              const productCodeMatch = m.match(/UTWIN_[A-Z_]+/);
              const productContext = (productCodeMatch && productCodeMatch[0]) ? productCodeMatch[0] : (result.formule || result.assurance || 'UTWIN');
              this.addToastOnce({
                severity: 'warn',
                summary: 'Information Utwin',
                detail: m,
                context: productContext
              });
            });
          }

          const propositions: any[] = Array.isArray(response?.propositions) ? response.propositions : [];

          // Logic for desiredProductCode was here, now moved up.
          // We still need selectedFormulaRaw and selectedFormulaNorm for filtering below.

          const selectedFormulaRaw = (formule || result.formule || '').toString();
          const selectedFormulaNorm = normalizeUtwin(selectedFormulaRaw)
            .replace(/\bhospi\b/g, 'hospitalisation');

          let codeFormuleToFind = '';
          const explicitCodeFormule = selectedFormulaRaw.match(/\bN\d+\b/i);
          if (explicitCodeFormule) {
            codeFormuleToFind = explicitCodeFormule[0].toUpperCase();
          }

          const extract = (p: any) => {
            const codeProduit = p.code_produit || p.codeProduit;
            const libelleProduit = p.libelle_produit || p.libelleProduit;
            const codeFormule = p.code_formule || p.codeFormule;
            const libelleFormule = p.libelle_formule || p.libelleFormule;
            const commission = p.code_taux_commission_retenu || p.codeTauxCommissionRetenu;
            const prix = p.cotisation_mensuelle_euros || p.cotisationMensuelleEuros;
            return { codeProduit, libelleProduit, codeFormule, libelleFormule, commission, prix };
          };

          let pool = propositions;

          if (desiredProductCode) {
            const byCode = pool.filter(p => extract(p).codeProduit === desiredProductCode);
            if (byCode.length > 0) {
              pool = byCode;
            } else {
              const byLabel = pool.filter(p => normalizeUtwin(extract(p).libelleProduit).includes(normalizeUtwin(desiredProductCode)));
              if (byLabel.length > 0) {
                pool = byLabel;
              } else {
                result.isPricingLoading = false;
                this.notifyPricingUnavailable(result, 'no_matching_product', {
                  extraDetail: `Aucune proposition UTWIN pour le produit ${desiredProductCode}.`
                });
                this.cdr.detectChanges();
                return;
              }
            }
          }

          const hasHospiHint = selectedFormulaNorm.includes('hospitalisation')
            || selectedFormulaNorm.includes('hospi')
            || selectedFormulaNorm.includes('soins')
            || selectedFormulaNorm.includes('courants');

          const hasPlusHint = selectedFormulaRaw.includes('+') || selectedFormulaNorm.includes(' plus ');
          const hasEcoHint = selectedFormulaNorm.includes('eco');
          const hasEcoPlusHint = hasEcoHint && selectedFormulaNorm.includes('plus');

          const formulaNumberMatch = selectedFormulaRaw.match(/(?:Niveau|Formule|F|N)\s*(\d+)/i);
          const extractNumber = (str: string) => { const m = str.match(/\d+/); return m ? parseInt(m[0], 10) : NaN; };
          const formulaNumber = formulaNumberMatch?.[1] ? parseInt(formulaNumberMatch[1], 10) : extractNumber(selectedFormulaRaw);

          const filterByLibelle = (
            items: any[],
            num: number,
            plusWanted: boolean,
            ecoWanted: boolean
          ): any[] => {
            if (!Number.isFinite(num)) {
              return [];
            }
            const re = new RegExp(`\\bformule\\s+${num}\\b`);
            return items.filter(p => {
              const labelNorm = normalizeUtwin(extract(p).libelleFormule || '')
                .replace(/\bhospi\b/g, 'hospitalisation');
              if (!re.test(labelNorm)) {
                return false;
              }
              const hasPlus = labelNorm.includes('plus');
              const hasEco = labelNorm.includes('eco');
              if (plusWanted) {
                return ecoWanted ? hasPlus && hasEco : hasPlus && !hasEco;
              }
              return ecoWanted ? hasEco && !hasPlus : !hasEco && !hasPlus;
            });
          };

          const filterByLibelleEcoPlus = (items: any[], num: number): any[] => {
            if (!Number.isFinite(num)) {
              return [];
            }
            const re = new RegExp(`\\bformule\\s+${num}\\b`);
            return items.filter(p => {
              const labelNorm = normalizeUtwin(extract(p).libelleFormule || '')
                .replace(/\bhospi\b/g, 'hospitalisation');
              return re.test(labelNorm) && labelNorm.includes('eco') && labelNorm.includes('plus');
            });
          };

          if (hasHospiHint) {
            const byHospi = pool.filter(p => {
              const labelNorm = normalizeUtwin(extract(p).libelleFormule || '')
                .replace(/\bhospi\b/g, 'hospitalisation');
              return labelNorm.includes('hospitalisation');
            });
            if (byHospi.length > 0) {
              pool = byHospi;
            }
          } else if (Number.isFinite(formulaNumber)) {
            let byLibelleFormule: any[] = [];

            if (hasEcoPlusHint) {
              byLibelleFormule = filterByLibelleEcoPlus(pool, formulaNumber);
            } else {
              byLibelleFormule = filterByLibelle(pool, formulaNumber, hasPlusHint, hasEcoHint);
            }

            if (byLibelleFormule.length === 0 && desiredProductCode === 'UTWIN_MIXI' && formulaNumber === 1 && !hasPlusHint) {
              byLibelleFormule = filterByLibelle(pool, 2, false, hasEcoHint);
            }

            if (byLibelleFormule.length > 0) {
              pool = byLibelleFormule;
            }
          }

          pool = this.applyUtwinVariantFilter(pool, selectedFormulaNorm, extract);

          const targetCommission = normalizeCasCommission(result.selectedCommission);

          const commissionRank = (value: any): number => {
            const mappedVal = normalizeCasCommission(value);
            return mappedVal === targetCommission ? 0 : 1;
          };

          const priceValue = (value: any): number => {
            if (typeof value === 'number') {
              return value;
            }
            if (value == null) {
              return Number.POSITIVE_INFINITY;
            }
            const parsed = parseFloat(value.toString().replace(',', '.'));
            return isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
          };

          const bestProposition = pool
            .slice()
            .sort((a: any, b: any) => {
              const ea = extract(a);
              const eb = extract(b);
              const ra = commissionRank(ea.commission);
              const rb = commissionRank(eb.commission);
              if (ra !== rb) {
                return ra - rb;
              }
              return priceValue(ea.prix) - priceValue(eb.prix);
            })[0];

          if (bestProposition) {
            const extractedPrice = extract(bestProposition).prix;
            const parsedPrice = typeof extractedPrice === 'number'
              ? extractedPrice
              : parseFloat((extractedPrice ?? '').toString().replace(',', '.'));
            if (extractedPrice == null || isNaN(parsedPrice)) {
              this.notifyPricingUnavailable(result, 'no_matching_formula', {
                extraDetail: `Aucune cotisation pour la formule « ${formule || result.formule} » (${desiredProductCode || 'UTWIN'}).`
              });
            } else {
              result.pricingDetail = undefined;
              result.prix = parsedPrice;
              const exBest = extract(bestProposition);
              (result as any).utwinCodeFormule = exBest.codeFormule;
              (result as any).utwinLibelleFormule = exBest.libelleFormule;
            }
          } else {
            this.notifyPricingUnavailable(result, 'no_matching_product', {
              extraDetail: `Aucune proposition UTWIN ne correspond à « ${formule || result.formule} ».`
            });
          }

          result.isPricingLoading = false;
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          result.isPricingLoading = false;
          this.notifyApiErrorToast({
            summary: 'Information Utwin',
            fallback: 'Erreur lors du calcul du tarif UTWIN.',
            error: err,
            context: desiredProductCode || 'UTWIN',
            result,
            severity: 'warn'
          });
          this.cdr.detectChanges();
        }
      });
      return;
    }

    if (assuranceLower.includes('apicil')) {
      const personalInfo = this.insuranceForm.get('personalInfo')?.value;
      if (!personalInfo) {
        this.notifyPricingUnavailable(result, 'incomplete_form');
        return;
      }

      result.isPricingLoading = true;

      const formatDate = (date: any): string => {
        if (!date) return '';
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          return `${year}-${month}-${day}`;
        }
        if (date instanceof Date && !isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return '';
      };

      const beneficiaires: ApicilBeneficiaire[] = [];
      beneficiaires.push({
        role: 'ASSUR',
        nom: personalInfo.nom || personalInfo.lastName || 'NOM',
        prenom: personalInfo.prenom || personalInfo.firstName || 'PRENOM',
        dateNaissance: formatDate(personalInfo.dateNaissance),
        regimeSocial: ApicilService.mapRegimeSocial(personalInfo.regime)
      });

      if (personalInfo.conjoint?.dateNaissance) {
        beneficiaires.push({
          role: 'CONJT',
          nom: personalInfo.conjoint.nom || personalInfo.nom || 'NOM',
          prenom: personalInfo.conjoint.prenom || personalInfo.prenom || 'PRENOM',
          dateNaissance: formatDate(personalInfo.conjoint.dateNaissance),
          regimeSocial: ApicilService.mapRegimeSocial(personalInfo.conjoint.regime || personalInfo.regime)
        });
      }

      if (personalInfo.enfants && Array.isArray(personalInfo.enfants)) {
        personalInfo.enfants.forEach((enfant: any) => {
          if (enfant.dateNaissance) {
            beneficiaires.push({
              role: 'ENFNT',
              nom: enfant.nom || personalInfo.nom || 'NOM',
              prenom: enfant.prenom || 'ENFANT',
              dateNaissance: formatDate(enfant.dateNaissance),
              regimeSocial: ApicilService.mapRegimeSocial(enfant.regime || personalInfo.regime)
            });
          }
        });
      }

      const apicilPayload: ApicilTarificationRequest = {
        typePartenaire: 'COURTAGE',
        codePartenaire: environment.apicilPartnerCode || '0039587',
        codeProduit: 'ApiSante',
        codePostalAssure: personalInfo.codePostal || '75001',
        dateEffetContrat: formatDate(personalInfo.dateEffet) || new Date().toISOString().split('T')[0],
        beneficiaires
      };

      this.apicilService.getTarification(apicilPayload).pipe(
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (response: any) => {
          const formules = response?.Formules || [];
          if (formules.length > 0) {
            const formuleNorm = (formule || result.formule || '').toString().toLowerCase();
            let targetFormuleNumber = 1;
            const numMatch = formuleNorm.match(/(\d+)/);
            if (numMatch) {
              targetFormuleNumber = parseInt(numMatch[1], 10);
            }

            let targetFormule = formules.find((f: any) => {
              const libelle = (f.LibelleFormule || '').toLowerCase();
              return libelle.includes(`equilibre${targetFormuleNumber}`) ||
                libelle.includes(`equilibre ${targetFormuleNumber}`) ||
                libelle.includes(`formule${targetFormuleNumber}`) ||
                libelle.includes(`formule ${targetFormuleNumber}`) ||
                libelle.includes(`niveau${targetFormuleNumber}`) ||
                libelle.includes(`niveau ${targetFormuleNumber}`);
            });

            if (!targetFormule && formules.length > 0) {
              targetFormule = formules[0];
            }

            if (targetFormule) {
              const tarifStr = targetFormule.TarifFormule || '0';
              const prix = parseFloat(tarifStr.replace(',', '.').replace(/[^\d.]/g, ''));
              if (isNaN(prix)) {
                this.notifyPricingUnavailable(result, 'empty_response', {
                  extraDetail: `Formule APICIL « ${formule || result.formule} » sans tarif exploitable.`
                });
              } else {
                result.prix = prix;
                result.apicilPrestations = targetFormule.PrestationsDeConfort || [];
              }
            } else {
              this.notifyPricingUnavailable(result, 'no_matching_formula', {
                extraDetail: `Niveau/formule « ${formule || result.formule} » introuvable dans la réponse APICIL.`
              });
            }
          } else {
            this.notifyPricingUnavailable(result, 'api_no_result', {
              extraDetail: 'APICIL n\'a renvoyé aucune formule tarifable.'
            });
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.notifyApiErrorToast({
            summary: 'Information APICIL',
            fallback: 'Erreur lors du calcul du tarif APICIL.',
            error: err,
            context: 'APICIL',
            result,
            severity: 'warn'
          });
          this.cdr.detectChanges();
        }
      });
      return;
    }

    if (assuranceLower.includes('alptis')) {
      this.fetchAlptisPrices();
      return;
    }

    if (assuranceLower.includes('2ma') || assuranceLower.includes('2maltitude')) {
      this.fetchDeuxMaPrices(result, formule);
      return;
    }

    if (assuranceLower.includes('swisslife') || assuranceLower.includes('swiss')) {
      this.fetchSwissLifePrices(result, formule);
      return;
    }

    // Generali - TNS et Santé Pro
    if (assuranceLower.includes('generali')) {
      console.log('🔷 GENERALI - Tarification via fetchAllGeneraliPrices');
      this.fetchAllGeneraliPrices();
      return;
    }

    if (assuranceLower.includes('neoliane')) {
      const personalInfo = this.insuranceForm.get('personalInfo')?.value;
      result.isPricingLoading = true;

      // Helper pour formater les dates
      const formatDate = (date: any): string => {
        if (!date) return '';
        if (date instanceof Date && !isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = ('0' + (date.getMonth() + 1)).slice(-2);
          const day = ('0' + date.getDate()).slice(-2);
          return `${year}-${month}-${day}`;
        }
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          return `${year}-${month}-${day}`;
        }
        return '';
      };

      // Construire les membres pour le profil V2
      // Helper pour extraire l'année de naissance correctement
      const getBirthYear = (date: any): string => {
        if (!date) return '1990';
        if (typeof date === 'string') {
          // Format dd/MM/yyyy
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
            return date.split('/')[2];
          }
          // Format yyyy-MM-dd
          if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date.split('-')[0];
          }
        }
        if (date instanceof Date && !isNaN(date.getTime())) {
          return date.getFullYear().toString();
        }
        // Essayer de parser
        const parsed = new Date(date);
        if (!isNaN(parsed.getTime())) {
          return parsed.getFullYear().toString();
        }
        return '1990';
      };

      const getScheme = (regime: string): string => {
        const norm = ApiMapperUtil.normalizeRegime(regime);
        if (norm === 'TNS') return 'selfEmployed';
        if (norm === 'ALSACE_MOSELLE') return 'alsaceMoselle';
        if (norm === 'AGRICOLE') return 'farmer';
        return 'employee';
      };

      // Construire les membres pour le profil V2 (profileHealth)
      const profileHealth: any[] = [];

      // Titulaire
      profileHealth.push({
        familyMember: 'holder',
        birthYear: getBirthYear(personalInfo?.dateNaissance),
        socialSecurityScheme: getScheme(personalInfo?.regime)
      });

      // Conjoint
      if (personalInfo?.conjoint?.dateNaissance) {
        profileHealth.push({
          familyMember: 'spouse',
          birthYear: getBirthYear(personalInfo.conjoint.dateNaissance),
          socialSecurityScheme: getScheme(personalInfo.conjoint.regime || personalInfo.regime)
        });
      }

      // Enfants
      if (Array.isArray(personalInfo?.enfants)) {
        personalInfo.enfants.forEach((enfant: any, index: number) => {
          profileHealth.push({
            familyMember: `child.${index}`,
            birthYear: getBirthYear(enfant.dateNaissance),
            socialSecurityScheme: getScheme(enfant.regime || personalInfo.regime)
          });
        });
      }

      // Créer le profil V2 avec la bonne structure
      const profileRequest: NeolianeV2ProfileRequest = {
        zipCode: personalInfo?.codePostal || '75001',
        dateEffect: formatDate(personalInfo?.dateEffet) || new Date().toISOString().split('T')[0],
        productType: 'sante',
        profileHealth: profileHealth
      };

      console.log('🚀 Neoliane V2 - Création profil:', profileRequest);

      // Workflow V2: Profile → GeneratePrices → Cart
      this.neolianeV2Service.createProfile(profileRequest).pipe(
        switchMap(profile => {
          console.log('✅ Profil créé:', profile);
          (result as any).neolianeProfileId = profile.id;
          // Stocker le lead_id depuis le profile V2 pour la souscription
          (result as any).neolianeLeadId = profile.lead;
          console.log('📦 Lead ID stocké:', profile.lead);
          return this.neolianeV2Service.generatePrices(profile.id);
        }),
        switchMap(prices => {
          console.log('✅ Prix générés:', prices);
          if (!prices || prices.length === 0) {
            throw new Error('Aucun prix disponible');
          }

          // Normaliser les chaînes (retirer accents, minuscules, espaces, apostrophes)
          const normalizeStr = (str: string) => 
            (str || '').normalize('NFD')
                       .replace(/[\u0300-\u036f]/g, '')
                       .replace(/['’\s-]/g, '')
                       .toLowerCase().trim();
          
          const fullGammeSearch = `${result.assurance || ''} ${result.contractName || ''}`;
          const assuranceName = normalizeStr(fullGammeSearch).replace('neoliane', '').trim();
          const formuleName = normalizeStr(formule || result.formule);
          
          const niveauMatch = formuleName.match(/(?:niveau|formule)\s*(\d+)/i);
          const niveau = niveauMatch?.[1] ? parseInt(niveauMatch[1]) : null;

          const matchNumber = (str: string) => {
            const m = str.match(/\d+/);
            return m ? m[0] : null;
          };

          const numFormuleName = matchNumber(formuleName);

          // Chercher le prix correspondant à la gamme et la formule
          let matchedPrices = prices.filter(p => {
            const pGamme = normalizeStr(p.gammeLabel).replace('neoliane', '').trim();
            const pFormule = normalizeStr(p.formulaLabel);
            
            const isGammeMatch = !assuranceName || pGamme.includes(assuranceName) || assuranceName.includes(pGamme);
            if (!isGammeMatch) return false;

            const isNiveauMatch = niveau !== null && pFormule.includes(niveau.toString());
            const isExactFormuleMatch = pFormule === formuleName || formuleName.includes(pFormule) || pFormule.includes(formuleName);
            const isNumberMatch = numFormuleName !== null && numFormuleName === matchNumber(pFormule);
            
            return isExactFormuleMatch || isNiveauMatch || isNumberMatch;
          });

          // Si on a plusieurs correspondances, on privilégie la version responsable ou la première
          let matchedPrice: any = matchedPrices.find(p => (p as any).isResponsable !== false) || matchedPrices[0];
          
          if (!matchedPrice) {
            // Fallback 1: match juste la gamme
            matchedPrice = prices.find(p => {
              const pGamme = normalizeStr(p.gammeLabel).replace('neoliane', '').trim();
              return !assuranceName || pGamme.includes(assuranceName) || assuranceName.includes(pGamme);
            });
          }

          if (!matchedPrice) {
            // Fallback 2: si des prix ont été générés pour ce profil, prendre le premier disponible
            matchedPrice = prices[0];
          }

          if (!matchedPrice) {
            throw new Error(`Produit Néoliane non éligible ou non trouvé pour ${result.assurance} ${result.formule}`);
          }

          (result as any).neolianePricingId = matchedPrice.id;
          (result as any).neolianeGammeId = matchedPrice.gammeId;
          (result as any).neolianeFormulaId = matchedPrice.formulaId;

          console.log('📦 IDs Néoliane stockés:', {
            gammeId: matchedPrice.gammeId,
            formulaId: matchedPrice.formulaId,
            pricingId: matchedPrice.id
          });

          const amount = matchedPrice?.amount;
          const parsedAmount = typeof amount === 'number' ? amount : parseFloat(String(amount ?? '').replace(',', '.'));
          if (amount == null || isNaN(parsedAmount)) {
            throw new Error('Aucun montant de cotisation dans la réponse Neoliane.');
          }
          result.pricingDetail = undefined;
          result.prix = parsedAmount;

          // Créer le panier (avec secours si l'API panier échoue)
          const cartItems = [{ pricingId: matchedPrice.id, members: profileHealth.map(m => m.familyMember) }];
          return this.neolianeV2Service.createCart((result as any).neolianeProfileId, cartItems).pipe(
            catchError(cartErr => {
              console.warn('⚠️ Erreur création panier Néoliane (conservé pour l\'affichage du tarif):', cartErr);
              return of({ totalAmount: result.prix, id: null } as any);
            })
          );
        }),
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (cart) => {
          console.log('✅ Panier créé / tarif Néoliane validé:', cart);
          // Stocker le totalAmount et cartId depuis le cart pour la souscription
          (result as any).neolianeTotalAmount = cart?.totalAmount || result.prix;
          (result as any).neolianeCartId = cart?.id;
          console.log('📦 TotalAmount stocké:', (result as any).neolianeTotalAmount, 'CartId:', (result as any).neolianeCartId);
          this.addToastOnce({
            severity: 'success',
            summary: 'Neoliane V2',
            detail: `Tarif calculé: ${result.prix}€/mois`,
            context: 'NEOLIANE'
          });
        },
        error: (err: any) => {
          // Si le profil est vraiment non éligible pour ce produit Néoliane (pas de tarif dans generatePrices)
          const errMsg = ((err?.message || err?.error?.message || err?.error || ''))
            .toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const isIneligible =
            errMsg.includes('non eligible') ||
            errMsg.includes('ineligible') ||
            errMsg.includes('pas eligible') ||
            errMsg.includes('aucun prix') ||
            errMsg.includes('aucun montant') ||
            errMsg.includes('aucun resultat');
          if (isIneligible) {
            console.log(`🚫 Neoliane — résultat masqué (non éligible) : ${result.assurance} ${result.formule}`);
            result.pricingDetail = 'non eligible';
            result.prix = 'N/A';
            this.cdr.detectChanges();
            return;
          }
          this.notifyApiErrorToast({
            summary: 'Information Neoliane V2',
            fallback: 'Erreur lors du calcul du tarif NEOLIANE V2.',
            error: err,
            context: 'NEOLIANE',
            result,
            severity: 'warn'
          });
        }
      });

      return;
    }

    if (this.isZeniooOffer(result.assurance)) {
      const personalInfo = this.insuranceForm.get('personalInfo')?.value;
      result.isPricingLoading = true;
      // ... (rest of the code remains the same)

      // Déclaration des variables manquantes pour éviter les erreurs TS2304
      const cp = (personalInfo?.codePostal || '').toString().trim();
      const members = 1
        + ((personalInfo?.conjoint?.dateNaissance) ? 1 : 0)
        + (Array.isArray(personalInfo?.enfants) ? personalInfo.enfants.length : 0);
      const adultCount = Math.min(2, members);
      const childrenCount = Math.max(0, members - adultCount);

      const normalize = (value: any): string => {
        return (value || '')
          .toString()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9 ]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      };

      const toIsoDate = (date: any): string => {
        if (!date) {
          return new Date().toISOString().split('T')[0];
        }
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          return `${year}-${month}-${day}`;
        }
        const d = (date instanceof Date) ? date : new Date(date);
        if (isNaN(d.getTime())) {
          return new Date().toISOString().split('T')[0];
        }
        return d.toISOString().split('T')[0];
      };

      const toDate = (date: any): Date => {
        if (!date) {
          return new Date();
        }
        if (date instanceof Date && !isNaN(date.getTime())) {
          return date;
        }
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
          return isNaN(d.getTime()) ? new Date() : d;
        }
        const d = new Date(date);
        return isNaN(d.getTime()) ? new Date() : d;
      };

      const birthDate = toDate(personalInfo?.dateNaissance);
      const effectDate = toDate(personalInfo?.dateEffet);
      const age = Math.max(0, effectDate.getFullYear() - birthDate.getFullYear() - (
        effectDate.getMonth() < birthDate.getMonth() || (effectDate.getMonth() === birthDate.getMonth() && effectDate.getDate() < birthDate.getDate()) ? 1 : 0
      ));

      const contractName = (result as any).contractName || '';
      const levelName = (result as any).levelName || '';

      const contractNorm = normalize(contractName || result.formule || result.assurance);
      const levelNorm = normalize(levelName || formule || result.formule);

      const findArrayInObjectGraph = (
        root: any,
        pred: (arr: any[]) => boolean,
        maxDepth: number = 6
      ): any[] => {
        if (!root || typeof root !== 'object') {
          return [];
        }
        const seen = new Set<any>();
        const queue: Array<{ v: any; d: number }> = [{ v: root, d: 0 }];

        while (queue.length > 0) {
          const { v, d } = queue.shift()!;
          if (!v || typeof v !== 'object') {
            continue;
          }
          if (seen.has(v)) {
            continue;
          }
          seen.add(v);

          if (Array.isArray(v)) {
            if (pred(v)) {
              return v;
            }
            continue;
          }

          if (d >= maxDepth) {
            continue;
          }

          Object.values(v).forEach(child => {
            if (child && typeof child === 'object') {
              queue.push({ v: child, d: d + 1 });
            }
          });
        }

        return [];
      };

      const extractArray = (obj: any): any[] => {
        const direct = [
          obj?.produits,
          obj?.products,
          obj?.product,
          obj?.data?.produits,
          obj?.data?.products,
          obj?.data?.product,
          obj?.GetProductsResponse?.produits,
          obj?.GetProductsResponse?.products,
          obj?.GetProductsResponse?.product,
          obj?.GetProductsResponse?.liste,
          obj?.GetProductsResponse?.list,
          obj?.getProductsResponse?.produits,
          obj?.getProductsResponse?.products,
          obj?.getProductsResponse?.liste,
          obj?.list,
          obj?.liste,
          obj?.items,
          obj?.data
        ].find((v: any) => Array.isArray(v));

        if (Array.isArray(direct)) {
          return direct;
        }

        return findArrayInObjectGraph(obj, (arr: any[]) => {
          if (!Array.isArray(arr) || arr.length === 0) {
            return false;
          }
          const first = arr[0];
          return typeof first === 'object' && !!(first?.code_produit || first?.codeProduit || first?.code || first?.id || first?.codeProduitLong);
        });
      };

      const extractFormulasArray = (obj: any): any[] => {
        const direct = [
          obj?.formules,
          obj?.formulas,
          obj?.formula,
          obj?.data?.formules,
          obj?.data?.formulas,
          obj?.data?.formula,
          obj?.GetFormulasResponse?.formules,
          obj?.GetFormulasResponse?.formulas,
          obj?.GetFormulasResponse?.liste,
          obj?.GetFormulasResponse?.list,
          obj?.getFormulasResponse?.formules,
          obj?.getFormulasResponse?.formulas,
          obj?.getFormulasResponse?.liste,
          obj?.list,
          obj?.liste,
          obj?.items,
          obj?.data
        ].find((v: any) => Array.isArray(v));

        if (Array.isArray(direct)) {
          return direct;
        }

        return findArrayInObjectGraph(obj, (arr: any[]) => {
          if (!Array.isArray(arr) || arr.length === 0) {
            return false;
          }
          const first = arr[0];
          return typeof first === 'object' && !!(first?.code_formule || first?.codeFormule || first?.code || first?.id || first?.codeFormuleLong);
        });
      };

      const pickProduct = (products: any[]): any => {
        if (!products || products.length === 0) {
          return null;
        }

        // Explicit keyword → code_produit mapping for all Zenioo Santé products
        const ZENIOO_CODE_MAP: Array<{ keywords: string[]; code: string }> = [
          { keywords: ['eco'], code: 'P80' },
          { keywords: ['equilibre'], code: 'P91' },
          { keywords: ['focus', '55'], code: 'P100' },
          { keywords: ['focus', 'famille'], code: 'P110' },
          { keywords: ['evolutive'], code: 'P120' },
          { keywords: ['proximite'], code: 'P140' },
          { keywords: ['integrale', '55'], code: 'P50' },
          { keywords: ['integrale', 'famille'], code: 'P60' },
        ];

        // Try to resolve the product code from contractNorm (which comes from contracts.json contract_name)
        if (contractNorm) {
          for (const entry of ZENIOO_CODE_MAP) {
            if (entry.keywords.every(kw => contractNorm.includes(kw))) {
              const found = products.find((p: any) => {
                const code = (p?.code_produit || p?.codeProduit || p?.code || p?.id || '').toString().toUpperCase();
                return code === entry.code;
              });
              if (found) {
                return found;
              }
            }
          }
        }

        // Fallback: fuzzy matching by label
        const matched = products.find(p => {
          const labelLong = normalize(
            p?.libelle_produit_long ||
            p?.libelleProduitLong ||
            p?.libelle_produit ||
            p?.libelleProduit ||
            p?.libelle ||
            p?.name ||
            p?.nom
          );
          const labelShort = normalize(
            p?.libelle_produit_court ||
            p?.libelleProduitCourt ||
            p?.shortLabel
          );
          const code = normalize(
            p?.code_produit ||
            p?.codeProduit ||
            p?.code ||
            p?.id
          );

          if (!contractNorm) {
            return false;
          }

          return (
            (labelLong && (labelLong.includes(contractNorm) || contractNorm.includes(labelLong))) ||
            (labelShort && (labelShort.includes(contractNorm) || contractNorm.includes(labelShort))) ||
            (code && (code.includes(contractNorm) || contractNorm.includes(code)))
          );
        });
        return matched || products[0];
      };

      const pickFormula = (formulas: any[]): any => {
        if (!formulas || formulas.length === 0) {
          return null;
        }
        const matched = formulas.find(f => {
          const label = normalize(
            f?.libelle_formule_long ||
            f?.libelle_formule_court ||
            f?.libelle_formule ||
            f?.libelleFormule ||
            f?.libelle ||
            f?.label ||
            f?.name ||
            f?.nom
          );
          const code = normalize(f?.code_formule || f?.codeFormule || f?.code || f?.id);
          if (!label && !code) {
            return false;
          }
          if (levelNorm && ((label && (label.includes(levelNorm) || levelNorm.includes(label))) || (code && (code.includes(levelNorm) || levelNorm.includes(code))))) {
            return true;
          }
          return false;
        });
        return matched || formulas[0];
      };

      const extractZeniooPrice = (resp: any, targetCodeFormule?: string): any => {
        const wrapper = resp?.GetTariffsResponse || resp?.getTariffsResponse || resp;
        const tarifsArray = wrapper?.tarification || wrapper?.tarifs || wrapper?.tariffs || wrapper?.list || wrapper?.liste || wrapper?.data?.tarifs || wrapper?.data?.tariffs;

        const pickFromItem = (item: any): any => {
          if (!item) {
            return null;
          }
          return item?.tarif ??
            item?.cotisation_mensuelle_euros ??
            item?.cotisationMensuelleEuros ??
            item?.cotisation_mensuelle ??
            item?.cotisationMensuelle ??
            item?.prime_mensuelle ??
            item?.primeMensuelle ??
            item?.tarif_mensuel ??
            item?.tarifMensuel ??
            item?.total_mensuel ??
            item?.totalMensuel ??
            item?.monthlyPayment ??
            item?.monthly ??
            item?.total_amount ??
            item?.montant ??
            item?.amount ??
            item?.price;
        };

        if (Array.isArray(tarifsArray) && tarifsArray.length > 0) {
          if (targetCodeFormule) {
            const matched = tarifsArray.find((t: any) => {
              const code = (t?.code_formule || t?.codeFormule || t?.code || '').toString().toUpperCase();
              return code === targetCodeFormule.toUpperCase();
            });
            if (matched) {
              return pickFromItem(matched);
            }
          }
          // Fallback to first one
          const fromFirst = pickFromItem(tarifsArray[0]);
          if (fromFirst != null) {
            return fromFirst;
          }
        }

        return wrapper?.tarif ??
          wrapper?.cotisation_mensuelle_euros ??
          wrapper?.cotisationMensuelleEuros ??
          wrapper?.cotisation_mensuelle ??
          wrapper?.cotisationMensuelle ??
          wrapper?.tarif_mensuel ??
          wrapper?.tarifMensuel ??
          wrapper?.total_mensuel ??
          wrapper?.totalMensuel ??
          wrapper?.monthlyPayment ??
          wrapper?.monthly ??
          wrapper?.total_amount ??
          wrapper?.montant ??
          wrapper?.amount ??
          wrapper?.price;
      };

      const normalizeZeniooResponse = (resp: any): any => {
        if (resp == null) {
          return resp;
        }

        const candidate = resp?.data ?? resp;

        if (candidate && typeof candidate === 'object' && typeof (candidate as any).Text === 'string') {
          try {
            return JSON.parse((candidate as any).Text);
          } catch {
            return candidate;
          }
        }

        if (typeof candidate === 'string') {
          try {
            return JSON.parse(candidate);
          } catch {
            return candidate;
          }
        }

        return candidate;
      };

      this.insuranceService.getZeniooProducts({}).pipe(
        switchMap((productsResp: any) => {
          const products = extractArray(normalizeZeniooResponse(productsResp));
          const selectedProduct = pickProduct(products);
          const codeProduit = selectedProduct?.code_produit || selectedProduct?.codeProduit || selectedProduct?.code || selectedProduct?.id || selectedProduct?.codeProduitLong;

          if (!codeProduit) {
            const anyCandidate = (products || []).find((p: any) => p?.code_produit || p?.codeProduit || p?.code || p?.id || p?.codeProduitLong);
            const fallbackCode = anyCandidate?.code_produit || anyCandidate?.codeProduit || anyCandidate?.code || anyCandidate?.id || anyCandidate?.codeProduitLong;
            if (!fallbackCode) {
              throw new Error('Impossible de récupérer le code produit Zenioo.');
            }
            return this.insuranceService.getZeniooFormulas({ code_produit: fallbackCode }).pipe(
              map((formulasResp: any) => ({ codeProduit: fallbackCode, formulasResp }))
            );
          }

          return this.insuranceService.getZeniooFormulas({ code_produit: codeProduit }).pipe(
            map((formulasResp: any) => ({ codeProduit, formulasResp }))
          );
        }),
        switchMap((data: any) => {
          console.log('ZENIOO DEBUG SWITCHMAP INPUT:', data);
          let { codeProduit, formulasResp } = data;
          if (!codeProduit) {
            console.warn('ZENIOO WARNING: codeProduit missing in switchMap, forcing P100L');
            codeProduit = 'P100L';
          }
          // Force capture result immediately
          result.zeniooCodeProduit = codeProduit;
          this.applyDefaultCommissionForResult(result);
          const formulas = extractFormulasArray(normalizeZeniooResponse(formulasResp));
          const selectedFormula = pickFormula(formulas);
          const codeFormule = selectedFormula?.code_formule || selectedFormula?.codeFormule || selectedFormula?.code || selectedFormula?.id || selectedFormula?.codeFormuleLong;
          // Force capture formule immediately
          if (codeFormule) result.zeniooCodeFormule = codeFormule;

          if (!codeFormule) {
            const anyFormula = (formulas || []).find((f: any) => f?.code_formule || f?.codeFormule || f?.code || f?.id || f?.codeFormuleLong);
            const fallbackFormula = anyFormula?.code_formule || anyFormula?.codeFormule || anyFormula?.code || anyFormula?.id || anyFormula?.codeFormuleLong;
            if (!fallbackFormula) {
              throw new Error('Impossible de récupérer le code formule Zenioo.');
            }

            const payload: any = {
              code_produit: codeProduit,
              code_formule: fallbackFormula,
              taux_commission: normalizeCasCommission(result.selectedCommission),
              cp: personalInfo?.codePostal || '',
              code_postal: personalInfo?.codePostal || '',
              codePostal: personalInfo?.codePostal || '',
              date_effet: toIsoDate(personalInfo?.dateEffet),
              dateEffet: toIsoDate(personalInfo?.dateEffet),
              annee_naissance_adherent_principal: String(birthDate.getFullYear()),
              age: age,
              regime_social_adherent_principal: (personalInfo?.regime || '').toString(),
              nb_adultes: adultCount,
              nb_enfants: childrenCount
            };

            return this.insuranceService.getZeniooTarif(payload).pipe(
              map((tarifResp: any) => ({ codeProduit, codeFormule: fallbackFormula, tarifResp }))
            );
          }

          const payload: any = {
            code_produit: codeProduit,
            code_formule: codeFormule,
            taux_commission: normalizeCasCommission(result.selectedCommission),
            cp: personalInfo?.codePostal || '',
            code_postal: personalInfo?.codePostal || '',
            codePostal: personalInfo?.codePostal || '',
            date_effet: toIsoDate(personalInfo?.dateEffet),
            dateEffet: toIsoDate(personalInfo?.dateEffet),
            annee_naissance_adherent_principal: String(birthDate.getFullYear()),
            age: age,
            regime_social_adherent_principal: (personalInfo?.regime || '').toString(),
            nb_adultes: adultCount,
            nb_enfants: childrenCount
          };

          // Force capture of Zenioo metadata even if API call fails
          if (codeProduit) result.zeniooCodeProduit = codeProduit;
          if (codeFormule) result.zeniooCodeFormule = codeFormule;

          return this.insuranceService.getZeniooTarif(payload).pipe(
            map((tarifResp: any) => {
              console.log('ZENIOO DEBUG MAP:', { codeProduit, codeFormule, tarifResp });
              return { codeProduit, codeFormule, tarifResp };
            })
          );
        }),
        finalize(() => {
          result.isPricingLoading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: ({ codeProduit, codeFormule, tarifResp }: any) => {
          console.log('ZENIOO DEBUG NEXT:', { codeProduit, codeFormule, tarifResp });
          const response = tarifResp;
          const normalizeResp = (resp: any): any => {
            if (resp == null) return resp;
            const candidate = resp?.data ?? resp;
            if (candidate && typeof candidate === 'object' && typeof (candidate as any).Text === 'string') {
              try { return JSON.parse((candidate as any).Text); } catch { return candidate; }
            }
            if (typeof candidate === 'string') {
              try { return JSON.parse(candidate); } catch { return candidate; }
            }
            return candidate;
          };
          const raw = extractZeniooPrice(normalizeResp(response), codeFormule);
          const parsed = typeof raw === 'number'
            ? raw
            : (raw != null ? parseFloat(raw.toString().replace(',', '.')) : NaN);
          if (isNaN(parsed)) {
            this.notifyPricingUnavailable(result, 'empty_response', {
              extraDetail: `Zenioo : aucun tarif pour le produit ${codeProduit || ''} / formule ${codeFormule || ''}.`
            });
          } else {
            result.pricingDetail = undefined;
            result.prix = parsed;
          }

          // Capture Zenioo metadata from response or context
          // codeProduit and codeFormule are captured via closure from switchMap lines above
          if (typeof codeProduit !== 'undefined') {
            result.zeniooCodeProduit = codeProduit;
          }
          if (typeof codeFormule !== 'undefined') {
            result.zeniooCodeFormule = codeFormule;
          }

          // Capture taux_commission from response if available
          const cleanResp = normalizeResp(response);
          const taux = cleanResp?.commission?.taux_commission || cleanResp?.data?.commission?.taux_commission;
          if (taux) {
            result.zeniooTauxCommission = taux;
          } else {
            // Fallback logic for taux if not in response (though it should be)
            // We can re-derive it or leave undefined (backend has fallback)
          }
        },
        error: (err: any) => {
          this.notifyApiErrorToast({
            summary: 'Information Zenioo',
            fallback: 'Erreur lors du calcul du tarif ZENIOO.',
            error: err,
            context: 'ZENIOO',
            result,
            severity: 'warn'
          });
        }
      });

      return;
    }
  }

  private getQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const normalizeName = (name: string): string => {
      return (name || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };

    const assuranceName = normalizeName(result.assurance);
    if (assuranceName.includes('april') && !assuranceName.includes('apivia')) {
      return this.buildAprilQuotePayload(result);
    } else if (assuranceName.includes('apivia')) {
      return this.buildApiviaQuotePayload(result);
    } else if (assuranceName.includes('utwin')) {
      return this.buildUtwinQuotePayload(result);
    } else if (assuranceName.includes('apicil')) {
      return this.buildApicilQuotePayload(result);
    } else if (this.isZeniooOffer(result.assurance)) {
      return this.buildZeniooQuotePayload(result, personalInfo);
    } else if (assuranceName.includes('generali')) {
      return { isGenerali: true };
    } else if (assuranceName.includes('alptis')) {
      return this.buildAlptisQuotePayload(result);
    } else if (assuranceName.includes('neoliane')) {
      return this.buildNeolianeQuotePayload(result);
    }

    return { isGeneric: true, personalInfo };
  }

  private buildApicilQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        return `${year}-${month}-${day}`;
      }
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const toCivilite = (value: string): 'MR' | 'MME' => {
      const v = (value || '').toUpperCase();
      return (v === 'M' || v.includes('MONSIEUR')) ? 'MR' : 'MME';
    };

    const toSituationFamiliale = (value: string): string => {
      const v = (value || '').toLowerCase();
      if (v.includes('marie')) return 'MARIE';
      if (v.includes('pac')) return 'PACSE';
      if (v.includes('union')) return 'UNION LIBRE';
      if (v.includes('divorc')) return 'DIVORCE';
      if (v.includes('separ')) return 'SEPARE';
      if (v.includes('veuf')) return 'VEUF';
      return 'CELIBATAIRE';
    };

    const toApicilSituationPro = (value: string): 'ACTIF' | 'CONJCOL' | 'ETUDIANT' | 'INACTIF' | 'NOCTR' | 'RETAITE' | 'SALARIE' => {
      const v = (value || '').toUpperCase();
      if (v.includes('RETRA')) return 'RETAITE';
      if (v.includes('ETUD')) return 'ETUDIANT';
      if (v.includes('INACT')) return 'INACTIF';
      if (v.includes('CONJ')) return 'CONJCOL';
      if (v.includes('SALAR')) return 'SALARIE';
      if (v.includes('NOCTR')) return 'NOCTR';
      return 'ACTIF';
    };

    const selectedFormule = (() => {
      const fromTarif = (result as any).apicilFormuleSelectionnee;
      if (fromTarif) return fromTarif;
      const numMatch = (result.formule || '').toString().match(/(\d+)/);
      const n = numMatch ? parseInt(numMatch[1], 10) : 1;
      return `siApiSanteEquilibre${n}`;
    })();

    const deriveApicilActivite = (): string => {
      const candidates = [
        personalInfo.activiteProfessionnelle,
        personalInfo.activitePro,
        personalInfo.codeActivite
      ].map((v: any) => (v ?? '').toString().trim()).filter((v: string) => !!v);
      // 180 apparaît dans le référentiel APICIL; fallback sûr quand le champ n'est pas saisi.
      return candidates[0] || '180';
    };

    const payload: any = {
      typePartenaire: 'COURTAGE',
      codePartenaire: environment.apicilPartnerCode || '0039587',
      codeProduit: 'ApiSante',
      dateEffetContrat: formatDate(personalInfo.dateEffet) || new Date().toISOString().split('T')[0],
      role: 'ASSUR',
      civilite: toCivilite(personalInfo.civilite),
      nom: personalInfo.nom || 'NOM',
      prenom: personalInfo.prenom || 'PRENOM',
      dateNaissance: formatDate(personalInfo.dateNaissance),
      regimeSocial: ApicilService.mapRegimeSocial(personalInfo.regime),
      adresseL2: '',
      adresseL3: '',
      adresseL4: personalInfo.adresse || '',
      adresseL5: '',
      codePostal: personalInfo.codePostal || '75001',
      ville: personalInfo.ville || '',
      pays: 'FR',
      situationFamiliale: toSituationFamiliale(personalInfo.etatCivil),
      telephone: personalInfo.telephone1 || '',
      indicatifParTelephone: '+33',
      email: personalInfo.email || '',
      paysDeNaissance: 'FR',
      villeDeNaissance: personalInfo.ville || '',
      situationProfessionnelle: toApicilSituationPro(personalInfo.situationProfessionnelle || personalInfo.statutProfessionnel),
      activiteProfessionnelle: deriveApicilActivite(),
      beneficiaires: [],
      formules: [
        {
          formuleSelectionnee: selectedFormule
        }
      ]
    };

    if (payload.situationProfessionnelle === 'RETAITE') {
      delete payload.activiteProfessionnelle;
    }

    if (payload.regimeSocial === 'SSI') {
      const now = new Date();
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      const siret = (personalInfo.siret || '').toString().replace(/\s/g, '');
      const hasValidSiret = /^\d{14}$/.test(siret) && siret !== '00000000000000';
      const hasRaisonSociale = !!(personalInfo.raisonSociale || '').toString().trim();

      // APICIL retourne parfois UNEXPECTED_ERROR si SSI est envoyé avec données TNS incomplètes.
      // Dans ce cas on fallback GENERAL pour préserver le parcours devis.
      if (!hasValidSiret || !hasRaisonSociale) {
        payload.regimeSocial = 'GENERAL';
        delete payload.siret;
        delete payload.raisonSociale;
        delete payload.dateCreation;
        delete payload.codePostalPM;
        delete payload.statutSocial;
        delete payload.defiscalisationMadelin;
      } else {
        payload.siret = siret;
        payload.raisonSociale = personalInfo.raisonSociale;
        payload.dateCreation = formatDate(personalInfo.dateCreationEntreprise || oneYearAgo);
        payload.codePostalPM = (personalInfo.codePostalEntreprise || personalInfo.codePostal || '75001').toString();
        payload.statutSocial = personalInfo.statutSocialApicil || 'ProfessionLiberaleNonMedicale';
        payload.defiscalisationMadelin = personalInfo.defiscalisationMadelin ?? true;
      }
    }

    if (Array.isArray((result as any).apicilPrestations) && (result as any).apicilPrestations.length > 0) {
      payload.formules[0].prestationConfort = (result as any).apicilPrestations[0]?.LibellePrestationDeConfort;
    }

    if (personalInfo.conjoint?.dateNaissance) {
      payload.beneficiaires.push({
        role: 'CONJT',
        nom: personalInfo.conjoint.nom || personalInfo.nom || 'NOM',
        prenom: personalInfo.conjoint.prenom || personalInfo.prenom || 'PRENOM',
        dateNaissance: formatDate(personalInfo.conjoint.dateNaissance),
        regimeSocial: ApicilService.mapRegimeSocial(personalInfo.conjoint.regime || personalInfo.regime)
      });
    }

    if (Array.isArray(personalInfo.enfants)) {
      personalInfo.enfants.forEach((enfant: any) => {
        if (enfant?.dateNaissance) {
          payload.beneficiaires.push({
            role: 'ENFNT',
            nom: enfant.nom || personalInfo.nom || 'NOM',
            prenom: enfant.prenom || 'ENFANT',
            dateNaissance: formatDate(enfant.dateNaissance),
            regimeSocial: ApicilService.mapRegimeSocial(enfant.regime || personalInfo.regime)
          });
        }
      });
    }

    return payload;
  }

  formatFormula(formule: string, assurance: string): string {
    if (!formule) return '';

    // Remove the assurance name from the beginning of the formula string
    const cleanedFormula = formule.toLowerCase().startsWith(assurance.toLowerCase())
      ? formule.substring(assurance.length).trim()
      : formule;

    const regex = /(Niveau|FORMULE)/i;
    const parts = cleanedFormula.split(regex);

    if (parts.length > 1) {
      const productName = parts[0].trim();
      const formulaPart = parts.slice(1).join('').trim();
      return `<strong style="color: #A102F2; font-weight: bold;">${productName}</strong><br><em>${formulaPart}</em>`;
    }

    // Fallback if keywords are not found
    return `<strong style="color: #A102F2; font-weight: bold;">${cleanedFormula}</strong>`;
  }

  private buildUtwinRequest(): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) {
      console.error('Personal info not available for Utwin request');
      return null;
    }

    console.log('Building Utwin request with personalInfo:', personalInfo);

    const formatDateISO = (date: any): string => {
      if (!date) {
        console.warn('Date is null/undefined, using current date');
        return new Date().toISOString();
      }

      let dateObj: Date;
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/');
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        dateObj = new Date(date);
      }

      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date, using current date:', date);
        return new Date().toISOString();
      }

      return dateObj.toISOString();
    };

    const assures: any[] = [];

    // Assuré principal - validation des données
    const principalData = {
      codeRegimeObligatoire: this.mapRegimeToUtwin(personalInfo.regime) || 'SSI',
      codeTypeRole: 'AssurePrincipal',
      codeSexe: personalInfo.civilite === 'Monsieur' || personalInfo.civilite === 'M' ? 'M' : 'F',
      codeCivilite: personalInfo.civilite || 'Monsieur',
      codeStatutFamilial: this.mapStatutToUtwin(personalInfo.etatCivil),
      dateDeNaissance: formatDateISO(personalInfo.dateNaissance)
    };
    console.log('Principal assuré data:', principalData);
    assures.push(principalData);

    // Conjoint - Debug détaillé
    console.log('🔍 DEBUG CONJOINT:');
    console.log('- personalInfo.conjoint:', personalInfo.conjoint);
    console.log('- personalInfo.etatCivil:', personalInfo.etatCivil);
    console.log('- conjoint.dateNaissance:', personalInfo.conjoint?.dateNaissance);

    // Logique intelligente : inclure le conjoint si marié OU si conjoint renseigné
    const hasConjointInfo = personalInfo.conjoint && personalInfo.conjoint.dateNaissance;
    if (hasConjointInfo) {
      const conjointDateNaissance = personalInfo.conjoint?.dateNaissance;

      const conjointRegime = personalInfo.conjoint?.regime || personalInfo.regime;

      const conjointData = {
        codeRegimeObligatoire: this.mapRegimeToUtwin(conjointRegime) || 'SSI',
        codeTypeRole: 'Conjoint',
        codeSexe: personalInfo.conjoint?.civilite === 'Monsieur' || personalInfo.conjoint?.civilite === 'M' ? 'M' : 'F',
        codeCivilite: personalInfo.conjoint?.civilite || 'Madame',
        codeStatutFamilial: this.mapStatutToUtwin(personalInfo.conjoint?.etatCivil || personalInfo.etatCivil),
        dateDeNaissance: formatDateISO(conjointDateNaissance)
      };

      console.log('✅ Conjoint ajouté (données complètes):', conjointData);
      assures.push(conjointData);
    } else {
      console.log('❌ Conjoint non ajouté (Utwin) - Conditions:');
      console.log('  - conjoint avec dateNaissance:', hasConjointInfo);
      console.log('  - Note: date de naissance du conjoint requise');
    }

    // Enfants
    if (personalInfo.enfants && Array.isArray(personalInfo.enfants)) {
      personalInfo.enfants.forEach((enfant: any, index: number) => {
        if (enfant && enfant.dateNaissance) {
          const enfantData = {
            codeRegimeObligatoire: this.mapRegimeToUtwin(enfant.regime) || 'SSI',
            codeTypeRole: 'Enfant',
            codeSexe: enfant.sexe === 'garcon' ? 'M' : 'F',
            codeCivilite: enfant.sexe === 'garcon' ? 'Monsieur' : 'Mademoiselle',
            codeStatutFamilial: 'CELIBATAIRE',
            dateDeNaissance: formatDateISO(enfant.dateNaissance)
          };
          console.log(`Enfant ${index} data:`, enfantData);
          assures.push(enfantData);
        }
      });
    }

    const utwinRequest = {
      souscripteur: {
        adresse: {
          codePostal: personalInfo.codePostal || '69000'
          // ville supprimée - non requise par Utwin
        }
      },
      besoin: {
        dateEffet: formatDateISO(personalInfo.dateEffet)
      },
      assures: assures
    };

    console.log('Final Utwin request:', utwinRequest);
    return utwinRequest;
  }

  private mapRegimeToUtwin(regime: string): string {
    return ApiMapperUtil.getUtwinRegime(regime);
  }

  private mapStatutToUtwin(statut: string): string {
    if (!statut) return 'CELIBATAIRE';
    const s = statut.toLowerCase();
    if (s.includes('marie') || s.includes('union')) return 'MARIE';
    if (s.includes('pacse')) return 'PACSE';
    if (s.includes('divorce')) return 'DIVORCE';
    if (s.includes('veuf')) return 'VEUF';
    return 'CELIBATAIRE';
  }

  private buildApiviaQuotePayload(result: ComparisonResult): any {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    if (!personalInfo) return null;

    const formatDate = (date: any): string => {
      if (!date) return '';

      // Si c'est déjà une chaîne au format DD/MM/YYYY, la retourner directement
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        return date;
      }

      if (date instanceof Date && !isNaN(date.getTime())) {
        const day = ('0' + date.getDate()).slice(-2);
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }

      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const day = ('0' + d.getDate()).slice(-2);
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }

      console.warn('Invalid date format:', date);
      return '';
    };

    const getProductCode = (productName: string): string => {
      return ApiMapperUtil.getApiviaProductCode(
        result.contractName,
        result.formule,
        productName || result.assurance
      );
    };

    const apiviaProductCode = getProductCode(result.assurance);

    const getFormulaCode = (formulaName: string): string => {
      if (!formulaName) return '';
      const match = formulaName.match(/\d+/);
      return match ? match[0] : formulaName;
    };

    // typeRegime Apivia : uniquement 'GE' (Général) ou 'AM' (Alsace-Moselle)
    const getTypeRegime = (regime: string): string => {
      const cp = personalInfo.codePostal || '';
      if (cp.startsWith('57') || cp.startsWith('67') || cp.startsWith('68')) return 'AM';
      return ApiMapperUtil.getApiviaRegime(regime);
    };

    const getRegimeSocial = (regime: string): string => {
      return ApiMapperUtil.getApiviaRegimeSocial(
        regime,
        apiviaProductCode,
        personalInfo.categorieSocioProfessionnelle
      );
    };

    const beneficiaires: any[] = [];

    beneficiaires.push({
      typeBeneficiaire: 'PRINCIPAL',
      dateDeNaissance: formatDate(personalInfo.dateNaissance),
      typeRegime: getTypeRegime(personalInfo.regime),
      regimeSocial: getRegimeSocial(personalInfo.regime)
    });

    if (personalInfo.conjoint?.dateNaissance) {
      beneficiaires.push({
        typeBeneficiaire: 'CONJOINT',
        dateDeNaissance: formatDate(personalInfo.conjoint.dateNaissance),
        typeRegime: getTypeRegime(personalInfo.conjoint.regime || personalInfo.regime),
        regimeSocial: getRegimeSocial(personalInfo.conjoint.regime || personalInfo.regime)
      });
    }

    personalInfo.enfants.forEach((enfant: any) => {
      beneficiaires.push({
        typeBeneficiaire: 'AUTRE',
        dateDeNaissance: formatDate(enfant.dateNaissance),
        typeRegime: getTypeRegime(enfant.regime || personalInfo.regime),
        regimeSocial: getRegimeSocial(enfant.regime || personalInfo.regime)
      });
    });

    return {
      action: 'test_generate_pdf_devis',
      beneficiaires: beneficiaires,
      cle: 'f85d0eb55e8e069acb908c1d11af4c6e',
      conseiller: {
        nom: localStorage.getItem('nom') || localStorage.getItem('username') || 'Brokins',
        prenom: localStorage.getItem('prenom') || localStorage.getItem('username') || 'Conseiller',
        typeConseiller: 'SALAR',
        orias: '16004540'
      },
      dateEffet: formatDate(personalInfo.dateEffet),
      dejaAssurance: true,
      format: 'json',
      formule: getFormulaCode(result.formule),
      frais: '15',
      isResiliationPourCompte: true,
      options: [],
      produit: apiviaProductCode,
      renforts: [],
      typeRdb: 'APIVIA',
      souscripteur: {
        typeCivilite: personalInfo.civilite === 'M' ? 'M' : 'MME',
        nom: personalInfo.nom,
        prenom: personalInfo.prenom,
        telephone: personalInfo.telephone1,
        mobile: personalInfo.telephone1, // Assuming mobile is same as phone1
        email: personalInfo.email,
        adresse: {
          numero: '', // Not available in form
          codeBtq: '', // Not available in form
          natureVoie: 'RUE', // Default value required by Apivia API
          nomVoie: personalInfo.adresse,
          complement: '', // Not available in form
          codePostal: personalInfo.codePostal,
          ville: personalInfo.ville,
          isAdresseComplete: true
        }
      },
      tarif: result.tarifGlobal || 0
    };
  }

  // Méthodes pour contrôler les sliders
  disableSliders(): void {
    const slidersGroup = this.insuranceForm.get('coverageSliders');
    if (slidersGroup) {
      slidersGroup.disable();
    }
  }

  enableSliders(): void {
    const slidersGroup = this.insuranceForm.get('coverageSliders');
    if (slidersGroup) {
      slidersGroup.enable();
    }
  }

  // 🚀 NOUVELLE MÉTHODE : Récupérer tous les prix automatiquement
  private fetchAllPricesAutomatically(): void {
    console.log('🚀 Démarrage de la récupération automatique de tous les prix...');

    // Utiliser comparisonResults pour être cohérent
    const resultsToCheck = (Array.isArray(this.comparisonResults) && this.comparisonResults.length > 0)
      ? this.comparisonResults
      : this.results;

    // Compter le nombre total de produits qui nécessitent une tarification
    let totalProductsWaitingForPrice = 0;
    resultsToCheck.forEach(r => {
      const assuranceLower = this.normalizeInsurerName(r.assurance);

      if (assuranceLower.includes('april') || assuranceLower.includes('utwin') ||
        assuranceLower.includes('apicil') ||
        assuranceLower.includes('apivia') ||
        assuranceLower.includes('generali') || assuranceLower.includes('neoliane') ||
        assuranceLower.includes('eca') || assuranceLower.includes('solly') || assuranceLower.includes('azar') ||
        assuranceLower.includes('2ma') || assuranceLower.includes('2maltitude') ||
        this.isZeniooOffer(r.assurance)) {

        totalProductsWaitingForPrice++;
      }
    });

    console.log(`📊 Total de produits en attente de prix: ${totalProductsWaitingForPrice}`);

    // Si aucun produit à tarifer, terminer immédiatement
    if (totalProductsWaitingForPrice === 0) {
      console.log('⚠️ Aucun produit à tarifer, fin du loading');
      this.finalizePriceLoading();
      return;
    }

    // ✅ SOLUTION : Appeler onFormuleSelect() pour chaque produit automatiquement
    console.log('🔄 Lancement automatique de la tarification pour tous les produits...');
    resultsToCheck.forEach((result, index) => {
      const assuranceLower = this.normalizeInsurerName(result.assurance);
      if (assuranceLower.includes('april') || assuranceLower.includes('utwin') ||
        assuranceLower.includes('apicil') ||
        assuranceLower.includes('apivia') ||
        assuranceLower.includes('generali') || assuranceLower.includes('neoliane') ||
        assuranceLower.includes('eca') || assuranceLower.includes('solly') || assuranceLower.includes('azar') ||
        assuranceLower.includes('2ma') || assuranceLower.includes('2maltitude') ||
        this.isZeniooOffer(result.assurance)) {

        console.log(`🎯 [${index + 1}/${totalProductsWaitingForPrice}] Tarification automatique: ${result.assurance} - ${result.formule}`);

        // Appeler la même méthode que le bouton "Choisir"
        this.onFormuleSelect(result, result.formule);
      }
    });

    this.fetchAlptisPrices();

    // Attendre 2 secondes avant de commencer à vérifier (laisser le temps aux requêtes HTTP de démarrer)
    console.log('⏳ Attente de 2 secondes avant de commencer la vérification...');
    setTimeout(() => {
      this.waitForAllPricesToLoad(totalProductsWaitingForPrice);
    }, 2000);
  }

  // Méthode pour attendre que tous les prix soient chargés
  private waitForAllPricesToLoad(totalProducts: number, maxAttempts: number = 30, currentAttempt: number = 0): void {
    // Nettoyer le timeout précédent s'il existe
    if (this.priceCheckTimeout) {
      clearTimeout(this.priceCheckTimeout);
      this.priceCheckTimeout = null;
    }

    // Vérifier combien de produits ont maintenant un prix
    let productsWithPrice = 0;
    let productsStillLoading = 0;

    // 🔍 DEBUG: Vérifier si results et comparisonResults sont synchronisés
    if (currentAttempt === 0) {
      console.log('🔍 DEBUG: results === comparisonResults?', this.results === this.comparisonResults);
      console.log('🔍 DEBUG: results length:', this.results?.length);
      console.log('🔍 DEBUG: comparisonResults length:', this.comparisonResults?.length);
    }

    // Utiliser comparisonResults au lieu de results pour être sûr
    const resultsToCheck = (Array.isArray(this.comparisonResults) && this.comparisonResults.length > 0)
      ? this.comparisonResults
      : this.results;

    resultsToCheck.forEach(r => {
      const assuranceLower = this.normalizeInsurerName(r.assurance);

      if (assuranceLower.includes('april') || assuranceLower.includes('utwin') ||
        assuranceLower.includes('apicil') ||
        assuranceLower.includes('apivia') ||
        assuranceLower.includes('generali') || assuranceLower.includes('neoliane') ||
        assuranceLower.includes('eca') || assuranceLower.includes('solly') || assuranceLower.includes('azar') ||
        this.isZeniooOffer(r.assurance)) {

        if (r.isPricingLoading) {
          productsStillLoading++;
        } else {
          // Un produit a "terminé" s'il a un prix (numérique, N/A, ou Erreur)
          const hasFinalPrice = r.isAprilProduct
            ? (this.isNumeric(r.tarifGlobal) || r.tarifGlobal === 'N/A' || r.tarifGlobal === 'Erreur')
            : (this.isNumeric(r.prix) || r.prix === 'N/A' || r.prix === 'Erreur');

          if (hasFinalPrice) {
            productsWithPrice++;
          } else if (currentAttempt < 3) {
            // Log de debug pour les 3 premières tentatives
            console.log(`🔍 ${r.assurance}: isPricingLoading=${r.isPricingLoading}, isAprilProduct=${r.isAprilProduct}, prix=${r.prix}, tarifGlobal=${r.tarifGlobal}`);
          }
        }
      }
    });

    console.log(`📊 Progression: ${productsWithPrice}/${totalProducts} prix chargés, ${productsStillLoading} en cours... (Tentative ${currentAttempt + 1}/${maxAttempts})`);

    // Si tous les prix sont chargés, terminer immédiatement
    if (productsWithPrice >= totalProducts) {
      console.log(`✅ Chargement terminé: ${productsWithPrice}/${totalProducts} prix récupérés`);
      // Nettoyer le timeout avant de finaliser
      if (this.priceCheckTimeout) {
        clearTimeout(this.priceCheckTimeout);
        this.priceCheckTimeout = null;
      }
      this.finalizePriceLoading();
      return;
    }

    // Si on a dépassé le nombre max de tentatives, terminer quand même
    if (currentAttempt >= maxAttempts) {
      console.log(`⏱️ Timeout atteint: ${productsWithPrice}/${totalProducts} prix récupérés`);
      // Nettoyer le timeout avant de finaliser
      if (this.priceCheckTimeout) {
        clearTimeout(this.priceCheckTimeout);
        this.priceCheckTimeout = null;
      }
      this.finalizePriceLoading();
      return;
    }

    // Si plus aucun loading en cours, terminer immédiatement (pas besoin d'attendre 10 secondes)
    if (productsStillLoading === 0 && currentAttempt >= 2) {
      console.log(`⚠️ Plus de loading en cours: ${productsWithPrice}/${totalProducts} prix récupérés`);
      // Nettoyer le timeout avant de finaliser
      if (this.priceCheckTimeout) {
        clearTimeout(this.priceCheckTimeout);
        this.priceCheckTimeout = null;
      }
      this.finalizePriceLoading();
      return;
    }

    // Sinon, attendre 1 seconde et réessayer
    this.priceCheckTimeout = setTimeout(() => {
      this.waitForAllPricesToLoad(totalProducts, maxAttempts, currentAttempt + 1);
    }, 1000);
  }

  // Méthode pour finaliser le chargement des prix
  private finalizePriceLoading(): void {
    console.log('🏁 finalizePriceLoading() APPELÉ - Timestamp:', new Date().toISOString());

    // Nettoyer tous les timeouts et subscriptions
    if (this.priceCheckTimeout) {
      clearTimeout(this.priceCheckTimeout);
      this.priceCheckTimeout = null;
    }

    // Terminer le loading
    this.submitting = false;
    this.isComparing = false;
    this.enableSliders();

    // Forcer la détection des changements
    this.cdr.detectChanges();

    // ✅ Garder les 10 résultats recommandés affichés
    this.results = [...this.comparisonResults];
    console.log(`🔄 Nouvelle référence du tableau results créée: ${this.results.length}/${this.comparisonResults.length} résultats affichés`);

    // ✅ AFFICHER LE TABLEAU maintenant que tous les prix sont chargés
    this.activeIndex = 2;
    console.log('📊 Affichage du tableau de comparaison avec tous les prix');

    // Compter les prix récupérés (numériques uniquement, pas N/A ou Erreur)
    let successCount = 0;
    let totalProcessed = 0;
    const resultsToCheck = (Array.isArray(this.comparisonResults) && this.comparisonResults.length > 0)
      ? this.comparisonResults
      : this.results;
    resultsToCheck.forEach(r => {
      const assuranceLower = r.assurance?.toLowerCase() || '';
      if (assuranceLower.includes('april') || assuranceLower.includes('utwin') ||
        assuranceLower.includes('apicil') ||
        assuranceLower.includes('apivia') || assuranceLower.includes('alptis') ||
        assuranceLower.includes('generali')) {
        totalProcessed++;
        
        const hasValidPrice = (r.isAprilProduct && this.isNumeric(r.tarifGlobal)) ||
          (!r.isAprilProduct && this.isNumeric(r.prix));

        if (hasValidPrice) {
          successCount++;
        }
      }
    });

    console.log(` Finalisation: ${successCount}/${totalProcessed} prix valides affichés dans le tableau`);
    console.log(' Prix finaux:', this.results.map(r => ({ assurance: r.assurance, prix: r.prix, tarifGlobal: r.tarifGlobal })));

    // 🔒 ENREGISTRER DANS L'HISTORIQUE une seule fois
    if (!this.historyAlreadySaved) {
      // ⚠️ IMPORTANT: Marquer IMMÉDIATEMENT pour éviter les race conditions
      this.historyAlreadySaved = true;
      console.log('🔒 Flag historyAlreadySaved défini à true AVANT l\'enregistrement');
      console.log(' Enregistrement de la comparaison dans l\'historique avec tous les prix...');
      this.saveComparisonToHistory();
      console.log('✅ Enregistrement terminé');
    } else {
      console.log('⚠️ Historique déjà enregistré, skip pour éviter les doublons');
    }

    // Forcer la détection des changements AVANT d'afficher le message
    this.cdr.detectChanges();

    // Utiliser setTimeout pour s'assurer que l'UI est mise à jour
    setTimeout(() => {
      // Forcer une dernière détection des changements
      this.cdr.detectChanges();
    }, 100);
  }

  // Méthode pour annuler la comparaison
  cancelComparison(): void {
    // Nettoyer le timeout de vérification des prix
    if (this.priceCheckTimeout) {
      clearTimeout(this.priceCheckTimeout);
      this.priceCheckTimeout = null;
    }

    if (this.comparisonSubscription) {
      this.comparisonSubscription.unsubscribe();
      this.comparisonSubscription = null;
    }

    this.submitting = false;
    this.isComparing = false;
    this.enableSliders();

    // Forcer la détection des changements
    this.cdr.detectChanges();

    this.messageService.add({
      severity: 'info',
      summary: 'Comparaison Annulée',
      detail: 'Le processus de comparaison a été interrompu.'
    });
  }

  // Méthodes pour l'extraction PDF
  onPdfFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      this.selectedPdfFile = file;
      this.comparisonAttachmentFiles = [file];
      this.pdfExtractionMessage = null;
    } else {
      this.selectedPdfFile = null;
      this.comparisonAttachmentFiles = [];
      this.pdfExtractionMessage = {
        type: 'error',
        text: 'Veuillez sélectionner un fichier PDF valide.'
      };
    }
  }

  extractPdfData(): void {
    if (!this.selectedPdfFile || !this.selectedInsurer || !this.selectedLevelName) {
      this.pdfExtractionMessage = {
        type: 'error',
        text: 'Veuillez sélectionner un fichier PDF, un assureur et indiquer le niveau/formule.'
      };
      return;
    }

    this.isExtractingPdf = true;
    this.pdfExtractionMessage = {
      type: 'info',
      text: 'Extraction en cours... Cela peut prendre quelques instants.'
    };

    const formData = new FormData();
    formData.append('pdf_file', this.selectedPdfFile);
    formData.append('level_name', this.selectedLevelName);

    // URL du service d'extraction PDF (comparateur_brokins-master)
    const extractionUrl = 'http://127.0.0.1:5000/extract';

    this.http.post(extractionUrl, formData).pipe(
      finalize(() => {
        this.isExtractingPdf = false;
      })
    ).subscribe({
      next: (response: any) => {
        console.log('PDF extraction response:', response);
        this.handlePdfExtractionSuccess(response);
      },
      error: (error) => {
        console.error('PDF extraction error:', error);
        this.handlePdfExtractionError(error);
      }
    });
  }

  private handlePdfExtractionSuccess(extractedData: any): void {
    try {
      // Stocker le contrat extrait
      this.extractedContract = extractedData;

      // Nouvelle logique : Analyser automatiquement les valeurs
      this.analyzeExtractedValues(extractedData);

      // Ancienne logique : Mapping des garanties PDF vers les sliders existants
      const guaranteeMapping = this.mapPdfToSliders(extractedData);

      if (guaranteeMapping) {
        // Remplir automatiquement les sliders existants
        this.populateSliders(guaranteeMapping);

        this.pdfExtractionMessage = {
          type: 'success',
          text: 'Extraction réussie ! Les sliders ont été configurés automatiquement selon les types de valeurs détectées.'
        };
      } else {
        this.pdfExtractionMessage = {
          type: 'warn',
          text: 'Extraction réussie mais aucune garantie compatible trouvée.'
        };
      }
    } catch (error) {
      console.error('Error processing extracted data:', error);
      this.pdfExtractionMessage = {
        type: 'error',
        text: 'Erreur lors du traitement des données extraites.'
      };
    }
  }

  private handlePdfExtractionError(error: any): void {
    let errorMessage = 'Erreur lors de l\'extraction du PDF.';

    if (error.status === 0) {
      errorMessage = 'Impossible de se connecter au service d\'extraction. Vérifiez que le serveur est démarré.';
    } else if (error.error?.error) {
      errorMessage = error.error.error;
    }

    this.pdfExtractionMessage = {
      type: 'error',
      text: errorMessage
    };
  }

  /**
   * Nouvelle méthode : Analyse automatique des valeurs extraites
   * Détecte si les valeurs sont en euros ou pourcentage et configure les sliders appropriés
   */
  private analyzeExtractedValues(extractedData: any): void {
    try {
      this.isAnalyzingValues = true;

      console.log('🔍 Analyse automatique des valeurs extraites:', extractedData);

      // Analyser le contrat avec le service ValueAnalyzer
      this.contractAnalysis = this.valueAnalyzerService.analyzeExtractedContract(extractedData);

      console.log('📊 Résultat de l\'analyse:', this.contractAnalysis);

      // Extraire les sliders dynamiques
      this.dynamicSliders = this.contractAnalysis.frontendConfig.sliders;

      // Organiser par catégorie
      this.slidersByCategory = this.contractAnalysis.frontendConfig.formStructure;

      // Activer l'affichage des sliders dynamiques
      this.showDynamicSliders = true;

      // Mettre à jour les sliders existants avec les nouvelles configurations
      this.updateExistingSlidersWithAnalysis();

      // Afficher un résumé de l'analyse
      this.displayAnalysisSummary();

    } catch (error) {
      console.error('❌ Erreur lors de l\'analyse des valeurs:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'Analyse des valeurs',
        detail: 'Impossible d\'analyser automatiquement les valeurs. Utilisation des sliders par défaut.'
      });
    } finally {
      this.isAnalyzingValues = false;
    }
  }

  /**
   * Met à jour les sliders existants avec les configurations analysées
   */
  private updateExistingSlidersWithAnalysis(): void {
    if (!this.contractAnalysis) return;

    this.dynamicSliders.forEach(slider => {
      // Mettre à jour les sliders existants si ils correspondent
      const existingSliderKey = this.findExistingSliderKey(slider.guarantee);

      if (existingSliderKey && this.insuranceForm.get('coverageSliders')?.get(existingSliderKey)) {
        // Mettre à jour la valeur par défaut
        this.insuranceForm.get('coverageSliders')?.get(existingSliderKey)?.setValue(slider.default);

        console.log(`🎛️ Slider ${existingSliderKey} mis à jour: ${slider.default}${slider.unit} (${slider.extractedValue})`);
      }
    });
  }

  /**
   * Trouve la clé du slider existant correspondant à une garantie
   */
  private findExistingSliderKey(guarantee: string): string | null {
    const guaranteeMapping: { [key: string]: string } = {
      'honoraires_chirurgien_optam': 'honoraires',
      'consultation_generaliste_optam': 'honoraires',
      'chambre_particuliere': 'chambreParticuliere',
      'soins_dentaires': 'dentaire',
      'verres_complexes': 'forfaitOptique',
      'implantologie': 'forfaitDentaire',
      'orthodontie': 'orthodontie'
    };

    return guaranteeMapping[guarantee] || null;
  }

  /**
   * Affiche un résumé de l'analyse dans la console et via toast
   */
  private displayAnalysisSummary(): void {
    if (!this.contractAnalysis) return;

    const summary = this.contractAnalysis.analysis.summary;

    console.log('📈 Résumé de l\'analyse automatique:');
    console.log(`   - Total garanties: ${summary.totalGuarantees}`);
    console.log(`   - Valeurs en pourcentage: ${summary.percentageCount}`);
    console.log(`   - Valeurs en euros: ${summary.eurosCount}`);
    console.log(`   - Valeurs inconnues: ${summary.unknownCount}`);

    // Afficher les sliders configurés
    console.log('🎛️ Sliders configurés automatiquement:');
    this.dynamicSliders.forEach(slider => {
      console.log(`   - ${slider.label}: ${slider.extractedValue} → Slider ${slider.min}-${slider.max}${slider.unit}`);
    });

    // Toast informatif
    this.messageService.add({
      severity: 'info',
      summary: 'Analyse automatique terminée',
      detail: `${summary.totalGuarantees} garanties analysées (${summary.percentageCount} en %, ${summary.eurosCount} en €)`,
      life: 5000
    });
  }

  /**
   * Applique le taux CAS unique (paramètres / tableau : localStorage `defaultCode`) puis lance la tarification.
   * Ne fusionne pas les codes CAC par assureur : même liste que la colonne « Commission » du tableau.
   */
  private applyCasCommissionsFromAdminSettingsThenFetchPrices(): void {
    this.applyCasTableCommissionToAllRows();
    this.fetchAllPricesAutomatically();
  }

  /** Taux CAS depuis l’écran commissions / tableau (stockage local). */
  private getCasCommissionFromSettings(): string {
    const raw = this.commissionSettingsService.getCasTableCommissionRaw();
    const n = normalizeCasCommission(raw) || (raw || '').trim();
    if (n && CAS_COMMISSION_LIST.includes(n)) {
      return n;
    }
    return CAS_COMMISSION_FALLBACK;
  }

  private ensureCasCommissionDropdownValue(value: string): void {
    if (!value) return;
    if (this.casCommissionOptions.some((o) => o.value === value)) return;
    this.casCommissionOptions = [...this.casCommissionOptions, { label: value, value }];
  }

  private normalizeText(value: string | undefined | null): string {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private getZeniooProductKey(result: ComparisonResult): string | null {
    if (!result) return null;

    const code = (result.zeniooCodeProduit || '').toUpperCase();
    const fromCode: Record<string, string> = {
      P80: 'ZSE',
      P91: 'ZSEQ',
      P100: 'ZSFO55',
      P110: 'ZSFOF',
      P120: 'ZSEVO',
      P140: 'ZSPX',
      P50: 'ZSI55',
      P60: 'ZSIF'
    };
    if (fromCode[code]) return fromCode[code];

    const assurance = this.normalizeText(result.assurance);
    const formule = this.normalizeText(result.formule);
    const blob = `${assurance} ${formule}`;

    if (blob.includes('focus') && blob.includes('55')) return 'ZSFO55';
    if (blob.includes('senior 2')) return 'ZSS2';
    if (blob.includes('focus') && blob.includes('famille')) return 'ZSFOF';
    if (blob.includes('evolutive')) return 'ZSEVO';
    if (blob.includes('optimum')) return 'ZSOPT';
    if (blob.includes('proximite')) return 'ZSPX';
    if (blob.includes('ciblee 2')) return 'ZSC2';
    if (blob.includes('pro essentiel 2')) return 'ZSPE2';
    if (blob.includes('integrale') && blob.includes('55')) return 'ZSI55';
    if (blob.includes('integrale') && blob.includes('famille')) return 'ZSIF';
    if (blob.includes('pro premium')) return 'ZSPP';
    if (blob.includes('eco')) return 'ZSE';
    if (blob.includes('flexi senior')) return 'ZSFS';
    if (blob.includes('equilibre')) return 'ZSEQ';

    return null;
  }

  getCommissionOptionsForResult(result: ComparisonResult): CommissionOption[] {
    if (!this.isZeniooOffer(result?.assurance)) {
      return this.casCommissionOptions;
    }

    const productKey = this.getZeniooProductKey(result);
    const rule = productKey ? this.zeniooCommissionRules[productKey] : null;
    if (!rule) {
      return this.casCommissionOptions;
    }

    const values = [rule.default, ...rule.allowed].filter(Boolean);
    const uniqueValues = Array.from(new Set(values));
    return uniqueValues.map((value) => ({ label: value, value }));
  }

  private applyDefaultCommissionForResult(result: ComparisonResult): void {
    if (!result) return;

    if (!this.isZeniooOffer(result.assurance)) {
      result.selectedCommission = this.getCasCommissionFromSettings();
      return;
    }

    const options = this.getCommissionOptionsForResult(result);
    if (!options.length) {
      result.selectedCommission = this.getCasCommissionFromSettings();
      return;
    }

    const current = normalizeCasCommission(result.selectedCommission) || (result.selectedCommission || '').trim();
    const allowed = options.map((o) => o.value);
    if (!current || !allowed.includes(current)) {
      result.selectedCommission = options[0].value;
    }
  }

  /** Une seule commission CAS pour toutes les lignes (identique au menu déroulant du tableau). */
  private applyCasTableCommissionToAllRows(): void {
    const chosen = this.getCasCommissionFromSettings();
    this.comparisonResults.forEach((r) => {
      if (this.isZeniooOffer(r.assurance)) {
        this.applyDefaultCommissionForResult(r);
      } else {
        r.selectedCommission = chosen;
        this.ensureCasCommissionDropdownValue(chosen);
      }
      r.selectedForQuote = false;
    });
  }

  /**
   * Applique les couleurs de proximité aux résultats de comparaison
   */
  applyProximityColorsToResults(): void {
    if (!this.comparisonResults || this.comparisonResults.length === 0) {
      return;
    }

    const userNeeds = this.getUserNeedsFromForm();

    this.comparisonResults = this.comparisonResults.map(result => {
      const enhancedResult = { ...result };

      // Calculer les couleurs de proximité pour chaque garantie
      enhancedResult.proximityColors = {};

      // Mapping des garanties du résultat vers les besoins utilisateur
      const guaranteeFields = [
        'hospitalisation', 'honoraires', 'orthodontie',
        'forfaitOptique', 'forfaitDentaire', 'dentaire',
        'chambreParticuliere'
      ];

      guaranteeFields.forEach(field => {
        if (result[field] && userNeeds[field]) {
          const proximityClass = this.proximityService.getCellClass(
            userNeeds[field],
            result[field]
          );
          enhancedResult.proximityColors[field] = proximityClass;
        }
      });

      // Calculer un score global de proximité
      enhancedResult.globalProximityScore = this.calculateGlobalProximityScore(result, userNeeds);
      enhancedResult.globalProximityClass = this.getGlobalProximityClass(enhancedResult.globalProximityScore);

      return enhancedResult;
    });

    console.log('🎨 Couleurs de proximité appliquées aux résultats:', this.comparisonResults);
  }

  /**
   * Affiche la valeur saisie par l'utilisateur (sliders) pour une garantie du tableau.
   */
  getUserNeedDisplay(formControlName: string, unit: string): string {
    const value = this.insuranceForm.get(`coverageSliders.${formControlName}`)?.value;
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (unit === '%') {
      return `${value}%`;
    }
    if (unit === '€') {
      return `${value}€`;
    }
    return `${value}${unit}`;
  }

  /**
   * Extrait les besoins utilisateur depuis le formulaire
   */
  private getUserNeedsFromForm(): any {
    const coverageSliders = this.insuranceForm.get('coverageSliders')?.value || {};

    return {
      hospitalisation: `${coverageSliders.hospitalisation || 100}%`,
      honoraires: `${coverageSliders.honoraires || 100}%`,
      orthodontie: `${coverageSliders.orthodontie || 150}%`,
      forfaitOptique: `${coverageSliders.forfaitOptique || 190}€`,
      forfaitDentaire: `${coverageSliders.forfaitDentaire || 130}€`,
      dentaire: `${coverageSliders.dentaire || 100}%`,
      chambreParticuliere: `${coverageSliders.chambreParticuliere || 50}€`
    };
  }

  /**
   * Calcule un score global de proximité pour un résultat
   */
  private calculateGlobalProximityScore(result: any, userNeeds: any): number {
    const guaranteeFields = [
      'hospitalisation', 'honoraires', 'orthodontie',
      'forfaitOptique', 'forfaitDentaire', 'dentaire',
      'chambreParticuliere'
    ];

    let totalScore = 0;
    let validFields = 0;

    guaranteeFields.forEach(field => {
      if (result[field] && userNeeds[field]) {
        const proximityInfo = this.proximityService.getProximityInfo(
          userNeeds[field],
          result[field]
        );
        totalScore += proximityInfo.proximityScore;
        validFields++;
      }
    });

    return validFields > 0 ? Math.round(totalScore / validFields) : 0;
  }

  /**
   * Détermine la classe CSS globale selon le score de proximité
   */
  private getGlobalProximityClass(score: number): string {
    if (score >= 95) return 'coverage-identical';
    if (score >= 80) return 'coverage-very-close';
    if (score >= 70) return 'coverage-close';
    if (score >= 50) return 'coverage-somewhat-far';
    if (score >= 30) return 'coverage-far';
    return 'coverage-very-far';
  }

  /**
   * Obtient la classe CSS de proximité pour une cellule spécifique
   */
  getCellProximityClass(result: any, field: string): string {
    if (result.proximityColors && result.proximityColors[field]) {
      return result.proximityColors[field];
    }
    return '';
  }

  private mapPdfToSliders(extractedData: any): any {
    if (!extractedData?.benefits) {
      console.log('❌ Aucun benefits trouvé dans extractedData');
      return null;
    }

    const benefits = extractedData.benefits;
    const mapping: any = {};

    console.log('📋 Mapping des garanties Apivia:', benefits);

    // Mapping selon les correspondances spécifiées
    // "honoraires_chirurgien_optam" -> Hospitalisation
    if (benefits.HOSPITALISATION?.honoraires_chirurgien_optam) {
      console.log('🏥 Hospitalisation:', benefits.HOSPITALISATION.honoraires_chirurgien_optam);
      mapping.hospitalisation = this.extractNumericValue(benefits.HOSPITALISATION.honoraires_chirurgien_optam);
    }

    // "chambre_particuliere" -> Chambre particulière
    if (benefits.HOSPITALISATION?.chambre_particuliere) {
      console.log('🛏️ Chambre particulière:', benefits.HOSPITALISATION.chambre_particuliere);
      mapping.chambreParticuliere = this.extractNumericValue(benefits.HOSPITALISATION.chambre_particuliere);
    }

    // "consultation_generaliste_optam" -> Honoraires
    if (benefits.SOINS_COURANTS?.consultation_generaliste_optam) {
      console.log('👨‍⚕️ Honoraires:', benefits.SOINS_COURANTS.consultation_generaliste_optam);
      mapping.honoraires = this.extractNumericValue(benefits.SOINS_COURANTS.consultation_generaliste_optam);
    }

    // "soins_dentaires" -> Dentaire
    if (benefits.DENTAIRE?.soins_dentaires) {
      console.log('🦷 Dentaire:', benefits.DENTAIRE.soins_dentaires);
      mapping.dentaire = this.extractNumericValue(benefits.DENTAIRE.soins_dentaires);
    }

    // "implantologie" -> Forfait dentaire
    if (benefits.DENTAIRE?.implantologie) {
      console.log('🦷💰 Forfait dentaire:', benefits.DENTAIRE.implantologie);
      mapping.forfaitDentaire = this.extractNumericValue(benefits.DENTAIRE.implantologie);
    }

    // "orthodontie" -> Orthodontie
    if (benefits.DENTAIRE?.orthodontie) {
      console.log('🦷📐 Orthodontie:', benefits.DENTAIRE.orthodontie);
      mapping.orthodontie = this.extractNumericValue(benefits.DENTAIRE.orthodontie);
    }

    // "verres_complexes" -> Forfait optique
    if (benefits.OPTIQUE?.verres_complexes) {
      console.log('👓 Forfait optique:', benefits.OPTIQUE.verres_complexes);
      mapping.forfaitOptique = this.extractNumericValue(benefits.OPTIQUE.verres_complexes);
    }

    console.log('✅ Mapping final:', mapping);
    return Object.keys(mapping).length > 0 ? mapping : null;
  }

  private extractNumericValue(value: string): number {
    if (!value) return NaN; // Utiliser NaN pour un échec clair

    const cleanValue = value.toString().trim();

    // Regex qui cible le premier nombre trouvé dans la chaîne
    const match = cleanValue.match(/(\d*\.?\d+)/);

    if (match && match[0]) {
      const num = parseFloat(match[0]);
      return num;
    }

    return NaN; // Retourner NaN si aucun nombre n'est trouvé
  }

  private populateSliders(mapping: any): void {
    const slidersGroup = this.insuranceForm.get('coverageSliders');
    if (!slidersGroup) return;

    // Remplir chaque slider avec les valeurs extraites
    Object.keys(mapping).forEach(key => {
      const control = slidersGroup.get(key);
      if (control && mapping[key] !== undefined) {
        control.setValue(mapping[key]);
      }
    });

    // Marquer les contrôles comme touchés pour déclencher la validation
    slidersGroup.markAsTouched();
  }

  // Méthode pour afficher les détails des points faibles
  showWeakPointDetails(result: any): void {
    this.selectedWeakPointDetails = result.weakPoint;
    this.showWeakPointDialog = true;
  }

  formatProductInfo(productName: string): { name: string, formula: string } {
    if (!productName) {
      return { name: '', formula: '' };
    }

    // Enlever les étoiles
    let cleanName = productName.replace(/\*\*/g, '').trim();
    let name = cleanName;
    let formula = '';

    // Nouvelle méthode : chercher d'abord les parenthèses
    const parenthesisMatch = cleanName.match(/(.*)\((.*)\)/);
    if (parenthesisMatch) {
      name = parenthesisMatch[1].trim();
      formula = parenthesisMatch[2].trim();
    } else {
      // Ancienne méthode si pas de parenthèses
      const formulaKeywords = ['Niveau', 'Formule', 'Sérénité', 'Tranquillité', 'Niv', 'Niv.', 'For'];
      for (const keyword of formulaKeywords) {
        const regex = new RegExp(`(.*)(${keyword}.*)`, 'i');
        const match = cleanName.match(regex);
        if (match) {
          name = match[1].trim();
          formula = match[2].trim();
          break;
        }
      }

      // Si aucune formule n'a été trouvée avec les mots-clés ci-dessus,
      // gérer les schémas comme "Optima OPTIMA 3", "Dynamique D3", "AltoSanté AS 2", "Énergik EN 6", etc.
      if (!formula) {
        // Optima OPTIMA 3
        let genericMatch = cleanName.match(/(.*)\s(OPTIMA\s*\d+)/i);
        if (genericMatch) {
          name = genericMatch[1].trim();
          formula = genericMatch[2].trim();
        } else {
          // Dynamique D3, D4, D5...
          genericMatch = cleanName.match(/(.*)\s(D\d+)/i);
          if (genericMatch) {
            name = genericMatch[1].trim();
            formula = genericMatch[2].trim();
          } else {
            // AltoSanté AS 1 / AS 2 ...
            genericMatch = cleanName.match(/(.*)\s(AS\s*\d+)/i);
            if (genericMatch) {
              name = genericMatch[1].trim();
              formula = genericMatch[2].trim();
            } else {
              // Énergik EN 6 ...
              genericMatch = cleanName.match(/(.*)\s(EN\s*\d+)/i);
              if (genericMatch) {
                name = genericMatch[1].trim();
                formula = genericMatch[2].trim();
              }
            }
          }
        }
      }
    }

    return { name, formula };
  }

  // Méthode pour obtenir les détails des points faibles
  getDetailedWeakPoints(result: any): string[] {
    const details: string[] = [];

    // Analyser les garanties pour identifier les points faibles
    if (result.garanties) {
      Object.keys(result.garanties).forEach(key => {
        const value = result.garanties[key];
        const garantie = this.garanties.find(g => g.formControlName === key);
        const userValue = this.insuranceForm.get('coverageSliders.' + key)?.value;

        if (garantie && userValue && value < userValue) {
          details.push(`${garantie.label}: Couverture insuffisante (${value}${garantie.unit} vs ${userValue}${garantie.unit} demandé)`);
        } else if (garantie && value === 0) {
          details.push(`${garantie.label}: Non couvert par cette formule`);
        }
      });
    }

    // Ajouter des points faibles spécifiques par assureur
    if (result.assurance.toLowerCase().includes('alptis')) {
      details.push('Réseau de soins limité dans certaines régions');
      details.push('Délais de remboursement parfois plus longs');
    } else if (result.assurance.toLowerCase().includes('apivia')) {
      details.push('Franchise élevée sur certaines prestations');
      details.push('Plafonds annuels restrictifs');
    } else if (result.assurance.toLowerCase().includes('utwin')) {
      details.push('Conditions d\'âge restrictives');
      details.push('Exclusions nombreuses la première année');
    }

    return details;
  }

  private fetchGeneraliPrices(): void {
    console.log('⚠️ GENERALI TNS - Méthode désactivée - Utilisation de fetchAllGeneraliPrices()');
    return; // Désactivé - remplacé par fetchAllGeneraliPrices()

    console.log('🔍 GENERALI TNS - Début de fetchGeneraliPrices');
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;

    const formatDate = (date: any): string => {
      if (!date) return '';

      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        return date;
      }

      if (date instanceof Date && !isNaN(date.getTime())) {
        const day = (`0${date.getDate()}`).slice(-2);
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }

      return '';
    };

    const generaliProducts = this.comparisonResults.filter(result => {
      const assuranceName = result.assurance?.toLowerCase() || '';
      const formuleName = result.formule?.toLowerCase() || '';
      // Exclure les produits déjà traités par Santé Pro
      const alreadyProcessed = (result as any).processedBySantePro;
      // Filtrer seulement les produits TNS (pas Santé Pro) et pas déjà traités
      return assuranceName.includes('generali') &&
        (assuranceName.includes('tns') || formuleName.includes('tns') || formuleName.includes('tnsr')) &&
        !alreadyProcessed;
    });

    console.log('🔍 GENERALI TNS - Produits trouvés:', generaliProducts.length);
    console.log('🔍 GENERALI TNS - Produits détails:', generaliProducts.map(p => ({
      assurance: p.assurance,
      formule: p.formule
    })));

    if (generaliProducts.length === 0) {
      console.log('❌ GENERALI TNS - Aucun produit trouvé');
      return;
    }

    // Calculer l'âge à partir de la date de naissance
    const calculateAge = (birthDate: string): number => {
      if (!birthDate) return 25; // âge par défaut

      const [day, month, year] = birthDate.split('/');
      const birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      return age;
    };

    // Mapper la composition familiale
    const mapCompositionFamiliale = (etatCivil: string, hasConjoint: boolean, hasChildren: boolean): 'isolé' | 'duo' | 'famille' => {
      if (hasConjoint && hasChildren) {
        return 'famille';
      }
      if ((etatCivil === 'marie' || etatCivil === 'unionLibre') && hasConjoint) {
        return 'duo';
      }
      return 'isolé';
    };

    const age = calculateAge(formatDate(personalInfo.dateNaissance));
    const codePostal = personalInfo.codePostal || '75001';
    const hasConjoint = personalInfo.conjoint && personalInfo.conjoint.dateNaissance;
    const hasChildren = personalInfo.enfants && personalInfo.enfants.length > 0;
    const compositionFamiliale = mapCompositionFamiliale(personalInfo.etatCivil, hasConjoint, hasChildren);

    generaliProducts.forEach((result, index) => {
      result.isPricingLoading = true;
      this.cdr.detectChanges();

      // Extraire la formule du nom du produit Generali
      // Extraire le numéro de formule de différents formats possibles
      let formuleNumber = '2'; // Par défaut
      if (result.formule) {
        // Chercher "Niveau X", "Formule X", "TNSR X", "F X", etc.
        const patterns = [
          /Niveau\s*(\d+)/i,
          /Formule\s*(\d+)/i,
          /TNSR\s*(\d+)/i,
          /F\s*(\d+)/i,
          /(\d+)/  // Juste un numéro
        ];

        for (const pattern of patterns) {
          const match = result.formule.match(pattern);
          if (match && match[1]) {
            formuleNumber = match[1];
            break;
          }
        }
      }
      const formule = formuleNumber;
      console.log('Formule extraite de:', result.formule, '-> Numéro de formule:', formule);

      const generaliRequest = {
        age: age,
        codePostal: codePostal,
        compositionFamiliale: compositionFamiliale,
        formule: formule,
        toutesFormules: false
      };

      this.generaliService.getTarification(generaliRequest).subscribe({
        next: (response) => {
          result.isPricingLoading = false;
          const mensuel = response?.tarifMensuel;
          const parsed = typeof mensuel === 'number' ? mensuel : parseFloat(String(mensuel ?? '').replace(',', '.'));
          if (mensuel == null || isNaN(parsed)) {
            this.notifyPricingUnavailable(result, 'empty_response', {
              extraDetail: `Generali : pas de tarif pour la formule ${formule} (âge ${age}, ${compositionFamiliale}).`
            });
          } else {
            result.prix = parsed;
            result.tarifGlobal = response.tarifAnnuel;
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          result.isPricingLoading = false;
          console.error('Erreur tarification Generali:', error);
          this.notifyApiErrorToast({
            summary: 'Erreur tarification',
            fallback: 'Erreur lors de la tarification Generali.',
            error,
            context: 'GENERALI',
            result,
            severity: 'warn'
          });
          this.cdr.detectChanges();
        }
      });
    });
  }

  private fetchGeneraliSanteProPrices(): void {
    console.log('⚠️ GENERALI SANTÉ PRO - Méthode désactivée - Utilisation de fetchAllGeneraliPrices()');
    return; // Désactivé - remplacé par fetchAllGeneraliPrices()

    console.log('🔍 GENERALI SANTÉ PRO - Début de fetchGeneraliSanteProPrices');
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;

    const formatDate = (date: any): string => {
      if (!date) return '';

      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        return date;
      }

      if (date instanceof Date && !isNaN(date.getTime())) {
        const day = (`0${date.getDate()}`).slice(-2);
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }

      return '';
    };

    // Filtrer les produits Generali Santé Pro (seulement ceux qui contiennent "santé pro")
    const generaliSanteProProducts = this.comparisonResults.filter(result => {
      const assuranceName = result.assurance?.toLowerCase() || '';
      const formuleName = result.formule?.toLowerCase() || '';
      // Filtrer seulement les produits Santé Pro (pas TNS)
      const isSantePro = assuranceName.includes('generali') &&
        (assuranceName.includes('santé pro') ||
          assuranceName.includes('santépro') ||
          assuranceName.includes('sante pro') ||
          formuleName.includes('santé pro') ||
          formuleName.includes('santépro') ||
          formuleName.includes('sante pro') ||
          // Détecter par formule P (P0, P1, P2, P3, etc.)
          /formule\s*p\d+/i.test(formuleName) ||
          /\bp\d+\b/i.test(formuleName) ||
          /p\d+/i.test(assuranceName));

      console.log('🔍 DEBUG - Produit:', assuranceName, '| Formule:', formuleName, '| isSantePro:', isSantePro);

      return isSantePro && !assuranceName.includes('tns') && !formuleName.includes('tns') && !formuleName.includes('tnsr');
    });

    console.log('🔍 GENERALI SANTÉ PRO - Produits trouvés:', generaliSanteProProducts.length);
    console.log('🔍 GENERALI SANTÉ PRO - Produits détails:', generaliSanteProProducts.map(p => ({
      assurance: p.assurance,
      formule: p.formule
    })));

    // Debug : afficher TOUS les produits Generali pour voir le problème
    const allGeneraliProducts = this.comparisonResults.filter(result => {
      const assuranceName = result.assurance?.toLowerCase() || '';
      return assuranceName.includes('generali');
    });
    console.log('🔍 DEBUG - TOUS les produits Generali:', allGeneraliProducts.map(p => ({
      assurance: p.assurance,
      formule: p.formule
    })));

    if (generaliSanteProProducts.length === 0) {
      console.log('❌ GENERALI SANTÉ PRO - Aucun produit trouvé');
      return;
    }

    // Calculer l'âge à partir de la date de naissance
    const calculateAge = (birthDate: string): number => {
      if (!birthDate) return 25; // âge par défaut

      const [day, month, year] = birthDate.split('/');
      const birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      return age;
    };

    // Mapper la composition des assurés
    const mapCompositionAssures = (etatCivil: string, hasConjoint: boolean, hasChildren: boolean): string => {
      if (hasConjoint && hasChildren) {
        return 'Assuré, conjoint et enfants';
      }
      if (hasConjoint) {
        return 'Assuré et conjoint';
      }
      if (hasChildren) {
        const nombreEnfants = personalInfo.enfants?.length || 1;
        return nombreEnfants === 1 ? 'Assuré et un seul enfant' : 'Assuré et plus d\'un enfant';
      }
      return 'Assuré seul';
    };

    const mapSituationFamiliale = (etatCivil: string): string => {
      switch (etatCivil) {
        case 'marie': return 'Marié';
        case 'celibataire': return 'Célibataire';
        case 'unionLibre': return 'PACS';
        case 'separe': return 'Divorcé';
        case 'veuf': return 'Veuf';
        default: return 'Célibataire';
      }
    };

    const age = calculateAge(formatDate(personalInfo.dateNaissance));
    const codePostal = personalInfo.codePostal || '75001';
    const hasConjoint = personalInfo.conjoint && personalInfo.conjoint.dateNaissance;
    const hasChildren = personalInfo.enfants && personalInfo.enfants.length > 0;
    const compositionAssures = mapCompositionAssures(personalInfo.etatCivil, hasConjoint, hasChildren);
    const situationFamiliale = mapSituationFamiliale(personalInfo.etatCivil);
    const nombreEnfants = personalInfo.enfants?.length || 0;

    generaliSanteProProducts.forEach((result, index) => {
      // Marquer comme traité par Santé Pro pour éviter le double traitement
      (result as any).processedBySantePro = true;
      result.isPricingLoading = true;
      this.cdr.detectChanges();

      // Extraire la formule du nom du produit Generali Santé Pro
      let formule = 'P0'; // Par défaut
      if (result.formule) {
        // Chercher "P0", "P1", "P2", "P3", "P4", "P5", etc.
        const patterns = [
          /P(\d+)/i,
          /Niveau\s*(\d+)/i,
          /Formule\s*(\d+)/i
        ];

        for (const pattern of patterns) {
          const match = result.formule.match(pattern);
          if (match && match[1]) {
            formule = 'P' + match[1];
            break;
          }
        }
      }

      const generaliSanteProRequest = {
        age: age,
        codePostal: codePostal,
        formule: formule,
        compositionAssures: compositionAssures,
        situationFamiliale: situationFamiliale,
        nombreEnfants: nombreEnfants,
        anneeEffet: new Date().getFullYear(),
        toutesFormules: false
      };

      console.log('🚀 GENERALI SANTÉ PRO - Requête:', generaliSanteProRequest);

      this.generaliSanteProService.getTarification(generaliSanteProRequest).subscribe({
        next: (response) => {
          result.isPricingLoading = false;
          const mensuel = response?.cotisationMensuelle;
          const parsed = typeof mensuel === 'number' ? mensuel : parseFloat(String(mensuel ?? '').replace(',', '.'));
          if (mensuel == null || isNaN(parsed)) {
            this.notifyPricingUnavailable(result as ComparisonResult, 'empty_response', {
              extraDetail: `Generali Santé Pro : pas de tarif pour la formule ${formule}.`
            });
          } else {
            result.pricingDetail = undefined;
            result.prix = parsed;
            result.tarifGlobal = response.cotisationAnnuelle;
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          result.isPricingLoading = false;
          console.error('❌ GENERALI SANTÉ PRO - Erreur:', error);
          this.notifyApiErrorToast({
            summary: 'Erreur tarification',
            fallback: 'Erreur lors de la tarification Generali Santé Pro.',
            error,
            context: 'GENERALI',
            result: result as ComparisonResult,
            severity: 'warn'
          });
          this.cdr.detectChanges();
        }
      });
    });
  }

  private fetchAllGeneraliPrices(): void {
    console.log('🔍 GENERALI UNIVERSAL - Début de fetchAllGeneraliPrices');
    console.log('🔍 GENERALI UNIVERSAL - comparisonResults length:', this.comparisonResults?.length);
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;

    // Filtrer TOUS les produits Generali
    const allGeneraliProducts = this.comparisonResults.filter(result => {
      const assuranceName = result.assurance?.toLowerCase() || '';
      const isGenerali = assuranceName.includes('generali');
      if (isGenerali) console.log('🔍 Generali trouvé dans comparisonResults:', result.assurance, '| Formule:', result.formule, '| Prix Actuel:', result.prix);
      return isGenerali;
    });

    console.log('🔍 GENERALI UNIVERSAL - Tous les produits Generali trouvés:', allGeneraliProducts.length);
    if (allGeneraliProducts.length === 0) {
      console.log('❌ GENERALI UNIVERSAL - Aucun produit trouvé dans comparisonResults. Abandon de la tarification.');
      return;
    }

    // Pour chaque produit, déterminer le bon endpoint
    allGeneraliProducts.forEach((result, index) => {
      const assuranceName = (result.assurance || '').toLowerCase();
      const formuleName = (result.formule || '').toLowerCase();

      // Détecter si c'est Santé Pro ou TNS
      // Santé Pro : mots-clés spécifiques ou formule commençant par P
      const isSantePro = assuranceName.includes('santé pro') ||
        assuranceName.includes('santépro') ||
        assuranceName.includes('sante pro') ||
        formuleName.includes('santé pro') ||
        formuleName.includes('santépro') ||
        formuleName.includes('sante pro') ||
        /^p\d+/i.test(formuleName) ||
        /formule\s*p\d+/i.test(formuleName) ||
        /\bp\d+\b/i.test(formuleName);

      // TNS : mots-clés TNS ou TNSR
      const isTNS = assuranceName.includes('tns') ||
        formuleName.includes('tns') ||
        formuleName.includes('tnsr');

      console.log(`🔍 [${index + 1}/${allGeneraliProducts.length}] Analyse Generali:`, {
        assurance: result.assurance,
        formule: result.formule,
        isSantePro,
        isTNS
      });

      if (isSantePro && !isTNS) {
        console.log('✅ Utilisation API Santé Pro');
        this.callGeneraliSanteProAPI(result, personalInfo);
      } else {
        // Par défaut, si non identifié Santé Pro, on utilise TNS (plus commun pour Generali)
        console.log('✅ Utilisation API TNS');
        this.callGeneraliTNSAPI(result, personalInfo);
      }
    });
  }

  private callGeneraliSanteProAPI(result: any, personalInfo: any): void {
    result.isPricingLoading = true;
    this.cdr.detectChanges();

    const formatDate = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
      if (date instanceof Date && !isNaN(date.getTime())) {
        const day = (`0${date.getDate()}`).slice(-2);
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return '';
    };

    const calculateAge = (birthDate: string): number => {
      if (!birthDate) return 25;
      const [day, month, year] = birthDate.split('/');
      const birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    };

    const mapCompositionAssures = (etatCivil: string, hasConjoint: boolean, hasChildren: boolean): string => {
      if (hasConjoint && hasChildren) return 'Assuré, conjoint et enfants';
      if (hasConjoint) return 'Assuré et conjoint';
      if (hasChildren) {
        const nombreEnfants = personalInfo.enfants?.length || 1;
        return nombreEnfants === 1 ? 'Assuré et un seul enfant' : 'Assuré et plus d\'un enfant';
      }
      return 'Assuré seul';
    };

    const mapSituationFamiliale = (etatCivil: string): string => {
      switch (etatCivil) {
        case 'marie': return 'Marié';
        case 'celibataire': return 'Célibataire';
        case 'unionLibre': return 'PACS';
        case 'separe': return 'Divorcé';
        case 'veuf': return 'Veuf';
        default: return 'Célibataire';
      }
    };

    const age = calculateAge(formatDate(personalInfo.dateNaissance));
    const codePostal = personalInfo.codePostal || '75001';
    const hasConjoint = personalInfo.conjoint && personalInfo.conjoint.dateNaissance;
    const hasChildren = personalInfo.enfants && personalInfo.enfants.length > 0;
    const compositionAssures = mapCompositionAssures(personalInfo.etatCivil, hasConjoint, hasChildren);
    const situationFamiliale = mapSituationFamiliale(personalInfo.etatCivil);
    const nombreEnfants = personalInfo.enfants?.length || 0;

    // Extraire la formule
    let formule = 'P0';
    if (result.formule) {
      const formuleLower = this.normalizeInsurerName(result.formule);
      // Cas spécifiques renforts P3
      if (formuleLower.includes('optique') || formuleLower.includes('tranquilite optique') || formuleLower.includes('tranquillite optique')) {
        formule = 'P3TO';
      } else if (formuleLower.includes('dentaire') || formuleLower.includes('tranquilite dentaire') || formuleLower.includes('tranquillite dentaire') || formuleLower.includes('renfort dentaire')) {
        formule = 'P3TD';
      }

      const patterns = [/P(\d+)/i, /Niveau\s*(\d+)/i, /Formule\s*(\d+)/i];
      for (const pattern of patterns) {
        const match = result.formule.match(pattern);
        if (match && match[1]) {
          // Ne pas écraser P3TO/P3TD si déjà détecté
          if (formule !== 'P3TO' && formule !== 'P3TD') {
            formule = 'P' + match[1];
          }
          break;
        }
      }
    }

    const request = {
      age: age,
      codePostal: codePostal,
      formule: formule,
      compositionAssures: compositionAssures,
      situationFamiliale: situationFamiliale,
      nombreEnfants: nombreEnfants,
      anneeEffet: new Date().getFullYear(),
      toutesFormules: false
    };

    console.log('🚀 GENERALI SANTÉ PRO API - Requête:', request);

    this.generaliSanteProService.getTarification(request).subscribe({
      next: (response) => {
        console.log('✅ GENERALI SANTÉ PRO API - Réponse complète:', response);
        result.isPricingLoading = false;
        const mensuel = response?.cotisationMensuelle;
        const parsed = typeof mensuel === 'number' ? mensuel : parseFloat(String(mensuel ?? '').replace(',', '.'));
        if (mensuel == null || isNaN(parsed)) {
          this.notifyPricingUnavailable(result as ComparisonResult, 'empty_response', {
            extraDetail: `Generali Santé Pro : pas de tarif pour la formule ${formule}.`
          });
        } else {
          result.pricingDetail = undefined;
          result.prix = parsed;
          result.tarifGlobal = response.cotisationAnnuelle;
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        result.isPricingLoading = false;
        console.error('❌ GENERALI SANTÉ PRO API - Erreur:', error);
        this.notifyApiErrorToast({
          summary: 'Erreur tarification',
          fallback: 'Erreur lors de la tarification Generali Santé Pro.',
          error,
          context: 'GENERALI',
          result: result as ComparisonResult,
          severity: 'warn'
        });
        this.cdr.detectChanges();
      }
    });
  }

  private callGeneraliTNSAPI(result: any, personalInfo: any): void {
    result.isPricingLoading = true;
    this.cdr.detectChanges();

    const formatDate = (date: any): string => {
      if (!date) return '';
      if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
      if (date instanceof Date && !isNaN(date.getTime())) {
        const day = (`0${date.getDate()}`).slice(-2);
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return '';
    };

    const calculateAge = (birthDate: string): number => {
      if (!birthDate) return 25;
      const [day, month, year] = birthDate.split('/');
      const birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    };

    const mapCompositionFamiliale = (etatCivil: string, hasConjoint: boolean, hasChildren: boolean): 'isolé' | 'duo' | 'famille' => {
      if (hasConjoint && hasChildren) return 'famille';
      if (hasConjoint) return 'duo';
      return 'isolé';
    };

    const age = calculateAge(formatDate(personalInfo.dateNaissance));
    const codePostal = personalInfo.codePostal || '75001';
    const hasConjoint = personalInfo.conjoint && personalInfo.conjoint.dateNaissance;
    const hasChildren = personalInfo.enfants && personalInfo.enfants.length > 0;
    const compositionFamiliale = mapCompositionFamiliale(personalInfo.etatCivil, hasConjoint, hasChildren);

    // Extraire la formule pour TNS
    let formule = '4';
    if (result.formule) {
      const patterns = [/F(\d+)/i, /Formule\s*(\d+)/i, /Niveau\s*(\d+)/i, /(\d+)/];
      for (const pattern of patterns) {
        const match = result.formule.match(pattern);
        if (match && match[1]) {
          formule = match[1];
          break;
        }
      }
    }

    const request = {
      age: age,
      codePostal: codePostal,
      compositionFamiliale: compositionFamiliale,
      formule: formule,
      toutesFormules: false
    };

    console.log('🚀 GENERALI TNS API - Requête:', request);

    this.generaliService.getTarification(request).subscribe({
      next: (response: any) => {
        result.isPricingLoading = false;
        const mensuel = response?.tarifMensuel || response?.tarif_mensuel || response?.cotisationMensuelle;
        const parsed = typeof mensuel === 'number' ? mensuel : parseFloat(String(mensuel ?? '').replace(',', '.'));
        if (mensuel == null || isNaN(parsed)) {
          this.notifyPricingUnavailable(result as ComparisonResult, 'empty_response', {
            extraDetail: `Generali TNS : pas de tarif pour la formule ${formule}.`
          });
        } else {
          result.pricingDetail = undefined;
          result.prix = parsed;
          result.tarifGlobal = response.tarifAnnuel || response.tarif_annuel || response.cotisationAnnuelle;
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        result.isPricingLoading = false;
        console.error('❌ GENERALI TNS API - Erreur:', error);
        this.notifyApiErrorToast({
          summary: 'Erreur tarification',
          fallback: 'Erreur lors de la tarification Generali TNS.',
          error,
          context: 'GENERALI',
          result: result as ComparisonResult,
          severity: 'warn'
        });
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Traitement complet du flux Zenioo: Devis PDF + Offre détaillée + Option souscription
   */
  private handleZeniooQuotation(result: ComparisonResult, payload: any): void {
    if (!payload || !result) {
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Données manquantes pour générer le devis Zenioo',
        context: 'ZENIOO'
      });
      return;
    }

    this.loadingQuote = true;
    const idProjet = `CASHEDI-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // ÉTAPE 1: Générer le document de devis PDF
    console.log('📄 Zenioo: Génération du devis PDF...');
    this.insuranceService.getZeniooQuotationDocument(payload).subscribe({
      next: (response: any) => {
        // Extraire le PDF base64
        const base64Content = this.extractZeniooPdfFromResponse(response);

        if (!base64Content) {
          this.addToastOnce({
            severity: 'warn',
            summary: 'Attention',
            detail: 'Devis vide ou non disponible',
            context: 'ZENIOO'
          });
          this.loadingQuote = false;
          return;
        }

        // Télécharger le PDF
        this.downloadZeniooPDF(base64Content, result.zeniooCodeProduit || 'devis-zenioo');

        // ÉTAPE 2: Proposer la souscription
        this.offerZeniooSubscription(result, payload, idProjet);
      },
      error: (error) => {
        console.error('❌ Erreur génération devis Zenioo:', error);
        this.notifyApiErrorToast({
          summary: 'Erreur Devis',
          fallback: 'Impossible de générer le devis Zenioo.',
          error,
          context: 'ZENIOO'
        });
        this.loadingQuote = false;
      }
    });
  }

  /**
   * Extraire le PDF base64 de la réponse Zenioo (gère plusieurs formats)
   */
  private extractZeniooPdfFromResponse(response: any): string | null {
    if (!response) return null;

    // Cas 1: response.retour.devis
    if (response?.retour?.devis) return response.retour.devis;

    // Cas 2: response.devis directement
    if (response?.devis) return response.devis;

    // Cas 3: response.document
    if (response?.document) return response.document;

    // Cas 4: response.content
    if (response?.content) return response.content;

    // Cas 5: Si response est une string JSON
    if (typeof response === 'string') {
      try {
        const parsed = JSON.parse(response);
        return this.extractZeniooPdfFromResponse(parsed); // Appel récursif
      } catch (e) {
        return null;
      }
    }

    // Cas 6: Si response a une propriété Text
    if (response?.Text && typeof response.Text === 'string') {
      try {
        const parsed = JSON.parse(response.Text);
        return this.extractZeniooPdfFromResponse(parsed); // Appel récursif
      } catch (e) {
        return response.Text;
      }
    }

    return null;
  }

  /**
   * Télécharger le PDF du devis Zenioo
   */
  private downloadZeniooPDF(base64Content: string, productCode: string): void {
    try {
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis-zenioo-${productCode || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.addToastOnce({
        severity: 'success',
        summary: 'Succès',
        detail: 'Devis PDF téléchargé avec succès',
        context: 'ZENIOO'
      });
    } catch (error) {
      console.error('❌ Erreur téléchargement PDF:', error);
      this.addToastOnce({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de télécharger le PDF',
        context: 'ZENIOO'
      });
    }
  }

  /**
   * Proposer à l'utilisateur de procéder à la souscription Zenioo
   */
  private offerZeniooSubscription(result: ComparisonResult, payload: any, idProjet: string): void {
    // Récupérer l'URL de souscription
    console.log('🖊️ Zenioo: Récupération du lien de souscription...');
    this.insuranceService.getZeniooSubscriptionUrl(payload).subscribe({
      next: (response: any) => {
        const subscriptionUrl = response?.url_zenioo || response?.retour?.url_zenioo;

        if (!subscriptionUrl) {
          this.loadingQuote = false;
          this.addToastOnce({
            severity: 'warn',
            summary: 'Info',
            detail: 'Devis généré. Lien de souscription non disponible.',
            context: 'ZENIOO'
          });
          return;
        }

        // Proposer la souscription via dialogue ou notification cliquable
        this.addToastOnce({
          severity: 'info',
          summary: 'Souscription disponible',
          detail: 'Cliquez pour ouvrir le formulaire de souscription Zenioo',
          context: 'ZENIOO',
          life: 10000 // 10 secondes pour laisser le temps à l'utilisateur de cliquer
        });

        // Éventuellement stocker l'URL pour un bouton "Souscrire"
        (result as any).zeniooSubscriptionUrl = subscriptionUrl;

        this.loadingQuote = false;
      },
      error: (error) => {
        console.warn('⚠️ Erreur récupération URL souscription (non bloquant):', error);
        this.loadingQuote = false;
        // Ne pas bloquer l'utilisateur si la souscription n'est pas disponible
      }
    });
  }

  private normalizeInsurerName(name: string | undefined | null): string {
    if (!name) {
      return '';
    }
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  public isZeniooOffer(assurance: string | undefined | null): boolean {
    if (!assurance) return false;
    const norm = this.normalizeInsurerName(assurance);
    return norm.includes('zenioo') || norm.includes('zen sante');
  }
  // Méthode pour obtenir le nom de l'assureur
  getInsurerName(assurance: string): string {
    if (assurance.toLowerCase().includes('alptis')) {
      return 'Alptis';
    } else if (assurance.toLowerCase().includes('apivia')) {
      return 'Apivia';
    } else if (assurance.toLowerCase().includes('generali') && assurance.toLowerCase().includes('santé pro')) {
      return 'Generali Santé Pro';
    } else if (assurance.toLowerCase().includes('generali')) {
      return 'Generali';
    } else if (assurance.toLowerCase().includes('utwin')) {
      return 'Utwin';
    }
    return assurance;
  }

  // Méthode pour obtenir seulement le premier mot du nom de l'assureur (pour le header)
  getInsurerFirstWord(assurance: string): string {
    if (!assurance) {
      return '';
    }

    const lower = assurance.toLowerCase();

    // Cas spécifique ECA : afficher simplement "ECA"
    if (lower.includes('eca')) {
      return 'ECA';
    }

    // Utiliser le premier mot par défaut pour les autres assureurs
    const fullAssuranceName = assurance.trim();
    return fullAssuranceName.split(' ')[0];
  }

  // Méthode pour obtenir le logo d'un assureur
  getInsurerLogo(assurance: string): string {
    const assuranceLower = this.normalizeInsurerName(assurance);

    // Mapping des noms d'assureurs vers les clés du dictionnaire
    if (assuranceLower.includes('2ma') || assuranceLower.includes('acheel') || assuranceLower.includes('3cao') || assuranceLower.includes('2maltitude')) {
      return this.insurerLogos['2ma'];
    } else if (assuranceLower.includes('aesio') || assuranceLower.includes('aésio')) {
      return this.insurerLogos['aesio'];
    } else if (assuranceLower.includes('alptis')) {
      return this.insurerLogos['alptis'];
    } else if (assuranceLower.includes('apicil')) {
      return this.insurerLogos['apicil'];
    } else if (assuranceLower.includes('apivia')) {
      return this.insurerLogos['apivia'];
    } else if (assuranceLower.includes('april')) {
      return this.insurerLogos['april'];
    } else if (assuranceLower.includes('asaf')) {
      return this.insurerLogos['asaf'];
    } else if (assuranceLower.includes('axa')) {
      return this.insurerLogos['axa'];
    } else if (assuranceLower.includes('entoria')) {
      return this.insurerLogos['entoria'];
    } else if (assuranceLower.includes('generali')) {
      return this.insurerLogos['generali'];
    } else if (assuranceLower.includes('harmonie')) {
      return this.insurerLogos['harmonie'];
    } else if (assuranceLower.includes('malakoff') || assuranceLower.includes('humanis')) {
      return this.insurerLogos['malakoff'];
    } else if (assuranceLower.includes('henner')) {
      return this.insurerLogos['henner'];
    } else if (assuranceLower.includes('solly') || assuranceLower.includes('azar')) {
      return this.insurerLogos['solly'];
    } else if (assuranceLower.includes('spvie')) {
      return this.insurerLogos['spvie'];
    } else if (assuranceLower.includes('swiss') || assuranceLower.includes('swisslife')) {
      return this.insurerLogos['swisslife'];
    } else if (assuranceLower.includes('utwin')) {
      return this.insurerLogos['utwin'];
    } else if (assuranceLower.includes('neoliane')) {
      return this.insurerLogos['neoliane'];
    } else if (assuranceLower.includes('zenioo')) {
      return this.insurerLogos['zenioo'];
    } else if (assuranceLower.includes('eca')) {
      return this.insurerLogos['eca'];
    } else if (assuranceLower.includes('smatis')) {
      return this.insurerLogos['smatis'];
    } else if (assuranceLower.includes('mutualia')) {
      return this.insurerLogos['mutualia'];
    } else if (assuranceLower.includes('miltis')) {
      return this.insurerLogos['miltis'];
    }

    // Logo par défaut si non trouvé
    return 'assets/images/logos/default.png';
  }

  // Méthode pour obtenir le reste du nom (sans le premier mot) avec la formule (pour la ligne Produit)
  getProductNameWithoutFirstWord(assurance: string, formule: string): string {
    // La formule contient déjà le reste du nom complet après séparation
    // Donc on retourne directement la formule qui contient "Santé Pro - Niveau 5" par exemple
    return formule;
  }

  // Méthode pour obtenir seulement le nom du produit (en gras)
  getProductNameOnly(assurance: string, formule: string): string {
    // Extraire le nom du produit sans le niveau/formule
    if (formule.includes(' - ')) {
      return formule.split(' - ')[0];
    }

    // Si pas de séparateur, chercher des patterns communs
    const patterns = [
      /^(.*?)\s+(Niveau|Formule|F)\s*\d+/i,
      /^(.*?)\s+(P)\d+/i,
      /^(.*?)\s+\d+$/
    ];

    for (const pattern of patterns) {
      const match = formule.match(pattern);
      if (match) {
        return match[1].trim();
      }
    }

    return formule;
  }

  // Méthode pour obtenir seulement le niveau/formule (poids normal)
  getFormulaLevel(formule: string): string {
    // Extraire le niveau/formule
    if (formule.includes(' - ')) {
      return formule.split(' - ')[1] || '';
    }

    // Si pas de séparateur, chercher des patterns communs
    const patterns = [
      /(Niveau|Formule|F)\s*\d+/i,
      /(P)\d+/i,
      /(\d+)$/
    ];

    for (const pattern of patterns) {
      const match = formule.match(pattern);
      if (match) {
        return match[0];
      }
    }

    return '';
  }

  /**
   * Extrait une valeur en préservant les tirets originaux du JSON
   */
  private extractValuePreservingDashes(value: string | undefined): number | string {
    // Si la valeur n'existe pas ou est undefined
    if (!value) {
      return '-';
    }

    // Si la valeur est explicitement un tiret dans le JSON, la conserver
    if (value === '-' || value.trim() === '-') {
      return '-';
    }

    // Si la valeur est vide ou contient seulement des espaces
    if (value.trim() === '') {
      return '-';
    }

    // Pour les valeurs spéciales comme "cf. grille optique", les conserver telles quelles
    if (value.toLowerCase().includes('cf.') ||
      value.toLowerCase().includes('grille') ||
      value.toLowerCase().includes('non précisé') ||
      value.toLowerCase().includes('non remboursé')) {
      return value;
    }

    // Sinon, utiliser la méthode d'extraction normale
    const numericValue = this.extractNumericValue(value);

    // Si l'extraction numérique échoue (retourne NaN), on affiche un tiret.
    if (isNaN(numericValue)) {
      return '-';
    }

    return numericValue;
  }

  /**
   * Enregistre la comparaison actuelle dans l'historique
   */
  private saveComparisonToHistory(): void {
    try {
      const formData = this.insuranceForm.value;
      const personalInfo = formData.personalInfo || {};
      // ANOMALIE CORRIGEE : le formulaire ne déclare que `personalInfo` et
      // `coverageSliders`. `formData.contactInfo` était toujours `undefined`,
      // donc le téléphone enregistré dans l'historique restait vide. Les
      // coordonnées vivent dans `personalInfo`.
      const contactInfo = {
        telephone: personalInfo.telephone1 || '',
        telephoneFixe: personalInfo.telephoneFixe || '',
      };

      console.log('🔍 DEBUG - FormData:', formData);
      console.log('🔍 DEBUG - PersonalInfo:', personalInfo);
      console.log('🔍 DEBUG - Results avant formatage:', this.results);

      // ⚠️ VALIDATION: Ne pas enregistrer si les données essentielles sont manquantes
      if (!personalInfo.nom || !personalInfo.prenom || !personalInfo.dateNaissance) {
        console.warn('⚠️ Données du formulaire incomplètes, enregistrement annulé');
        console.warn('⚠️ Nom:', personalInfo.nom);
        console.warn('⚠️ Prénom:', personalInfo.prenom);
        console.warn('⚠️ Date naissance:', personalInfo.dateNaissance);
        return; // Ne pas enregistrer
      }

      // ⚠️ VALIDATION: Ne pas enregistrer si aucun résultat
      if (!this.results || this.results.length === 0) {
        console.warn('⚠️ Aucun résultat de comparaison, enregistrement annulé');
        return; // Ne pas enregistrer
      }

      // ⚠️ VALIDATION SUPPLÉMENTAIRE: Vérifier que les résultats ont des prix VALIDES (numériques)
      // Compter combien de produits API ont des prix valides
      let productsWithValidPrice = 0;
      let totalApiProducts = 0;

      this.results.forEach(r => {
        const assuranceLower = r.assurance?.toLowerCase() || '';
        const isApiProduct = assuranceLower.includes('april') || assuranceLower.includes('utwin') ||
          assuranceLower.includes('apicil') ||
          assuranceLower.includes('apivia') || assuranceLower.includes('alptis') ||
          assuranceLower.includes('generali');

        if (isApiProduct) {
          totalApiProducts++;
          const prix = r.prix || r.tarifGlobal;
          // Vérifier que le prix est un nombre valide ET supérieur à 0
          const isValidPrice = typeof prix === 'number' && !isNaN(prix) && prix > 0;

          if (isValidPrice) {
            productsWithValidPrice++;
          }

          console.log(`🔍 Validation ${r.assurance}: prix=${prix}, type=${typeof prix}, isValid=${isValidPrice}`);
        }
      });

      console.log(`📊 Validation finale: ${productsWithValidPrice}/${totalApiProducts} produits API avec prix valides`);

      // Exiger qu'au moins UN produit API ait un prix valide
      if (totalApiProducts > 0 && productsWithValidPrice === 0) {
        console.warn('⚠️ Résultats incomplets (aucun prix numérique valide > 0), enregistrement annulé');
        console.warn('⚠️ Total produits API:', totalApiProducts);
        console.warn('⚠️ Produits avec prix valide:', productsWithValidPrice);
        return; // Ne pas enregistrer
      }

      console.log('✅ Validation OK - Données complètes détectées');
      console.log('🎯 ENREGISTREMENT AUTORISÉ - Timestamp:', new Date().toISOString());

      // Obtenir les informations de l'utilisateur connecté
      const currentUser = this.authService.getCurrentUser();
      if (!currentUser) {
        console.warn('⚠️ Aucun utilisateur authentifié, enregistrement annulé');
        return;
      }

      const userName = `${currentUser.prenom} ${currentUser.nom}`;
      const userEmail = currentUser.email;

      // Préparer les données de comparaison avec la bonne structure
      const comparisonData = {
        userName,
        userEmail,
        formData: {
          // Informations de l'assuré principal
          nomPrincipal: personalInfo.nom || '',
          prenomPrincipal: personalInfo.prenom || '',
          dateNaissancePrincipal: personalInfo.dateNaissance || '',
          civilitePrincipal: personalInfo.civilite || '',

          // Informations du conjoint
          nomConjoint: personalInfo.conjoint?.nom || '',
          prenomConjoint: personalInfo.conjoint?.prenom || '',
          dateNaissanceConjoint: personalInfo.conjoint?.dateNaissance || '',

          // Situation familiale - Calculée en fonction de la composition réelle
          situationFamiliale: this.calculateSituationFamiliale(personalInfo),

          // Enfants
          nombreEnfants: personalInfo.enfants?.length || 0,
          ...this.extractEnfantsData(personalInfo.enfants || []),

          // Autres informations
          regimeObligatoire: personalInfo.regime || '',
          codePostal: personalInfo.codePostal || '',
          dateEffet: personalInfo.dateEffet || '',
          
          // Contact
          telephone: contactInfo.telephone || '',
          telephoneFixe: contactInfo.telephoneFixe || ''
        },
        results: this.results,
        emailValidity: this.emailValidationState.valid === true ? 'valid' : (this.emailValidationState.valid === false ? 'invalid' : 'untested'),

        // Instantané brut du formulaire. Les champs à plat ci-dessus servent à
        // la liste d'historique ; celui-ci permet de rouvrir le projet avec
        // TOUS ses champs, y compris ceux que l'historique n'a jamais stockés
        // (adresse, lieu de naissance, catégorie socioprofessionnelle,
        // curseurs de garanties).
        formulaire: JSON.parse(JSON.stringify(this.insuranceForm.value))
      };

      console.log('📝 DEBUG - Données préparées:', comparisonData);

      // Enregistrer dans l'historique
      this.comparisonHistoryService.addComparison(comparisonData);

      console.log('✅ Comparaison enregistrée dans l\'historique');
    } catch (error) {
      console.error('❌ Erreur lors de l\'enregistrement dans l\'historique:', error);
    }
  }

  /**
   * Calculer la situation familiale réelle en fonction de la composition
   */
  private calculateSituationFamiliale(personalInfo: any): string {
    const hasConjoint = personalInfo.conjoint && personalInfo.conjoint.dateNaissance;
    const hasEnfants = personalInfo.enfants && personalInfo.enfants.length > 0;
    const etatCivil = personalInfo.etatCivil || 'celibataire';

    // Si marié/union libre avec ou sans enfants
    if (etatCivil === 'marie' || etatCivil === 'unionLibre' || etatCivil === 'pacs' || etatCivil === 'concubin') {
      if (hasEnfants) {
        return 'Famille (conjoint + enfants)';
      }
      return 'Couple';
    }

    // Si célibataire/divorcé/veuf avec enfants = parent isolé
    if (hasEnfants) {
      return 'Parent isolé';
    }

    // Sinon, retourner l'état civil de base
    const etatCivilLabels: { [key: string]: string } = {
      'celibataire': 'Célibataire',
      'marie': 'Marié(e)',
      'divorce': 'Divorcé(e)',
      'separe': 'Séparé(e)',
      'veuf': 'Veuf(ve)',
      'parentIsole': 'Parent isolé',
      'unionLibre': 'Union libre',
      'pacs': 'Pacsé(e)',
      'concubin': 'Concubin(e)',
      'situationNonDeclaree': 'Non déclaré'
    };

    return etatCivilLabels[etatCivil] || 'Célibataire';
  }

  /**
   * Extraire les données des enfants du formulaire
   */
  private extractEnfantsData(enfants: any[]): any {
    const enfantsData: any = {};

    enfants.forEach((enfant, index) => {
      const enfantIndex = index + 1;
      enfantsData[`dateNaissanceEnfant${enfantIndex}`] = enfant.dateNaissance || '';
    });

    return enfantsData;
  }

  /**
   * NOUVEAU : Récupère la valeur d'affichage correcte pour une garantie
   */
  getDisplayValue(sliderKey: string): string {
    console.log(`🔍 getDisplayValue appelé pour: ${sliderKey}`);

    // Chercher le slider correspondant dans dynamicSliders avec un matching plus flexible
    const slider = this.dynamicSliders.find(s => {
      const match = s.id === sliderKey ||
        s.id.includes(sliderKey) ||
        sliderKey.includes(s.guarantee) ||
        s.guarantee.includes(sliderKey) ||
        // Matching spécifique pour les garanties
        (sliderKey === 'hospitalisation' && (s.guarantee.includes('honoraires') || s.guarantee.includes('chirurgien'))) ||
        (sliderKey === 'honoraires' && s.guarantee.includes('honoraires')) ||
        (sliderKey === 'chambreParticuliere' && s.guarantee.includes('chambre')) ||
        (sliderKey === 'orthodontie' && s.guarantee.includes('orthodontie')) ||
        (sliderKey === 'forfaitOptique' && s.guarantee.includes('verres')) ||
        (sliderKey === 'forfaitDentaire' && s.guarantee.includes('implantologie'));

      if (match) {
        console.log(`✅ Match trouvé: ${sliderKey} → ${s.guarantee} (${s.extractedValue})`);
      }
      return match;
    });

    if (slider && slider.extractedValue) {
      return slider.extractedValue;
    }

    // Fallback : utiliser la valeur du formulaire avec l'unité appropriée
    const formValue = this.insuranceForm.get('coverageSliders')?.get(sliderKey)?.value;
    if (formValue !== null && formValue !== undefined) {
      // Déterminer l'unité selon le nom de la garantie
      if (sliderKey.includes('chambre') || sliderKey.includes('Optique') || sliderKey.includes('Dentaire')) {
        console.log(`💰 Fallback euros pour ${sliderKey}: ${formValue}€`);
        return `${formValue}€`;
      } else {
        console.log(`📊 Fallback pourcentage pour ${sliderKey}: ${formValue}%`);
        return `${formValue}%`;
      }
    }

    console.log(`❌ Aucune valeur trouvée pour ${sliderKey}`);
    return '0%';
  }

  // ==================== SÉLECTION MULTIPLE POUR ENVOI EMAIL ====================

  allSelected: boolean = false;

  hasSelectedQuoteSelection(): boolean {
    return (this.results || []).some(r => r.selectedForQuote === true);
  }

  toggleSelectAll(checked: boolean): void {
    this.allSelected = checked;
    this.results.forEach(r => r.selectedForQuote = checked);
  }

  onSingleCheckboxChange(): void {
    this.allSelected = this.results.length > 0 && this.results.every(r => r.selectedForQuote === true);
  }

  async sendSelectedQuotesByEmail(type: 'particulier' | 'entreprise' = 'particulier'): Promise<void> {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value;
    const recipientEmail = (personalInfo?.email || '').toString().trim();

    if (!recipientEmail) {
      this.addToastOnce({
        severity: 'error',
        summary: 'Email manquant',
        detail: 'Veuillez renseigner votre email dans le formulaire.',
        context: 'DEVIS'
      });
      return;
    }

    // 1. Filtrer les résultats sélectionnés
    const uniqueSelected = new Map<string, any>();
    (this.results || []).forEach(r => {
      if (r.selectedForQuote === true) {
        const key = `${r.insurer || r.assurance}||${r.contractName || ''}||${r.levelName || r.formule || ''}`;
        if (!uniqueSelected.has(key)) {
          uniqueSelected.set(key, r);
        }
      }
    });

    const selected = Array.from(uniqueSelected.values());

    if (selected.length === 0) {
      this.addToastOnce({
        severity: 'warn',
        summary: 'Aucune sélection',
        detail: 'Veuillez cocher au moins une offre dans le tableau.',
        context: 'DEVIS'
      });
      return;
    }

    this.addToastOnce({
      severity: 'info',
      summary: 'Préparation',
      detail: 'Génération des devis PDF en cours...',
      context: 'DEVIS'
    });

    const userNeeds = this.getUserNeedsFromForm();

    const offersWithPdfs = await Promise.all(
      selected.map(async (r: any) => {
        const productInfo = this.formatProductInfo(r?.formule);
        const price = r?.isAprilProduct ? r?.tarifGlobal : r?.prix;

        const guarantees: any = {};
        (this.garanties || []).forEach(g => {
          guarantees[g.formControlName] = r?.garanties ? r.garanties[g.formControlName] : null;
        });

        let correspondence = r?.correspondencePercentage;
        if (correspondence == null && r) {
          if (r.globalProximityScore != null) {
            correspondence = r.globalProximityScore;
          } else {
            correspondence = this.calculateGlobalProximityScore(r, userNeeds);
          }
        }
        if (typeof correspondence === 'number' && !isFinite(correspondence)) {
          correspondence = 0;
        }

        const offer: any = {
          insurer: r?.insurer || r?.assurance,
          assurance: r?.assurance,
          productName: r?.contractName || productInfo?.name,
          formula: r?.levelName || productInfo?.formula,
          price,
          correspondencePercentage: correspondence ?? 0,
          weakPoint: r?.weakPoint,
          guarantees
        };

        const insurerNorm = this.normalizeInsurerName(r?.assurance || r?.insurer);
        const assuranceLower = insurerNorm.toLowerCase();

        // Génération du PDF pour les assureurs qui disposent d'un endpoint dédié
        try {
          const quotePayload = this.getQuotePayload(r);
          if (quotePayload) {
            let pdfBlob: Blob | null = null;

            if (assuranceLower.includes('april')) {
              // APRIL : intercepter le vrai PDF officiel April depuis Gmail
              // Le backend appelle l'API April (Quotation), April envoie leur email à contact@brokins.fr
              // Le backend scrute Gmail jusqu'à 56s pour récupérer le PDF April joint
              // Timeout Angular = 70s pour laisser le temps au backend de réussir
              try {
                const aprilDocsResponse = await this.insuranceService.generateAprilQuoteDocuments(quotePayload)
                  .pipe(timeout(70000))
                  .toPromise();
                const aprilDocs = Array.isArray(aprilDocsResponse?.documents) ? aprilDocsResponse.documents : [];

                if (aprilDocs.length > 0) {
                  const insurerSlug = this.toSlug(r?.insurer || r?.assurance || 'april');
                  offer.pdfAttachments = aprilDocs
                    .map((doc: any, index: number) => {
                      const rawBase64 = (doc?.contentBase64 || '').toString().trim();
                      if (!rawBase64) return null;
                      const base64 = rawBase64.includes(',') ? rawBase64.split(',')[1] : rawBase64;
                      const name = (doc?.filename || `document-april-${index + 1}.pdf`).toString();
                      const safeName = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
                      return {
                        filename: `devis-${insurerSlug}-${safeName}`,
                        contentType: (doc?.contentType || 'application/pdf').toString(),
                        contentBase64: base64
                      };
                    })
                    .filter((att: any) => !!att?.contentBase64);
                  console.log('[Email PDF] ✅ Vrai PDF APRIL officiel récupéré et joint:', aprilDocs.length, 'document(s)');
                } else {
                  console.warn('[Email PDF] Aucun document APRIL retourné → fallback PDF Brokins');
                }
              } catch (aprilErr) {
                console.warn('[Email PDF] Échec récupération PDF APRIL (timeout ou erreur) → fallback PDF Brokins:', aprilErr);
              }
            } else if (assuranceLower.includes('apivia')) {
              const result = await this.insuranceService.generateApiviaDevisPdf(quotePayload).toPromise();
              pdfBlob = result || null;
            } else if (assuranceLower.includes('utwin')) {
              // UTWIN: ne pas utiliser l'endpoint APRIL /commercial-proposal.
              // Générer une proposition UTWIN puis récupérer le document via son identifiant.
              const utwinResponse = await this.insuranceService.utwinPropositionCommerciale(
                quotePayload,
                r?.formule || '',
                (r as any)?.utwinCodeProduit || 'UTWIN_MULTI',
                'telechargement'
              ).toPromise();

              const propositions = utwinResponse?.propositions || [];
              let propositionDoc: any = null;
              for (const prop of propositions) {
                const docs = prop?.documents || [];
                propositionDoc = docs.find((d: any) => d?.type === 'PropositionCommercialeSante');
                if (propositionDoc) break;
              }

              if (propositionDoc?.identifiant) {
                const docResponse = await this.insuranceService.getUtwinDocumentById(propositionDoc.identifiant).toPromise();
                const rawBase64 = (docResponse?.base64 || '').toString().trim();
                const normalizedBase64 = rawBase64.includes(',')
                  ? rawBase64.split(',')[1]
                  : rawBase64;

                if (normalizedBase64) {
                  const byteCharacters = atob(normalizedBase64);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
                } else {
                  console.warn('[Email PDF] Document UTWIN sans contenu base64:', propositionDoc?.identifiant);
                }
              } else {
                console.warn('[Email PDF] Aucun document PropositionCommercialeSante trouvé pour UTWIN:', utwinResponse);
              }
            } else if (assuranceLower.includes('alptis')) {
              // Uniquement Santé Pro+ : generateEtudePersonnalisee. Select/Protect ont leur propre génération PDF.
              const isSanteProPlus = assuranceLower.includes('sante pro+') ||
                (r?.formule || '').toLowerCase().includes('sante pro+');
              const isFrontaliersSuisses = assuranceLower.includes('frontaliers suisses') ||
                (r?.formule || '').toLowerCase().includes('frontaliers suisses') ||
                (r?.productName || '').toLowerCase().includes('frontaliers suisses');

              if (isSanteProPlus) {
                const alptisPayload = this.buildAlptisQuotePayload(r);
                if (alptisPayload) {
                  const alptisResult = await this.alptisService.generateEtudePersonnalisee(alptisPayload).toPromise();
                  pdfBlob = alptisResult || null;
                }
              } else if (isFrontaliersSuisses) {
                // Remplacement du HTML par le générateur de PDF simple
                const personalInfo = this.insuranceForm.get('personalInfo')?.value;
                const data = {
                  nom: personalInfo?.nom || '',
                  prenom: personalInfo?.prenom || '',
                  dateNaissance: personalInfo?.dateNaissance ? (personalInfo.dateNaissance instanceof Date ? `${personalInfo.dateNaissance.getDate().toString().padStart(2, '0')}/${(personalInfo.dateNaissance.getMonth() + 1).toString().padStart(2, '0')}/${personalInfo.dateNaissance.getFullYear()}` : personalInfo.dateNaissance) : '',
                  adresse: personalInfo?.adresse || '',
                  codePostal: personalInfo?.codePostal || '',
                  ville: personalInfo?.ville || '',
                  email: personalInfo?.email || '',
                  telephone: personalInfo?.telephone || '',
                  assurance: 'Alptis - SANTÉ FRONTALIERS SUISSES',
                  formule: r?.formule || r?.levelName || 'Niveau 1',
                  niveau: r?.niveau || '1',
                  dateEffet: personalInfo?.dateEffet ? (personalInfo.dateEffet instanceof Date ? `${personalInfo.dateEffet.getDate().toString().padStart(2, '0')}/${(personalInfo.dateEffet.getMonth() + 1).toString().padStart(2, '0')}/${personalInfo.dateEffet.getFullYear()}` : personalInfo.dateEffet) : '',
                  prix: price ? price.toString() : (r?.prix ? r.prix.toString() : ''),
                  headerTitle: 'ALPTIS',
                  headerSubtitle: 'Santé Frontaliers Suisses - Devis Personnalisé'
                };
                
                try {
                  const simplePdfResponse = await this.insuranceService.generateSimpleDevisPdf(data).toPromise();
                  if (simplePdfResponse && simplePdfResponse.contentBase64) {
                    const byteCharacters = atob(simplePdfResponse.contentBase64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
                  }
                } catch (err) {
                  console.error('[Email PDF] Erreur génération PDF Alptis Frontaliers:', err);
                }
              }
            } else if (assuranceLower.includes('zenioo')) {
              // Pour Zenioo: récupérer le document de devis (PDF en base64 dans la réponse JSON)
              const zeniooPayload = this.buildZeniooQuotePayload(r, this.insuranceForm.get('personalInfo')?.value);
              if (zeniooPayload) {
                const zeniooResponse = await this.insuranceService.getZeniooQuotationDocument(zeniooPayload).toPromise();
                const base64Content = this.extractZeniooPdfFromResponse(zeniooResponse);
                if (base64Content) {
                  // Convertir base64 en Blob
                  const byteCharacters = atob(base64Content);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
                }
              }
            } else if (assuranceLower.includes('neoliane')) {
              const profileId = (r as any).neolianeProfileId;
              const pricingId = (r as any).neolianePricingId;
              console.log('[Email PDF] Neoliane IDs found:', { profileId, pricingId });

              if (profileId && pricingId) {
                const qReq: NeolianeV2QuotationRequest = {
                  gender: (personalInfo?.civilite === 'M' || personalInfo?.civilite === 'M.') ? 'M' : 'Mme',
                  lastname: personalInfo?.nom || 'Dupont',
                  firstname: personalInfo?.prenom || 'Jean',
                  address: `${personalInfo?.adresse || ''} ${personalInfo?.codePostal || ''} ${personalInfo?.ville || ''}`.trim(),
                  email: personalInfo?.email || ''
                };
                console.log('[Email PDF] Requesting Neoliane V2 PDF with:', qReq);
                pdfBlob = await this.neolianeV2Service.downloadQuotation(profileId, pricingId, qReq).toPromise() || null;
                console.log('[Email PDF] Neoliane PDF received:', !!pdfBlob);
              } else {
                console.warn('[Email PDF] Missing Neoliane IDs for result:', r);
              }
            }

            if (!pdfBlob) {
              try {
                const personalInfo = this.insuranceForm.get('personalInfo')?.value;
                const data = {
                  nom: personalInfo?.nom || '',
                  prenom: personalInfo?.prenom || '',
                  dateNaissance: personalInfo?.dateNaissance ? (personalInfo.dateNaissance instanceof Date ? `${personalInfo.dateNaissance.getDate().toString().padStart(2, '0')}/${(personalInfo.dateNaissance.getMonth() + 1).toString().padStart(2, '0')}/${personalInfo.dateNaissance.getFullYear()}` : personalInfo.dateNaissance) : '',
                  adresse: personalInfo?.adresse || '',
                  codePostal: personalInfo?.codePostal || '',
                  ville: personalInfo?.ville || '',
                  email: personalInfo?.email || '',
                  telephone: personalInfo?.telephone1 || personalInfo?.telephone || '',
                  assurance: r?.assurance || r?.insurer || 'Assurance Santé',
                  formule: r?.formule || r?.levelName || r?.contractName || 'Offre sélectionnée',
                  niveau: r?.niveau || '1',
                  dateEffet: personalInfo?.dateEffet ? (personalInfo.dateEffet instanceof Date ? `${personalInfo.dateEffet.getDate().toString().padStart(2, '0')}/${(personalInfo.dateEffet.getMonth() + 1).toString().padStart(2, '0')}/${personalInfo.dateEffet.getFullYear()}` : personalInfo.dateEffet) : '',
                  prix: price ? price.toString() : (r?.prix ? r.prix.toString() : ''),
                  headerTitle: r?.assurance || r?.insurer || 'BROKINS',
                  headerSubtitle: 'Devis Personnalisé Santé',
                  guarantees: guarantees,
                  userNeeds: userNeeds
                };

                const simplePdfResponse = await this.insuranceService.generateSimpleDevisPdf(data).toPromise();
                if (simplePdfResponse && simplePdfResponse.contentBase64) {
                  const byteCharacters = atob(simplePdfResponse.contentBase64);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
                }
              } catch (e) {
                console.warn('[Email PDF] Échec génération PDF simple:', e);
              }
            }

            if (pdfBlob) {
              const base64 = await this.blobToBase64(pdfBlob);
              const insurerSlug = this.toSlug(r?.insurer || r?.assurance || 'assureur');
              const namePart = this.toSlug(r?.levelName || r?.formule || offer?.formula || 'devis');
              offer.pdfAttachment = {
                filename: `devis-${insurerSlug}-${namePart || 'devis'}.pdf`,
                contentType: 'application/pdf',
                contentBase64: base64
              };
            }
          }
        } catch (error) {
        }

        return offer;
      })
    );

    let currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      const username = localStorage.getItem('username') || '';
      const email = localStorage.getItem('email') || '';
      const telephone = localStorage.getItem('telephone') || '';
      const telephone_fixe = localStorage.getItem('telephone_fixe') || '';
      const photo_url = localStorage.getItem('photo_url') || '';
      currentUser = {
        id: '0',
        username,
        email,
        role: 'USER' as any,
        status: 'ACTIVE' as any,
        telephone,
        telephone_fixe,
        photo_url
      };
    }
    console.log('[Email Debug] Utilisateur connecté:', currentUser);
    const conseillerNom = currentUser?.username || `${currentUser?.prenom || ''} ${currentUser?.nom || ''}`.trim() || '';
    const conseillerEmail = currentUser?.email || '';
    const conseillerTelephone = currentUser?.telephone || '';
    const conseillerTelephoneFixe = (currentUser as any)?.telephone_fixe || '';
    const conseillerPhotoUrl = (currentUser as any)?.photo_url || '';
    console.log('[Email Debug] Conseiller info - Nom:', conseillerNom, 'Téléphone:', conseillerTelephone);

    const civilityCode = (personalInfo?.civilite || '').toString().toUpperCase();
    const recipientCivility =
      (civilityCode === 'M' || civilityCode === 'MONSIEUR' || civilityCode === 'M.') ? 'Monsieur' :
      (civilityCode === 'F' || civilityCode === 'MADAME' || civilityCode === 'MME' || civilityCode === 'MME.') ? 'Madame' :
      '';

    const comparisonAttachments = await this.buildComparisonEmailAttachments();

    const payload: any = {
      recipientEmail,
      recipientFirstName: (personalInfo?.prenom || '').toString(),
      recipientLastName: (personalInfo?.nom || '').toString(),
      recipientCivility,
      phone: (personalInfo?.telephone1 || '').toString(),
      needs: userNeeds,
      offers: offersWithPdfs,
      conseillerNom,
      conseillerEmail,
      conseillerTelephone,
      conseillerTelephoneFixe,
      conseillerPhotoUrl,
      ...(comparisonAttachments.length ? { attachments: comparisonAttachments } : {})
    };

    const endpoint = `${environment.casApiUrl}/api/v1/devis/email/${type}`;
    this.http.post(endpoint, payload).subscribe({
      next: () => {
        // Mettre à jour l'historique avec tous les devis (ceux envoyés auront selectedForQuote = true)
        if (this.results && this.results.length > 0) {
          this.comparisonHistoryService.updateLastSavedResults(this.results);
        }
        
        const pjMsg = comparisonAttachments.length
          ? ` (${comparisonAttachments.length} PDF de comparaison joint(s))`
          : '';
        this.addToastOnce({
          severity: 'success',
          summary: 'Email envoyé',
          detail: `${selected.length} devis sélectionné(s) envoyé(s) par email${pjMsg}.`,
          context: 'DEVIS'
        });
        this.results.forEach(r => r.selectedForQuote = false);
        this.allSelected = false;
      },
      error: (err: any) => {
        const msg = err?.error?.message || err?.message || 'Impossible d\'envoyer le devis par email.';
        this.addToastOnce({
          severity: 'error',
          summary: 'Erreur Email',
          detail: msg,
          context: 'DEVIS'
        });
      }
    });
  }

  /** Pièces jointes PDF uploadées pour la comparaison (contrat actuel, etc.) */
  private async buildComparisonEmailAttachments(): Promise<Array<{
    filename: string;
    contentType: string;
    contentBase64: string;
  }>> {
    const files = this.comparisonAttachmentFiles.length
      ? this.comparisonAttachmentFiles
      : (this.selectedPdfFile ? [this.selectedPdfFile] : []);

    const attachments: Array<{ filename: string; contentType: string; contentBase64: string }> = [];
    const seen = new Set<string>();

    for (const file of files) {
      if (!file || file.type !== 'application/pdf') continue;
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (seen.has(key)) continue;
      seen.add(key);
      try {
        const base64 = await this.blobToBase64(file);
        attachments.push({
          filename: file.name.toLowerCase().endsWith('.pdf') ? file.name : `${file.name}.pdf`,
          contentType: 'application/pdf',
          contentBase64: base64
        });
      } catch (e) {
        console.warn('[Email PDF] Impossible de joindre le PDF de comparaison:', file.name, e);
      }
    }
    return attachments;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private toSlug(input: string): string {
    if (!input) return '';
    return input
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Génère le tableau HTML de comparaison pour l'email
   */
  private buildEmailComparisonTable(selected: any[], userNeeds: any): string {
    const tableWidth = "800";
    const mainColor = "#A102F2";
    const borderStyle = "1px solid #E9D5FF";

    let html = `
      <div align="center" style="margin: 20px 0;">
        <table width="${tableWidth}" cellpadding="0" cellspacing="0" border="0" style="width:${tableWidth}px; border-collapse: collapse; font-family: Arial, sans-serif; background-color: #FFFFFF;">
          <thead>
            <tr bgcolor="${mainColor}">
              <th width="150" style="padding: 12px 8px; border: ${borderStyle}; background-color: ${mainColor}; color: #FFFFFF; text-align: left; font-size: 14px; text-transform: uppercase;">Assureur</th>
              <th style="padding: 12px 8px; border: ${borderStyle}; background-color: ${mainColor}; color: #FFFFFF; text-align: left; font-size: 14px; text-transform: uppercase;">Produit</th>
              <th style="padding: 12px 8px; border: ${borderStyle}; background-color: ${mainColor}; color: #FFFFFF; text-align: center; font-size: 14px; text-transform: uppercase;">Formule</th>
              <th width="100" style="padding: 12px 8px; border: ${borderStyle}; background-color: ${mainColor}; color: #FFFFFF; text-align: center; font-size: 14px; text-transform: uppercase;">Prix</th>
              <th width="120" style="padding: 12px 8px; border: ${borderStyle}; background-color: ${mainColor}; color: #FFFFFF; text-align: center; font-size: 14px; text-transform: uppercase;">Correspondance</th>
            </tr>
          </thead>
          <tbody>
    `;

    selected.forEach((r, index) => {
      const price = r?.isAprilProduct ? r?.tarifGlobal : r?.prix;
      const priceStr = price ? `${price} €` : '-';
      const correspondence = r?.correspondencePercentage ?? r?.globalProximityScore ?? 0;
      const rowBg = (index % 2 === 0) ? '#FFFFFF' : '#FAFAFA';

      html += `
        <tr bgcolor="${rowBg}">
          <td style="padding: 10px 8px; border: ${borderStyle}; background-color: ${rowBg}; font-size: 14px; font-weight: bold; color: #111827; font-family: Arial, sans-serif;">${this.escapeHtml(r?.insurer || r?.assurance || '-')}</td>
          <td style="padding: 10px 8px; border: ${borderStyle}; background-color: ${rowBg}; font-size: 14px; color: #111827; font-family: Arial, sans-serif;">${this.escapeHtml(r?.contractName || '-')}</td>
          <td style="padding: 10px 8px; border: ${borderStyle}; background-color: ${rowBg}; font-size: 14px; text-align: center; color: #111827; font-family: Arial, sans-serif;">${this.escapeHtml(r?.levelName || r?.formule || '-')}</td>
          <td style="padding: 10px 8px; border: ${borderStyle}; background-color: ${rowBg}; font-size: 15px; text-align: center; font-weight: bold; color: ${mainColor}; font-family: Arial, sans-serif;">${priceStr}</td>
          <td style="padding: 10px 8px; border: ${borderStyle}; background-color: ${rowBg}; font-size: 14px; text-align: center; color: #059669; font-weight: bold; font-family: Arial, sans-serif;">${correspondence}%</td>
        </tr>
      `;
    });

    html += `
          </tbody>
        </table>
        <p style="margin-top: 15px; font-size: 11px; color: #666666; font-style: italic;">
          Ceci est un tableau de comparaison généré depuis CASHedi.
        </p>
      </div>
    `;

    return html;
  }

  startZeniooSubscription(result: ComparisonResult): void {
    console.log('Subscribe to Zenioo called for:', result);
    const payload = this.getQuotePayload(result);
    if (!payload) {
      this.addToastOnce({ severity: 'error', summary: 'Erreur', detail: 'Données manquantes pour la souscription Zenioo.', context: 'ZENIOO' });
      return;
    }

    this.loadingQuote = true;
    this.insuranceService.getZeniooSubscriptionUrl(payload).subscribe({
      next: (response: any) => {
        this.loadingQuote = false;
        console.log('Zenioo Subscription Response:', response);
        const rawResp = response?.data ?? response;
        let cleanResponse = rawResp;
        if (rawResp && typeof rawResp === 'object' && typeof rawResp.Text === 'string') {
          try { cleanResponse = JSON.parse(rawResp.Text); } catch { /* keep rawResp */ }
        } else if (typeof rawResp === 'string') {
          try { cleanResponse = JSON.parse(rawResp); } catch { /* keep rawResp */ }
        }
        const url = cleanResponse?.url || cleanResponse?.redirection_url || cleanResponse?.data?.url;
        if (url) {
          window.open(url, '_blank');
          this.addToastOnce({ severity: 'success', summary: 'Redirection', detail: 'Redirection vers le formulaire de souscription Zenioo.', context: 'ZENIOO' });
        } else {
          this.addToastOnce({ severity: 'warn', summary: 'Attention', detail: 'URL de souscription introuvable.', context: 'ZENIOO' });
        }
      },
      error: (err) => {
        this.loadingQuote = false;
        console.error('Erreur Souscription Zenioo:', err);
        this.notifyApiErrorToast({
          summary: 'Erreur',
          fallback: 'Impossible d\'initier la souscription Zenioo.',
          error: err,
          context: 'ZENIOO'
        });
      }
    });

    // Also try to get detailed offer (optional logs)
    this.insuranceService.getZeniooDetailedOffer(payload).subscribe({
      next: (resp) => console.log('Zenioo Detailed Offer:', resp),
      error: (err) => console.error('Error fetching Zenioo detailed offer:', err)
    });
  }

  /**
   * Ouvre le portail de connexion Alptis dans un nouvel onglet
   */
  openAlptisPortal(): void {
    const alptisPortalUrl = 'https://api-portal.alptis.org/user/login';
    window.open(alptisPortalUrl, '_blank');
    this.addToastOnce({
      severity: 'info',
      summary: 'Redirection Alptis',
      detail: 'Vous allez être redirigé vers le portail de souscription Alptis.',
      context: 'ALPTIS'
    });
  }

  /**
   * Ouvre le portail partenaire ECA dans un nouvel onglet
   */
  openEcaPortal(): void {
    const ecaPortalUrl = 'https://partenaire.heomi.fr/login';
    window.open(ecaPortalUrl, '_blank');
    this.addToastOnce({
      severity: 'info',
      summary: 'Redirection ECA',
      detail: 'Vous allez être redirigé vers le portail partenaire ECA.',
      context: 'ECA'
    });
  }

  /**
   * Ouvre le portail de connexion Generali dans un nouvel onglet
   */
  openGeneraliPortal(): void {
    const generaliPortalUrl = 'https://www.monespace.generali.fr/connexion';
    window.open(generaliPortalUrl, '_blank');
    this.addToastOnce({
      severity: 'info',
      summary: 'Redirection Generali',
      detail: 'Vous allez être redirigé vers le portail Generali.',
      context: 'GENERALI'
    });
  }

  /**
   * Ouvre le portail adhérent Apivia dans un nouvel onglet
   */
  openApiviaPortal(): void {
    const apiviaPortalUrl = 'https://adherents.apivia.fr/login#/';
    window.open(apiviaPortalUrl, '_blank', 'noopener,noreferrer');
    this.addToastOnce({
      severity: 'info',
      summary: 'Redirection Apivia',
      detail: 'Vous allez être redirigé vers le portail Apivia.',
      context: 'APIVIA'
    });
  }

  /**
   * Génère un NIR (numéro de sécurité sociale) valide basé sur les infos personnelles
   * Format: 1 (sexe) + 2 (année naissance) + 2 (mois) + 5 (COG) + 3 (ordre) + 2 (clé)
   * COG = Code Officiel Géographique = 2 (département) + 3 (commune INSEE)
   * La clé est calculée avec l'algorithme mod 97
   * 
   * Codes INSEE spéciaux:
   * - Paris (75): 751 + arrondissement (ex: 75101 pour 1er)
   * - Marseille (13): 132 + arrondissement (ex: 13201 pour 1er)  
   * - Lyon (69): 381-389 pour arrondissements (ex: 381 pour 1er)
   */
  private generateValidNIR(personalInfo: any): string {
    console.log('🔍 generateValidNIR - personalInfo reçu:', {
      civilite: personalInfo.civilite,
      dateNaissance: personalInfo.dateNaissance,
      codePostalNaissance: personalInfo.codePostalNaissance,
      villeNaissance: personalInfo.villeNaissance,
      codePostal: personalInfo.codePostal,
      ville: personalInfo.ville
    });

    // Sexe: 1 = homme, 2 = femme
    const sexe = (personalInfo.civilite === 'M' || personalInfo.civilite === 'Monsieur') ? '1' : '2';

    // Date de naissance - extraire année et mois
    let year = '00';
    let month = '01';

    if (personalInfo.dateNaissance) {
      const dateStr = personalInfo.dateNaissance.toString();
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        month = parts[1].padStart(2, '0');
        year = parts[2].slice(-2); // 2 derniers chiffres de l'année
      }
    }

    // Utiliser le code postal de NAISSANCE (pas résidence)
    const codePostalNaissance = personalInfo.codePostalNaissance || personalInfo.codePostal || '69001';
    const villeNaissance = (personalInfo.villeNaissance || personalInfo.ville || 'Lyon').toLowerCase().trim();

    // Département (2 premiers chiffres du code postal)
    const dept = codePostalNaissance.slice(0, 2);

    // Code commune INSEE (3 chiffres)
    let commune = '001';

    // Cas spéciaux pour les grandes villes avec arrondissements
    if (dept === '75') {
      // Paris: arrondissement = derniers chiffres du CP (75001 -> 01, 75016 -> 16)
      const arrondissement = codePostalNaissance.slice(3, 5) || '01';
      commune = arrondissement.padStart(2, '0') + '1'; // 01 -> 011, 16 -> 161
    } else if (dept === '13') {
      // Marseille: arrondissement = derniers chiffres du CP (13001 -> 01, 13016 -> 16)
      if (villeNaissance.includes('marseille')) {
        const arrondissement = codePostalNaissance.slice(3, 5) || '01';
        commune = '2' + arrondissement.padStart(2, '0');
      } else {
        commune = '001';
      }
    } else if (dept === '69') {
      // Lyon: arrondissement = dernier chiffre du CP (69001 -> 1, 69009 -> 9)
      if (villeNaissance.includes('lyon')) {
        const arrondissement = codePostalNaissance.slice(4, 5) || '1'; // Dernier chiffre
        commune = '38' + arrondissement; // 1 -> 381, 9 -> 389
      } else {
        // Hors Lyon dans le Rhône: code INSEE standard (ex: 269 pour Jons)
        commune = '269';
      }
    } else {
      // Autres départements: code commune standard
      commune = '001';
    }

    // Numéro d'ordre sur le registre (3 chiffres)
    const ordre = '001';

    // NIR sans clé (13 chiffres)
    const nir13 = sexe + year + month + dept + commune + ordre;

    // Calcul de la clé de contrôle NIR (algorithme mod 97)
    // La clé = 97 - ((NIR * 100) mod 97)
    const nirBigInt = BigInt(nir13 + '00');
    const remainder = Number(nirBigInt % 97n);
    const checkSum = (97 - remainder).toString().padStart(2, '0');

    const nir = nir13 + checkSum;
    console.log('🔢 NIR généré:', nir);
    console.log('   Détails: sexe=' + sexe + ', année=' + year + ', mois=' + month + ', dept=' + dept + ', commune=' + commune + ', ordre=' + ordre + ', clé=' + checkSum);
    console.log('   Ville naissance normalisée:', villeNaissance, '-> commune:', commune);

    return nir;
  }

  openEconomiserEmailDialog(): void {
    const personalInfo = this.insuranceForm.get('personalInfo')?.value || {};
    this.economiserClientName = (personalInfo.prenom || '') + ' ' + (personalInfo.nom || '');
    this.economiserCivility = personalInfo.civilite || 'Monsieur';
    this.economiserRecipientEmail = personalInfo.email || '';
    this.economiserAssurance = this.selectedInsurer || '';
    this.economiserRef = '';
    this.economiserProduit = this.selectedLevelName || '';
    this.showEconomiserEmailDialog = true;
  }

  closeEconomiserEmailDialog(): void {
    this.showEconomiserEmailDialog = false;
    this.sendingEconomiserEmail = false;
  }

  confirmSendEconomiserEmail(): void {
    const email = (this.economiserRecipientEmail || '').trim();
    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'L\'email du destinataire est requis.'
      });
      return;
    }

    const personalInfo = this.insuranceForm.get('personalInfo')?.value || {};
    const clientName = (this.economiserClientName || '').trim() || 'Client';

    const requestPayload = {
      recipientEmail: email,
      recipientFirstName: personalInfo.prenom || '',
      recipientLastName: personalInfo.nom || '',
      recipientCivility: this.economiserCivility,
      phone: personalInfo.telephone1 || '',
      needs: {
        assurance: (this.economiserAssurance || '').trim(),
        ref: (this.economiserRef || '').trim(),
        produit: (this.economiserProduit || '').trim(),
        regime: personalInfo.regime || ''
      }
    };

    this.sendingEconomiserEmail = true;
    const endpoint = `${environment.casApiUrl}/api/v1/devis/email/economiser`;
    const headers = {
      'Authorization': `Bearer ${this.authService.getToken()}`
    };

    this.http.post(endpoint, requestPayload, { headers }).subscribe({
      next: () => {
        this.sendingEconomiserEmail = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: `L'email ECONOMISER a été envoyé à ${email}.`
        });
        this.closeEconomiserEmailDialog();
      },
      error: (err) => {
        this.sendingEconomiserEmail = false;
        console.error('Erreur lors de l\'envoi de l\'email Economiser:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Échec de l\'envoi de l\'email.'
        });
      }
    });
  }

  private escapeHtml(v: unknown): string {
    const s = v == null ? '' : String(v);
    return s.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // --- SMS LOGIC ---

  prepareSms(type: 'rdv' | 'devis_envoye' | 'relance'): void {
    this.smsType = type;
    this.rdvTime = '14H30';
    this.updateSmsContent();
    const personalInfo = this.insuranceForm?.get('personalInfo')?.value || {};
    this.smsRecipientPhone = personalInfo.telephone1 || personalInfo.telephone || '';
    
    // On ouvre toujours la dialog pour permettre de visualiser et modifier le SMS
    this.showSmsDialog = true;
  }

  updateSmsContent(): void {
    const personalInfo = this.insuranceForm?.get('personalInfo')?.value || {};
    const civ = personalInfo.civilite || ''; 
    const nomFamille = personalInfo.nom || '';
    const prenom = personalInfo.prenom || '';
    
    let greeting = '';
    if (civ.toLowerCase().includes('mme') || civ.toLowerCase().includes('madame')) {
      greeting = `Chère Mme. ${prenom}`;
    } else if (civ.toLowerCase().includes('m.') || civ.toLowerCase().includes('monsieur') || civ.toLowerCase() === 'm') {
      greeting = `Cher M. ${nomFamille}`;
    } else {
      greeting = `Cher(e) ${nomFamille}`.trim();
    }
    
    const user = this.authService.getCurrentUser();
    const signatureName = user ? (user.prenom + ' ' + user.nom) : 'Votre conseiller';
    const signaturePhone = user?.telephone || '0143271243'; // Default to fixed line if no phone
    const signatureEmail = user?.email || 'contact@brokins.fr';

    // Dynamic Date
    const today = new Date();
    const formattedDate = today.toLocaleDateString('fr-FR');
    
    let content = '';

    if (this.smsType === 'rdv') {
      content = `${greeting},

Nous avions RDV téléphonique le ${formattedDate} à ${this.rdvTime} pour des économies sur votre assurance mutuelle Santé.
Nous sommes le 1er courtier en Assurance Mutuelle Santé et Crédit. Nous pouvons atteindre jusqu'à 20% d'économies dès le mois prochain grâce à des accords particuliers avec nos assureurs. 
Vous n'avez rien faire, juste 4 minutes de votre temps suffisent.
Prestation sans frais.

Renseignez-vous sur le sérieux de notre Société de Courtage.

Bien à vous

${signatureName}
${signaturePhone}
${signatureEmail}
Cabinet BROKINS
www.brokins.fr`;
    } else if (this.smsType === 'devis_envoye') {
      // Dynamic Devies & Insurers
      const checkedProducts = this.results?.filter(r => r.selectedForQuote) || [];
      let nbDevis = 4;
      let assureursList: string[] = [];
      
      const formatInsurerName = (r: any): string => {
        let name = r.insurer || r.assurance || r.nomAssureur || 'Partenaire';
        if (typeof name === 'string') {
          // Si le nom contient un tiret (ex: NÉOLIANE - Innov'santé IS3), on ne garde que la première partie
          if (name.includes(' - ')) {
            name = name.split(' - ')[0];
          }
          // On capitalise pour faire plus propre (ex: NÉOLIANE -> Néoliane)
          name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        }
        return name.toString().trim();
      };
      
      if (checkedProducts.length > 0) {
         nbDevis = checkedProducts.length;
         assureursList = checkedProducts.map(formatInsurerName);
      } else if (this.results && this.results.length > 0) {
         nbDevis = Math.min(4, this.results.length);
         assureursList = this.results.slice(0, nbDevis).map(formatInsurerName);
      }

      // Supprimer les doublons (si on a sélectionné 2 Néoliane, on ne l'écrit qu'une fois)
      const assureursUnique = Array.from(new Set(assureursList));
      let assureurs = assureursUnique.length > 0 ? assureursUnique.join(', ') : "nos partenaires";

      content = `${greeting},

Je me permets de revenir vers vous au sujet de votre demande de devis assurance mutuelle Santé.
Je vous ai envoyé nos ${nbDevis} meilleurs devis : ${assureurs}.
N'hésitez pas à revenir vers nous, nous sommes ouverts tous les jours jusqu'à 19h30.

Bien à vous

${signatureName}
${signaturePhone}
${signatureEmail}
Cabinet BROKINS
www.brokins.fr`;
    } else if (this.smsType === 'relance') {
      content = `${greeting},

Vous nous avez sollicités pour des devis en assurance Mutuelle Santé. Nous vous avions envoyé plusieurs devis.
Votre besoin est-il toujours d'actualité ? 
Le cas échéant, pouvons-nous échanger 5 minutes ? 

Bien à vous

${signatureName}
${signaturePhone}
${signatureEmail}
Cabinet BROKINS
www.brokins.fr`;
    }

    this.smsContent = content;
  }

  sendSms(): void {
    if (!this.smsContent.trim()) return;
    
    // Si on est dans le cas Renvoi DEVIS+SMS, on envoie d'abord le devis
    if (this.smsType === 'devis_envoye') {
      this.sendSelectedQuotesByEmail('particulier');
    }

    // Meme correction : le numero est dans `personalInfo.telephone1`, pas
    // dans un groupe `contactInfo` qui n'a jamais existe. Sans cela, l'envoi
    // SMS concluait toujours « numero manquant ».
    const personalInfoSms = this.insuranceForm?.get('personalInfo')?.value || {};
    let phone = personalInfoSms.telephone1 || personalInfoSms.telephoneFixe;
    
    if (!phone) {
      if (this.smsType === 'devis_envoye') {
        // Le boss a dit : si pas de mobile, le devis part quand même (déjà fait ci-dessus)
        this.messageService.add({ severity: 'success', summary: 'Devis envoyé', detail: 'Devis envoyé par email. Aucun SMS envoyé (numéro manquant).' });
        this.showSmsDialog = false;
        return;
      }

      const promptedPhone = prompt("Numéro de téléphone du client introuvable. Veuillez le saisir pour envoyer le SMS (ex: 06... ou +336...) :");
      if (!promptedPhone) {
        return;
      }
      phone = promptedPhone;
    }

    this.sendingSms = true;
    this.ringoverService.sendSms({ toNumber: phone, message: this.smsContent }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Succès', detail: this.smsType === 'devis_envoye' ? 'Devis et SMS envoyés avec succès.' : 'SMS envoyé avec succès.' });
        this.sendingSms = false;
        this.showSmsDialog = false;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Échec de l'envoi du SMS." });
        this.sendingSms = false;
        console.error(err);
      }
    });
  }
}
